export type Language = 'en' | 'es' | 'pt';

export const supportedLanguages: Language[] = ['en', 'es', 'pt'];
export const defaultLanguage: Language = 'en';

type TranslationValue = string | number | boolean | TranslationDictionary | TranslationValue[];
interface TranslationDictionary {
  [key: string]: TranslationValue;
}

export const translations: Record<Language, TranslationDictionary> = {
  en: {
    common: {
      appName: 'FlowSoft Funnel Panel',
      creditsExhausted: 'AI credits used up — renews on {{date}}',
      creditsExhaustedNoDate: 'AI credits used up — they renew with your next billing period',
      selectSearchPlaceholder: 'Search…',
      selectNoMatches: 'No matches',
      tagline: 'Track, analyze, and optimize your sales funnel',
      startFreeTrial: 'Start Free Trial',
      watchDemo: 'Watch Demo →',
      languageLabel: 'Language',
      english: 'English',
      spanish: 'Spanish',
      portuguese: 'Portuguese',
      englishShort: 'EN',
      spanishShort: 'ES',
      portugueseShort: 'PT',
      statuses: {
        active: 'Active',
        paused: 'Paused',
        pending: 'Pending',
        processing: 'Processing',
        running: 'Running',
        completed: 'Completed',
        failed: 'Failed',
        queued: 'Queued',
        cancelled: 'Cancelled',
        success: 'Success',
        partialSuccess: 'Partial success',
        error: 'Error',
        live: 'Live',
        offline: 'Offline',
      },
      errors: {
        generic: 'Something went wrong. Please try again.',
      },
      loading: 'Loading...',
      actions: {
        on: 'On',
        off: 'Off',
        cancel: 'Cancel',
        delete: 'Delete',
        confirm: 'Confirm',
        save: 'Save',
        saveChanges: 'Save Changes',
        edit: 'Edit',
        create: 'Create',
        remove: 'Remove',
        add: 'Add',
        clear: 'Clear',
        setPrimary: 'Set as primary',
        close: 'Close',
        goBack: 'Go Back',
        tryAgain: 'Try Again',
        send: 'Send',
        go: 'Go',
        done: 'Done',
        move: 'Move',
        export: 'Export',
        import: 'Import',
        refresh: 'Refresh',
        logout: 'Logout',
        addLead: 'Add Lead',
        revoke: 'Revoke',
        sendInvite: 'Send Invite',
        call: 'Call',
      },
      unsavedChanges: {
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Discard them?',
        discard: 'Discard',
        keepEditing: 'Keep editing',
      },
      offline: 'You are offline. Some features may be unavailable.',
      and: 'and',
      states: {
        loading: 'Loading...',
        saving: 'Saving...',
        creating: 'Creating...',
        processing: 'Processing...',
        on: 'On',
        off: 'Off',
      },
      sources: {
        email: 'Email',
        linkedin: 'LinkedIn',
        instagram: 'Instagram',
        facebook: 'Facebook',
        twitter: 'Twitter',
        form: 'Form',
        scraping: 'Scraping',
        manual: 'Manual',
        website: 'Website',
        referral: 'Referral',
        other: 'Other',
        allSources: 'All Sources',
        webForm: 'Web Form',
        linkedinEnrichment: 'LinkedIn Enrichment',
        linkedinSegmentSearch: 'LinkedIn Segment Search',
        instagramEnrichment: 'Instagram Enrichment',
        instagramSegmentSearch: 'Instagram Segment Search',
        import: 'Import',
        event: 'Event',
      },
      deviceTypes: {
        mobile: 'Mobile',
        desktop: 'Desktop',
        tablet: 'Tablet',
        unknown: 'Unknown',
      },
      agentTypes: {
        leadQualification: 'Lead Qualification',
        leadEnrichment: 'Lead Enrichment',
        followUpScheduler: 'Follow-Up Scheduler',
        sentimentAnalysis: 'Sentiment Analysis',
        promptEnricher: 'Prompt Enricher',
        linkedinCompanyResearch: 'LinkedIn Company Research',
        browserCompanyResearch: 'Web Company Research',
        flowsellaiCompanyResearch: 'FlowSellAI Data Research',
        instagramCompanyResearch: 'Instagram Company Research',
        chatOrchestrator: 'Chat Orchestrator',
        stageProspectIntake: 'Prospect Intake',
        stageFirstContact: 'First Contact',
        stageQualificationDeep: 'Deep Qualification',
        stageProposalPrep: 'Proposal Prep',
        stageWonOnboarding: 'Won Onboarding',
        stageLostAnalysis: 'Lost Analysis',
        linkedinEnrichment: 'LinkedIn Enrichment',
        instagramEnrichment: 'Instagram Enrichment',
        instagramCompanyEnrichment: 'Instagram Company Enrichment',
        companyEnrichment: 'Company Enrichment',
        voiceCall: 'Call summary',
        generic: 'AI agent',
      },
      agentLabels: {
        qualifier: 'Qualifier',
        enricher: 'Enricher',
        scheduler: 'Scheduler',
        analyst: 'Analyst',
      },
      timeRanges: {
        sevenDays: '7D',
        thirtyDays: '30D',
        ninetyDays: '90D',
      },
      fallbacks: {
        unknownLead: 'Unknown Lead',
        unknown: 'Unknown',
        user: 'User',
        member: 'Member',
      },
      detail: 'Detail',
      roles: {
        owner: 'Owner',
        admin: 'Admin',
        member: 'Member',
        viewer: 'Viewer',
      },
    },
    layout: {
      title: 'FlowSellAI',
      login: 'Login',
      dashboard: 'Dashboard',
      analytics: 'Analytics',
      workflows: 'Workflows',
      workflowExecutions: 'Workflow Executions',
      sidebar: {
        brandName: 'FlowsellAI',
        brandSubtitle: 'Command Deck',
        settings: 'Settings',
        logout: 'Logout',
        expandSidebar: 'Expand sidebar',
        collapseSidebar: 'Collapse sidebar',
      },
      mobileTabBar: {
        dashboard: 'Dashboard',
        pipeline: 'Pipeline',
        leads: 'Leads',
        chat: 'Chat',
        calls: 'Calls',
        analytics: 'Analytics',
        settings: 'Settings',
      },
      nav: {
        dashboard: 'Dashboard',
        pipeline: 'Pipeline',
        leads: 'Leads',
        calendar: 'Calendar',
        analytics: 'Analytics',
        chat: 'Chat',
        aiAgents: 'AI Agents',
        missionControl: 'Mission Control',
        workflows: 'Workflows',
        voiceAgent: 'Voice Agent',
        voiceAgents: 'Voice Agents',
        campaigns: 'Campaigns',
        whatsapp: 'WhatsApp',
        leadSources: 'Lead Sources',
        settings: 'Settings',
      },
      navSections: {
        main: 'MAIN',
        insights: 'INSIGHTS',
        automation: 'AUTOMATION',
      },
    },
    auth: {
      title: 'FlowSellAI',
      subtitle: 'Track, analyze, and optimize your sales funnel',
      panelHeadline: 'Close more deals, on autopilot.',
      panelSubhead:
        'FlowSellAI captures, qualifies, and nurtures every lead—so your team only steps in for the conversation that matters.',
      panelSocialProof: 'Founders are joining the waitlist now.',
      getStarted: 'Get started',
      magicLinkHint: "We'll email you a 6-digit code to sign in.",
      checkSpam: "Didn't get it? Check your spam folder.",
      features: {
        analytics: {
          title: 'Real-time Analytics',
          description: 'Monitor your funnel performance instantly',
        },
        conversion: {
          title: 'Conversion Tracking',
          description: 'Track every step of your customer journey',
        },
        leads: {
          title: 'Lead Management',
          description: 'Organize and nurture your leads effectively',
        },
        campaigns: {
          title: 'Campaign Optimization',
          description: 'Improve ROI with data-driven insights',
        },
      },
      cta: 'Continue with Auth0',
      secureNote: 'Secure authentication powered by Auth0',
      footer: '© 2026 FlowSoftDev. All rights reserved.',
      sendMagicLink: 'Send magic link',
      checkInbox: 'Check your inbox',
      enterCode: 'Enter the 6-digit code we emailed you',
      verifyCode: 'Verify code',
      codeInvalid: 'That code is invalid or expired — try again.',
      linkInvalid: 'Magic link no longer valid — request a new one.',
      resendIn: 'Resend in {{seconds}} seconds',
      codeResent: 'Code resent to {{email}}',
      accountDisabled:
        'This account has been disabled. Please contact your workspace administrator.',
      connectionUnavailable: "We're unable to send a link right now. Please try again later.",
      differentEmail: 'Wrong address? Use a different email.',
      inviteMismatch: 'This invite was sent to a different email address.',
      emailTaken: 'This email is already linked to another account.',
      confirmJoinOrg: 'Join {{organizationName}} as {{role}}?',
      invitedBy: 'Invited by {{inviter}}',
      confirmJoin: 'Join',
      declineJoin: 'Not now',
      rateLimited: 'Too many requests. Please wait before trying again.',
      unknownError: 'Something went wrong. Please try again.',
      freeCapacityExhaustedTitle: 'No free spots right now',
      freeCapacityExhausted:
        'Free access is full at the moment. Join the waitlist and we will email you as soon as a spot opens.',
      joinWaitlist: 'Join the waitlist',
      orgDomainExistsTitle: 'Your company already has a workspace',
      orgDomainExists:
        '{{organizationName}} is already set up on FlowSoft. Ask an admin to invite you, then sign in with the same email.',
      orgDomainExistsInline:
        'This company already has a workspace. Ask an admin for an invite, or use a different email.',
      orgDomainExistsOrgFallback: 'Your company',
    },
    dashboard: {
      overviewTitle: 'Dashboard Overview',
      greeting: {
        morning: 'Good morning',
        afternoon: 'Good afternoon',
        evening: 'Good evening',
      },
      header: {
        badge: 'Overview',
        subtitle: "Welcome back! Here's your performance summary for today.",
      },
      metrics: {
        revenue: {
          title: 'Total Revenue',
          subtitle: 'Last 30 days',
        },
        visitors: {
          title: 'Total Visitors',
          subtitle: 'Last 30 days',
        },
        conversion: {
          title: 'Conversion Rate',
          subtitle: 'Leads to Purchases',
        },
        campaigns: {
          title: 'Active Campaigns',
          subtitle: 'Running this month',
        },
        comingSoon: 'Coming soon',
        vsLastPeriod: 'vs last period',
      },
      recentActivity: {
        title: 'Recent Activity',
        viewAll: 'View All',
        loading: 'Loading activity...',
        empty: {
          title: 'No recent activity',
          description: 'Activity from your leads will appear here',
        },
        activities: {
          leadCreated: {
            title: 'New lead added',
            description: 'joined via LinkedIn',
          },
          stageChanged: {
            title: 'Lead moved to Qualified',
            description: 'progressed in the funnel',
          },
          workflowTriggered: {
            title: 'Enrichment completed',
            description: 'Profile data updated for',
          },
          noteAdded: {
            title: 'Note added',
            description: 'Follow-up scheduled with Enterprise lead',
          },
        },
        timeAgo: {
          minutes: 'minutes ago',
          hour: 'hour ago',
          hours: 'hours ago',
        },
      },
      quickActions: {
        title: 'Quick Actions',
        newLead: {
          label: 'New Lead',
          description: 'Add a new lead to your funnel',
        },
        analytics: {
          label: 'Analytics',
          description: 'View performance metrics',
        },
        runWorkflow: {
          label: 'Run Workflow',
          description: 'Trigger an automation',
        },
        voiceCampaign: {
          label: 'Voice Campaign',
          description: 'Launch an AI voice outreach',
        },
      },
      comingSoonTitle: 'Coming Soon',
      comingSoonItems: [
        'Sales Funnel Visualization',
        'Traffic Sources Chart',
        'Geographic Distribution Map',
        'Real-time Analytics',
      ],
      activityTypes: {
        leadCreated: 'Lead created',
        stageChanged: 'Stage changed',
        noteAdded: 'Note added',
        leadAssigned: 'Lead assigned',
        emailSent: 'Email sent',
        activity: 'Activity',
        unknownLead: 'Unknown lead',
      },
    },
    analytics: {
      comingSoon: {
        title: 'Coming Soon',
        predictiveAnalytics: 'Advanced predictive analytics with ML models',
        cohortAnalysis: 'Cohort analysis and customer segmentation',
        funnelBuilder: 'Custom funnel builder with drag-and-drop',
      },
      overview: {
        metrics: {
          leadVelocityRate: 'LEAD VELOCITY RATE',
          leadVelocityRateSubtitle: 'Leads reaching qualified vs last period',
          pipelineScore: 'PIPELINE SCORE',
          pipelineScoreSubtitle: 'Stage-weighted lead count',
          pipelineScoreCoverage_one: 'Stage-weighted count of {{count}} lead in open stages',
          pipelineScoreCoverage_other: 'Stage-weighted count of {{count}} leads in open stages',
          winRate: 'WIN RATE',
          winRateSubtitle: 'Won of everything closed in period',
        },
        untouchedAlert_one: '{{count}} lead has had no contact attempt',
        untouchedAlert_other: '{{count}} leads have had no contact attempt',
        leadsOverTime: 'Leads Over Time',
        leadsOverTimeSubtitle_one: '{{count}} lead created in this period',
        leadsOverTimeSubtitle_other: '{{count}} leads created in this period',
        leadSources: 'Lead Sources',
        leadSourcesSubtitle_one: '{{count}} lead by source',
        leadSourcesSubtitle_other: '{{count}} leads by source',
        conversionFunnel: 'Conversion Funnel',
        activityByDay: 'Activity by Day',
        activityByDaySubtitle: 'AI + human touches, summed by weekday over this period',
        qualificationScore: 'AI Qualification Score',
        qualificationCoverage: '{{scored}} of {{total}} leads scored',
        noActivity: 'No lead activity or qualification scores for this period',
        insights: 'Insights',
        topSource: 'TOP SOURCE',
        topSourceDescription_one: '{{count}} lead from this source',
        topSourceDescription_other: '{{count}} leads from this source',
        busiestStage: 'BUSIEST STAGE',
        busiestStageDescription_one: '{{count}} open lead waiting here',
        busiestStageDescription_other: '{{count}} open leads waiting here',
        busiestStageEmpty: 'No open stage holds a lead',
        newLeads: 'NEW LEADS',
        newLeadsDescription: 'Created in this period',
        weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      },
      funnel: {
        metrics: {
          avgCycleTime: 'AVG CYCLE TIME',
          median: 'Median: {{value}}',
          bottleneck: 'BOTTLENECK',
          bottleneckSubtitle: 'Slowest stage leads actually moved through',
        },
        conversionFunnelSubtitle:
          'Bars: leads sitting in each stage right now. %: share of the entry stage that reached this stage — for closed stages, the share that ended there.',
        timeInStage: {
          title: 'Time in Stage',
          subtitle:
            'Average days taken by leads that moved on from each stage this period — not the leads standing there now.',
        },
        stageDropoff: {
          title: 'Stage Drop-off Analysis',
          subtitle: 'Outcomes of every lead that was in each stage during this period',
          population_one: '{{count}} lead',
          population_other: '{{count}} leads',
          categories: {
            advanced: 'Advanced',
            won: 'Won',
            active: 'Active',
            stale: 'Stale',
            lost: 'Lost',
            regressed: 'Regressed',
          },
        },
      },
      team: {
        metrics: {
          avgResponseTime: 'AVG RESPONSE TIME',
          median: 'Median: {{value}}',
          responseTimeCoverage: 'Median: {{value}} · {{responded}} of {{total}} leads contacted',
          responseTimeFallback: 'Time to first contact',
          untouchedLeads: 'UNTOUCHED LEADS',
          untouchedSubtitle: '{{percentage}} of leads older than {{days}} days',
          untouchedFallback: 'Leads with no contact attempt',
        },
        untouchedAlert_one: '{{count}} lead has had no contact attempt in over {{days}} days',
        untouchedAlert_other: '{{count}} leads have had no contact attempt in over {{days}} days',
        leaderboard: {
          title: 'Agent Leaderboard',
          subtitle: 'Performance ranked by conversion rate',
          subtitleSingle: 'The only agent with leads assigned',
          empty: 'No team performance data available',
          coverageNote_one:
            'Covers {{assigned}} of {{total}} leads. {{count}} unassigned lead is not shown here.',
          coverageNote_other:
            'Covers {{assigned}} of {{total}} leads. {{count}} unassigned leads are not shown here.',
          columns: {
            rank: '#',
            agent: 'AGENT',
            assigned: 'ASSIGNED',
            won: 'WON',
            conversion: 'CONV %',
            cycle: 'CYCLE',
          },
        },
      },
      campaigns: {
        sourcePerformance: {
          title: 'Source Performance',
          subtitle: 'Leads and conversion rate by source',
        },
        deviceBreakdown: {
          title: 'Device Breakdown',
          subtitle: 'Leads by device type',
          centerLabel: 'Leads',
        },
        geo: {
          title: 'Geographic Distribution',
          subtitle: 'Leads by country',
          leads_one: '{{count}} lead',
          leads_other: '{{count}} leads',
        },
        empty: 'No campaign data available for this period',
      },
      sourcePerformance: {
        columns: {
          source: 'SOURCE',
          leadCount: 'LEADS',
          wonCount: 'WON',
          conversionRate: 'CONV %',
        },
        empty: 'No source data available',
      },
      timeInStage: {
        empty: 'No stage data available',
        terminalMarker: 'closed',
        legend: {
          fast: '< 3 days',
          medium: '3-7 days',
          slow: '> 7 days',
          terminal: 'closed outcome',
          notMeasured: '{{dash}} not measured',
        },
      },
      screen: {
        title: 'Analytics',
        subtitle: 'Track performance across your funnel',
      },
      tabs: {
        overview: 'Overview',
        funnel: 'Funnel',
        campaigns: 'Campaigns',
        team: 'Team',
      },
      states: {
        errorTitle: 'Something went wrong',
        errorMessage: 'Failed to load analytics data. Please try again.',
        retry: 'Retry',
        retryAccessibilityLabel: 'Retry loading data',
        emptyTitle: 'Nothing to show yet',
        emptyMessage: 'No data for this period',
      },
      alert: {
        dismiss: 'Dismiss alert',
      },
      units: {
        days: '{{value}} days',
      },
    },
    topbar: {
      dateRange: 'Last 30 days',
      searchPlaceholder: 'Search anything...',
      workspace: 'Workspace',
    },
    landing: {
      hero: {
        badge: 'AI-POWERED SALES AUTOMATION',
        social: 'Founders are joining the waitlist now',
        trust: {
          founding: 'Founding-member pricing',
          noCard: 'No credit card',
          earlyAccess: 'Early access',
        },
        ctaNote: 'No credit card required',
        preTitle: 'Your AI works the funnel.',
        mainTitle: 'Your reps close.',
        titleLead: 'Transform Your',
        titleHighlight: 'Sales Funnel',
        titleTail: 'with Intelligent AI',
        subtitle:
          'FlowSellAI captures, qualifies, and nurtures every lead automatically—your team only steps in for the conversation that needs a human.',
        primaryCta: 'Log In',
        secondaryCta: 'Watch Demo →',
        stats: {
          fasterResponse: 'Faster Response',
          automationRate: 'Automation Rate',
          conversionGrowth: 'Conversion Growth',
        },
      },
      features: {
        badge: 'POWERFUL FEATURES',
        title: 'Everything you need',
        accent: 'to scale',
        subtitle:
          'AI-powered automation that transforms how your sales team works. Close more deals with less effort.',
        learnMore: 'Learn more →',
        sourceFlow: {
          linkedin: 'LinkedIn',
          email: 'Email',
          webForm: 'Web form',
          enrichedLead: 'Enriched lead',
        },
        items: {
          aiLeadCapture: {
            title: 'AI Lead Capture',
            description:
              'Automatically capture and enrich leads from email, LinkedIn, and web forms with intelligent categorization.',
          },
          smartQualification: {
            title: 'Smart Qualification',
            description:
              'AI analyzes engagement, behavior, and fit to score leads and prioritize your highest-value opportunities.',
          },
          automatedNurturing: {
            title: 'Automated Nurturing',
            description:
              'Personalized follow-ups at scale with AI-optimized timing and messaging for each prospect.',
          },
          realTimeAnalytics: {
            title: 'Real-Time Analytics',
            description:
              'Track funnel performance, conversion rates, and AI effectiveness with actionable insights.',
          },
        },
      },
      product: {
        badge: 'THE PRODUCT',
        title: 'Your pipeline,',
        accent: 'fully automated',
        subtitle:
          'Watch every lead move through your funnel—captured, qualified, and nurtured by AI in real time. Your team only steps in to close.',
        board: {
          live: 'LIVE',
          stages: {
            newLead: 'New Lead',
            discovery: 'Discovery',
            proposal: 'Proposal',
            closedWon: 'Closed Won',
          },
        },
        items: {
          pipeline: {
            title: 'Real-time Pipeline',
            description: 'Live updates on every deal',
          },
          aiActivity: {
            title: 'AI Agent Activity',
            description: 'Monitor automation performance',
          },
          analytics: {
            title: 'Advanced Analytics',
            description: 'Data-driven insights',
          },
        },
      },
      foundingMembers: {
        badge: 'FOUNDING MEMBERS',
        title: 'Get in early.',
        accent: 'Help shape what we build',
        subtitle:
          'FlowSellAI is pre-launch. Founding members get more than a discount—they get a seat at the table.',
        benefits: {
          pricing: {
            title: 'Founding pricing, locked for life',
            description: 'Pay the founding-member rate forever—even after public launch.',
          },
          access: {
            title: 'Early access',
            description: 'Get in first as we open in waves. No waiting once you’re in.',
          },
          voice: {
            title: 'A direct line to the team',
            description:
              'Your feedback shapes the roadmap. We build with founding members, not just for them.',
          },
          onboarding: {
            title: 'Priority onboarding',
            description: 'White-glove setup so you’re capturing leads on day one.',
          },
        },
        vision: {
          quote:
            'We’re building the sales tool we always wanted—one system that captures, qualifies, and nurtures every lead, so reps can do what only humans can: close. Join us early and help shape it.',
          attribution: 'The FlowSellAI team',
        },
      },
      pricing: {
        badge: 'PRICING AT LAUNCH',
        title: 'Plans That Scale With You',
        subtitle:
          'Lock in founding-member pricing before we launch. No commitment, no credit card—just your spot in line.',
        subtitleComingSoon:
          'We are finalising our pricing. In the meantime, here is what each plan includes.',
        monthly: 'Monthly',
        yearly: 'Yearly',
        yearlyDiscount: 'Save 20%',
        mostPopular: 'Most Popular',
        included: 'Included',
        notIncluded: 'Not included',
        perMonth: '/mo',
        billedYearly: 'billed yearly',
        from: 'From',
        priceLoading: 'Loading price',
        priceUnavailable: 'Price unavailable',
        comingSoon: 'Coming soon',
        currencyNote: 'Prices shown in your local currency:',
        countrySelector: {
          label: 'Billing region:',
          colombia: 'Colombia (COP)',
          international: 'International (USD)',
        },
        checkoutUnavailable: 'Checkout is not available for this organization or plan right now.',
        checkoutUnavailableCta: 'Checkout unavailable',
        creditsDisclaimer:
          'AI voice is billed via credits. Rates adjust automatically by destination country.',
        usageBased: 'Usage-based',
        plans: {
          starter: {
            name: 'Starter',
            description: 'Perfect for solo founders validating outbound with email-only outreach.',
            cta: 'Start Checkout',
          },
          growth: {
            name: 'Growth',
            description: 'For growing teams scaling outbound with email and AI voice outreach.',
            cta: 'Start Checkout',
          },
          professional: {
            name: 'Professional',
            description:
              'For scaling teams that need higher volume and advanced AI across every channel.',
            cta: 'Start Checkout',
          },
          business: {
            name: 'Business',
            description:
              'For high-volume sales orgs operating across multiple countries with dedicated support.',
            cta: 'Start Checkout',
          },
          enterprise: {
            name: 'Enterprise',
            description:
              'Custom solutions for large organizations with advanced needs and SLA guarantees.',
            cta: 'Talk to Sales',
          },
        },
        features: {
          credits: 'Credits / month',
          aiAgents: 'AI Agents',
          aiLeadGeneration: 'AI Lead Generation',
          emailOutreach: 'Email Outreach',
          whatsapp: 'WhatsApp',
          sms: 'SMS',
          aiVoiceAgent: 'AI Voice Agent',
          aiLeadScoring: 'AI Lead Scoring',
          analytics: 'Analytics',
          workspaces: 'Workspaces',
          teamMembers: 'Team Members',
          support: 'Support',
          onboarding: 'Onboarding',
        },
        // TODO(pricing): confirm Professional/Business feature limits — placeholder values
        values: {
          starter: {
            credits: '25K',
            aiAgents: 'Unlimited (credit-based)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: false,
            sms: false,
            aiVoiceAgent: false,
            aiLeadScoring: 'Basic',
            analytics: 'Basic',
            workspaces: '1',
            teamMembers: '1',
            support: 'Email',
            onboarding: 'Self-serve',
          },
          growth: {
            credits: '150K',
            aiAgents: 'Unlimited (credit-based)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: 'Usage-based',
            sms: 'Usage-based',
            aiVoiceAgent: 'Usage-based',
            aiLeadScoring: 'Advanced',
            analytics: 'Advanced + Forecasting',
            workspaces: '5',
            teamMembers: '2',
            support: 'Priority + Chat',
            onboarding: 'Guided setup',
          },
          // TODO(pricing): confirm Professional feature limits — placeholder values
          professional: {
            credits: '350K',
            aiAgents: 'Unlimited (credit-based)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: 'Usage-based',
            sms: 'Usage-based',
            aiVoiceAgent: 'Usage-based',
            aiLeadScoring: 'Advanced + Custom',
            analytics: 'Advanced + Forecasting',
            workspaces: '12',
            teamMembers: '5',
            support: 'Priority + Chat',
            onboarding: 'Guided setup',
          },
          // TODO(pricing): confirm Business feature limits — placeholder values
          business: {
            credits: '750K',
            aiAgents: 'Unlimited (credit-based)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: 'Usage-based',
            sms: 'Usage-based',
            aiVoiceAgent: 'Usage-based',
            aiLeadScoring: 'Advanced + Custom',
            analytics: 'Full + Custom Reports',
            workspaces: '25',
            teamMembers: '5',
            support: 'Dedicated Manager',
            onboarding: '1-on-1 Implementation',
          },
          enterprise: {
            credits: 'Custom',
            aiAgents: 'Unlimited (credit-based)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: true,
            sms: true,
            aiVoiceAgent: 'Custom',
            aiLeadScoring: 'Custom Models',
            analytics: 'Full + BI Export',
            workspaces: 'Custom',
            teamMembers: '5',
            support: 'Dedicated + SLA',
            onboarding: 'Full onboarding',
          },
        },
      },
      problemSolution: {
        badge: 'THE PROBLEM',
        title: 'Your sales stack is scattered.',
        accent: 'We bring it together',
        subtitle:
          'One system that captures, qualifies, and nurtures every lead—instead of five tools that never talk to each other.',
        todayLabel: 'How sales teams work today',
        ourLabel: 'How FlowSellAI works',
        problems: {
          spreadsheets: 'Leads buried in spreadsheets',
          followups: 'Follow-ups slip through the cracks',
          qualify: 'No way to qualify at scale',
          channels: "Channels that don't talk to each other",
          guessing: 'Reps guessing who to call next',
        },
        solutions: {
          capture: {
            title: 'Every lead captured automatically',
            description:
              'Email, LinkedIn, and web forms—enriched and deduped the moment they arrive.',
          },
          qualify: {
            title: 'Qualified by real behavior',
            description: 'AI scores fit and intent, so reps work the hottest opportunities first.',
          },
          nurture: {
            title: 'Nurtured on autopilot',
            description: 'Personalized follow-ups at the right moment, across every channel.',
          },
          unified: {
            title: 'One unified pipeline',
            description: 'Every channel and signal in a single funnel your whole team can see.',
          },
        },
      },
      automation: {
        badge: 'AI AUTOMATION FLOW',
        title: 'AI handles everything',
        accent: 'except the close',
        subtitle:
          'Our AI agents automate every stage of your funnel - from first contact to qualified meeting - so your team can focus on what they do best: closing deals.',
        steps: {
          capture: {
            stage: 'Capture & Qualify',
            automation: '100%',
            description: 'AI captures leads from every channel and scores them by fit and intent.',
          },
          nurture: {
            stage: 'Nurture & Book',
            automation: '100%',
            description: 'Personalized follow-ups and smart scheduling until they are sales-ready.',
          },
          close: {
            stage: 'Close',
            automation: 'Human',
            description: 'Your team steps in for the one conversation that needs a human.',
            isHuman: true,
          },
        },
        ctaTitle: 'Put your funnel on autopilot',
        ctaText:
          'Be one of the first to automate capture, qualification, and nurturing—founding members get early access and pricing locked for life.',
      },
      socialProof: {
        badge: '✨ TRUSTED WORLDWIDE',
        title: 'Join High-Performing Sales Teams',
        stats: {
          salesTeams: 'Sales Teams',
          leadsProcessed: 'Leads Processed',
          automationRate: 'Automation Rate',
          averageRoi: 'Average ROI',
        },
        testimonialsTitle: 'What Sales Leaders Say',
        testimonials: [
          {
            quote:
              "Funnel Panel's AI completely transformed our sales process. We're closing 3x more deals with the same team size. The automation is incredible.",
            author: 'Sarah Chen',
            role: 'VP of Sales',
            company: 'TechCorp',
          },
          {
            quote:
              'Our team spends 80% less time on admin work and 80% more time talking to qualified leads. This platform is a game changer for B2B sales.',
            author: 'Michael Rodriguez',
            role: 'Sales Director',
            company: 'Growth Labs',
          },
          {
            quote:
              'From hours of manual lead qualification to instant AI-powered scoring. The ROI was immediate and continues to compound every quarter.',
            author: 'Emily Watson',
            role: 'CEO',
            company: 'SaaS Ventures',
          },
        ],
        companiesLabel: 'TRUSTED BY LEADING COMPANIES',
        companies: ['Stripe', 'Shopify', 'Atlassian', 'Notion', 'Linear', 'Figma'],
      },
      faq: {
        badge: 'FAQ',
        title: 'Questions?',
        accent: "We've got answers",
        subtitle: 'Everything you need to know before you join the waitlist.',
        items: {
          live: {
            q: 'Is FlowSellAI live yet?',
            a: "We're in pre-launch. Join the waitlist and founding members get early access the moment we open—plus pricing locked in for life.",
          },
          cost: {
            q: 'What will it cost?',
            a: "Founding-member pricing starts well below our public launch rates and stays locked in. You'll see full plans before you ever pay—no card required to join the waitlist.",
          },
          channels: {
            q: 'Which channels does it support?',
            a: 'Email, LinkedIn, web forms, and AI voice. Every channel feeds one unified pipeline.',
          },
          qualify: {
            q: 'How does the AI qualify leads?',
            a: 'It scores each lead on fit and intent using behavior, engagement, and enrichment data—so your reps always work the hottest opportunities first.',
          },
          security: {
            q: 'Is my data secure?',
            a: 'Your data is encrypted in transit and at rest, isolated per organization, and never sold. You stay in control of it.',
          },
          access: {
            q: 'When do founding members get access?',
            a: "We're onboarding founding members in waves as we open. Join the waitlist to claim your spot—earlier signups get in sooner.",
          },
        },
      },
      cta: {
        title: 'Be First to Transform Your Sales Process',
        subtitle:
          'Join the waitlist and be among the first to automate your funnel with AI. Founding members get early access and locked-in pricing.',
        primaryCta: 'Log In',
        perks: {
          freeTrial: 'Early access at launch',
          noCard: 'Founding-member pricing',
          setup: 'Priority onboarding',
        },
      },
      footer: {
        brand: 'FlowSellAI',
        tagline: 'AI-powered sales funnel automation for modern teams',
        columns: {
          product: 'Product',
          company: 'Company',
          resources: 'Resources',
          legal: 'Legal',
        },
        links: {
          features: 'Features',
          pricing: 'Pricing',
          integrations: 'Integrations',
          changelog: 'Changelog',
          about: 'About',
          blog: 'Blog',
          careers: 'Careers',
          press: 'Press Kit',
          documentation: 'Documentation',
          helpCenter: 'Help Center',
          api: 'API Reference',
          status: 'Status',
          privacy: 'Privacy Policy',
          terms: 'Terms of Service',
          cookie: 'Cookie Policy',
          gdpr: 'GDPR',
        },
        copyright: 'All rights reserved.',
        social: {
          twitter: 'Twitter',
          linkedin: 'LinkedIn',
          github: 'GitHub',
        },
      },
    },
    changelog: {
      title: 'Changelog',
      subtitle: 'New features, improvements, and fixes.',
      backToChangelog: 'Back to Changelog',
      backToHome: 'Back to Home',
      loading: 'Loading...',
      empty: 'No changelog entries yet. Check back soon.',
      error: 'Failed to load changelog. Please try again.',
      version: 'v',
      publishedOn: 'Published on',
      noEntries: 'No updates yet',
    },
    blog: {
      title: 'Blog',
      subtitle: 'Insights, guides, and product stories.',
      backToBlog: 'Back to Blog',
      backToHome: 'Back to Home',
      loading: 'Loading...',
      empty: 'No posts yet. Check back soon.',
      error: 'Failed to load the blog. Please try again.',
      publishedOn: 'Published on',
      readMore: 'Read more',
    },
    waitlist: {
      emailLabel: 'Email Address',
      emailPlaceholder: 'your@email.com',
      emailRequired: 'Email is required',
      emailInvalid: 'Please enter a valid email address',
      nameLabel: 'Full Name (Optional)',
      namePlaceholder: 'John Doe',
      nameInvalid: 'Name must be between 2 and 100 characters',
      companyLabel: 'Company (Optional)',
      companyPlaceholder: 'Acme Inc.',
      companyInvalid: 'Company name must be between 2 and 100 characters',
      phoneLabel: 'Phone (Optional)',
      phonePlaceholder: '+1 (555) 123-4567',
      phoneInvalid: 'Please enter a valid phone number (at least 10 digits)',
      submitting: 'Joining waitlist...',
      alreadyRegistered: 'This email is already on the waitlist',
      errorGeneric: 'Something went wrong. Please try again.',
      successTitle: "You're on the list!",
      successMessage: "Thank you for joining our waitlist. We'll notify you when we launch.",
    },
    workflow: {
      title: 'Trigger Profile Enrichment',
      description: 'Fill in the details below to trigger the profile enrichment workflow.',
      closeButton: 'Close',
      nameLabel: 'Name',
      namePlaceholder: 'Enter name',
      nameRequired: 'Name is required',
      mode: {
        profile: 'Enrich a profile',
        discover: 'Discover accounts',
      },
      discovery: {
        title: 'Discover Instagram accounts',
        intro:
          'Find Instagram accounts you don\u2019t know yet. Searching creates no leads \u2014 you choose which accounts to enrich afterwards.',
        hashtagsLabel: 'Hashtags',
        hashtagsPlaceholder: '#specialtycoffee, #baristalife',
        keywordLabel: 'Keyword',
        keywordPlaceholder: 'barista, coffee roaster\u2026',
        placeLabel: 'Place',
        placeHelper:
          'With a keyword or hashtag, narrows those results to local accounts. Alone, finds accounts that post here.',
        placePlaceholder: 'Bogot\u00e1',
        search: 'Search Instagram',
        searching: 'Searching\u2026',
        maxResultsLabel: 'Max results',
        limitedByPlan:
          'Your plan allows {{max}} more results this month, so the search was narrowed to that.',
        found: '{{count}} accounts found \u2014 {{selected}} selected',
        alreadyKnown: '{{count}} more were already in your pipeline, so they were left out.',
        selectAll: 'Select all',
        clearAll: 'Clear all',
        business: 'Business',
        matches: '{{count}} matching posts',
        localityBadge: 'Local',
        localityReasonPostedAtPlace: 'Posted here',
        localityReasonPostTaggedAtPlace: 'Tagged a post here',
        localityReasonProfileMentionsPlace: 'Mentions this place in their profile',
        droppedForLocalityNote:
          '{{count}} accounts matched your keyword or hashtag but showed no sign of being in {{place}}.',
        hiddenOneTimePostersNote: '{{count}} accounts with a single post there were hidden.',
        placeSearchFailedWarning:
          'We could not look up that place, so results were narrowed using the remaining signals: posts tagged there and what profiles mention.',
        searchPartiallyFailedWarning:
          'Part of the search did not respond in time, so some results may be missing.',
        segmentNameLabel: 'Segment name',
        segmentNamePlaceholder: 'Coffee people in Bogot\u00e1',
        import: 'Enrich {{count}} accounts',
        importing: 'Queueing\u2026',
        queued: '{{count}} accounts queued for enrichment',
        queuedHint:
          'Each one goes through the same Instagram analysis as a pasted profile. They will appear in your pipeline as they finish.',
        done: 'Done',
        emptyNoResults:
          'No accounts matched those criteria. Try a broader hashtag, keyword, or place.',
        emptyPlaceNotFound:
          'We could not find that place on Instagram. Check the name or try again.',
        emptyPlaceSearchFailed:
          'We could not look up that place, so we could not find any local accounts. Try again, or search without a place.',
        emptyNoLocalMatch:
          '{{count}} accounts matched your keyword or hashtag, but none showed signs of being in {{place}}.',
        emptyAllKnown:
          'Every account found ({{count}}) is already in your pipeline, so nothing new was added.',
        emptySearchFailed: 'The search could not complete. Please try again in a few minutes.',
        tryAgain: 'Try again',
      },
      socialMediaLabel: 'Social Media Platform',
      socialMediaRequired: 'Social media platform is required',
      profileUrlLabel: 'Profile URL',
      profileUrlPlaceholder: 'https://linkedin.com/in/username',
      profileUrlPlaceholderInstagram: 'https://instagram.com/username',
      profileUrlRequired: 'Profile URL is required',
      profileUrlInvalid: 'Please enter a valid URL',
      profileUrlInvalidLinkedIn:
        'Enter a LinkedIn profile URL like linkedin.com/in/username — post, company, and feed links cannot be enriched',
      profileUrlInvalidInstagram:
        'Enter an Instagram profile URL like instagram.com/username — post, reel, and story links cannot be enriched',
      submitButton: 'Trigger Workflow',
      submittingButton: 'Triggering...',
      errorGeneric: 'Failed to trigger workflow. Please try again.',
      errors: {
        authRequired:
          'LinkedIn authentication expired. Please reconnect your LinkedIn access in settings.',
        enrichmentUnavailable:
          'We could not enrich this profile right now. The data provider is temporarily unavailable — please try again later.',
        profileNotFound: 'We could not find this profile. Check the username or URL and try again.',
      },
      success: {
        title: 'Workflow Triggered Successfully!',
        platform: 'Platform',
        webhookId: 'Webhook ID',
        enrichedDataTitle: 'Enriched Data',
        dataItems: {
          name: 'Name',
          username: 'Username',
          profileUrl: 'Profile',
          score: 'Score',
          economicCapacity: 'Economic Capacity',
          status: 'Status',
          industry: 'Industry',
          painPoints: 'Pain Points',
          capturedContent: 'Captured Content',
        },
      },
      platforms: {
        linkedin: 'LinkedIn',
        facebook: 'Facebook',
        twitter: 'Twitter',
        instagram: 'Instagram',
      },
      progress: {
        title: 'Enrichment in Progress',
        scraping: 'Scraping profile...',
        scraping_comments: 'Scraping comments...',
        analyzing: 'Analyzing with AI...',
        persisting: 'Saving enriched lead...',
        completed: 'Enrichment Complete!',
        partialSuccess: 'Enrichment completed with issues',
        insufficientData: 'Profile could not be analyzed',
        failed: 'Enrichment Failed',
        viewLead: 'View Lead',
        retry: 'Try Again',
        score: 'Score',
        newRun: 'New Enrichment',
      },
      stages: {
        title: 'Step-by-step result',
        profile: 'Contact profile',
        posts: 'Contact posts',
        reels: 'Reels',
        comments: 'Post comments',
        analysis: 'AI analysis',
        company: 'Company profile',
        company_posts: 'Company posts',
      },
      stageStatus: {
        success: 'Completed',
        failed: 'Failed',
        skipped: 'Skipped',
        not_reached: 'Not started',
      },
      stageReasons: {
        provider_unavailable:
          'The data provider could not complete this step. Please try again later.',
        auth_required: 'Platform authentication expired. Please reconnect your access in settings.',
        profile_not_found: 'This profile was not found. Check the username and try again.',
        unknown: 'Something unexpected happened at this step. Please try again.',
      },
      attachLead: {
        title: 'Is this the same lead?',
        description:
          'These leads look related. Attach this profile to one of them, or create a separate lead.',
        attachTo: 'Attach to',
        alreadyHasProfile: 'already has this profile',
        createNew: 'Create a new lead',
      },
      leadExists: {
        title: 'Lead Already Exists',
        description:
          'A lead with this profile URL already exists in your organization. You can view the existing lead instead.',
        viewLead: 'View Existing Lead',
        newSearch: 'Try Different URL',
      },
      selectPlatform: 'Select platform',
      platformLockedHint: 'Platform is locked to LinkedIn for this workflow.',
      cancel: 'Cancel',
      page: {
        badge: 'Automation',
        title: 'Workflow Hub',
        subtitle: 'Automate your lead enrichment and profile analysis with powerful workflows.',
        newWorkflow: 'New Workflow',
        emptyTitle: 'No workflows yet',
        emptyDescription: 'Create your first workflow to start automating lead enrichment.',
        emptyAction: 'Create Workflow',
      },
      card: {
        status: {
          active: 'Active',
          paused: 'Paused',
          error: 'Error',
        },
        neverRun: 'Never run',
        executions: 'executions',
        pauseButton: 'Pause',
        resumeButton: 'Resume',
        runButton: 'Run',
        comingSoon: 'Coming Soon',
        workflowPaused: 'This workflow is paused. Resume it before running.',
      },
      types: {
        linkedinEnrichment: 'LinkedIn Enrichment',
        instagramEnrichment: 'Instagram Enrichment',
        webSearchContact: 'Web Search',
      },
      executions: {
        title: 'Workflow Executions',
        subtitle: 'Full history of all workflow runs across your organization.',
        backToFlowCenter: 'Back to Flow Center',
        totalRuns: 'Total Runs',
        successful: 'Successful',
        failed: 'Failed',
        emptyTitle: 'No executions yet',
        emptyDescription: 'Run a workflow to see execution history here.',
      },
      visualizer: {
        nodes: {
          manualTrigger: 'Manual Trigger',
          fetchProfile: 'Fetch Profile',
          enrichData: 'Enrich Data',
          updateLead: 'Update Lead',
        },
      },
      history: {
        title: 'Recent Executions',
        previous: 'Previous',
        next: 'Next',
        page: 'Page',
        of: 'of',
        loading: 'Loading executions...',
        emptyTitle: 'No executions yet',
        emptyDescription: 'Run a workflow to see execution history',
        status: {
          success: 'Success',
          failed: 'Failed',
          running: 'Running',
        },
      },
      detail: {
        title: 'Execution details',
        outcome: 'Run outcome',
        results: 'Business impact',
        leadsCreated: 'Leads created',
        leadsUpdated: 'Leads updated',
        errors: 'Errors',
        timing: 'Timeline',
        started: 'Started',
        completed: 'Completed',
        duration: 'Duration',
        stageProgress: 'Stage breakdown',
        triggerContext: 'Trigger context',
        platform: 'Platform',
        profileUrl: 'Profile URL',
        company: 'Company',
        leadId: 'Lead ID',
        executionId: 'Execution ID',
        openHint: 'Open execution details',
      },
      mock: {
        linkedinEnrichment: {
          name: 'LinkedIn Enrichment',
          description:
            'Enrich lead profiles with data from LinkedIn. Fetches job title, company, and connections.',
        },
        facebookScraper: {
          name: 'Facebook Scraper',
          description:
            'Scrape public Facebook profile data for lead enrichment and social insights.',
        },
        leadScoring: {
          name: 'Lead Scoring',
          description:
            'Automatically calculate lead scores based on engagement and profile completeness.',
        },
        webScraper: {
          name: 'Web Scraper',
          description: 'Extract company information from websites for B2B lead enrichment.',
        },
        instagramEnrichment: {
          name: 'Instagram Enrichment',
          description:
            'Scrape Instagram profiles, posts and comments for lead enrichment and social insights.',
        },
      },
      time: {
        hoursAgo: '{{count}} hours ago',
        dayAgo: '1 day ago',
        minsAgo: '{{count}} mins ago',
        twoHoursAgo: '2 hours ago',
        threeHoursAgo: '3 hours ago',
        fiveHoursAgo: '5 hours ago',
        thirtyMinsAgo: '30 mins ago',
        twoMinsAgo: '2 mins ago',
      },
    },
    calendar: {
      eventSource: {
        manual: {
          label: 'Manual',
        },
        voiceAgent: {
          label: 'Voice agent',
          accessibilityLabel: 'Booked by voice agent',
        },
        platformAgent: {
          label: 'Platform agent',
          accessibilityLabel: 'Booked by platform agent',
        },
        providerSync: {
          label: 'Synced',
          accessibilityLabel: 'Synced from external calendar',
        },
      },
      eventStatus: {
        confirmed: 'Confirmed',
        cancelled: 'Cancelled',
        completed: 'Completed',
        noShow: 'No-show',
      },
      agenda: {
        today: 'Today',
        allDay: 'All day',
        meetingCount: '{{count}} meetings',
        cancelledCount: '{{count}} cancelled',
        emptyTitle: 'Nothing coming up',
        emptyDescription: 'Meetings booked for the next 30 days will appear here.',
      },
      filter: {
        hideCancelled: 'Hide cancelled',
      },
      joinCall: {
        label: 'Join call',
        accessibilityLabel: 'Join the video call for this meeting',
      },
    },
    voice: {
      page: {
        badge: 'Voice Agent',
        title: 'AI Voice Assistant',
        subtitle: 'Create personalized voice messages for your leads using Eleven Labs AI',
        newTemplate: 'New Template',
      },
      tabs: {
        templates: 'Templates',
        recentJobs: 'Recent Jobs',
      },
      stats: {
        title: 'Voice Statistics',
        totalJobs: 'Total Jobs',
        completed: 'Completed',
        pending: 'Pending',
        failed: 'Failed',
        charactersUsed: 'Characters Used',
        creditsUsed: 'Credits Used',
      },
      template: {
        status: {
          active: 'Active',
          inactive: 'Inactive',
        },
        characters: 'characters',
        voice: 'Voice',
        noPreview: 'No preview saved',
        preview: 'Preview',
        sendToLead: 'Send to Lead',
        actions: {
          edit: 'Edit',
          duplicate: 'Duplicate',
          delete: 'Delete',
        },
        empty: {
          title: 'No voice templates yet',
          cta: 'Create Your First Template',
        },
        deleteModal: {
          title: 'Delete Template',
          message: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
          confirm: 'Delete',
          cancel: 'Cancel',
        },
      },
      job: {
        status: {
          pending: 'Pending',
          processing: 'Processing',
          completed: 'Completed',
          failed: 'Failed',
          cancelled: 'Cancelled',
        },
        type: {
          single_message: 'Single Message',
          bulk_campaign: 'Bulk Campaign',
          voice_note: 'Voice Note',
          lead_qualification: 'Lead Qualification',
          scheduled_call: 'Scheduled Call',
        },
        delivery: {
          in_app: 'In App',
          phone_call: 'Phone Call',
          voicemail: 'Voicemail',
          download: 'Download',
        },
        customScript: 'Custom Script',
        created: 'Created',
        processed: 'Processed',
        duration: 'Duration',
        characters: 'Characters',
        credits: 'Credits',
        retry: 'Retry',
        cancel: 'Cancel',
        hideAudio: 'Hide Audio',
        showAudio: 'Show Audio',
        empty: {
          title: 'No voice jobs yet',
          description: 'Create a template and send a voice message to a lead.',
        },
      },
      form: {
        createTitle: 'New Voice Template',
        editTitle: 'Edit Template',
        name: {
          label: 'Template Name',
          placeholder: 'e.g., Welcome Message',
          required: 'Template name is required',
        },
        description: {
          label: 'Description',
          placeholder: 'Brief description of when to use this template',
        },
        voice: {
          label: 'Voice',
          placeholder: 'Select a voice',
          required: 'Voice is required',
        },
        script: {
          label: 'Script Template',
          placeholder:
            'Hi {{lead.fullName}}, this is {{user.fullName}} from {{organization.name}}...',
          required: 'Script is required',
          characters: 'characters',
          insertVariables: 'Insert variables:',
        },
        variables: {
          leadName: 'Lead Name',
          leadEmail: 'Lead Email',
          company: 'Company',
          userName: 'User Name',
          organization: 'Organization',
        },
        settings: {
          title: 'Voice Settings',
          stability: 'Stability',
          similarityBoost: 'Similarity Boost',
          speakerBoost: 'Speaker Boost',
          templateActive: 'Template Active',
        },
        preview: {
          title: 'Preview',
          generate: 'Generate Preview',
          generateAndSave: 'Generate & Save Preview',
          generating: 'Generating...',
        },
        submit: {
          create: 'Create Template',
          update: 'Update Template',
          saving: 'Saving...',
          cancel: 'Cancel',
        },
      },
      player: {
        webOnly: 'Audio playback available on web only',
        error: 'Failed to load audio',
        failedToPlay: 'Failed to play audio',
        generating: 'Generating audio...',
      },
    },
    voiceAgents: {
      tabs: {
        agents: 'Agents',
        calls: 'Calls',
      },
      deleteDialog: {
        title: 'Delete agent',
        step1Body: 'This permanently removes the agent and its configuration:',
        step2Body: 'This action cannot be undone. Confirm deletion?',
        cancel: 'Cancel',
        continue: 'Continue',
        back: 'Back',
        delete: 'Delete',
        errorPrefix: 'Could not delete the agent.',
      },
      wizard: {
        rootA11y: 'Voice agent setup form',
        stepsA11yLabel: 'Wizard steps',
        publishedOnlyNameEditable:
          'This agent is published: only its name can be changed. Republish to alter anything else.',
        stepCounter: 'Step',
        stepCounterOf: 'of',
        steps: {
          useCase: 'Use Case',
          identity: 'Identity',
          voice: 'Voice',
          script: 'Script',
          knowledge: 'Knowledge',
          objections: 'Objections',
          toolsCompliance: 'Tools & Compliance',
          callingHours: 'Calling Hours',
          retryPolicy: 'Retry Policy',
          reviewPublish: 'Review & Publish',
          completedHint: 'Completed step. Activate to review.',
          availableHint: 'Available step. Activate to navigate.',
          lockedHint: 'Complete the previous steps to unlock.',
        },
        useCase: {
          initial_qualification: 'First Contact & Qualification',
          warm_recontact: 'Warm Recontact',
          meeting_confirmation: 'Meeting Confirmation',
          cart_recovery: 'Cart Recovery',
          nps_survey: 'NPS Survey',
          custom: 'Custom',
        },
        language: {
          es_CO: 'ES (Colombia)',
          es_LATAM: 'ES (LATAM)',
          es_MX: 'ES (México)',
          en_US: 'EN (US)',
          pt_BR: 'PT (Brasil)',
        },
        tone: {
          friendly_formal: 'Friendly & Formal',
          professional: 'Professional',
          empathetic: 'Empathetic',
          energetic: 'Energetic',
          neutral: 'Neutral',
        },
        errors: {
          useCaseRequired: 'Choose a use-case template or start blank to continue.',
          nameRequired: 'Agent name is required.',
          voiceIdRequired: 'Voice ID is required.',
          openingRequired: 'Opening script is required (min 10 chars).',
          callingHoursRangeInvalid: 'Each enabled day must start earlier than it ends.',
          draftSaveFailed: 'Failed to save agent draft. Please try again.',
          saveBeforePublish: 'Save the agent first by completing all steps.',
          discloseRequired:
            'AI identity disclosure is required to publish. Enable "Always disclose AI identity" in Tools & Compliance.',
          recordingAnnouncementRequired:
            'Recording notice is required to publish. Enable "Recording announcement" in Tools & Compliance.',
          publishFailed: 'Publish failed',
        },
        templates: {
          initial_qualification: {
            name: 'First Contact & Qualification Agent',
            description:
              'First outreach to a newly created lead. Introduces itself, discovers interest, budget, need, decision authority and timeline, and proposes the next step (meeting or follow-up).',
          },
          warm_recontact: {
            name: 'Re-contact Agent',
            description:
              'Re-engage cold leads. Picks up a previous conversation and proposes booking a meeting or a near-term follow-up.',
          },
          meeting_confirmation: {
            name: 'Meeting Confirmation Agent',
            description:
              "Confirms an already-scheduled meeting. If the lead can't make it, it reschedules in the same flow.",
          },
          cart_recovery: {
            name: 'Cart Recovery Agent',
            description:
              'E-commerce. Calls leads who abandoned a quote, probes the blocker, and offers to resolve it or transfer to sales.',
          },
          nps_survey: {
            name: 'NPS Survey Agent',
            description:
              'Post-purchase/post-service NPS question. Captures a 0-10 score and free-form comment. Short call.',
          },
        },
        useCaseStep: {
          eyebrow: 'Agent blueprint',
          intro:
            'Pick a use case to pre-fill the wizard with a curated script, calling hours and tool set. You can edit anything afterwards.',
          choicesA11y: 'Agent use case templates',
          loadingTemplates: 'Loading templates',
          loadError: 'Could not load templates. You can still start blank below.',
          useTemplatePrefix: 'Use',
          useTemplateSuffix: 'template',
          startBlankA11y: 'Start blank (Custom)',
          startBlankTitle: 'Start blank',
          startBlankDescription:
            'Start from a completely empty setup and write everything yourself.',
        },
        identity: {
          nameLabel: 'Agent Name *',
          namePlaceholder: 'e.g. Qualification Bot Colombia',
          nameA11y: 'Agent name',
          descriptionLabel: 'Description',
          descriptionPlaceholder: 'What does this agent do?',
          descriptionA11y: 'Agent description',
          useCaseLabel: 'Use Case *',
          useCaseA11y: 'Select use case',
          languageLabel: 'Language *',
          languageA11y: 'Select language',
          autoDetectLanguageLabel: 'Detect the language automatically',
          autoDetectLanguageA11y: 'Detect the language automatically for each lead',
          autoDetectLanguageHint:
            "The agent speaks the language matched to the lead's country, or the lead's own preferred language when one is set. Leave this off to always speak the language chosen above.",
          toneLabel: 'Tone',
          toneA11y: 'Select agent tone',
        },
        voice: {
          voiceIdLabel: 'ElevenLabs Voice ID *',
          voiceIdPlaceholder: 'Paste an ElevenLabs voice ID',
          voiceIdA11y: 'Voice ID',
          hint: 'Find more voices in ElevenLabs → Voice Library.',
          selectLabel: 'Voice *',
          selectPlaceholder: 'Select a voice',
          selectA11y: 'Select a voice',
          catalogHint: 'Tap a voice to select it, and press play to hear a sample.',
          selectVoiceA11yPrefix: 'Select voice',
          loading: 'Loading voices…',
          loadError: "Couldn't load voices. Please try again.",
          empty: 'No voices available for this language yet.',
          customLabel: 'Or enter a custom Voice ID',
          previewVoice: 'Preview voice',
          previewPlay: 'Play voice sample',
          previewPause: 'Pause voice sample',
          previewPlaying: 'Playing…',
          previewUnavailable: 'No sample available',
        },
        script: {
          openingLabel: 'Opening *',
          openingPlaceholder: 'How the agent introduces itself',
          openingA11y: 'Opening script',
          ctaLabel: 'Call to Action *',
          ctaPlaceholder: 'e.g. Schedule a demo',
          ctaA11y: 'Call to action script',
          closingLabel: 'Closing *',
          closingPlaceholder: 'Closing words',
          closingA11y: 'Closing script',
          builderTitle: 'Conversation workbench',
          builderHint: 'Write naturally, then insert reliable CRM data from the library.',
          liveSafe: 'Live-safe',
          variableLibrary: 'Variable library',
          variableLibraryHint: 'Adds the selected value where your cursor is active.',
          searchVariables: 'Search variables',
          searchVariablesA11y: 'Search available script variables',
          loadingVariables: 'Loading safe variables…',
          loadVariablesError: 'Variable library is unavailable. Your script is still editable.',
          usedVariables: 'Using',
          variableMap: 'Variable map',
          autocompleteTitle: 'Insert a variable',
          autocompleteKeyboardHint: '↑↓ Navigate · Enter select · Esc close',
          autocompleteEmpty: 'No variables match this tag.',
          noVariables: 'No variables yet',
          unknownVariable: '“{{variable}}” is not supported and will block publishing.',
          previewTitle: 'Example-resolved preview',
          previewEmpty: 'Start writing to see how this section will sound.',
          openingShort: 'Opening',
          ctaShort: 'Call to action',
          closingShort: 'Closing',
          examplePrefix: 'Example: {{example}}',
          noVariableResults: 'No safe variables match this search.',
        },
        knowledge: {
          introOnboarding:
            'Optional — give your agent a snippet of context so it can answer questions about your product.',
          introFull:
            'Knowledge base items can be added after publishing. Use the agent detail page to upload documents (PDF / text / URL).',
          seedLabel: 'Quick context (optional)',
          seedPlaceholder:
            'Paste a paragraph about your product, FAQ, or value prop. You can add full docs later.',
          seedA11y: 'Optional knowledge base seed text',
          skipHint:
            'Hit Next to skip — admins can upload PDFs and URLs from the agent detail view once the agent is published.',
          seedTitle: 'Quick context',
          addLabel: 'Add knowledge',
          addText: 'Text',
          addUrl: 'URL',
          addPdf: 'PDF',
          titlePlaceholder: 'Title',
          titleA11y: 'Knowledge item title',
          textPlaceholder: 'Paste text the agent can reference during calls.',
          textA11y: 'Knowledge item text content',
          urlPlaceholder: 'https://example.com/page',
          urlA11y: 'Knowledge item URL',
          submitAdd: 'Add',
          submitTextA11y: 'Add text knowledge item',
          submitUrlA11y: 'Add URL knowledge item',
          pickPdf: 'Choose PDF',
          pickPdfA11y: 'Choose a PDF file to upload',
          pdfHint: 'The PDF is uploaded and its text is extracted and attached to the agent.',
          listLabel: 'Added items',
          listEmpty: 'No knowledge items added yet.',
          deleteItemA11y: 'Delete knowledge item',
          completeEarlierSteps: 'Complete the earlier steps to start adding knowledge.',
          sourceType: {
            text: 'Text',
            url: 'URL',
            pdf: 'PDF',
            notion: 'Notion',
          },
          errors: {
            textRequired: 'Add a title and some text before saving.',
            urlRequired: 'Add a title and a URL before saving.',
            uploadFailed: 'Could not add the knowledge item. Try again.',
            removeFailed: 'Could not remove the knowledge item. Try again.',
            pdfParseFailed: 'This PDF could not be read. Try a different file.',
          },
        },
        objections: {
          note: 'Objection handling can be configured after saving. Full objection editor coming soon.',
        },
        compliance: {
          heading: 'Disclosure Policy',
          alwaysDiscloseLabel: 'Always Disclose AI Identity',
          alwaysDiscloseA11y: 'Always disclose AI identity',
          recordingLabel: 'Recording Announcement',
          recordingA11y: 'Recording announcement',
          wordingLabel: 'Disclosure Wording (optional)',
          wordingPlaceholder: 'Custom disclosure text (min 20 chars)',
          wordingA11y: 'Disclosure wording',
        },
        tools: {
          heading: 'Enabled Tools',
          hint: 'Choose which actions this agent can take during a call. Pre-filled from the selected template — edit anytime.',
          schedule_meeting: 'Schedule meeting',
          transfer_to_human: 'Transfer to human',
          mark_do_not_call: 'Mark do-not-call',
          update_lead_qualification: 'Update lead qualification',
          increment_objection_counter: 'Increment objection counter',
          finalize_call: 'Finalize call',
          lookup_knowledge_base: 'Look up knowledge base',
        },
        callingHours: {
          useLeadTimezoneLabel: "Use Lead's Timezone",
          useLeadTimezoneA11y: 'Use lead timezone',
          windowStartLabel: 'Window Start (HH:mm)',
          windowStartA11y: 'Calling window start time',
          windowEndLabel: 'Window End (HH:mm)',
          windowEndA11y: 'Calling window end time',
          callingDaysLabel: 'Calling Days',
          dayMon: 'Monday',
          dayTue: 'Tuesday',
          dayWed: 'Wednesday',
          dayThu: 'Thursday',
          dayFri: 'Friday',
          daySat: 'Saturday',
          daySun: 'Sunday',
          dayToggleA11y: 'Enable {{day}}',
          dayStartA11y: 'Start time for {{day}}',
          dayEndA11y: 'End time for {{day}}',
          blackoutDatesLabel: 'Holidays / Blackout Dates',
          blackoutDatePlaceholder: 'YYYY-MM-DD',
          blackoutDateAddLabel: 'Add',
          blackoutDateAddA11y: 'Add blackout date',
          blackoutDateRemoveA11y: 'Remove blackout date {{date}}',
          blackoutDateInvalid: 'Enter a valid date in YYYY-MM-DD format.',
        },
        retry: {
          maxAttemptsLabel: 'Max Attempts (1–5)',
          maxAttemptsA11y: 'Maximum retry attempts',
        },
        review: {
          name: 'Name',
          useCase: 'Use Case',
          language: 'Language',
          tone: 'Tone',
          voiceId: 'Voice ID',
          opening: 'Opening',
          cta: 'CTA',
          closing: 'Closing',
          disclosure: 'Disclosure',
          callingHours: 'Calling Hours',
          maxRetries: 'Max Retries',
          enabled: 'Enabled',
          disabled: 'Disabled',
          draftSaved: 'Draft saved — ID:',
          edit: 'Edit',
          editPrefix: 'Edit',
        },
        footer: {
          back: 'Back',
          backA11y: 'Go to previous step',
          cancel: 'Cancel',
          cancelA11y: 'Cancel wizard',
          publish: 'Publish',
          publishA11y: 'Publish voice agent',
          continue: 'Continue',
          continueToPrefix: 'Continue to',
        },
        video: {
          forStepA11y: 'Tutorial video for step',
          tutorialSuffix: 'tutorial',
          openForStepPrefix: 'Open tutorial video for step',
          openForStepSuffix: 'in your browser',
          tapToOpen: 'Tap to open the tutorial video.',
          placeholderA11y: 'Tutorial video placeholder for step',
          comingSoon: 'Tutorial video coming soon. Follow the form below to continue.',
        },
      },
      list: {
        title: 'Voice Agents',
        countSingular: 'agent',
        countPlural: 'agents',
        newAgent: 'New Agent',
        newAgentA11y: 'Create new voice agent',
        filterAll: 'All',
        filterDraft: 'Draft',
        filterPublished: 'Published',
        filterArchived: 'Archived',
        filterUseCaseLabel: 'Use case',
        filterLanguageLabel: 'Language',
        filterAllUseCases: 'All use cases',
        filterAllLanguages: 'All languages',
        emptyTitle: 'No voice agents yet',
        emptyDescription:
          'Create your first AI voice agent to start qualifying leads automatically.',
        emptyMatchTitle: 'No matches',
        emptyMatchDescription: 'No agents match the selected filter.',
        startOnboarding: 'Start onboarding',
        listA11y: 'Voice agents list',
        errorTitle: 'Could not load voice agents',
        errorDescription: 'Something went wrong while loading your voice agents.',
        retry: 'Retry',
      },
      detail: {
        loadingA11y: 'Loading voice agent',
        notFound: 'Agent not found.',
        tabOverview: 'Overview',
        tabConfig: 'Config',
        tabVersions: 'Versions',
        tabKnowledge: 'Knowledge',
        tabExperiments: 'Experiments',
        tabAnalytics: 'Analytics',
        tabCalls: 'Calls',
        configTitle: 'Agent configuration',
        configDescription: 'Review the script, compliance safeguards, and retry behavior.',
        editConfig: 'Edit configuration',
        descriptionTitle: 'Description',
        detailsTitle: 'Details',
        useCase: 'Use Case',
        language: 'Language',
        voiceId: 'Voice ID',
        model: 'Model',
        published: 'Published',
        analyticsSoonA11y: 'Analytics metrics coming soon',
        analyticsSoon: 'Analytics metrics coming in a future release.',
        scriptTitle: 'Script',
        opening: 'Opening',
        cta: 'CTA',
        closing: 'Closing',
        disclosureTitle: 'Disclosure',
        alwaysDisclose: 'Always Disclose',
        recording: 'Recording',
        yes: 'Yes',
        no: 'No',
        retryPolicyTitle: 'Retry Policy',
        maxAttempts: 'Max Attempts',
        noVersions: 'No versions yet.',
        version: 'Version',
        publishedLower: 'published',
        callsSoonA11y: 'Calls history coming soon',
        callsSoon: 'Call history coming in a future release.',
        statusLabel: 'Status',
        preview: 'Preview',
        previewA11y: 'Preview voice agent in browser',
        edit: 'Edit',
        editA11y: 'Edit voice agent',
        renameA11y: 'Rename voice agent',
        renameInputA11y: 'Voice agent name',
        renameSaveA11y: 'Save name',
        renameCancelA11y: 'Cancel rename',
        republish: 'Re-publish',
        republishA11y: 'Re-publish this agent as a new version',
      },
      edit: {
        loadingA11y: 'Loading voice agent',
        screenA11y: 'Edit voice agent',
      },
      previewScreen: {
        loadingA11y: 'Loading agent…',
      },
      newScreen: {
        screenA11y: 'Create new voice agent wizard',
      },
      onboardingScreen: {
        screenA11y: 'Voice agent onboarding wizard',
      },
      refusal: {
        dailyLimit:
          'This lead already received {{used}} of {{limit}} calls allowed in a day. You can call again from {{freesAt}}.',
        dailyLimitNoHorizon:
          'This lead already received {{used}} of {{limit}} calls allowed in a day. The quota frees up within the next 24 hours.',
        doNotCall: 'This lead asked not to be contacted again, so calls to them are blocked.',
        consentRefused:
          'This lead declined the processing of their data, so calls to them are blocked.',
        generic: 'The call could not be placed. Try again in a moment.',
      },
      callsList: {
        deleteCall: 'Delete call',
        deleteTitle: 'Delete call',
        deleteBody: 'This permanently removes the call record for:',
        deleteConfirm: 'Delete',
        deleteError: 'Could not delete the call.',
        agentSelectLabel: 'Agent',
        leadSelectLabel: 'Lead',
        selectAgentPlaceholder: 'Select a published agent',
        selectLeadPlaceholder: 'Select a lead',
        noPublishedAgents: 'No published agents. Publish an agent first.',
        noLeads: 'No leads with a phone number.',
        title: 'Calls',
        results: 'calls',
        triggerCall: 'Trigger call',
        triggerNewCall: 'Trigger new call',
        filterByPrefix: 'Filter by',
        callToPrefix: 'Call to',
        statusPrefix: 'Status:',
        unknown: 'unknown',
        unknownLead: 'Unknown lead',
        unknownAgent: 'Unknown agent',
        statusFiltersA11y: 'Call status filters',
        listA11y: 'Call list',
        loading: 'Loading calls',
        loadingSkeleton: 'Loading call list',
        emptyTitle: 'No calls',
        emptyDescription: 'No calls were found with the selected filters.',
        firstEmptyTitle: 'No calls yet',
        firstEmptyDescription: 'Trigger your first AI call and follow its progress here.',
        errorTitle: 'Could not load calls',
        errorDescription: 'Something went wrong while loading calls. Please try again.',
        retry: 'Retry',
        filterAll: 'All',
        filterScheduled: 'Scheduled',
        filterInProgress: 'In progress',
        filterCompleted: 'Completed',
        filterNoAnswer: 'No answer',
        filterError: 'Error',
        fromPlaceholder: 'From (YYYY-MM-DD)',
        toPlaceholder: 'To (YYYY-MM-DD)',
        countryPlaceholder: 'Country (CO)',
        fromDateA11y: 'From date',
        toDateA11y: 'To date',
        dateHint: 'Year-month-day format, for example 2026-05-05',
        countryA11y: 'Filter by country',
        countryHint: 'Two-letter country code, for example CO or MX',
        filtersTitle: 'Filters',
        advancedFiltersA11y: 'Advanced call filters',
        showFilters: 'More filters',
        hideFilters: 'Hide filters',
        clearFilters: 'Clear filters',
        metricsTotal: 'Total calls',
        metricsActive: 'In progress',
        metricsCompleted: 'Completed',
        metricsNoAnswer: 'No answer',
        metricsAvgDuration: 'Avg. duration',
        fromLabel: 'From',
        toLabel: 'To',
        countryLabel: 'Country',
        agentIdLabel: 'Agent ID',
        agentIdPlaceholder: 'Agent UUID',
        agentIdA11y: 'Voice agent ID',
        leadIdLabel: 'Lead ID',
        leadIdPlaceholder: 'Lead UUID',
        leadIdA11y: 'Lead ID',
        cancel: 'Cancel',
        confirmTrigger: 'Confirm call trigger',
        trigger: 'Trigger',
        triggering: 'Triggering…',
        errorPrefix: 'Error:',
        unknownError: 'Unknown error',
        consentRejectedError:
          'This lead refused data-processing consent, so calls to them are permanently blocked. Reach out through another channel instead.',
        triggerConflictError:
          'This call cannot be placed right now. The lead may be on the do-not-call list, have refused consent, be outside the calling hours, or have reached the daily call limit.',
        outsideCallingHoursWarning:
          "This agent can only place calls within its configured calling hours for the lead's timezone. Try again during the allowed window.",
        status: {
          scheduled: 'Scheduled',
          dialing: 'Dialing',
          completed: 'Completed',
          noAnswer: 'No ans.',
          error: 'Error',
          cancelled: 'Cancelled',
        },
        filterSelectAll: 'All',
        filterAgentLabel: 'Agent',
        filterAgentPlaceholder: 'All agents',
        filterAgentA11y: 'Filter by agent',
        filterLeadLabel: 'Lead',
        filterLeadPlaceholder: 'All leads',
        filterLeadA11y: 'Filter by lead',
        filterOutcomeLabel: 'Outcome',
        filterOutcomePlaceholder: 'All outcomes',
        filterOutcomeA11y: 'Filter by outcome',
        outcomeFilter: {
          qualified: 'Qualified',
          not_qualified: 'Not qualified',
          callback_requested: 'Callback requested',
          meeting_booked: 'Meeting booked',
          do_not_call: 'Do not call',
          no_answer: 'No answer',
          voicemail_left: 'Voicemail',
          technical_failure: 'Technical failure',
          incomplete: 'Incomplete',
        },
      },
      callDetail: {
        title: 'Call',
        screenA11y: 'Call detail',
        statusPrefix: 'Status:',
        loadingDetail: 'Loading call detail',
        notFound: 'Call not found.',
        loadingAudio: 'Loading audio',
        audioUnavailable: 'Audio unavailable.',
        audioUnavailableA11y: 'Audio unavailable',
        signedUrlExpires: 'Signed URL expires:',
        downloadAudio: 'Download audio',
        downloadAudioA11y: 'Download call audio (signed URL with 10-minute expiration)',
        audioSection: 'Audio',
        transcript: 'Transcript',
        summary: 'Summary',
        details: 'Details',
        from: 'From',
        to: 'To',
        country: 'Country',
        duration: 'Duration',
        startedAt: 'Start',
        endedAt: 'End',
        agent: 'Agent',
        agentVersion: 'Agent version',
        latencyP50: 'Latency P50 (ms)',
        latencyP95: 'Latency P95 (ms)',
        telephonyCost: 'Telephony cost',
        conversationCost: 'Conversation cost',
        viewReplay: 'View Replay',
        viewReplayA11y: 'View call replay',
        retry: 'Retry',
        retrying: 'Retrying…',
        retryA11y: 'Retry call',
        status: {
          scheduled: 'Scheduled',
          contextLoaded: 'Context loaded',
          dialing: 'Dialing',
          detecting: 'Detecting',
          disclosure: 'Disclosure',
          discovery: 'Discovery',
          handling: 'Handling',
          cta: 'CTA',
          closing: 'Closing',
          postProcessing: 'Post-processing',
          completed: 'Completed',
          noAnswer: 'No answer',
          voicemail: 'Voicemail',
          hungUpByLead: 'Lead hung up',
          technicalFailure: 'Technical failure',
          cancelled: 'Cancelled',
        },
      },
      replay: {
        title: 'Replay',
        screenA11y: 'Turn-by-turn call replay',
        turns: 'turns',
        tools: 'Tools',
        toolsPanelPrefix: 'Tools panel,',
        callsLabel: 'calls',
        noToolCalls: 'No tool calls in this session.',
        turnLabel: 'Turn',
        toolPrefix: 'Tool',
        latencyLabel: 'latency',
        statusLabel: 'status',
        toolExecuted: 'Tool executed',
        agent: 'Agent',
        lead: 'Lead',
        turnPrefix: 'Turn',
        atTime: 'at',
        tapToSeek: 'Tap to jump to audio.',
        seekHint: 'Plays audio from this turn',
        restrictedTitle: 'Restricted access',
        restrictedBody: 'Only admins and managers can view call replays.',
        loading: 'Loading replay',
        unavailable: 'Replay unavailable.',
        audioUnavailable: 'Audio unavailable for this call.',
        audioUnavailableA11y: 'Audio unavailable',
        noTurnsA11y: 'No turns in the replay',
        noReplayData: 'No replay data available.',
      },
      card: {
        delete: 'Delete',
        voiceAgent: 'voice agent',
        statusPrefix: 'Status',
        version: 'Version',
        publishedOn: 'Published',
        duplicate: 'Duplicate',
        edit: 'Edit',
        archive: 'Archive',
        tooltipEdit: 'Edit agent configuration',
        tooltipDuplicate: 'Duplicate agent',
        tooltipArchive: 'Archive agent',
        tooltipDelete: 'Delete agent',
        status: {
          draft: 'Draft',
          published: 'Published',
          archived: 'Archived',
        },
        useCase: {
          initial_qualification: 'First Contact & Qualification',
          warm_recontact: 'Warm Recontact',
          meeting_confirmation: 'Meeting Confirmation',
          cart_recovery: 'Cart Recovery',
          nps_survey: 'NPS Survey',
          custom: 'Custom',
        },
        language: {
          es_LATAM: 'ES (LATAM)',
          es_CO: 'ES (Colombia)',
          es_MX: 'ES (México)',
          en_US: 'EN (US)',
          pt_BR: 'PT (Brasil)',
        },
      },
      analytics: {
        panelLabel: 'Agent analytics panel',
        showWindowPrefix: 'Show window',
        loading: 'Loading analytics',
        loadError: 'Could not load analytics',
        unknownError: 'unknown error',
        emptyPrefix: 'No calls yet in the last',
        emptySuffix: 'Trigger a call to populate this panel.',
        totalCalls: 'Total calls',
        meetingBooked: 'Meeting booked',
        ofCallsWithOutcome: 'of calls with outcome',
        avgDuration: 'Avg duration',
        totalCost: 'Total cost',
        costPerMeeting: 'Cost per meeting',
        noMeetingsBooked: 'no meetings booked',
        turnTaking: 'Turn-taking',
        outcomesTitle: 'Outcomes',
        noOutcomes: 'No outcomes recorded yet.',
        of: 'of',
        period: {
          '7d': '7 days',
          '30d': '30 days',
          '90d': '90 days',
        },
        outcome: {
          qualified: 'Qualified',
          not_qualified: 'Not qualified',
          callback_requested: 'Callback requested',
          meeting_booked: 'Meeting booked',
          do_not_call: 'Do not call',
          no_answer: 'No answer',
          voicemail_left: 'Voicemail',
          technical_failure: 'Technical failure',
          incomplete: 'Incomplete',
        },
      },
      outcome: {
        none: 'No result',
        resultPrefix: 'Result',
        qualified: 'Qualified',
        meeting_booked: 'Meeting booked',
        not_qualified: 'Not qualified',
        do_not_call: 'Do not call',
        callback_requested: 'Callback requested',
        voicemail_left: 'Voicemail left',
        no_answer: 'No answer',
        technical_failure: 'Technical failure',
        incomplete: 'Incomplete',
      },
      previewCall: {
        voiceAgentFallback: 'voice agent',
        thisAgentFallback: 'this agent',
        emailSubjectPrefix: 'Preview of',
        emailBody: 'Open this link in your browser (Chrome or Safari) to test the agent:',
        browserOnlyTitle: 'Preview is only available in a browser',
        browserOnlyBodyPrefix: 'The preview of',
        browserOnlyBodySuffix:
          'requires a web browser. We sent you the link by email so you can open it on your computer with Chrome or Safari.',
        sendEmail: 'Send link by email',
        sendEmailA11y: 'Send link by email to open in browser',
        resumeFoundTitle: 'Previous session found',
        resumeFoundBody: 'You have an active preview session. Do you want to resume it?',
        newSession: 'New session',
        newSessionA11y: 'Start a new preview session',
        resumeSession: 'Resume session',
        resumeSessionA11y: 'Resume previous preview session',
        resumeFailed: 'This preview session is no longer available. Start a new one.',
        previewTitle: 'Preview',
        startDescription:
          'Start a preview session to talk with this agent in test mode. The transcript is saved so you can resume the conversation later.',
        startPreview: 'Start preview',
        startPreviewA11y: 'Start agent preview',
        statusPrefix: 'Status',
        elapsedPrefix: 'Elapsed time',
        agentSpeaking: 'The agent is speaking…',
        listening: 'Listening…',
        micPaused: 'Microphone paused',
        mute: 'Mute',
        unmute: 'Enable mic',
        muteMicA11y: 'Mute microphone',
        unmuteMicA11y: 'Enable microphone',
        endPreview: 'End preview',
        endPreviewA11y: 'End preview',
        liveTranscript: 'Live transcript',
        toolCalls: 'Tool calls (no effect)',
        toolCallPrefix: 'Tool call',
        toolCallSimulated: 'No effect',
        previousSession: 'Previous session',
        sessionHistory: 'Session history',
        previousSessionA11y: 'Previous session transcript',
        connectionError:
          "Preview couldn't connect. Check that the agent is published and try again.",
        errorPayment: {
          title: 'Voice service unavailable',
          body: 'The voice provider rejected this session due to a billing issue on the account. Please contact your administrator to review the contracted plan.',
        },
        errorGeneric: {
          title: 'Preview connection failed',
          body: 'The preview could not connect. Please try again, and if the problem persists, contact your administrator.',
        },
        startFailed:
          "Couldn't start the preview. Make sure the agent is published to ElevenLabs and try again.",
        submitError:
          'Failed to save preview session. Your conversation data may not have been persisted.',
        status: {
          idle: 'Idle',
          connecting: 'Connecting…',
          connected: 'Connected',
          disconnected: 'Disconnected',
          error: 'Error',
        },
      },
      tour: {
        a11yLabel: 'Voice agent onboarding tour',
        tip: 'Tip',
        of: 'of',
        dismissA11y: 'Dismiss onboarding tour',
        skip: 'Skip',
        skipA11y: 'Skip onboarding tour',
        next: 'Next',
        nextA11y: 'Next onboarding tip',
        gotIt: 'Got it',
        finishA11y: 'Finish onboarding tour',
        steps: {
          welcome: {
            title: 'Welcome to your new agent',
            body: 'This is the agent dashboard. Everything you configured in onboarding lives here, organised by tab.',
          },
          preview: {
            title: 'Preview before launch',
            body: 'Use the Preview button at the top to talk to the agent right from the browser before you put it on a live call.',
          },
          analytics: {
            title: 'Watch analytics',
            body: 'The Analytics tab tracks call volume, qualification rate and duration so you can spot what to tune.',
          },
        },
      },
      audio: {
        playerLabel: 'Audio player',
        playing: 'playing',
        paused: 'paused',
        position: 'position',
        of: 'of',
        forward: 'Forward',
        rewind: 'Rewind',
        seconds: 'seconds',
        pause: 'Pause',
        play: 'Play',
        pauseAudio: 'Pause audio',
        playAudio: 'Play audio',
      },
      summary: {
        callSummary: 'Call summary',
        structuredSummary: 'Structured call summary',
        keyPoints: 'Key points',
        objections: 'Objections',
        nextStep: 'Next step',
      },
      experiments: {
        loading: 'Loading experiments',
        emptyLabel: 'No experiments yet',
        emptyTitle: 'No A/B experiments yet for this agent.',
        emptyHint: 'Create one to compare two versions of your script or voice.',
        listLabel: 'Experiments list',
        experimentPrefix: 'Experiment',
        statusPrefix: 'status',
        split: 'Split',
        target: 'Target',
        pause: 'Pause',
        pauseA11y: 'Pause experiment',
        start: 'Start',
        resume: 'Resume',
        startA11y: 'Start experiment',
        lift: 'Lift',
        pValue: 'p-value',
        significant: '✓ 95% sig.',
        notSignificant: 'not sig.',
        significantA11y: 'Statistically significant at 95%',
        notSignificantA11y: 'Not yet significant',
        insufficientSample: 'Sample too small — at least 30 calls per variant recommended.',
        variant: 'Variant',
        calls: 'calls',
        conv: 'conv.',
        status: {
          draft: 'draft',
          running: 'running',
          paused: 'paused',
          concluded: 'concluded',
        },
        newExperiment: '+ New experiment',
        newExperimentA11y: 'Create a new experiment',
        createTitle: 'New A/B experiment',
        nameLabel: 'Name',
        namePlaceholder: 'e.g. Friendlier opening line',
        descriptionLabel: 'Description (optional)',
        descriptionPlaceholder: 'What are you testing?',
        variantALabel: 'Variant A version',
        variantBLabel: 'Variant B version',
        splitLabel: 'Split percent (A/B)',
        outcomeTargetLabel: 'Outcome target',
        cancel: 'Cancel',
        create: 'Create experiment',
        createA11y: 'Create experiment',
        creating: 'Creating…',
        errorPrefix: 'Could not create the experiment',
        outcomeOptions: {
          meeting_booked: 'Meeting booked',
          callback_requested: 'Callback requested',
          qualified: 'Qualified',
          disqualified: 'Disqualified',
          voicemail: 'Voicemail',
          no_answer: 'No answer',
          do_not_call: 'Do not call',
          other: 'Other',
        },
      },
      transcript: {
        agent: 'Agent',
        lead: 'Lead',
        turn: 'Turn',
        at: 'at',
        seekHint: 'Tap to jump to the timestamp in the audio',
        emptyLabel: 'No transcript entries available',
        empty: 'No transcript available.',
        callTranscript: 'Call transcript',
      },
      batchCall: {
        title_one: 'Call {{count}} lead',
        title_other: 'Call {{count}} leads',
        confirm: 'Call leads',
        progressLabel: '{{completed}} of {{total}} leads processed',
        creditsSkippedLabel: '{{count}} not called: out of credits',
      },
      regulatoryDocs: {
        uploadSuccess: 'Document uploaded successfully.',
        uploadErrorGeneric: 'Could not upload the document. Please try again.',
        fileTooLarge: 'File is too large. Max size is 10 MB.',
        unsupportedFileType: 'Unsupported file type. Allowed: PDF, PNG, JPEG.',
        countryInvalid: 'Country must be a 2-letter ISO code (e.g. MX).',
        pickFilePrompt: 'Pick a file first (PDF, PNG, or JPEG).',
      },
    },
    leads: {
      moveBlocked:
        "This lead can't be moved yet because its job title doesn't meet your target profile. Force-qualify it to move it forward.",
      moveBlockedNonPrivileged:
        "This lead can't be moved yet: its job title doesn't meet your target profile. Ask a manager or admin to force-qualify it.",
      qualificationBlocked: {
        notQualified:
          "This lead's job title doesn't match your target customer profile. A manager or admin can force-qualify it to move it forward.",
        pending:
          "This lead's job title is still being classified. A manager or admin can force-qualify it to move it forward.",
      },
      stages: {
        prospect: 'Prospect',
        contacted: 'Contacted',
        qualified: 'Qualified',
        proposal: 'Proposal',
        won: 'Won',
        lost: 'Lost',
        negotiation: 'Negotiation',
        customer: 'Customer',
      },
      pipeline: {
        title: 'Sales Pipeline',
        loading: 'Loading pipeline...',
        failed: 'Failed to load pipeline',
        noStages: 'No funnel stages configured',
        noStagesHint: 'Create a funnel template to get started',
        stagesLabel: 'Pipeline stages',
        dragHint: 'Drag cards to move between stages',
        noPipelineFound: 'No Pipeline Found',
        noPipelineHint: 'Create a funnel template to get started',
        noActivePipeline: 'No Active Pipeline',
        noActiveHint: 'Activate a funnel template to see your sales pipeline',
        goToSettings: 'Go to Settings',
      },
      kanban: {
        dropHere: 'Drop here',
        scrollPrevious: 'Show previous stages',
        scrollNext: 'Show next stages',
        noLeads: 'No leads in this stage',
      },
      list: {
        title: 'Leads',
        searchPlaceholder: 'Search by name, email, company, or job title...',
        allStages: 'All Stages',
        noMatchFilters: 'No leads match your filters',
        noLeadsYet: 'No leads yet',
        noMatchDescription: 'Try adjusting your search or filters',
        noLeadsDescription: 'Add your first lead to get started',
        selected: 'selected',
        totalLeads: 'total leads',
        totalLead: 'total lead',
        sortBy: 'Sort by',
        sortNewest: 'Newest',
        sortOldest: 'Oldest',
        sortName: 'Name',
        sortCompany: 'Company',
        sortUpdated: 'Recently Updated',
        dateRange: 'Date Range',
        startDate: 'From',
        endDate: 'To',
        clearFilters: 'Clear filters',
        jobTitle: 'Job Title',
        industry: 'Industry',
        economicCapacity: 'Economic Capacity',
        allCapacities: 'All Capacities',
        capacityHigh: 'High',
        capacityMedium: 'Medium',
        capacityLow: 'Low',
        location: 'Location',
        tags: 'Tags',
        tagsMore: '+{{count}} more',
        tagLabel: 'Tag {{tag}}',
        autoTagLabel: 'Auto tag {{tag}}',
        tagsListLabel: 'Lead tags',
      },
      detail: {
        notFound: 'Lead not found',
        deleteConfirm: 'Are you sure you want to delete this lead?',
        deleteTitle: 'Delete Lead',
        callAction: {
          label: 'Call lead',
          tooltip: 'Call',
          started: 'Call started',
          noTemplates: 'No voice templates yet. Set one up in Settings first.',
          goToVoiceSettings: 'Go to voice settings',
          chooseTitle: 'How do you want to call?',
          chooseContact: 'Which contact do you want to call?',
          humanOption: 'Call yourself',
          humanOptionHint: 'Dial the lead from your own phone',
          agentOption: 'AI voice agent',
          agentOptionHint: 'Let a published voice agent place the call',
          unnamedContact: 'Contact',
          dialFailed: 'Could not start the call',
        },
        forceQualify: {
          button: 'Force Qualify',
          title: 'Force Qualify Lead',
          description:
            'Override the automatic qualification and mark this lead as qualified. Please provide a reason.',
          reasonPlaceholder: 'Reason for force-qualifying this lead...',
          confirm: 'Qualify',
          error: 'Failed to force-qualify this lead. Please try again.',
        },
        qualification: {
          qualified: 'Title Verified',
          notQualified: 'Not Verified - Title',
          pending: 'Pending Title Verification',
        },
        contactInfo: 'Contact Information',
        fullName: 'Lead Name',
        contactName: 'Contact Name',
        email: 'Email',
        phone: 'Phone',
        company: 'Company',
        jobTitle: 'Job Title',
        sourceUrl: 'Source URL',
        companyWebsiteUrl: 'Company Website',
        otherProfileUrl: '{{platform}} profile',
        otherNetworkEnrichment: '{{platform}} profile data',
        companyLinkedinUrl: 'Company LinkedIn',
        source: 'Source',
        assignedTo: 'Assigned To',
        productInterest: 'Product / Service of Interest',
        productInterestEditor: {
          placeholder: 'What are you selling to this lead?',
          inputLabel: 'Product or service of interest',
          editLabel: 'Edit product or service of interest',
          saveLabel: 'Save product or service of interest',
          cancelLabel: 'Cancel editing',
          suggestionHint: 'Suggested by AI',
          closestSuggestionHint: 'Closest product · low confidence',
          confirmSuggestion: 'Confirm',
          confirmSuggestionLabel: 'Confirm the suggested product or service of interest',
          unavailable: 'Product no longer available',
        },
        review: {
          title: 'Needs manual review',
          reasonConsentRefused:
            'The lead refused data processing consent during an agent call. Further calls are blocked — decide whether to reach out through another channel, discard the lead, or correct their data.',
          flaggedOn: 'Flagged on {{date}}',
        },
        consent: {
          title: 'Consent for Calls (Voice)',
          granted: 'Consent granted',
          notGranted: 'Consent revoked',
          unknown: 'No consent on record',
          grant: 'Grant',
          revoke: 'Revoke',
          grantTitle: 'Grant consent for calls',
          grantBody: 'Record proof that this lead agreed to be contacted by voice.',
          revokeTitle: 'Revoke consent for calls',
          revokeBody:
            'This records that consent is no longer valid. The lead can no longer be called until consent is granted again.',
          sourceLabel: 'How was consent obtained?',
          sourceForm: 'Form',
          sourceWhatsapp: 'WhatsApp',
          sourceWeb: 'Website',
          sourceManual: 'Manual',
          sourceApi: 'API',
          sourceVoiceCall: 'Voice agent call',
          sourceUnknown: 'Another channel',
          evidenceUrlLabel: 'Evidence URL (optional)',
          evidenceUrlPlaceholder: 'https://...',
          dateObtainedLabel: 'Date obtained',
          dateObtainedPlaceholder: 'YYYY-MM-DD',
          dateInvalid: 'Enter a valid date in YYYY-MM-DD format.',
          grantedOn: 'Granted on {{date}} via {{source}}',
          refusedOn: 'Refused on {{date}} via {{source}}',
          submit: 'Save consent',
          saving: 'Saving…',
          successGranted: 'Consent recorded',
          successRevoked: 'Consent revoked',
          forbidden: 'Only owners and admins can manage consent.',
          error: 'Could not save consent. Please try again.',
        },
        marketingConsent: {
          title: 'WhatsApp marketing consent',
          granted: 'Opted in',
          notGranted: 'Opted out',
          unknown: 'Not defined',
          grant: 'Grant',
          revoke: 'Revoke',
          grantTitle: 'Grant WhatsApp marketing consent',
          grantBody: 'Record that this lead agreed to receive marketing messages on WhatsApp.',
          revokeTitle: 'Revoke WhatsApp marketing consent',
          revokeBody:
            'This records that the lead no longer accepts marketing messages. WhatsApp messages to them will be blocked until consent is granted again.',
          sourceLabel: 'How was consent obtained?',
          submit: 'Save consent',
          saving: 'Saving…',
          successGranted: 'WhatsApp consent recorded',
          successRevoked: 'WhatsApp consent revoked',
          forbidden: 'You do not have permission to manage this lead’s consent.',
          error: 'Could not save consent. Please try again.',
        },
        brand: {
          badge: 'Company',
        },
        tags: 'Tags',
        tagsEditor: {
          placeholder: 'Add a tag',
          inputLabel: 'New tag',
          addLabel: 'Add tag',
          removeLabel: 'Remove tag {{tag}}',
          autoTagLabel: 'Automatic tag {{tag}}, not editable',
          suggestionLabel: 'Add suggested tag {{tag}}',
        },
        lastContacted: 'Last Contacted',
        customFields: 'Custom Fields',
        notes: 'Notes',
        activity: 'Activity',
        linkedinPosts: {
          title: 'LinkedIn Activity',
          instagramTitle: 'Instagram Activity',
          comments: 'Comments',
          reactions: 'Reactions',
          showMore: 'Show more',
          showLess: 'Show less',
          likes: 'likes',
          shares: 'shares',
          noComments: 'No comments',
          noReactions: 'No reactions',
          postedBy: 'Posted by',
          refreshButton: 'Refresh latest post',
          refreshing: 'Refreshing...',
          noNewPosts: 'No new posts found',
          refreshSuccess: 'Posts updated successfully',
          refreshError: 'Could not refresh posts',
          lastRefreshed: 'Last refreshed',
          enrichCommenter: 'Enrich',
          enriching: 'Enriching...',
          alreadyEnriched: 'Already enriched',
          noProfileUrl: 'Profile URL not available',
          more: 'more',
        },
        enrichmentProgress: {
          checking: 'Checking existing leads...',
          title: 'Enriching Lead',
          completed: 'Enrichment Complete',
          failed: 'Enrichment Failed',
          viewLead: 'View Lead',
          retry: 'Retry',
          missingProfileId: 'Cannot enrich: profile identifier missing',
        },
        enrichmentProcessing: 'Generating AI analysis...',
        enrichProfile: 'Enrich Profile',
        enrichFromUrl: 'Enrich from URL',
        linkedinUrlPlaceholder: 'https://linkedin.com/in/...',
        instagramUrlPlaceholder: 'https://instagram.com/...',
        enrichLinkedin: 'Enrich from LinkedIn',
        enrichInstagram: 'Enrich from Instagram',
      },
      new: {
        title: 'Add Prospect',
        subtitle: 'New Lead',
        noFunnel: 'No active funnel template found. Create a funnel template first.',
        contactInfo: 'Contact Information',
        fullName: 'Full Name',
        fullNamePlaceholder: 'Enter full name',
        contactName: 'Contact Name',
        contactNamePlaceholder: 'Enter contact name',
        companyOrContactRequired: 'Company or Contact Name is required',
        emailAddress: 'Email Address',
        emailPlaceholder: 'Enter email address',
        emailOrPhoneRequired: 'Email or phone number is required',
        phoneInvalid: 'Enter a valid phone number with country code (at least 10 digits)',
        jobTitleRequired: 'Job title is required',
        phoneNumber: 'Phone Number',
        phonePlaceholder: 'Enter phone number',
        phoneCountryTitle: 'Select country',
        phoneCountrySearch: 'Search country or code',
        company: 'Company',
        companyPlaceholder: 'Enter company name',
        companyWebsiteUrl: 'Company Website URL',
        companyWebsiteUrlPlaceholder: 'https://company.com',
        companyLinkedinUrl: 'Company LinkedIn URL',
        companyLinkedinUrlPlaceholder: 'https://www.linkedin.com/company/...',
        jobTitle: 'Job Title',
        jobTitlePlaceholder: 'Enter job title',
        roleLabel: 'Role',
        rolePlaceholder: 'Select role',
        source: 'Source',
        sourceUrl: 'Profile URL',
        sourceUrlPlaceholder: 'https://linkedin.com/in/... or https://instagram.com/...',
        tags: 'Tags (comma-separated)',
        tagsPlaceholder: 'Enter tags',
        createError: 'Failed to create lead. Please check your input and try again.',
        duplicateError: 'A lead with this email already exists in your organization.',
        createLead: 'Create Lead',
        successToast: 'Lead created',
      },
      edit: {
        title: 'Edit Prospect',
        subtitle: 'Edit Lead',
        updateError: 'Failed to update lead. Please check your input and try again.',
        successToast: 'Lead updated',
      },
      contacts: {
        title: 'Contacts',
        singular: 'Contact',
        primary: 'Primary',
        add: 'Add Contact',
        addNew: 'New Contact',
        remove: 'Remove',
        removeTitle: 'Remove Contact',
        removeMessage: 'Are you sure you want to remove',
        removeWarning:
          'All information associated with this contact will be permanently lost, and this action cannot be undone.',
        setPrimary: 'Set as primary',
        nameLabel: 'Name',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
        jobTitleLabel: 'Job Title',
        profileLabel: 'Profile',
        namePlaceholder: 'Contact name',
        emailPlaceholder: 'Email address',
        phonePlaceholder: 'Phone number',
        jobTitlePlaceholder: 'Job title',
        roleLabel: 'Role',
        rolePlaceholder: 'Select role',
        roles: {
          decision_maker: 'Decision Maker',
          influencer: 'Influencer',
          technical: 'Technical',
          financial: 'Financial',
          end_user: 'End User',
          gatekeeper: 'Gatekeeper',
          champion: 'Champion',
        },
      },
      assigneePicker: {
        unassigned: 'Unassigned',
        placeholder: 'Select assignee',
        overCapacityTitle: 'Over Capacity',
        overCapacityWarning:
          '{{name}} already has {{current}}/{{max}} leads assigned. Do you want to assign anyway?',
        assignAnyway: 'Assign Anyway',
      },
      activityTimeline: {
        created: 'Created',
        updated: 'Updated',
        stageChanged: 'Stage Changed',
        noteAdded: 'Note Added',
        assigned: 'Seller Assigned',
        unassigned: 'Unassigned',
        emailSent: 'Email Sent',
        titleException: 'Title force-verified',
        activity: 'Activity',
        noActivities: 'No activities yet',
        by: 'by',
        aiAgent: 'AI Agent',
        system: 'System',
        agentExecutionStarted: 'Agent started',
        agentExecutionCompleted: 'Agent completed',
        agentExecutionFailed: 'Agent failed',
        chatToolCompleted: 'AI chat action completed',
        voiceCallStarted: 'Voice call started',
        voiceCallCompleted: 'Voice call completed',
        voiceCallSkippedNoCredits: 'Call not made: out of credits',
        voiceCallStatusChanged: 'Voice call status changed',
        voiceCallAnalyzed: 'Voice call analyzed',
        voiceMessageSent: 'Voice message sent',
        autoCallCompleted: 'Automatic call completed',
        leadEnriched: 'Lead enriched',
        automationSkipped: 'Step skipped',
        enrichmentCompleted: 'Enrichment completed',
        showAutomation: 'Show automation',
        loadMore: 'Load more',
        viaChat: 'via chat',
      },
      analysis: {
        hot: 'Hot',
        warm: 'Warm',
        cold: 'Cold',
        lead: 'Lead',
        aiAnalysis: 'AI Analysis',
        sentiment: 'Sentiment',
        intent: 'Intent',
        industry: 'Industry',
        size: 'Size',
        keyInsights: 'Key Insights',
      },
      instagramContent: {
        title: 'Instagram content',
        posts: 'Posts',
        reels: 'Reels',
        analysis: 'Analysis',
        postBadge: 'Post',
        reelBadge: 'Reel',
        transcript: 'Transcript',
        comments: 'comments',
        noCaption: 'No caption on this post',
        open: 'Open',
        previous: 'Previous',
        next: 'Next',
        refresh: 'Refresh',
        refreshing: 'Refreshing…',
        refreshUpdated: 'Content updated',
        refreshNoChanges: 'No new content',
        refreshError: 'Could not refresh',
        clientProfile: 'Their clients',
        evidence: 'Evidence',
        priceSignals: 'Price signals',
        confidence: {
          high: 'Well evidenced',
          medium: 'Partly evidenced',
          low: 'Thin evidence',
        },
        contentIdeas: 'Content ideas',
        proposals: 'What we could offer them',
      },
      enrichment: {
        title: 'Lead Enrichment',
        brandAnalysisTitle: 'Brand Analysis',
        interestsAnalysisTitle: 'Interests Analysis',
        interestsSummary: 'Interests',
        interestTopics: 'Recurring themes',
        engagementLevel: 'Engagement Level',
        interestScoreClear: 'Interests read clearly',
        interestScorePartial: 'Interests partly clear',
        interestScoreUnclear: 'Interests unclear',
        contentAffinityScore: 'Content Affinity',
        postingActivityScore: 'Posting Activity',
        audienceReachScore: 'Audience Reach',
        audienceInteractionScore: 'Audience Interaction',
        signalGap: {
          title: 'Not scored',
          private_profile:
            'This Instagram account is private, so there is no public content to analyze. Scoring it would be guesswork.',
          no_public_content:
            'This Instagram account has no public bio or readable posts, so there is nothing to base an analysis on.',
        },
        brandBio: 'Bio',
        brandNiche: 'Niche',
        brandInsight: 'Brand Analysis',
        contentType: 'Content Type',
        brandCapacity: 'Brand Capacity',
        brandFocusScore: 'Brand Focus',
        contentQualityScore: 'Content Quality',
        digitalPresenceScore: 'Digital Presence',
        marketPositionScore: 'Market Position',
        brandFitHigh: 'Strong Brand Fit',
        brandFitMedium: 'Medium Brand Fit',
        brandFitLow: 'Low Brand Fit',
        highPotential: 'High Potential',
        mediumPotential: 'Medium Potential',
        lowPotential: 'Low Potential',
        company: 'Company',
        location: 'Location',
        followers: 'Followers',
        industry: 'Industry',
        economicCapacity: 'Economic Capacity',
        scoreBreakdown: 'Score Breakdown',
        painPointsScore: 'Pain Points',
        economicScore: 'Economic Capacity',
        investmentScore: 'Investment Mentality',
        urgencyScore: 'Urgency',
        aiInsight: 'AI Insight',
        painPoints: 'Pain Points',
        enrichedLabel: 'Enriched',
        capacityHigh: 'High',
        capacityMedium: 'Medium',
        capacityLow: 'Low',
        sentimentVeryPositive: 'Very Positive',
        sentimentPositive: 'Positive',
        sentimentNegative: 'Negative',
        sentimentVeryNegative: 'Very Negative',
        sentimentNeutral: 'Neutral',
        qualificationHot: 'Hot',
        qualificationWarm: 'Warm',
        qualificationCold: 'Cold',
        qualificationUnqualified: 'Unqualified',
        qualificationUnknown: 'Unknown',
        potentialLabel: 'Potential',
        method: {
          direct_apify: 'Social profile',
          web_search: 'Web search',
          other: 'Automatic',
        },
        translating: 'Translating...',
      },
      companyProfile: {
        title: 'Company Profile',
        industry: 'Industry',
        employees: 'Employees',
        followers: 'Followers',
        website: 'Website',
        headquarters: 'Headquarters',
        founded: 'Founded',
        specialities: 'Specialities',
        description: 'Description',
        scrapedLabel: 'Scraped',
        highPotential: 'High Potential',
        mediumPotential: 'Medium Potential',
        lowPotential: 'Low Potential',
        capacity: 'Company Capacity',
        capacityHigh: 'High',
        capacityMedium: 'Medium',
        capacityLow: 'Low',
        scoreBreakdown: 'Company Score Breakdown',
        industryFit: 'Industry Fit',
        companySizeGrowth: 'Size & Growth',
        digitalPresence: 'Digital Presence',
        marketPosition: 'Market Position',
        aiAnalysis: 'Company AI Analysis',
      },
      notes: {
        placeholder: 'Add a note...',
        addNote: 'Add Note',
        noNotes: 'No notes yet',
        aiAgent: 'AI Agent',
      },
      pipelineSummary: {
        title: 'Pipeline Overview',
        loading: 'Loading pipeline data...',
        noLeads: 'No leads in pipeline yet',
        total: 'total',
        bySource: 'By Source',
      },
      stageMove: {
        title: 'Move to Stage',
      },
      viewMode: {
        myBoard: 'My Board',
        teamBoard: 'Team Board',
        readOnly: 'Read-only',
      },
      webSearch: {
        searchContact: 'Search Contact (Web)',
        searching: 'Searching the web...',
        scraping: 'Visiting pages...',
        extracting: 'Extracting contacts...',
        viewResults: 'View Results',
        title: 'Web Search',
        resultsTitle: 'Search Results',
        foundSummary: '{{count}} contact(s) found',
        reviewingLinks: 'Reviewing links',
        currentlyVisiting: 'Visiting',
        confirmSave: 'Confirm & Save',
        discard: 'Discard',
        emailsFound: 'Emails found',
        phonesFound: 'Phones found',
        noResults: 'No contacts found',
        noResultsDescription:
          'We searched multiple pages but could not find contact information for this lead.',
        urlsVisited: 'URLs visited',
        cancelled: 'Search cancelled',
        cancelledDescription: 'The web search was cancelled.',
        tryAgain: 'Try Again',
        cancel: 'Cancel',
        requiresCompany: 'Lead must have a company name to search.',
        sourceTypes: {
          official_website: 'Official Website',
          contact_page: 'Contact Page',
          about_page: 'About Page',
          social_network: 'Social Network',
          third_party_directory: 'Directory',
          other: 'Other',
        },
        confidence: {
          high: 'High',
          medium: 'Medium',
          low: 'Low',
        },
        contactType: {
          email: 'Email',
          phone: 'Phone',
        },
        confirmModal: {
          title: 'Save Contacts',
          message: 'The selected contacts will be saved to this lead.',
        },
        successToast: 'Contacts saved successfully',
        errorToast: 'Web search failed',
        sourceUrl: 'Source',
        selectedCount: '{{count}} selected',
        existingWarning:
          'Some selected types already exist on this lead and will not be overwritten.',
        overwriteTitle: 'Overwrite existing data?',
        overwriteDescription:
          'The existing {{fields}} on this lead will be replaced with the new values.',
        overwriteConfirm: 'Yes, overwrite',
        emailLabel: 'email',
        phoneLabel: 'phone',
      },
      callResults: {
        sectionTitle: 'Call Results',
        loading: 'Loading calls…',
        noCalls: 'No calls yet',
        followUpScheduled: 'Follow-up call scheduled',
        call: 'Call',
        viewCallDetail: 'View call detail',
        structuredSummary: 'Structured call summary',
        interestLevel: 'Interest',
        interestLevel_hot: 'Hot',
        interestLevel_warm: 'Warm',
        interestLevel_cold: 'Cold',
        closingProbability: 'Closing probability',
        fields: {
          resumen: 'Summary',
          dolor_principal: 'Main pain',
          necesidad: 'Need',
          presupuesto: 'Budget',
          timeline_decision: 'Decision timeline',
          nivel_interes: 'Interest level',
          probabilidad_cierre: 'Closing probability',
          propuesta_sugerida: 'Suggested proposal',
          proximos_pasos: 'Next steps',
        },
        edit: {
          action: 'Edit',
          title: 'Edit call summary',
          save: 'Save',
          saving: 'Saving…',
          cancel: 'Cancel',
          interestLabel: 'Interest level',
          probabilityLabel: 'Closing probability (0-100)',
          probabilityInvalid: 'Enter a number between 0 and 100',
          interestInvalid: 'Select a valid interest level',
          success: 'Call summary updated',
          forbidden: 'You do not have permission to edit this call',
          error: 'Could not update the call summary',
        },
        reprocess: {
          action: 'Reprocess analysis',
          pending: 'Reanalyzing…',
          success: 'Analysis reprocessing started',
          forbidden: 'You do not have permission to reprocess this call',
          error: 'Could not reprocess the analysis',
        },
      },
    },
    agents: {
      generalContext: {
        title: 'General Context',
        helper:
          'These shared company details guide the agents that do not have their own customization.',
        empty: 'No general context yet.',
        industry: 'Industry',
        product: 'Product',
        salesMotion: 'Sales Motion',
        targetAudience: 'Target Audience',
        tone: 'Tone',
        website: 'Website',
        languages: 'Languages',
      },
      insights: {
        title: 'AI Agent Insights',
        runAll: 'Run All',
        cancel: 'Cancel',
        cancelPipeline: 'Cancel pipeline',
        generating: 'Generating AI analysis...',
        qualification: 'Qualification',
        enrichment: 'Enrichment',
        sentiment: 'Sentiment',
        followUp: 'Follow-Up',
        statusNotRun: 'Not run',
        statusDone: 'Done',
        statusRuleBased: 'Rule-based',
        ruleBasedHint: 'The AI was unavailable, so this result was calculated from rules.',
        statusRunning: 'Running',
        statusQueued: 'Queued',
        statusFailed: 'Failed',
        statusCancelled: 'Cancelled',
        noResults: 'No results yet. Run this agent to analyze the lead.',
        confidence: 'confidence',
        rerun: 'Run {{agent}} again',
        enrichmentFieldsFound: '{{count}} details found',
        enrichmentNothingFound: 'No new details were found for this lead.',
        enrichmentField: {
          industry: 'Industry',
          jobTitle: 'Job title',
          companySize: 'Company size',
          location: 'Location',
          website: 'Website',
          revenue: 'Revenue',
          linkedinUrl: 'LinkedIn',
          timezone: 'Time zone',
          technologies: 'Technologies',
        },
        sentimentNoSignals:
          'No signals to analyze yet: the lead has no messages or interactions, so confidence is low.',
        risks: 'Risks',
        opportunities: 'Opportunities',
        followUpNoSteps: 'No follow-up steps were planned for this lead.',
        qualificationScoreLabel: 'AI qualification',
        qualificationScoreA11y: 'AI qualification: {{score}} out of 100',
        channel: {
          email: 'Email',
          call: 'Call',
          whatsapp: 'WhatsApp',
          meeting: 'Meeting',
        },
        urgency: {
          immediate: 'Immediate',
          this_week: 'This week',
          next_week: 'Next week',
          nurture: 'Nurture',
        },
      },
      status: {
        paused: 'Paused',
        processing: 'Processing',
        active: 'Active',
        queued: '{{count}} queued',
        executionsToday: 'Executions today',
        successful: 'Successful',
        successRate: 'Success rate',
      },
      activityFeed: {
        title: 'Recent Activity',
        emptyTitle: 'No agent activity yet',
        emptyDescription: 'Agent executions will appear here once triggered',
        showToolCalls: 'Show tool calls',
        hideToolCalls: 'Hide tool calls',
        viaChat: 'via chat',
      },
      usage: {
        title: 'Plan usage',
        percentUsed: '{{percent}}% of your plan used this month',
        warningBanner: 'You have used {{percent}}% of your plan. Upgrade before you run out.',
        criticalBanner: 'You have used {{percent}}% of your plan. Your agents will pause at 100%.',
        upgradeCta: 'View plans',
        unavailable: 'Plan usage is not available right now.',
        runs: 'runs',
      },
      config: {
        title: 'Agent Configuration',
        tiers: {
          fast: 'Fast',
          balanced: 'Balanced',
          premium: 'Premium',
        },
      },
      customInstructions: {
        title: 'Custom Instructions',
        hint: "Add your own instructions to extend the agent's base behavior...",
        placeholder: 'e.g., Always prioritize leads from the healthcare industry...',
        hidePreview: 'Hide Preview',
        showPreview: 'Preview Prompt',
        saving: 'Saving...',
        save: 'Save',
        previewTitle: 'Composed Prompt Preview',
        loadingPreview: 'Loading preview...',
        noPreview: 'No preview available',
        aiGenerate: 'Generate with AI',
        aiEnrich: 'Improve with AI',
        aiGenerating: 'Generating...',
        aiError: 'AI suggestion failed. Please try again.',
        aiUndo: 'Undo AI suggestion',
      },
      customization: {
        configure: 'Configure {{name}}',
        skillsTab: 'Skills',
        instructionsTab: 'Instructions',
        skillsDescription: 'Toggle predefined skills or create custom ones to tailor the agent.',
        createCustomSkill: '+ Create Custom Skill',
        yourCustomSkills: 'YOUR CUSTOM SKILLS',
        deleteSkillTitle: 'Delete Skill',
        deleteSkillMessage: 'Are you sure you want to delete this custom skill?',
        noSkills: 'No skills available for this agent type.',
      },
      execution: {
        noSelection: 'No execution selected',
        lead: 'Lead',
        timing: 'Timing',
        model: 'Model',
        error: 'Error',
        reasoning: 'Reasoning',
        name: 'Name',
        email: 'Email',
        started: 'Started',
        completed: 'Completed',
        duration: 'Duration',
        tokens: 'Tokens',
        cost: 'Cost',
        cancelExecution: 'Cancel Execution',
        toolActions: 'Tool Actions',
      },
      skills: {
        custom: 'Custom',
        autoExec: 'Auto-run',
        approval: 'Needs approval',
      },
      toolActions: {
        noActions: 'No tool actions yet.',
        approve: 'Approve',
        reject: 'Reject',
      },
      createSkill: {
        createTitle: 'Create Custom Skill',
        editTitle: 'Edit Skill',
        nameLabel: 'Name',
        namePlaceholder: 'e.g., Healthcare Compliance',
        categoryLabel: 'Category',
        categoryPlaceholder: 'e.g., industry, behavior, compliance',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'Short description shown in the skill list...',
        promptLabel: 'Prompt Snippet',
        promptHint: 'The actual instruction injected into the AI prompt when this skill is active.',
        promptPlaceholder:
          'e.g., When analyzing leads, check for healthcare industry compliance indicators...',
        executionModeLabel: 'Execution Mode',
        toolsLabel: 'Tools',
        toolsHint: 'Tools this skill is allowed to call while it runs.',
        maxToolCallsLabel: 'Max tool calls',
        saving: 'Saving...',
        createSkill: 'Create Skill',
        saveChanges: 'Save Changes',
      },
    },
    settings: {
      title: 'Settings',
      subtitle: 'Manage your account, organization, and preferences',
      sections: {
        profile: 'Profile',
        organization: 'Organization',
        team: 'Team Management',
        notifications: 'Notifications',
        appearance: 'Appearance',
      },
      nav: {
        profile: 'Profile',
        organization: 'Organization',
        businessContext: 'Business Context',
        team: 'Team',
        sellerProfiles: 'Seller Profiles',
        leadAssignment: 'Lead Assignment',
        jobTitles: 'Job Titles',
        notifications: 'Notifications',
        appearance: 'Appearance',
        analytics: 'Analytics',
        boardVisibility: 'Board Visibility',
        businessHours: 'Business hours',
        voiceCalling: 'Voice Calling',
        regulatoryDocs: 'Regulatory Docs',
        billing: 'Billing',
        account: 'Account',
      },
      billing: {
        title: 'Billing & Plan',
        currentPlan: 'Current plan',
        noPlan: 'No active plan',
        noStatus: 'Unknown',
        finalizing: 'Finalizing your upgrade…',
        paymentIncomplete:
          'Payment was not completed. You can retry now; a late processed payment will update automatically.',
        pendingSelected: 'You selected',
        completePayment: 'Complete payment',
        monthly: 'Monthly',
        yearly: 'Yearly',
        selectPlan: 'Select',
        currentPlanCta: 'Current plan',
        contactSales: 'Contact sales',
        currencyNote: 'Checkout currency:',
        checkoutUnavailable: 'Checkout is not available for this organization or plan right now.',
        checkoutUnavailableCta: 'Unavailable',
      },
      sellerProfiles: {
        title: 'Seller Profiles',
        loading: 'Loading profiles...',
        noMembers: 'No team members found.',
        noProfile: 'No profile',
        createProfile: 'Create Profile',
        editProfile: 'Edit Profile',
        deleteProfile: 'Delete Profile',
        deleteConfirmTitle: 'Delete Seller Profile',
        deleteConfirmMessage:
          'Are you sure you want to delete the profile for {{name}}? This action cannot be undone.',
        sellerType: 'Seller Type',
        sellerTypePlaceholder: 'Select seller type',
        status: 'Status',
        statusPlaceholder: 'Select status',
        maxLeads: 'Max Leads',
        maxLeadsPlaceholder: 'e.g., 50',
        bio: 'Bio',
        bioPlaceholder: 'Brief description...',
        phone: 'Phone',
        phonePlaceholder: 'e.g., +1 555 0100',
        industries: 'Industries',
        noIndustries: 'No industries available',
        saveProfile: 'Save Profile',
        saveError: 'Failed to save profile. Please try again.',
        deleteError: 'Failed to delete profile. Please try again.',
        industriesTitle: 'Industries',
        addIndustry: 'Add Industry',
        industryPlaceholder: 'e.g., SaaS, FinTech',
        noIndustriesList: 'No industries configured yet.',
        deleteIndustryTitle: 'Delete Industry',
        deleteIndustryMessage: 'Are you sure you want to delete this industry?',
        deleteIndustryError: 'Cannot delete a default industry.',
        addIndustryError: 'Failed to add industry. Please try again.',
      },
      profile: {
        title: 'Profile Information',
        fullName: 'Full Name',
        fullNamePlaceholder: 'Enter your full name',
        email: 'Email',
        successMessage: 'Profile updated successfully.',
        errorMessage: 'Failed to update profile. Please try again.',
      },
      organization: {
        title: 'Organization Settings',
        nameLabel: 'Organization Name',
        namePlaceholder: 'Organization name',
        yourRole: 'Your Role',
        created: 'Created',
        ownerOnly: 'Only the organization owner can edit these settings.',
        successMessage: 'Organization updated successfully.',
        errorMessage: 'Failed to update. Please try again.',
        timezoneLabel: 'Organization timezone',
        timezoneHint:
          'Used for calendar bookings and event times when a lead has no timezone of its own.',
        useDeviceTimezone: 'Use my device timezone',
        timezoneNotSet: 'Not set (calls use America/Bogota)',
        timezoneUnsetHint:
          'No timezone chosen yet. Calls and bookings use America/Bogota until you pick one.',
      },
      businessContext: {
        title: 'Business Context',
        subtitle:
          'The shared context your AI agents use to qualify, enrich, and talk to your leads. Collected during onboarding — update it here whenever your business changes.',
        websiteLabel: 'Website (optional)',
        websitePlaceholder: 'https://yourcompany.com',
        languagesLabel: 'Languages (optional)',
        languagesPlaceholder: 'English, Spanish',
        languagesHelper: 'Separate multiple languages with commas.',
        savedSuccess: 'Business context updated successfully.',
        saveError: 'Failed to update. Please try again.',
        readOnlyNotice: 'Only organization owners and admins can edit the business context.',
        regenerateOfferTitle: 'Context saved',
        regenerateOfferBody:
          'Your AI setup was generated from the previous context. Regenerate the plan to apply the updated context to your agents.',
        regenerateCta: 'Regenerate AI plan',
        regenerateLater: 'Not now',
        appliedSuccess: 'AI plan applied with your updated context.',
        products: {
          title: 'Product catalog',
          subtitle:
            'The products and services your agents can name in a conversation. Separate from the summary above.',
          empty: 'No products yet. Add the first one so your agents know what you sell.',
          add: 'Add product',
          edit: 'Edit product',
          remove: 'Remove product',
          save: 'Save product',
          cancel: 'Cancel editing',
          saveError: 'Could not save the catalog change. Please try again.',
        },
      },
      team: {
        title: 'Team Members',
        loading: 'Loading team...',
        noMembers: 'No team members found.',
        pendingInvites: 'Pending Invites',
        noPending: 'No pending invites.',
        inviteSent: 'Invitation sent',
        inviteMember: 'Invite Member',
        emailPlaceholder: 'colleague@company.com',
        role: 'Role',
        invited: 'Invited',
        seatUsage: 'Seats used: {{current}}/{{max}}',
        seatLimitTitle: 'Seat limit reached',
        seatLimitMessage:
          "You've reached the seat limit for your current plan. Upgrade your plan to invite more members.",
        removeMember: {
          title: 'Remove Member',
          message: 'Remove {{name}} from the team? This action cannot be undone.',
        },
        revokeInvite: {
          title: 'Revoke Invite',
          message: 'Revoke the invite for {{email}}?',
        },
      },
      appearance: {
        title: 'Appearance',
        language: 'Language',
        theme: 'Theme',
        dark: 'Dark',
        lightComingSoon: 'Light (Coming Soon)',
        languages: {
          english: 'English',
          spanish: 'Español',
          portuguese: 'Português',
        },
      },
      analytics: {
        title: 'Analytics',
        toggleLabel: 'Product analytics',
        toggleHint:
          'Allow anonymous usage analytics to help us improve the product. When off, no analytics events are sent.',
      },
      businessHours: {
        title: 'Business hours',
        description:
          'The days and hours your team can meet. Once enabled, agents only offer and book meetings inside these windows.',
        enabledLabel: 'Enforce business hours',
        enabledHint: 'Agents will not offer or book meetings outside the windows below.',
        disabledHint: 'Off: agents may propose any day and time the calendar is free.',
        daysLabel: 'Working days',
        startLabel: 'Opens at',
        endLabel: 'Closes at',
        timezoneHint: 'Times are read in the organization timezone:',
        timezoneNotSet: 'not set (America/Bogota)',
        perDayNotice:
          'These hours are configured per day or with several windows per day. Changing the days or times here would overwrite that configuration, so it is kept as is; you can still turn business hours on or off and edit closed dates.',
        blackoutLabel: 'Closed dates',
        blackoutHint: 'Holidays or closures. Nothing is booked on these dates.',
        saveSuccess: 'Business hours updated',
        saveError: 'Could not update business hours',
        days: {
          mon: 'Mon',
          tue: 'Tue',
          wed: 'Wed',
          thu: 'Thu',
          fri: 'Fri',
          sat: 'Sat',
          sun: 'Sun',
        },
        errors: {
          noDays: 'Select at least one working day, or turn business hours off.',
          endBeforeStart: 'Closing time must be later than opening time.',
          perDayHours:
            'The days or times cannot be changed here because the hours are configured per day. Restore the original values to save.',
        },
      },
      boardVisibility: {
        title: 'Board Visibility',
        description: 'Choose how leads appear on the funnel board',
        individual: 'Individual',
        individualDesc: 'Each seller only sees their own assigned leads',
        shared: 'Shared',
        sharedDesc: 'All team members can see every lead on the board',
        hybrid: 'Hybrid',
        hybridDesc: 'Sellers have their own board plus access to a shared team view',
        saveSuccess: 'Board visibility updated',
        saveError: 'Failed to update board visibility',
        loading: 'Loading...',
      },
      voiceCalling: {
        title: 'Voice Calling',
        description: 'Set up a voice template and configure the auto-call rule for your team.',
        restricted: 'Only admins and managers can configure voice calling.',
        newTemplate: 'New Template',
      },
      account: {
        title: 'Account',
        preamble: 'Danger Zone',
        description:
          'Sign out of your account on this device. You will need to log in again to access the app.',
        signOut: 'Sign Out',
        signOutLoading: 'Signing out...',
      },
      leadAssignment: {
        title: 'Lead Assignment',
        description:
          'Configure how incoming leads are automatically distributed among your sales team.',
        loading: 'Loading assignment settings...',
        enableAutoAssign: 'Enable Auto-Assignment',
        enableAutoAssignDesc:
          'Automatically assign new leads to sellers based on the selected strategy.',
        strategyLabel: 'Assignment Strategy',
        strategy: {
          round_robin: 'Round Robin',
          round_robin_desc: 'Leads are distributed evenly in rotation among active sellers.',
          load_based: 'Load Based',
          load_based_desc: 'Leads go to the seller with the fewest active leads.',
          manual_only: 'Manual Only',
          manual_only_desc: 'No automatic assignment. Leads must be assigned manually.',
        },
        maxLeadsPerUser: 'Max Leads Per Seller',
        maxLeadsPerUserPlaceholder: 'No limit',
        assigneePool: 'Assignee Pool',
        assigneePoolDesc: 'Select which team members participate in auto-assignment.',
        noMembers: 'No team members found.',
        selected: 'In pool',
        saveError: 'Failed to save assignment settings. Please try again.',
      },
      jobTitles: {
        title: 'Job Title Filters',
        description:
          'When enabled, leads are automatically qualified based on their job title. Unqualified leads cannot advance stages.',
        enableFilter: 'Enable Job Title Filter',
        enableFilterDescription:
          'When disabled, all leads are treated as qualified regardless of job title.',
        addTitle: 'Add Title',
        removeTitle: 'Remove title',
        titlePlaceholder: 'e.g., CEO, CTO, VP Sales',
        countryPlaceholder: 'Country (optional)',
        industryPlaceholder: 'Industry (optional)',
        noTitles: 'No job title filters configured yet.',
        synonyms: 'Synonyms',
        addSynonym: 'Add Synonym',
        removeSynonym: 'Remove synonym',
        removeConfirm: 'Are you sure you want to remove this item? This action cannot be undone.',
        addError: 'Failed to save. Please try again.',
        synonymPlaceholder: 'e.g., Chief Exec',
        canonicalPlaceholder: 'Maps to (e.g., CEO)',
        noSynonyms: 'No synonyms configured yet.',
      },
      notifications: {
        title: 'Notification Preferences',
        email: {
          label: 'Email Notifications',
          description: 'Receive updates about leads, assignments, and team activity via email',
        },
        push: {
          label: 'Push Notifications',
          description: 'Get real-time alerts on your device for important events',
        },
        inApp: {
          label: 'In-App Notifications',
          description: 'Show notification badges and banners within the app',
        },
        digest: {
          label: 'Weekly Digest',
          description: 'Receive a weekly summary of your pipeline activity',
        },
        info: 'Notification preferences are saved automatically.',
        updateError: 'We could not save your notification preferences. Please try again.',
      },
    },
    notifications: {
      title: 'Notifications',
      markAllRead: 'Mark all as read',
      empty: 'No notifications yet',
      sectionToday: 'Today',
      sectionYesterday: 'Yesterday',
      sectionEarlier: 'Earlier',
      emailDigest: 'Email frequency',
      categories: {
        lead_activity: 'Lead Activity',
        agent_automation: 'Agent & Automation',
        team_organization: 'Team & Organization',
        billing_system: 'Billing & System',
      },
      channels: {
        push: 'Push Notifications',
        pushDesc: 'Real-time alerts on your device',
        email: 'Email Notifications',
        emailDesc: 'Receive updates via email',
        inApp: 'In-App Notifications',
        inAppDesc: 'Show notifications inside the app',
      },
      digest: {
        immediate: 'Immediate',
        daily: 'Daily',
        weekly: 'Weekly',
      },
    },
    missionControl: {
      title: 'Mission Control',
      loading: 'Loading Mission Control...',
      hud: {
        badge: 'Mission Control',
        activeCount: '{{count}} active',
        roomLabel: 'Room',
        boardLabel: 'Board',
        showRoom: 'Show room view',
        showBoard: 'Show task board view',
        standingBy: 'Standing by...',
        processingTask: 'Processing task',
        analyzing: 'Analyzing...',
        errorOccurred: 'Error occurred',
      },
      chat: {
        tabs: {
          chat: 'Chat',
          activity: 'Activity',
        },
        agent: 'Agent',
        orchestratorError:
          "We couldn't process your message right now. If this keeps happening, please contact your administrator.",
        cancelledWithActions:
          'Stopped. These actions had already run before you cancelled: {{actions}}.',
        cancelledNoActions: 'Stopped. No changes were made before you cancelled.',
        editLast: 'Edit last message',
        emptyChat: 'Send a message to start chatting with this agent.',
        placeholder: 'Type a message...',
        send: 'Send',
        noActivity: 'No activity yet for this agent.',
      },
      taskBoard: {
        title: 'Task Board',
        columns: {
          queued: 'Queued',
          running: 'Running',
          completed: 'Completed',
          failed: 'Failed',
        },
        runningCount: '{{count}} running',
        newTask: 'New Task',
        noTasks: 'No tasks',
        noLead: 'No lead',
      },
      newTask: {
        title: 'New Task',
        subtitle: 'Select an agent to execute a task',
        creating: 'Creating...',
        createTask: 'Create Task',
      },
    },
    onboarding: {
      title: 'Get Started',
      complete: '{{completed}}/{{total}} complete',
      dismiss: 'Dismiss onboarding checklist',
      milestones: {
        createLead: 'Create your first lead',
        moveLead: 'Move a lead through stages',
        inviteMember: 'Invite a team member',
      },
    },
    onboardingWizard: {
      navigation: {
        back: 'Back',
        stepCounter: 'Step',
        stepCounterOf: 'of',
        regenerate: 'Regenerate plan',
      },
      profile: {
        title: 'Tell us about your business',
        subtitle: 'This helps our AI recommend the right agents, skills, and voice setup for you.',
        organizationNameLabel: 'Organization name',
        organizationNamePlaceholder: 'Acme Consulting',
        targetMarketLocationLabel: 'Target market/location',
        targetMarketLocationPlaceholder: 'Mexico City, Colombia, LATAM, South Florida',
        industryLabel: 'Industry',
        industryPlaceholder: 'e.g. SaaS, Real Estate, Insurance',
        productSummaryLabel: 'What do you sell?',
        productSummaryPlaceholder: 'Briefly describe your product or service',
        targetAudienceLabel: 'Who is your target audience?',
        targetAudiencePlaceholder: 'e.g. Small business owners, IT managers',
        salesMotionLabel: 'Sales motion (optional)',
        salesMotionPlaceholder: 'e.g. Inbound, outbound, self-serve',
        toneLabel: 'Brand tone (optional)',
        tonePlaceholder: 'e.g. Friendly and consultative',
        next: 'Continue',
      },
      products: {
        title: 'What do you sell?',
        subtitle:
          'Add your products or services manually or upload a spreadsheet. Your AI agents will reference them by name when they talk to leads.',
        nameLabel: 'Product or service name',
        namePlaceholder:
          'What do your customers call it? e.g. Bread and cheese, Starter Plan, Accounting advisory',
        descriptionLabel: 'Short description (optional)',
        descriptionPlaceholder: 'What it includes and who it is for',
        priceRangeLabel: 'Price range (optional)',
        priceRangePlaceholder: 'e.g. USD 200-400 per month',
        add: 'Add to catalog',
        remove: 'Remove product',
        edit: 'Edit product',
        save: 'Save changes',
        cancel: 'Cancel editing',
        kind: {
          label: 'Type',
          product: 'Product',
          service: 'Service',
        },
        aliases: {
          label: 'Other names your customers use',
          placeholder: 'e.g. bread and cheese, breakfast combo',
          helper:
            'Separate names with commas or press Add. Your AI agents will recognize these names too.',
          add: 'Add name',
          removeLabel: 'Remove name {{alias}}',
          limit: 'You can add up to {{max}} other names.',
          rejected: {
            tooLong: 'Names can be up to {{max}} characters. Shorten it and press Add again.',
            capped:
              'You can have up to {{max}} other names. The names left in the field were not added.',
            duplicate:
              'These names are already listed or match the product name. Edit or clear them.',
          },
          alsoKnownAs: 'also:',
        },
        naming: {
          genericWarning:
            'This name looks generic. Your AI agents and sellers will see exactly this name, so use the one your customers say.',
          suggestion: 'Is your product called «{{suggestion}}»?',
          useAsName: 'Use as name',
          useAsNameLabel: 'Use «{{suggestion}}» as the product name',
          previewTitle: 'How the AI agents will see it',
          savedGenericNotice:
            'This product has a generic name. Your AI agents and sellers will see it as is.',
        },
        empty: 'No products added yet. You can add them later from settings.',
        errors: {
          product_name_taken: 'You already have a product with that name. Use a different name.',
          product_name_required: 'Enter a product name.',
          product_name_invalid: 'The product name must be text.',
          product_name_too_long: 'The product name can be up to {{max}} characters.',
          product_alias_invalid: 'Each other name must be text.',
          product_alias_too_long: 'Each other name can be up to {{max}} characters.',
          product_aliases_too_many: 'You can add up to {{max}} other names.',
          product_description_too_long: 'The description can be up to {{max}} characters.',
          product_price_range_too_long: 'The price range can be up to {{max}} characters.',
        },
        deleteDialog: {
          title: 'Delete «{{name}}»?',
          linkedLeadsOne:
            '{{count}} lead has this product as an interest; it will keep the text and show «Product no longer available».',
          linkedLeadsOther:
            '{{count}} leads have this product as an interest; they will keep the text and show «Product no longer available».',
          cancel: 'Cancel',
          confirm: 'Delete product',
          linkedLeadsError:
            'We could not check whether leads use this product. You can still delete it.',
        },
        import: {
          divider: 'or upload a file',
          hint: 'Upload a CSV, XLS or XLSX file with the columns Name (required), Description, Price range, Aliases (separated by commas) and Type (product or service). Rows without a name are skipped.',
          downloadTemplate: 'Download sample template',
          summary: '{{added}} products added, {{skipped}} rows skipped',
          rowLabel: 'Row {{row}}:',
          warningsTitle: 'Imported, but worth a second look:',
          warnings: {
            generic_name: '“{{name}}” looks like a generic name.',
            aliases_truncated:
              '“{{name}}”: some aliases were not saved (max {{maxCount}}, up to {{maxLength}} characters each).',
            unknown_kind: '“{{name}}”: we did not recognize the type; it was saved as a product.',
            unknown: 'Review the name in this row.',
            generic_name_with_suggestion:
              '“{{name}}” looks like a generic name. Is it called “{{suggestion}}”?',
          },
          error: 'We could not read that file. Use the .xlsx template and try again.',
          reasons: {
            missing_name: 'missing name',
            name_too_long: 'name is longer than {{max}} characters',
            description_too_long: 'description is longer than {{max}} characters',
            price_range_too_long: 'price range is longer than {{max}} characters',
            duplicate_in_file: 'repeated in the file',
            already_exists: 'already in your catalog',
          },
        },
        next: 'Continue',
      },
      generating: {
        title: 'Building your setup',
        subtitle:
          'Our AI is analyzing your business to recommend agents, skills, and a voice assistant.',
        errorTitle: "We couldn't generate your setup",
        retry: 'Try again',
      },
      review: {
        title: 'Review your recommended setup',
        fallbackBanner:
          'We used our recommended defaults for your agents since we could not confidently tailor them to your business.',
        agentsTitle: 'AI Agents',
        skillsTitle: 'Skills',
        toolsTitle: 'Tools included',
        voiceTitle: 'Voice Agent',
        voiceUseCase: 'Use case',
        voiceSuccessCriteria: 'Success criteria',
        apply: 'Apply setup',
        applying: 'Applying...',
      },
      success: {
        title: "You're all set!",
        subtitle: 'Your AI agents and configuration have been applied to your organization.',
        cta: 'Go to AI Agent',
      },
      modality: {
        guidedLabel: 'Guided form',
        voiceLabel: 'Voice',
        voiceScaffoldTitle: 'Voice onboarding is coming soon',
        voiceScaffoldSubtitle:
          "We're building a voice interview to set up your organization. For now, continue with the guided form.",
        continueGuided: 'Continue with guided form',
      },
    },
    components: {
      errorBoundary: {
        title: 'Oops! Something went wrong',
        description:
          "We're sorry for the inconvenience. The error has been reported and we'll fix it soon.",
        devError: 'Error Details (Dev Only):',
      },
      confirmModal: {
        processing: 'Processing...',
        warningIconLabel: 'Warning',
      },
      primitives: {
        searchPlaceholder: 'Search...',
        selectPlaceholder: 'Select...',
        closeSheet: 'Close sheet',
        close: 'Close',
        removeBadge: 'Remove',
        avatar: 'Avatar',
        avatarFor: 'Avatar for {{name}}',
      },
      landing: {
        aiLeadCapture: 'AI Lead Capture',
        smartQualification: 'Smart Qualification',
        realTimeAnalytics: 'Real-time Analytics',
        aiAutomation: 'AI Automation',
        emailConfirmed: 'Email confirmed',
        earlyAccessSecured: 'Early access secured',
        humanTouch: 'Human Touch',
        automated: 'Automated',
      },
    },
    pages: {
      login: {
        heroTitle: 'Manage Your Sales Pipeline',
        heroSubtitle:
          'Track leads, automate workflows, and close deals faster with AI-powered insights.',
        statRevenue: 'Revenue',
        statRevenueValue: '$48.2K',
        statLeads: 'Active Leads',
        statLeadsValue: '1,284',
        statConversion: 'Conversion',
        statConversionValue: '24.8%',
        loginFailed: 'Login failed. Please try again.',
        googleFailed: 'Google login failed. Please try again.',
        linkedinFailed: 'LinkedIn login failed. Please try again.',
        welcomeBack: 'Welcome back',
        loginSubtitle: 'Log in to your account',
        continueAuth0: 'Log In',
        noAccount: "Don't have an account?",
        signUp: 'Sign up',
      },
      signup: {
        heroTitle: 'Start Growing Your Sales',
        heroSubtitle: 'Join thousands of teams using AI-powered insights to close deals faster.',
        getStarted: 'GET STARTED',
        createAccount: 'Create your account',
        continueAuth0: 'Sign Up',
        alreadyHaveAccount: 'Already have an account?',
        logIn: 'Log in',
        signupFailed: 'Sign up failed. Please try again.',
        googleFailed: 'Google sign up failed. Please try again.',
        linkedinFailed: 'LinkedIn sign up failed. Please try again.',
        joiningOrg: 'Joining',
        inviteInvalid: 'This invite link is no longer valid.',
        inviteExpired: 'This invite has expired.',
      },
      waitlist: {
        backToHome: 'Back to Home',
        earlyAccess: 'EARLY ACCESS',
        joinThe: 'Join the ',
        future: 'Future',
        ofSales: ' of Sales',
        subtitle: 'Be the first to experience AI-powered sales automation...',
        teamsWaiting: 'sales teams already waiting',
        reserveSpot: 'Reserve Your Spot',
        reserveSubtitle: 'Join the waitlist and get notified when we launch',
        whatYouGet: "What you'll get",
        benefits: {
          earlyAccess: {
            title: 'Early Access',
            description: 'Be the first to try new features',
          },
          launchPricing: {
            title: 'Launch Pricing',
            description: 'Lock in special launch pricing forever',
          },
          productUpdates: {
            title: 'Product Updates',
            description: 'Get exclusive behind-the-scenes updates',
          },
          shapeProduct: {
            title: 'Shape the Product',
            description: 'Your feedback directly influences our roadmap',
          },
        },
        trustBadges: {
          noCreditCard: 'No credit card required',
          cancelAnytime: 'Cancel anytime',
          prioritySupport: 'Priority support',
        },
      },
      funnel: {
        title: 'Sales Pipeline',
        refreshTooltip: 'Refresh',
      },
    },
    campaigns: {
      title: 'Campaigns',
      createNew: 'New Campaign',
      empty: {
        title: 'No campaigns yet',
        description: 'Create your first multi-channel campaign to reach your leads',
      },
      form: {
        name: 'Campaign Name',
        namePlaceholder: 'e.g., Q1 Email Outreach',
        description: 'Description',
        descriptionPlaceholder: 'Describe the purpose of this campaign',
        channels: 'Channels',
        channelsHint: 'Select at least one channel',
        audienceType: 'Audience',
        manual: 'Manual Selection',
        segment: 'Filter-based Segment',
        schedule: 'Schedule',
        scheduleLater: 'Schedule for later',
        create: 'Create Campaign',
      },
      channels: {
        email: 'Email',
        sms: 'SMS',
        whatsapp: 'WhatsApp',
        voice: 'Voice',
      },
      status: {
        draft: 'Draft',
        active: 'Active',
        completed: 'Completed',
      },
      actions: {
        activate: 'Activate',
        complete: 'Complete',
        edit: 'Edit',
        delete: 'Delete',
      },
      detail: {
        leads: 'Leads',
        sent: 'Sent',
        delivered: 'Delivered',
        failed: 'Failed',
        createdBy: 'Created by',
        activatedOn: 'Activated on',
        completedOn: 'Completed on',
      },
      segment: {
        stageFilter: 'Funnel Stage',
        tagsFilter: 'Tags',
        sourceFilter: 'Lead Source',
        dateRangeFilter: 'Created Date Range',
        previewCount: 'leads match this filter',
      },
      confirmActivate: 'Are you sure you want to activate this campaign?',
      confirmDelete: 'Are you sure you want to delete this campaign?',
    },
    whatsapp: {
      title: 'WhatsApp',
      subtitle: 'Conversations with your leads',
      searchPlaceholder: 'Search conversations...',
      empty: {
        title: 'No conversations yet',
        description: 'WhatsApp conversations will appear here when leads message you.',
      },
      window: {
        open: '24h window open',
        closed: 'Window closed',
        useTemplate: 'Send Template Message',
        hint: '24h window closed. Use a template to re-engage.',
        remaining: 'remaining',
      },
      input: {
        placeholder: 'Type a message...',
      },
      templates: {
        title: 'Message Templates',
        sync: 'Sync from Meta',
        syncing: 'Syncing...',
        empty: 'No approved templates found. Sync from Meta to load your templates.',
      },
      status: {
        sent: 'Sent',
        delivered: 'Delivered',
        read: 'Read',
        failed: 'Failed',
      },
    },
    linkedinSearch: {
      title: 'LinkedIn Search',
      description: 'Create search segments to find and enrich LinkedIn profiles',
      newSegment: 'New Segment',
      backToSegments: 'Back to segments',
      emptyTitle: 'No search segments yet',
      emptyDescription: 'Create a segment to start searching and enriching LinkedIn profiles',
      statuses: {
        draft: 'Draft',
        running: 'Running',
        completed: 'Completed',
        failed: 'Failed',
      },
      segments: {
        found: 'Found',
        enriched: 'Enriched',
        skipped: 'Skipped',
        failed: 'Failed',
        running: 'Running...',
        execute: 'Execute',
        viewResults: 'View Results',
        deleteSegment: 'Delete segment',
        failedHint:
          'This search failed. Try again — if it keeps failing, contact support to check LinkedIn access.',
      },
      results: {
        title: 'Results',
        score: 'Score',
        leads: 'leads',
      },
      form: {
        title: 'New Search Segment',
        segmentName: 'Segment Name',
        segmentNamePlaceholder: 'e.g. Marketing CEOs in LATAM',
        keywords: 'Keywords',
        keywordsPlaceholder: 'e.g. CEO, Marketing Director, Founder',
        roleFilter: 'Role / Title Filter',
        roleFilterPlaceholder: 'e.g. C-Level, VP, Director',
        location: 'Location',
        locationPlaceholder: 'e.g. Mexico City, United States',
        industry: 'Industry',
        industryPlaceholder: 'e.g. Marketing & Advertising',
        companySize: 'Company Size',
        maxResults: 'Max Results (1-200)',
        createSegment: 'Create Segment',
      },
    },
    leadImport: {
      title: 'Import Leads',
      steps: {
        upload: 'Import Leads',
        mapping: 'Map Columns',
        preview: 'Preview Import',
        importing: 'Importing...',
        complete: 'Import Complete',
      },
      mappingHint: 'AI mapped your columns. Review and adjust if needed.',
      maxRows: 'Max 5,000 rows',
      uploadAndAnalyze: 'Upload & Analyze',
      preview: 'Preview',
      importNLeads: 'Import {{n}} Leads',
      noActiveTemplate:
        'No active funnel template found. Please activate a template before importing.',
      done: 'Done',
      totalRowsPreview: '{{total}} total rows found. Preview of first {{preview}} rows:',
      fileUpload: {
        dragAndDrop: 'Click to browse or drag & drop',
        fileTypes: 'CSV, XLS, or XLSX (max 10MB)',
        chooseFile: 'Choose File',
        unsupportedType: 'That file type is not supported. Use a CSV, XLS, or XLSX file.',
      },
      columnMapping: {
        fileColumn: 'File Column',
        crmField: 'CRM Field',
      },
      progress: {
        title: 'Importing Leads',
        processing: 'Processing {{n}} records...',
        doNotClose: "Please don't close this window",
      },
      summary: {
        title: 'Import Complete',
        imported: 'Imported',
        duplicates: 'Duplicates',
        errors: 'Errors',
        errorDetails: 'Error Details',
        duplicateDetails: 'Duplicate Emails',
        andMore: '...and {{count}} more',
        rowError: 'Row {{row}}: {{message}}',
      },
      confidence: {
        high: 'High',
        medium: 'Medium',
        low: 'Low',
      },
      downloadTemplate: 'Download Template',
      crmFields: {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        phone: 'Phone',
        company: 'Company',
        jobTitle: 'Job Title',
        companyUrl: 'Company URL',
        linkedinUrl: 'LinkedIn URL',
        tags: 'Tags',
        skip: 'Skip',
      },
    },
    leadSources: {
      page: {
        title: 'Lead Sources',
        subtitle: 'Connect external forms and tools to send leads directly into your funnel.',
      },
      sections: {
        connected: 'Connected',
        available: 'Available',
      },
      empty: {
        connectedTitle: 'No connections yet',
        connectedDescription:
          'Connect a form or tool below to start receiving leads automatically.',
      },
      status: {
        active: 'Active',
        paused: 'Paused',
        archived: 'Removed',
      },
      actions: {
        create: 'New connection',
        rotate: 'Rotate credential',
        revoke: 'Remove connection',
        delete: 'Delete permanently',
        viewDetails: 'View details',
        copy: 'Copy',
        done: 'Done',
        cancel: 'Cancel',
        retry: 'Retry',
      },
      readOnlyNotice:
        'Ask an owner or admin to manage connections. You can still see connection health and mapping.',
      confirmRevoke: {
        title: 'Remove this connection?',
        message: 'Intake stops immediately. Leads already received keep their source.',
      },
      confirmDelete: {
        title: 'Delete this connection permanently?',
        message: 'Leads already received are kept, but lose their connection reference.',
      },
      errors: {
        loadFailed: 'Could not load lead sources. Pull to refresh or try again later.',
        catalogLoadFailedTitle: 'Could not load providers',
        catalogLoadFailed: 'Something went wrong loading the available providers. Please retry.',
      },
      secretModal: {
        title: 'Save your credential now',
        warning:
          "This is the only time we'll show this value. We only keep a hash — if you lose it, rotate to get a new one.",
        urlLabel: 'Intake URL',
        keyLabel: 'API key',
        copied: 'Copied to clipboard',
        acknowledge: 'I have saved this credential',
        doneDisabledHint: 'Confirm you saved the credential to continue',
      },
      detail: {
        title: 'Connection details',
        mappingTitle: 'Field mapping',
        credentialsTitle: 'Credentials',
        errorsTitle: 'Recent errors',
        noErrors: 'No errors reported',
        lastErrorAt: 'Last error',
        lastErrorCode: 'Error code',
        lastSubmissionAt: 'Last submission',
        rateLimitLabel: 'Rate limit',
      },
    },
    legal: {
      terms: {
        heading: 'Terms of Service',
        placeholder: 'TODO: content pending — please fill in with legal copy before publishing.',
      },
      cookiePolicy: {
        heading: 'Cookie Policy',
        placeholder: 'TODO: content pending — please fill in with legal copy before publishing.',
      },
      gdpr: {
        heading: 'GDPR & Data Privacy',
        placeholder: 'TODO: content pending — please fill in with legal copy before publishing.',
      },
    },
    marketing: {
      about: {
        heading: 'About Us',
        placeholder: 'TODO: content pending — please fill in with company story before publishing.',
      },
      press: {
        heading: 'Press Kit',
        placeholder: 'TODO: content pending — please fill in with press assets before publishing.',
      },
    },
  },
  es: {
    common: {
      appName: 'Panel de Embudos FlowSoft',
      creditsExhausted: 'Créditos de IA agotados — se renuevan el {{date}}',
      creditsExhaustedNoDate:
        'Créditos de IA agotados — se renuevan en tu próximo período de facturación',
      selectSearchPlaceholder: 'Buscar…',
      selectNoMatches: 'Sin coincidencias',
      tagline: 'Mide, analiza y optimiza tu embudo de ventas',
      startFreeTrial: 'Inicia prueba gratis',
      watchDemo: 'Ver demo →',
      languageLabel: 'Idioma',
      english: 'Inglés',
      spanish: 'Español',
      portuguese: 'Portugués',
      englishShort: 'EN',
      spanishShort: 'ES',
      portugueseShort: 'PT',
      statuses: {
        active: 'Activo',
        paused: 'Pausado',
        pending: 'Pendiente',
        processing: 'Procesando',
        running: 'En ejecución',
        completed: 'Completado',
        failed: 'Fallido',
        queued: 'En cola',
        cancelled: 'Cancelado',
        success: 'Éxito',
        partialSuccess: 'Éxito parcial',
        error: 'Error',
        live: 'En vivo',
        offline: 'Desconectado',
      },
      errors: {
        generic: 'Algo salió mal. Inténtalo de nuevo.',
      },
      loading: 'Cargando...',
      actions: {
        on: 'Activado',
        off: 'Desactivado',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        confirm: 'Confirmar',
        save: 'Guardar',
        saveChanges: 'Guardar cambios',
        edit: 'Editar',
        create: 'Crear',
        remove: 'Quitar',
        add: 'Agregar',
        clear: 'Limpiar',
        setPrimary: 'Establecer como principal',
        close: 'Cerrar',
        goBack: 'Volver',
        tryAgain: 'Reintentar',
        send: 'Enviar',
        go: 'Ir',
        done: 'Listo',
        move: 'Mover',
        export: 'Exportar',
        import: 'Importar',
        refresh: 'Actualizar',
        logout: 'Cerrar sesión',
        addLead: 'Agregar lead',
        revoke: 'Revocar',
        sendInvite: 'Enviar invitación',
        call: 'Llamar',
      },
      unsavedChanges: {
        title: 'Cambios sin guardar',
        message: 'Tienes cambios sin guardar. ¿Descartarlos?',
        discard: 'Descartar',
        keepEditing: 'Seguir editando',
      },
      offline: 'Sin conexion. Algunas funciones pueden no estar disponibles.',
      and: 'y',
      states: {
        loading: 'Cargando...',
        saving: 'Guardando...',
        creating: 'Creando...',
        processing: 'Procesando...',
        on: 'Sí',
        off: 'No',
      },
      sources: {
        email: 'Email',
        linkedin: 'LinkedIn',
        instagram: 'Instagram',
        facebook: 'Facebook',
        twitter: 'Twitter',
        form: 'Formulario',
        scraping: 'Scraping',
        manual: 'Manual',
        website: 'Sitio web',
        referral: 'Referido',
        other: 'Otro',
        allSources: 'Todas las fuentes',
        webForm: 'Formulario web',
        linkedinEnrichment: 'Enriquecimiento de LinkedIn',
        linkedinSegmentSearch: 'Búsqueda por segmento de LinkedIn',
        instagramEnrichment: 'Enriquecimiento de Instagram',
        instagramSegmentSearch: 'Búsqueda por segmento de Instagram',
        import: 'Importación',
        event: 'Evento',
      },
      deviceTypes: {
        mobile: 'Móvil',
        desktop: 'Escritorio',
        tablet: 'Tablet',
        unknown: 'Desconocido',
      },
      agentTypes: {
        leadQualification: 'Calificación de leads',
        leadEnrichment: 'Enriquecimiento de leads',
        followUpScheduler: 'Programador de seguimiento',
        sentimentAnalysis: 'Análisis de sentimiento',
        promptEnricher: 'Enriquecedor de Prompts',
        linkedinCompanyResearch: 'Investigación LinkedIn',
        browserCompanyResearch: 'Investigación Web',
        flowsellaiCompanyResearch: 'Datos FlowSellAI',
        instagramCompanyResearch: 'Investigación Instagram',
        chatOrchestrator: 'Orquestador de Chat',
        stageProspectIntake: 'Ingreso de prospectos',
        stageFirstContact: 'Primer contacto',
        stageQualificationDeep: 'Calificación profunda',
        stageProposalPrep: 'Preparación de propuesta',
        stageWonOnboarding: 'Incorporación de ganados',
        stageLostAnalysis: 'Análisis de perdidos',
        linkedinEnrichment: 'Enriquecimiento LinkedIn',
        instagramEnrichment: 'Enriquecimiento Instagram',
        instagramCompanyEnrichment: 'Enriquecimiento de empresa Instagram',
        companyEnrichment: 'Enriquecimiento de empresa',
        voiceCall: 'Resumen de llamada',
        generic: 'Agente de IA',
      },
      agentLabels: {
        qualifier: 'Calificador',
        enricher: 'Enriquecedor',
        scheduler: 'Programador',
        analyst: 'Analista',
      },
      timeRanges: {
        sevenDays: '7D',
        thirtyDays: '30D',
        ninetyDays: '90D',
      },
      fallbacks: {
        unknownLead: 'Lead desconocido',
        unknown: 'Desconocido',
        user: 'Usuario',
        member: 'Miembro',
      },
      detail: 'Detalle',
      roles: {
        owner: 'Propietario',
        admin: 'Administrador',
        member: 'Miembro',
        viewer: 'Visualizador',
      },
    },
    layout: {
      title: 'FlowSellAI',
      login: 'Iniciar sesión',
      dashboard: 'Panel',
      analytics: 'Analítica',
      workflows: 'Flujos de trabajo',
      workflowExecutions: 'Ejecuciones de Flujos',
      sidebar: {
        brandName: 'FlowsellAI',
        brandSubtitle: 'Centro de Comando',
        settings: 'Configuración',
        logout: 'Cerrar sesión',
        expandSidebar: 'Expandir barra lateral',
        collapseSidebar: 'Contraer barra lateral',
      },
      mobileTabBar: {
        dashboard: 'Panel',
        pipeline: 'Pipeline',
        leads: 'Leads',
        chat: 'Chat',
        calls: 'Llamadas',
        analytics: 'Analítica',
        settings: 'Ajustes',
      },
      nav: {
        dashboard: 'Panel',
        pipeline: 'Pipeline',
        leads: 'Leads',
        calendar: 'Calendario',
        analytics: 'Analítica',
        chat: 'Chat',
        aiAgents: 'Agentes IA',
        missionControl: 'Centro de Misión',
        workflows: 'Flujos de trabajo',
        voiceAgent: 'Agente de Voz',
        voiceAgents: 'Agentes de Voz',
        campaigns: 'Campañas',
        whatsapp: 'WhatsApp',
        leadSources: 'Fuentes de Leads',
        settings: 'Configuración',
      },
      navSections: {
        main: 'PRINCIPAL',
        insights: 'INFORMACIÓN',
        automation: 'AUTOMATIZACIÓN',
      },
    },
    auth: {
      title: 'FlowSellAI',
      subtitle: 'Mide, analiza y optimiza tu embudo de ventas',
      panelHeadline: 'Cerrá más ventas, en piloto automático.',
      panelSubhead:
        'FlowSellAI captura, califica y nutre cada lead—para que tu equipo solo intervenga en la conversación que importa.',
      panelSocialProof: 'Los fundadores se están sumando a la lista de espera.',
      getStarted: 'Comenzar',
      magicLinkHint: 'Te enviaremos un código de 6 dígitos para ingresar.',
      checkSpam: '¿No llegó? Revisá tu carpeta de spam.',
      features: {
        analytics: {
          title: 'Analítica en tiempo real',
          description: 'Supervisa el rendimiento de tu embudo al instante',
        },
        conversion: {
          title: 'Seguimiento de conversiones',
          description: 'Mide cada paso del recorrido de tus clientes',
        },
        leads: {
          title: 'Gestión de leads',
          description: 'Organiza y nutre tus leads de forma efectiva',
        },
        campaigns: {
          title: 'Optimización de campañas',
          description: 'Mejora el ROI con decisiones basadas en datos',
        },
      },
      cta: 'Continuar con Auth0',
      secureNote: 'Autenticación segura con Auth0',
      footer: '© 2026 FlowSoftDev. Todos los derechos reservados.',
      sendMagicLink: 'Enviar enlace mágico',
      checkInbox: 'Revisá tu bandeja de entrada',
      enterCode: 'Ingresá el código de 6 dígitos que te enviamos',
      verifyCode: 'Verificar código',
      codeInvalid: 'Ese código es inválido o expiró — intentá de nuevo.',
      linkInvalid: 'El enlace mágico ya no es válido — solicitá uno nuevo.',
      resendIn: 'Reenviar en {{seconds}} segundos',
      codeResent: 'Código reenviado a {{email}}',
      accountDisabled:
        'Esta cuenta fue deshabilitada. Por favor contactá al administrador de tu espacio de trabajo.',
      connectionUnavailable:
        'No podemos enviar el enlace en este momento. Por favor intentá más tarde.',
      differentEmail: '¿Dirección incorrecta? Usá un email diferente.',
      inviteMismatch: 'Esta invitación fue enviada a otro correo electrónico.',
      emailTaken: 'Este correo ya está vinculado a otra cuenta.',
      confirmJoinOrg: '¿Unirte a {{organizationName}} como {{role}}?',
      invitedBy: 'Invitado por {{inviter}}',
      confirmJoin: 'Unirme',
      declineJoin: 'Ahora no',
      rateLimited: 'Demasiados intentos. Esperá antes de volver a intentar.',
      unknownError: 'Algo salió mal. Por favor intentá de nuevo.',
      freeCapacityExhaustedTitle: 'No hay cupos disponibles',
      freeCapacityExhausted:
        'El acceso gratuito está completo por ahora. Unite a la lista de espera y te avisaremos por email apenas se libere un cupo.',
      joinWaitlist: 'Unirme a la lista de espera',
      orgDomainExistsTitle: 'Tu empresa ya tiene un espacio de trabajo',
      orgDomainExists:
        '{{organizationName}} ya está registrada en FlowSoft. Pedile a un administrador que te invite y luego iniciá sesión con el mismo email.',
      orgDomainExistsInline:
        'Esta empresa ya tiene un espacio de trabajo. Pedile a un administrador que te invite o usá otro email.',
      orgDomainExistsOrgFallback: 'Tu empresa',
    },
    dashboard: {
      overviewTitle: 'Resumen del panel',
      greeting: {
        morning: 'Buenos días',
        afternoon: 'Buenas tardes',
        evening: 'Buenas noches',
      },
      header: {
        badge: 'Resumen',
        subtitle: '¡Bienvenido de nuevo! Aquí está tu resumen de rendimiento de hoy.',
      },
      metrics: {
        revenue: {
          title: 'Ingresos totales',
          subtitle: 'Últimos 30 días',
        },
        visitors: {
          title: 'Visitantes totales',
          subtitle: 'Últimos 30 días',
        },
        conversion: {
          title: 'Tasa de conversión',
          subtitle: 'Leads a compras',
        },
        campaigns: {
          title: 'Campañas activas',
          subtitle: 'En ejecución este mes',
        },
        comingSoon: 'Próximamente',
        vsLastPeriod: 'vs período anterior',
      },
      recentActivity: {
        title: 'Actividad Reciente',
        viewAll: 'Ver Todo',
        loading: 'Cargando actividad...',
        empty: {
          title: 'Sin actividad reciente',
          description: 'La actividad de tus leads aparecerá aquí',
        },
        activities: {
          leadCreated: {
            title: 'Nuevo lead agregado',
            description: 'se unió vía LinkedIn',
          },
          stageChanged: {
            title: 'Lead movido a Calificado',
            description: 'progresó en el embudo',
          },
          workflowTriggered: {
            title: 'Enriquecimiento completado',
            description: 'Datos de perfil actualizados para',
          },
          noteAdded: {
            title: 'Nota agregada',
            description: 'Seguimiento programado con lead Empresarial',
          },
        },
        timeAgo: {
          minutes: 'minutos',
          hour: 'hora',
          hours: 'horas',
        },
      },
      quickActions: {
        title: 'Acciones Rápidas',
        newLead: {
          label: 'Nuevo Lead',
          description: 'Agrega un nuevo lead a tu embudo',
        },
        analytics: {
          label: 'Analítica',
          description: 'Ver métricas de rendimiento',
        },
        runWorkflow: {
          label: 'Ejecutar Flujo',
          description: 'Activar una automatización',
        },
        voiceCampaign: {
          label: 'Campaña de Voz',
          description: 'Lanzar contacto con IA por voz',
        },
      },
      comingSoonTitle: 'Próximamente',
      comingSoonItems: [
        'Visualización del embudo de ventas',
        'Gráfico de fuentes de tráfico',
        'Mapa de distribución geográfica',
        'Analítica en tiempo real',
      ],
      activityTypes: {
        leadCreated: 'Lead creado',
        stageChanged: 'Etapa cambiada',
        noteAdded: 'Nota agregada',
        leadAssigned: 'Lead asignado',
        emailSent: 'Email enviado',
        activity: 'Actividad',
        unknownLead: 'Lead desconocido',
      },
    },
    analytics: {
      comingSoon: {
        title: 'Próximamente',
        predictiveAnalytics: 'Analítica predictiva avanzada con modelos de ML',
        cohortAnalysis: 'Análisis de cohortes y segmentación de clientes',
        funnelBuilder: 'Constructor de embudos personalizado con arrastrar y soltar',
      },
      overview: {
        metrics: {
          leadVelocityRate: 'TASA DE VELOCIDAD DE LEADS',
          leadVelocityRateSubtitle: 'Leads que llegan a calificado frente al período anterior',
          pipelineScore: 'PUNTUACIÓN DEL PIPELINE',
          pipelineScoreSubtitle: 'Conteo de leads ponderado por etapa',
          pipelineScoreCoverage_one:
            'Conteo ponderado por etapa de {{count}} lead en etapas abiertas',
          pipelineScoreCoverage_other:
            'Conteo ponderado por etapa de {{count}} leads en etapas abiertas',
          winRate: 'TASA DE CIERRE',
          winRateSubtitle: 'Ganados sobre todo lo cerrado en el período',
        },
        untouchedAlert_one: '{{count}} lead no ha tenido ningún intento de contacto',
        untouchedAlert_other: '{{count}} leads no han tenido ningún intento de contacto',
        leadsOverTime: 'Leads a lo largo del tiempo',
        leadsOverTimeSubtitle_one: '{{count}} lead creado en este período',
        leadsOverTimeSubtitle_other: '{{count}} leads creados en este período',
        leadSources: 'Fuentes de leads',
        leadSourcesSubtitle_one: '{{count}} lead por fuente',
        leadSourcesSubtitle_other: '{{count}} leads por fuente',
        conversionFunnel: 'Embudo de conversión',
        activityByDay: 'Actividad por día',
        activityByDaySubtitle:
          'Contactos de IA y humanos, sumados por día de la semana en este período',
        qualificationScore: 'Puntuación de calificación por IA',
        qualificationCoverage: '{{scored}} de {{total}} leads con puntuación',
        noActivity: 'No hay actividad de leads ni puntuaciones de calificación en este período',
        insights: 'Información',
        topSource: 'FUENTE PRINCIPAL',
        topSourceDescription_one: '{{count}} lead de esta fuente',
        topSourceDescription_other: '{{count}} leads de esta fuente',
        busiestStage: 'ETAPA CON MÁS CARGA',
        busiestStageDescription_one: '{{count}} lead abierto esperando aquí',
        busiestStageDescription_other: '{{count}} leads abiertos esperando aquí',
        busiestStageEmpty: 'Ninguna etapa abierta tiene leads',
        newLeads: 'LEADS NUEVOS',
        newLeadsDescription: 'Creados en este período',
        weekdays: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
      },
      funnel: {
        metrics: {
          avgCycleTime: 'TIEMPO DE CICLO PROMEDIO',
          median: 'Mediana: {{value}}',
          bottleneck: 'CUELLO DE BOTELLA',
          bottleneckSubtitle: 'La etapa más lenta por la que los leads pasaron realmente',
        },
        conversionFunnelSubtitle:
          'Barras: leads que están en cada etapa ahora mismo. %: porcentaje de la etapa inicial que llegó hasta esta; en las etapas de cierre, el que terminó ahí.',
        timeInStage: {
          title: 'Tiempo por etapa',
          subtitle:
            'Días promedio que tardaron los leads que avanzaron desde cada etapa en este período, no los leads que están ahí ahora.',
        },
        stageDropoff: {
          title: 'Análisis de abandono por etapa',
          subtitle:
            'Resultados de todos los leads que estuvieron en cada etapa durante este período',
          population_one: '{{count}} lead',
          population_other: '{{count}} leads',
          categories: {
            advanced: 'Avanzados',
            won: 'Ganados',
            active: 'Activos',
            stale: 'Estancados',
            lost: 'Perdidos',
            regressed: 'Retrocedidos',
          },
        },
      },
      team: {
        metrics: {
          avgResponseTime: 'TIEMPO DE RESPUESTA PROMEDIO',
          median: 'Mediana: {{value}}',
          responseTimeCoverage: 'Mediana: {{value}} · {{responded}} de {{total}} leads contactados',
          responseTimeFallback: 'Tiempo hasta el primer contacto',
          untouchedLeads: 'LEADS SIN CONTACTAR',
          untouchedSubtitle: '{{percentage}} de los leads con más de {{days}} días',
          untouchedFallback: 'Leads sin ningún intento de contacto',
        },
        untouchedAlert_one:
          '{{count}} lead no ha tenido ningún intento de contacto en más de {{days}} días',
        untouchedAlert_other:
          '{{count}} leads no han tenido ningún intento de contacto en más de {{days}} días',
        leaderboard: {
          title: 'Ranking de agentes',
          subtitle: 'Rendimiento ordenado por tasa de conversión',
          subtitleSingle: 'El único agente con leads asignados',
          empty: 'No hay datos de rendimiento del equipo',
          coverageNote_one:
            'Cubre {{assigned}} de {{total}} leads. {{count}} lead sin asignar no se muestra aquí.',
          coverageNote_other:
            'Cubre {{assigned}} de {{total}} leads. {{count}} leads sin asignar no se muestran aquí.',
          columns: {
            rank: '#',
            agent: 'AGENTE',
            assigned: 'ASIGNADOS',
            won: 'GANADOS',
            conversion: 'CONV %',
            cycle: 'CICLO',
          },
        },
      },
      campaigns: {
        sourcePerformance: {
          title: 'Rendimiento por fuente',
          subtitle: 'Leads y tasa de conversión por fuente',
        },
        deviceBreakdown: {
          title: 'Desglose por dispositivo',
          subtitle: 'Leads por tipo de dispositivo',
          centerLabel: 'Leads',
        },
        geo: {
          title: 'Distribución geográfica',
          subtitle: 'Leads por país',
          leads_one: '{{count}} lead',
          leads_other: '{{count}} leads',
        },
        empty: 'No hay datos de campañas para este período',
      },
      sourcePerformance: {
        columns: {
          source: 'FUENTE',
          leadCount: 'LEADS',
          wonCount: 'GANADOS',
          conversionRate: 'CONV %',
        },
        empty: 'No hay datos de fuentes disponibles',
      },
      timeInStage: {
        empty: 'No hay datos de etapas disponibles',
        terminalMarker: 'cerrado',
        legend: {
          fast: '< 3 días',
          medium: '3-7 días',
          slow: '> 7 días',
          terminal: 'resultado cerrado',
          notMeasured: '{{dash}} sin medir',
        },
      },
      screen: {
        title: 'Analítica',
        subtitle: 'Sigue el rendimiento de tu embudo',
      },
      tabs: {
        overview: 'Resumen',
        funnel: 'Embudo',
        campaigns: 'Campañas',
        team: 'Equipo',
      },
      states: {
        errorTitle: 'Algo salió mal',
        errorMessage: 'No se pudieron cargar los datos de analítica. Inténtalo de nuevo.',
        retry: 'Reintentar',
        retryAccessibilityLabel: 'Reintentar la carga de datos',
        emptyTitle: 'Aún no hay nada que mostrar',
        emptyMessage: 'No hay datos para este período',
      },
      alert: {
        dismiss: 'Descartar alerta',
      },
      units: {
        days: '{{value}} días',
      },
    },
    topbar: {
      dateRange: 'Últimos 30 días',
      searchPlaceholder: 'Busca lo que necesites...',
      workspace: 'Espacio de trabajo',
    },
    landing: {
      hero: {
        badge: 'AUTOMATIZACIÓN DE VENTAS CON IA',
        social: 'Fundadores se están sumando a la lista ahora',
        trust: {
          founding: 'Precio de miembro fundador',
          noCard: 'Sin tarjeta',
          earlyAccess: 'Acceso anticipado',
        },
        ctaNote: 'Sin tarjeta de crédito',
        preTitle: 'Tu IA trabaja el embudo.',
        mainTitle: 'Tus vendedores cierran.',
        titleLead: 'Transforma tu',
        titleHighlight: 'Embudo de Ventas',
        titleTail: 'con IA inteligente',
        subtitle:
          'FlowSellAI captura, califica y nutre cada lead automáticamente—tu equipo solo entra para la conversación que necesita un humano.',
        primaryCta: 'Iniciar sesión',
        secondaryCta: 'Ver demo →',
        stats: {
          fasterResponse: 'Respuesta más rápida',
          automationRate: 'Tasa de automatización',
          conversionGrowth: 'Crecimiento de conversión',
        },
      },
      features: {
        badge: 'FUNCIONES POTENTES',
        title: 'Todo lo que necesitas',
        accent: 'para escalar',
        subtitle:
          'Automatización con IA que transforma cómo trabaja tu equipo de ventas. Cierra más acuerdos con menos esfuerzo.',
        learnMore: 'Ver más →',
        sourceFlow: {
          linkedin: 'LinkedIn',
          email: 'Correo',
          webForm: 'Formulario web',
          enrichedLead: 'Lead enriquecido',
        },
        items: {
          aiLeadCapture: {
            title: 'Captura de leads con IA',
            description:
              'Captura y enriquece leads automáticamente desde email, LinkedIn y formularios web con categorización inteligente.',
          },
          smartQualification: {
            title: 'Calificación inteligente',
            description:
              'La IA analiza interacción, comportamiento y encaje para puntuar leads y priorizar las mejores oportunidades.',
          },
          automatedNurturing: {
            title: 'Nutrición automatizada',
            description:
              'Seguimientos personalizados a escala con timing y mensajes optimizados por IA para cada prospecto.',
          },
          realTimeAnalytics: {
            title: 'Analítica en tiempo real',
            description:
              'Mide el rendimiento del embudo, tasas de conversión y efectividad de la IA con insights accionables.',
          },
        },
      },
      product: {
        badge: 'EL PRODUCTO',
        title: 'Tu pipeline,',
        accent: 'totalmente automatizado',
        subtitle:
          'Mira cómo cada lead avanza por tu embudo—capturado, calificado y nutrido por IA en tiempo real. Tu equipo solo entra para cerrar.',
        board: {
          live: 'EN VIVO',
          stages: {
            newLead: 'Nuevo Lead',
            discovery: 'Descubrimiento',
            proposal: 'Propuesta',
            closedWon: 'Ganado',
          },
        },
        items: {
          pipeline: {
            title: 'Embudo en tiempo real',
            description: 'Actualizaciones en vivo de cada oportunidad',
          },
          aiActivity: {
            title: 'Actividad de agentes IA',
            description: 'Monitorea el desempeño de la automatización',
          },
          analytics: {
            title: 'Analítica avanzada',
            description: 'Perspectivas basadas en datos',
          },
        },
      },
      foundingMembers: {
        badge: 'MIEMBROS FUNDADORES',
        title: 'Entra temprano.',
        accent: 'Ayuda a definir lo que construimos',
        subtitle:
          'FlowSellAI está en prelanzamiento. Los miembros fundadores obtienen más que un descuento—obtienen un lugar en la mesa.',
        benefits: {
          pricing: {
            title: 'Precio fundador, bloqueado de por vida',
            description:
              'Paga la tarifa de miembro fundador para siempre—incluso tras el lanzamiento público.',
          },
          access: {
            title: 'Acceso anticipado',
            description:
              'Entra primero a medida que abrimos por oleadas. Sin esperas una vez que estás dentro.',
          },
          voice: {
            title: 'Línea directa con el equipo',
            description:
              'Tu feedback define el roadmap. Construimos con los miembros fundadores, no solo para ellos.',
          },
          onboarding: {
            title: 'Onboarding prioritario',
            description: 'Configuración guiada para que captures leads desde el primer día.',
          },
        },
        vision: {
          quote:
            'Estamos construyendo la herramienta de ventas que siempre quisimos—un solo sistema que captura, califica y nutre cada lead, para que los vendedores hagan lo que solo un humano puede: cerrar. Únete temprano y ayuda a darle forma.',
          attribution: 'El equipo de FlowSellAI',
        },
      },
      pricing: {
        badge: 'PRECIOS DE LANZAMIENTO',
        title: 'Planes que crecen contigo',
        subtitle:
          'Asegura el precio de miembro fundador antes del lanzamiento. Sin compromiso, sin tarjeta—solo tu lugar en la fila.',
        subtitleComingSoon:
          'Estamos ajustando los precios. Mientras tanto, mira lo que incluye cada plan.',
        monthly: 'Mensual',
        yearly: 'Anual',
        yearlyDiscount: 'Ahorra 20%',
        mostPopular: 'Más Popular',
        included: 'Incluido',
        notIncluded: 'No incluido',
        perMonth: '/mes',
        billedYearly: 'facturado anualmente',
        from: 'Desde',
        priceLoading: 'Cargando precio',
        priceUnavailable: 'Precio no disponible',
        comingSoon: 'Próximamente',
        currencyNote: 'Precios mostrados en tu moneda local:',
        countrySelector: {
          label: 'Región de facturación:',
          colombia: 'Colombia (COP)',
          international: 'Internacional (USD)',
        },
        checkoutUnavailable:
          'El pago no está disponible para esta organización o plan en este momento.',
        checkoutUnavailableCta: 'Pago no disponible',
        creditsDisclaimer:
          'La voz con IA se factura por créditos. Las tarifas se ajustan automáticamente según el país de destino.',
        usageBased: 'Por consumo',
        plans: {
          starter: {
            name: 'Starter',
            description: 'Perfecto para fundadores que validan ventas outbound solo por email.',
            cta: 'Iniciar pago',
          },
          growth: {
            name: 'Growth',
            description:
              'Para equipos en crecimiento que amplían su alcance con email y voz con IA.',
            cta: 'Iniciar pago',
          },
          professional: {
            name: 'Professional',
            description:
              'Para equipos en expansión que necesitan mayor volumen e IA avanzada en cada canal.',
            cta: 'Iniciar pago',
          },
          business: {
            name: 'Business',
            description:
              'Para equipos de ventas multinacionales de alto volumen con soporte dedicado.',
            cta: 'Iniciar pago',
          },
          enterprise: {
            name: 'Enterprise',
            description:
              'Soluciones personalizadas para grandes organizaciones con necesidades avanzadas y SLA.',
            cta: 'Hablar con Ventas',
          },
        },
        features: {
          credits: 'Créditos / mes',
          aiAgents: 'Agentes IA',
          aiLeadGeneration: 'Generación de leads con IA',
          emailOutreach: 'Email Outreach',
          whatsapp: 'WhatsApp',
          sms: 'SMS',
          aiVoiceAgent: 'Agente de Voz IA',
          aiLeadScoring: 'Lead Scoring IA',
          analytics: 'Analítica',
          workspaces: 'Espacios de trabajo',
          teamMembers: 'Miembros del equipo',
          support: 'Soporte',
          onboarding: 'Onboarding',
        },
        // TODO(pricing): confirm Professional/Business feature limits — placeholder values
        values: {
          starter: {
            credits: '25K',
            aiAgents: 'Ilimitados (según créditos)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: false,
            sms: false,
            aiVoiceAgent: false,
            aiLeadScoring: 'Básico',
            analytics: 'Básico',
            workspaces: '1',
            teamMembers: '1',
            support: 'Email',
            onboarding: 'Autoservicio',
          },
          growth: {
            credits: '150K',
            aiAgents: 'Ilimitados (según créditos)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: 'Por consumo',
            sms: 'Por consumo',
            aiVoiceAgent: 'Por consumo',
            aiLeadScoring: 'Avanzado',
            analytics: 'Avanzado + Pronósticos',
            workspaces: '5',
            teamMembers: '2',
            support: 'Prioritario + Chat',
            onboarding: 'Configuración guiada',
          },
          // TODO(pricing): confirm Professional feature limits — placeholder values
          professional: {
            credits: '350K',
            aiAgents: 'Ilimitados (según créditos)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: 'Por consumo',
            sms: 'Por consumo',
            aiVoiceAgent: 'Por consumo',
            aiLeadScoring: 'Avanzado + Custom',
            analytics: 'Avanzado + Pronósticos',
            workspaces: '12',
            teamMembers: '5',
            support: 'Prioritario + Chat',
            onboarding: 'Configuración guiada',
          },
          // TODO(pricing): confirm Business feature limits — placeholder values
          business: {
            credits: '750K',
            aiAgents: 'Ilimitados (según créditos)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: 'Por consumo',
            sms: 'Por consumo',
            aiVoiceAgent: 'Por consumo',
            aiLeadScoring: 'Avanzado + Custom',
            analytics: 'Completo + Reportes',
            workspaces: '25',
            teamMembers: '5',
            support: 'Gerente dedicado',
            onboarding: 'Implementación 1-a-1',
          },
          enterprise: {
            credits: 'Personalizado',
            aiAgents: 'Ilimitados (según créditos)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: true,
            sms: true,
            aiVoiceAgent: 'Personalizado',
            aiLeadScoring: 'Modelos Custom',
            analytics: 'Completo + Exportación BI',
            workspaces: 'Personalizado',
            teamMembers: '5',
            support: 'Dedicado + SLA',
            onboarding: 'Onboarding completo',
          },
        },
      },
      problemSolution: {
        badge: 'EL PROBLEMA',
        title: 'Tu stack de ventas está disperso.',
        accent: 'Lo unimos todo',
        subtitle:
          'Un solo sistema que captura, califica y nutre cada lead—en lugar de cinco herramientas que nunca se hablan entre sí.',
        todayLabel: 'Cómo trabajan los equipos de ventas hoy',
        ourLabel: 'Cómo funciona FlowSellAI',
        problems: {
          spreadsheets: 'Leads enterrados en hojas de cálculo',
          followups: 'Seguimientos que se pierden',
          qualify: 'Sin forma de calificar a escala',
          channels: 'Canales que no se hablan entre sí',
          guessing: 'Vendedores adivinando a quién llamar',
        },
        solutions: {
          capture: {
            title: 'Cada lead capturado automáticamente',
            description: 'Email, LinkedIn y formularios web—enriquecidos y depurados al instante.',
          },
          qualify: {
            title: 'Calificado por comportamiento real',
            description:
              'La IA puntúa encaje e intención, así los vendedores atacan primero lo más caliente.',
          },
          nurture: {
            title: 'Nutrido en piloto automático',
            description: 'Seguimientos personalizados en el momento justo, en cada canal.',
          },
          unified: {
            title: 'Un pipeline unificado',
            description: 'Cada canal y señal en un solo embudo que todo tu equipo puede ver.',
          },
        },
      },
      automation: {
        badge: 'FLUJO DE AUTOMATIZACIÓN IA',
        title: 'La IA maneja todo',
        accent: 'excepto el cierre',
        subtitle:
          'Nuestros agentes de IA automatizan cada etapa de tu embudo, desde el primer contacto hasta la reunión calificada, para que tu equipo se enfoque en cerrar.',
        steps: {
          capture: {
            stage: 'Captura y califica',
            automation: '100%',
            description: 'La IA captura leads de cada canal y los puntúa por encaje e intención.',
          },
          nurture: {
            stage: 'Nutre y agenda',
            automation: '100%',
            description:
              'Seguimientos personalizados y agendado inteligente hasta que están listos para vender.',
          },
          close: {
            stage: 'Cierre',
            automation: 'Humano',
            description: 'Tu equipo entra para la única conversación que necesita un humano.',
            isHuman: true,
          },
        },
        ctaTitle: 'Pon tu embudo en piloto automático',
        ctaText:
          'Sé de los primeros en automatizar la captura, calificación y nutrición—los miembros fundadores obtienen acceso anticipado y precio bloqueado de por vida.',
      },
      socialProof: {
        badge: '✨ CONFIADO EN TODO EL MUNDO',
        title: 'Únete a equipos de ventas de alto rendimiento',
        stats: {
          salesTeams: 'Equipos de ventas',
          leadsProcessed: 'Leads procesados',
          automationRate: 'Tasa de automatización',
          averageRoi: 'ROI promedio',
        },
        testimonialsTitle: 'Lo que dicen los líderes de ventas',
        testimonials: [
          {
            quote:
              'La IA de Funnel Panel transformó por completo nuestro proceso comercial. Cerramos 3 veces más acuerdos con el mismo equipo. La automatización es increíble.',
            author: 'Sarah Chen',
            role: 'VP de Ventas',
            company: 'TechCorp',
          },
          {
            quote:
              'Nuestro equipo pasa 80% menos tiempo en tareas administrativas y 80% más hablando con leads calificados. Esta plataforma cambió el juego para las ventas B2B.',
            author: 'Michael Rodriguez',
            role: 'Director de Ventas',
            company: 'Growth Labs',
          },
          {
            quote:
              'Pasamos de horas de calificación manual a puntuación instantánea impulsada por IA. El ROI fue inmediato y sigue creciendo cada trimestre.',
            author: 'Emily Watson',
            role: 'CEO',
            company: 'SaaS Ventures',
          },
        ],
        companiesLabel: 'CONFIAN EMPRESAS LÍDERES',
        companies: ['Stripe', 'Shopify', 'Atlassian', 'Notion', 'Linear', 'Figma'],
      },
      faq: {
        badge: 'FAQ',
        title: '¿Preguntas?',
        accent: 'Tenemos respuestas',
        subtitle: 'Todo lo que necesitas saber antes de unirte a la lista de espera.',
        items: {
          live: {
            q: '¿FlowSellAI ya está disponible?',
            a: 'Estamos en prelanzamiento. Únete a la lista de espera y los miembros fundadores obtienen acceso anticipado apenas abramos—con precio bloqueado de por vida.',
          },
          cost: {
            q: '¿Cuánto costará?',
            a: 'El precio de miembro fundador empieza muy por debajo de las tarifas públicas y queda bloqueado. Verás los planes completos antes de pagar—sin tarjeta para unirte a la lista.',
          },
          channels: {
            q: '¿Qué canales soporta?',
            a: 'Email, LinkedIn, formularios web y voz con IA. Cada canal alimenta un solo pipeline.',
          },
          qualify: {
            q: '¿Cómo califica los leads la IA?',
            a: 'Puntúa cada lead por encaje e intención usando comportamiento, engagement y datos de enriquecimiento—así tus vendedores atacan primero lo más caliente.',
          },
          security: {
            q: '¿Mis datos están seguros?',
            a: 'Tus datos se cifran en tránsito y en reposo, se aíslan por organización y nunca se venden. Tú mantienes el control.',
          },
          access: {
            q: '¿Cuándo acceden los miembros fundadores?',
            a: 'Incorporamos miembros fundadores por oleadas a medida que abrimos. Únete a la lista para reservar tu lugar—los primeros entran antes.',
          },
        },
      },
      cta: {
        title: 'Sé el primero en transformar tu proceso de ventas',
        subtitle:
          'Únete a la lista de espera y sé de los primeros en automatizar tu embudo con IA. Los miembros fundadores obtienen acceso anticipado y precio bloqueado.',
        primaryCta: 'Iniciar sesión',
        perks: {
          freeTrial: 'Acceso anticipado al lanzamiento',
          noCard: 'Precio de miembro fundador',
          setup: 'Onboarding prioritario',
        },
      },
      footer: {
        brand: 'FlowSellAI',
        tagline: 'Automatización de embudos de ventas con IA para equipos modernos',
        columns: {
          product: 'Producto',
          company: 'Empresa',
          resources: 'Recursos',
          legal: 'Legal',
        },
        links: {
          features: 'Funciones',
          pricing: 'Precios',
          integrations: 'Integraciones',
          changelog: 'Historial de cambios',
          about: 'Nosotros',
          blog: 'Blog',
          careers: 'Empleos',
          press: 'Kit de prensa',
          documentation: 'Documentación',
          helpCenter: 'Centro de ayuda',
          api: 'Referencia API',
          status: 'Estado',
          privacy: 'Política de privacidad',
          terms: 'Términos del servicio',
          cookie: 'Política de cookies',
          gdpr: 'GDPR',
        },
        copyright: 'Todos los derechos reservados.',
        social: {
          twitter: 'Twitter',
          linkedin: 'LinkedIn',
          github: 'GitHub',
        },
      },
    },
    changelog: {
      title: 'Historial de cambios',
      subtitle: 'Nuevas funciones, mejoras y correcciones.',
      backToChangelog: 'Volver al historial',
      backToHome: 'Volver al inicio',
      loading: 'Cargando...',
      empty: 'Aún no hay entradas. Volvé pronto.',
      error: 'No se pudo cargar el historial. Intentá de nuevo.',
      version: 'v',
      publishedOn: 'Publicado el',
      noEntries: 'Sin actualizaciones aún',
    },
    blog: {
      title: 'Blog',
      subtitle: 'Ideas, guías e historias de producto.',
      backToBlog: 'Volver al blog',
      backToHome: 'Volver al inicio',
      loading: 'Cargando...',
      empty: 'Aún no hay publicaciones. Volvé pronto.',
      error: 'No se pudo cargar el blog. Intentá de nuevo.',
      publishedOn: 'Publicado el',
      readMore: 'Leer más',
    },
    waitlist: {
      emailLabel: 'Correo Electrónico',
      emailPlaceholder: 'tu@email.com',
      emailRequired: 'El correo electrónico es requerido',
      emailInvalid: 'Por favor ingresa un correo electrónico válido',
      nameLabel: 'Nombre Completo (Opcional)',
      namePlaceholder: 'Juan Pérez',
      nameInvalid: 'El nombre debe tener entre 2 y 100 caracteres',
      companyLabel: 'Empresa (Opcional)',
      companyPlaceholder: 'Mi Empresa S.A.',
      companyInvalid: 'El nombre de la empresa debe tener entre 2 y 100 caracteres',
      phoneLabel: 'Teléfono (Opcional)',
      phonePlaceholder: '+34 555 123 456',
      phoneInvalid: 'Por favor ingresa un número de teléfono válido (al menos 10 dígitos)',
      submitting: 'Uniéndote a la lista...',
      alreadyRegistered: 'Este correo ya está en la lista de espera',
      errorGeneric: 'Algo salió mal. Por favor intenta de nuevo.',
      successTitle: '¡Estás en la lista!',
      successMessage:
        'Gracias por unirte a nuestra lista de espera. Te notificaremos cuando lancemos.',
    },
    workflow: {
      title: 'Activar Enriquecimiento de Perfil',
      description:
        'Completa los detalles a continuación para activar el flujo de trabajo de enriquecimiento de perfil.',
      closeButton: 'Cerrar',
      nameLabel: 'Nombre',
      namePlaceholder: 'Ingresa nombre',
      nameRequired: 'El nombre es requerido',
      mode: {
        profile: 'Enriquecer un perfil',
        discover: 'Descubrir cuentas',
      },
      discovery: {
        title: 'Descubrir cuentas de Instagram',
        intro:
          'Encuentra cuentas de Instagram que todav\u00eda no conoces. Buscar no crea leads: despu\u00e9s eliges cu\u00e1les enriquecer.',
        hashtagsLabel: 'Hashtags',
        hashtagsPlaceholder: '#cafedeespecialidad, #barista',
        keywordLabel: 'Palabra clave',
        keywordPlaceholder: 'barista, tostador de caf\u00e9\u2026',
        placeLabel: 'Lugar',
        placeHelper:
          'Con una palabra clave o hashtag, filtra esos resultados a cuentas locales. Sola, encuentra cuentas que publican aqu\u00ed.',
        placePlaceholder: 'Bogot\u00e1',
        search: 'Buscar en Instagram',
        searching: 'Buscando\u2026',
        maxResultsLabel: 'Resultados m\u00e1ximos',
        limitedByPlan:
          'Tu plan permite {{max}} resultados m\u00e1s este mes, as\u00ed que la b\u00fasqueda se ajust\u00f3 a esa cantidad.',
        found: '{{count}} cuentas encontradas \u2014 {{selected}} seleccionadas',
        alreadyKnown: 'Otras {{count}} ya estaban en tu pipeline, as\u00ed que se omitieron.',
        selectAll: 'Seleccionar todas',
        clearAll: 'Quitar todas',
        business: 'Negocio',
        matches: '{{count}} publicaciones coincidentes',
        localityBadge: 'Local',
        localityReasonPostedAtPlace: 'Public\u00f3 aqu\u00ed',
        localityReasonPostTaggedAtPlace: 'Etiquet\u00f3 una publicaci\u00f3n aqu\u00ed',
        localityReasonProfileMentionsPlace: 'Menciona este lugar en su perfil',
        droppedForLocalityNote:
          '{{count}} cuentas coincidieron con tu palabra clave o hashtag, pero no mostraron se\u00f1ales de estar en {{place}}.',
        hiddenOneTimePostersNote:
          '{{count}} cuentas con una sola publicaci\u00f3n ah\u00ed fueron ocultadas.',
        placeSearchFailedWarning:
          'No pudimos buscar ese lugar, as\u00ed que los resultados se filtraron con las dem\u00e1s se\u00f1ales: publicaciones etiquetadas ah\u00ed y lo que mencionan los perfiles.',
        searchPartiallyFailedWarning:
          'Parte de la b\u00fasqueda no respondi\u00f3; puede que falten resultados.',
        segmentNameLabel: 'Nombre del segmento',
        segmentNamePlaceholder: 'Gente del caf\u00e9 en Bogot\u00e1',
        import: 'Enriquecer {{count}} cuentas',
        importing: 'Encolando\u2026',
        queued: '{{count}} cuentas encoladas para enriquecimiento',
        queuedHint:
          'Cada una pasa por el mismo an\u00e1lisis de Instagram que un perfil pegado a mano. Van a aparecer en tu pipeline a medida que terminen.',
        done: 'Listo',
        emptyNoResults:
          'Ninguna cuenta coincidi\u00f3 con esos criterios. Prueba con un hashtag, palabra clave o lugar m\u00e1s amplio.',
        emptyPlaceNotFound:
          'No encontramos ese lugar en Instagram. Revisa el nombre o intenta de nuevo.',
        emptyPlaceSearchFailed:
          'No pudimos buscar ese lugar, as\u00ed que no encontramos cuentas locales. Intenta de nuevo o busca sin lugar.',
        emptyNoLocalMatch:
          '{{count}} cuentas coincidieron con tu palabra clave o hashtag, pero ninguna mostr\u00f3 se\u00f1ales de estar en {{place}}.',
        emptyAllKnown:
          'Todas las cuentas encontradas ({{count}}) ya estaban en tu pipeline, as\u00ed que no se agreg\u00f3 nada nuevo.',
        emptySearchFailed:
          'La b\u00fasqueda no se pudo completar. Intenta de nuevo en unos minutos.',
        tryAgain: 'Intentar de nuevo',
      },
      socialMediaLabel: 'Plataforma de Redes Sociales',
      socialMediaRequired: 'La plataforma de redes sociales es requerida',
      profileUrlLabel: 'URL del Perfil',
      profileUrlPlaceholder: 'https://linkedin.com/in/usuario',
      profileUrlPlaceholderInstagram: 'https://instagram.com/usuario',
      profileUrlRequired: 'La URL del perfil es requerida',
      profileUrlInvalid: 'Por favor ingresa una URL válida',
      profileUrlInvalidLinkedIn:
        'Ingresa una URL de perfil de LinkedIn como linkedin.com/in/usuario — los enlaces a publicaciones, empresas o el feed no se pueden enriquecer',
      profileUrlInvalidInstagram:
        'Ingresa una URL de perfil de Instagram como instagram.com/usuario — los enlaces a publicaciones, reels o historias no se pueden enriquecer',
      submitButton: 'Activar Flujo de Trabajo',
      submittingButton: 'Activando...',
      errorGeneric: 'Falló al activar el flujo de trabajo. Por favor intenta de nuevo.',
      errors: {
        authRequired:
          'La autenticación de LinkedIn expiró. Por favor vuelve a conectar tu acceso de LinkedIn en configuración.',
        enrichmentUnavailable:
          'No pudimos enriquecer este perfil en este momento. El proveedor de datos no está disponible temporalmente — por favor intenta de nuevo más tarde.',
        profileNotFound:
          'No encontramos este perfil. Revisa el usuario o la URL e intenta de nuevo.',
      },
      success: {
        title: '¡Flujo de Trabajo Activado Exitosamente!',
        platform: 'Plataforma',
        webhookId: 'ID del Webhook',
        enrichedDataTitle: 'Datos Enriquecidos',
        dataItems: {
          name: 'Nombre',
          username: 'Usuario',
          profileUrl: 'Perfil',
          score: 'Puntuación',
          economicCapacity: 'Capacidad Económica',
          status: 'Estado',
          industry: 'Industria',
          painPoints: 'Puntos de Dolor',
          capturedContent: 'Contenido Capturado',
        },
      },
      platforms: {
        linkedin: 'LinkedIn',
        facebook: 'Facebook',
        twitter: 'Twitter',
        instagram: 'Instagram',
      },
      progress: {
        title: 'Enriquecimiento en Progreso',
        scraping: 'Obteniendo perfil...',
        scraping_comments: 'Obteniendo comentarios...',
        analyzing: 'Analizando con IA...',
        persisting: 'Guardando lead enriquecido...',
        completed: '¡Enriquecimiento Completo!',
        partialSuccess: 'Enriquecimiento completado con problemas',
        insufficientData: 'El perfil no se pudo analizar',
        failed: 'Enriquecimiento Fallido',
        viewLead: 'Ver Lead',
        retry: 'Intentar de Nuevo',
        score: 'Puntuación',
        newRun: 'Nuevo Enriquecimiento',
      },
      stages: {
        title: 'Resultado paso a paso',
        profile: 'Perfil del contacto',
        posts: 'Publicaciones del contacto',
        reels: 'Reels',
        comments: 'Comentarios de publicaciones',
        analysis: 'Análisis con IA',
        company: 'Perfil de la empresa',
        company_posts: 'Publicaciones de la empresa',
      },
      stageStatus: {
        success: 'Completado',
        failed: 'Falló',
        skipped: 'Omitido',
        not_reached: 'No iniciado',
      },
      stageReasons: {
        provider_unavailable:
          'El proveedor de datos no pudo completar este paso. Por favor intenta de nuevo más tarde.',
        auth_required:
          'La autenticación de la plataforma expiró. Por favor vuelve a conectar tu acceso en configuración.',
        profile_not_found:
          'No se encontró este perfil. Verifica el nombre de usuario e intenta de nuevo.',
        unknown: 'Ocurrió un error inesperado en este paso. Por favor intenta de nuevo.',
      },
      attachLead: {
        title: '\u00bfEs el mismo lead?',
        description:
          'Estos leads parecen relacionados. Adjunt\u00e1 este perfil a uno de ellos o cre\u00e1 un lead aparte.',
        attachTo: 'Adjuntar a',
        alreadyHasProfile: 'ya tiene este perfil',
        createNew: 'Crear un lead nuevo',
      },
      leadExists: {
        title: 'El Lead Ya Existe',
        description:
          'Ya existe un lead con esta URL de perfil en tu organizacion. Puedes ver el lead existente.',
        viewLead: 'Ver Lead Existente',
        newSearch: 'Probar Otra URL',
      },
      selectPlatform: 'Seleccionar plataforma',
      platformLockedHint: 'La plataforma está fijada en LinkedIn para este flujo.',
      cancel: 'Cancelar',
      page: {
        badge: 'Automatización',
        title: 'Centro de Flujos',
        subtitle:
          'Automatiza el enriquecimiento de leads y análisis de perfiles con flujos de trabajo potentes.',
        newWorkflow: 'Nuevo Flujo',
        emptyTitle: 'Sin flujos de trabajo',
        emptyDescription:
          'Crea tu primer flujo de trabajo para automatizar el enriquecimiento de leads.',
        emptyAction: 'Crear Flujo',
      },
      card: {
        status: {
          active: 'Activo',
          paused: 'Pausado',
          error: 'Error',
        },
        neverRun: 'Nunca ejecutado',
        executions: 'ejecuciones',
        pauseButton: 'Pausar',
        resumeButton: 'Reanudar',
        runButton: 'Ejecutar',
        comingSoon: 'Muy pronto',
        workflowPaused: 'Este workflow está pausado. Reanudalo antes de ejecutar.',
      },
      types: {
        linkedinEnrichment: 'Enriquecimiento LinkedIn',
        instagramEnrichment: 'Enriquecimiento Instagram',
        webSearchContact: 'Búsqueda Web',
      },
      executions: {
        title: 'Ejecuciones de Workflows',
        subtitle:
          'Historial completo de todas las ejecuciones de workflows de tu organizaci\u00f3n.',
        backToFlowCenter: 'Volver al Centro de Flujos',
        totalRuns: 'Total',
        successful: 'Exitosas',
        failed: 'Fallidas',
        emptyTitle: 'Sin ejecuciones a\u00fan',
        emptyDescription: 'Ejecuta un workflow para ver el historial aqu\u00ed.',
      },
      visualizer: {
        nodes: {
          manualTrigger: 'Disparador Manual',
          fetchProfile: 'Obtener Perfil',
          enrichData: 'Enriquecer Datos',
          updateLead: 'Actualizar Lead',
        },
      },
      history: {
        title: 'Ejecuciones Recientes',
        previous: 'Anterior',
        next: 'Siguiente',
        page: 'Página',
        of: 'de',
        loading: 'Cargando ejecuciones...',
        emptyTitle: 'Sin ejecuciones aún',
        emptyDescription: 'Ejecuta un flujo de trabajo para ver el historial',
        status: {
          success: 'Éxito',
          failed: 'Fallido',
          running: 'Ejecutando',
        },
      },
      detail: {
        title: 'Detalle de ejecución',
        outcome: 'Resultado de ejecución',
        results: 'Impacto comercial',
        leadsCreated: 'Leads creados',
        leadsUpdated: 'Leads actualizados',
        errors: 'Errores',
        timing: 'Cronología',
        started: 'Inicio',
        completed: 'Finalización',
        duration: 'Duración',
        stageProgress: 'Desglose por etapa',
        triggerContext: 'Contexto de activación',
        platform: 'Plataforma',
        profileUrl: 'URL del perfil',
        company: 'Empresa',
        leadId: 'ID del lead',
        executionId: 'ID de ejecución',
        openHint: 'Abrir detalle de ejecución',
      },
      mock: {
        linkedinEnrichment: {
          name: 'Enriquecimiento LinkedIn',
          description:
            'Enriquece perfiles de leads con datos de LinkedIn. Obtiene cargo, empresa y conexiones.',
        },
        facebookScraper: {
          name: 'Scraper de Facebook',
          description:
            'Extrae datos públicos de perfiles de Facebook para enriquecimiento de leads e insights sociales.',
        },
        leadScoring: {
          name: 'Puntuación de Leads',
          description:
            'Calcula automáticamente puntuaciones de leads basándose en engagement y completitud del perfil.',
        },
        webScraper: {
          name: 'Scraper Web',
          description:
            'Extrae información de empresas de sitios web para enriquecimiento de leads B2B.',
        },
        instagramEnrichment: {
          name: 'Enriquecimiento Instagram',
          description:
            'Extrae perfiles, publicaciones y comentarios de Instagram para enriquecimiento de leads e insights sociales.',
        },
      },
      time: {
        hoursAgo: 'hace {{count}} horas',
        dayAgo: 'hace 1 día',
        minsAgo: 'hace {{count}} mins',
        twoHoursAgo: 'hace 2 horas',
        threeHoursAgo: 'hace 3 horas',
        fiveHoursAgo: 'hace 5 horas',
        thirtyMinsAgo: 'hace 30 mins',
        twoMinsAgo: 'hace 2 mins',
      },
    },
    calendar: {
      eventSource: {
        manual: {
          label: 'Manual',
        },
        voiceAgent: {
          label: 'Agente de voz',
          accessibilityLabel: 'Agendado por un agente de voz',
        },
        platformAgent: {
          label: 'Agente de la plataforma',
          accessibilityLabel: 'Agendado por un agente de la plataforma',
        },
        providerSync: {
          label: 'Sincronizado',
          accessibilityLabel: 'Sincronizado desde un calendario externo',
        },
      },
      eventStatus: {
        confirmed: 'Confirmada',
        cancelled: 'Cancelada',
        completed: 'Completada',
        noShow: 'No asistió',
      },
      agenda: {
        today: 'Hoy',
        allDay: 'Todo el día',
        meetingCount: '{{count}} reuniones',
        cancelledCount: '{{count}} canceladas',
        emptyTitle: 'No hay nada próximo',
        emptyDescription: 'Las reuniones agendadas para los próximos 30 días aparecerán aquí.',
      },
      filter: {
        hideCancelled: 'Ocultar canceladas',
      },
      joinCall: {
        label: 'Unirse',
        accessibilityLabel: 'Unirse a la videollamada de esta reunión',
      },
    },
    voice: {
      page: {
        badge: 'Agente de Voz',
        title: 'Asistente de Voz IA',
        subtitle: 'Crea mensajes de voz personalizados para tus leads usando Eleven Labs IA',
        newTemplate: 'Nueva Plantilla',
      },
      tabs: {
        templates: 'Plantillas',
        recentJobs: 'Trabajos Recientes',
      },
      stats: {
        title: 'Estadísticas de Voz',
        totalJobs: 'Total de Trabajos',
        completed: 'Completados',
        pending: 'Pendientes',
        failed: 'Fallidos',
        charactersUsed: 'Caracteres Usados',
        creditsUsed: 'Créditos Usados',
      },
      template: {
        status: {
          active: 'Activa',
          inactive: 'Inactiva',
        },
        characters: 'caracteres',
        voice: 'Voz',
        noPreview: 'Sin vista previa guardada',
        preview: 'Vista Previa',
        sendToLead: 'Enviar a Lead',
        actions: {
          edit: 'Editar',
          duplicate: 'Duplicar',
          delete: 'Eliminar',
        },
        empty: {
          title: 'Sin plantillas de voz aún',
          cta: 'Crea Tu Primera Plantilla',
        },
        deleteModal: {
          title: 'Eliminar Plantilla',
          message:
            '¿Estás seguro de que deseas eliminar "{{name}}"? Esta acción no se puede deshacer.',
          confirm: 'Eliminar',
          cancel: 'Cancelar',
        },
      },
      job: {
        status: {
          pending: 'Pendiente',
          processing: 'Procesando',
          completed: 'Completado',
          failed: 'Fallido',
          cancelled: 'Cancelado',
        },
        type: {
          single_message: 'Mensaje Individual',
          bulk_campaign: 'Campaña Masiva',
          voice_note: 'Nota de Voz',
          lead_qualification: 'Calificación de Lead',
          scheduled_call: 'Llamada Programada',
        },
        delivery: {
          in_app: 'En la App',
          phone_call: 'Llamada Telefónica',
          voicemail: 'Buzón de Voz',
          download: 'Descargar',
        },
        customScript: 'Script Personalizado',
        created: 'Creado',
        processed: 'Procesado',
        duration: 'Duración',
        characters: 'Caracteres',
        credits: 'Créditos',
        retry: 'Reintentar',
        cancel: 'Cancelar',
        hideAudio: 'Ocultar Audio',
        showAudio: 'Mostrar Audio',
        empty: {
          title: 'Sin trabajos de voz aún',
          description: 'Crea una plantilla y envía un mensaje de voz a un lead.',
        },
      },
      form: {
        createTitle: 'Nueva Plantilla de Voz',
        editTitle: 'Editar Plantilla',
        name: {
          label: 'Nombre de la Plantilla',
          placeholder: 'ej., Mensaje de Bienvenida',
          required: 'El nombre de la plantilla es requerido',
        },
        description: {
          label: 'Descripción',
          placeholder: 'Breve descripción de cuándo usar esta plantilla',
        },
        voice: {
          label: 'Voz',
          placeholder: 'Selecciona una voz',
          required: 'La voz es requerida',
        },
        script: {
          label: 'Guión de la Plantilla',
          placeholder: 'Hola {{lead.fullName}}, soy {{user.fullName}} de {{organization.name}}...',
          required: 'El guión es requerido',
          characters: 'caracteres',
          insertVariables: 'Insertar variables:',
        },
        variables: {
          leadName: 'Nombre del Lead',
          leadEmail: 'Email del Lead',
          company: 'Empresa',
          userName: 'Nombre del Usuario',
          organization: 'Organización',
        },
        settings: {
          title: 'Configuración de Voz',
          stability: 'Estabilidad',
          similarityBoost: 'Impulso de Similitud',
          speakerBoost: 'Impulso del Hablante',
          templateActive: 'Plantilla Activa',
        },
        preview: {
          title: 'Vista Previa',
          generate: 'Generar Vista Previa',
          generateAndSave: 'Generar y Guardar Vista Previa',
          generating: 'Generando...',
        },
        submit: {
          create: 'Crear Plantilla',
          update: 'Actualizar Plantilla',
          saving: 'Guardando...',
          cancel: 'Cancelar',
        },
      },
      player: {
        webOnly: 'Reproducción de audio disponible solo en web',
        error: 'Error al cargar el audio',
        failedToPlay: 'Error al reproducir el audio',
        generating: 'Generando audio...',
      },
    },
    voiceAgents: {
      tabs: {
        agents: 'Agentes',
        calls: 'Llamadas',
      },
      deleteDialog: {
        title: 'Eliminar agente',
        step1Body: 'Esto elimina permanentemente el agente y su configuración:',
        step2Body: 'Esta acción no se puede deshacer. ¿Confirmas la eliminación?',
        cancel: 'Cancelar',
        continue: 'Continuar',
        back: 'Atrás',
        delete: 'Eliminar',
        errorPrefix: 'No se pudo eliminar el agente.',
      },
      wizard: {
        rootA11y: 'Formulario de configuración del agente de voz',
        stepsA11yLabel: 'Pasos del asistente',
        publishedOnlyNameEditable:
          'Este agente est\u00e1 publicado: s\u00f3lo se puede cambiar su nombre. Vuelve a publicarlo para modificar lo dem\u00e1s.',
        stepCounter: 'Paso',
        stepCounterOf: 'de',
        steps: {
          useCase: 'Caso de uso',
          identity: 'Identidad',
          voice: 'Voz',
          script: 'Guion',
          knowledge: 'Conocimiento',
          objections: 'Objeciones',
          toolsCompliance: 'Herramientas y cumplimiento',
          callingHours: 'Horario de llamadas',
          retryPolicy: 'Política de reintentos',
          reviewPublish: 'Revisar y publicar',
          completedHint: 'Paso completado. Actívalo para revisarlo.',
          availableHint: 'Paso disponible. Actívalo para navegar.',
          lockedHint: 'Completa los pasos anteriores para desbloquearlo.',
        },
        useCase: {
          initial_qualification: 'Primer contacto y calificación',
          warm_recontact: 'Recontacto en caliente',
          meeting_confirmation: 'Confirmación de reunión',
          cart_recovery: 'Recuperación de carrito',
          nps_survey: 'Encuesta NPS',
          custom: 'Personalizado',
        },
        language: {
          es_CO: 'ES (Colombia)',
          es_LATAM: 'ES (LATAM)',
          es_MX: 'ES (México)',
          en_US: 'EN (EE. UU.)',
          pt_BR: 'PT (Brasil)',
        },
        tone: {
          friendly_formal: 'Amable y formal',
          professional: 'Profesional',
          empathetic: 'Empático',
          energetic: 'Enérgico',
          neutral: 'Neutral',
        },
        errors: {
          useCaseRequired: 'Elige una plantilla de caso de uso o empieza en blanco para continuar.',
          nameRequired: 'El nombre del agente es obligatorio.',
          voiceIdRequired: 'El ID de voz es obligatorio.',
          openingRequired: 'El guion de apertura es obligatorio (mín. 10 caracteres).',
          callingHoursRangeInvalid: 'Cada día habilitado debe comenzar antes de su hora de cierre.',
          draftSaveFailed: 'No se pudo guardar el borrador del agente. Inténtalo de nuevo.',
          saveBeforePublish: 'Primero guarda el agente completando todos los pasos.',
          discloseRequired:
            'La divulgación de identidad de IA es obligatoria para publicar. Habilita "Siempre divulgar identidad de IA" en Herramientas y Cumplimiento.',
          recordingAnnouncementRequired:
            'El aviso de grabación es obligatorio para publicar. Habilita "Anuncio de grabación" en Herramientas y Cumplimiento.',
          publishFailed: 'Error al publicar',
        },
        templates: {
          initial_qualification: {
            name: 'Agente de primer contacto y calificación',
            description:
              'Primer contacto con un lead recién creado. Se presenta, indaga interés, presupuesto, necesidad, autoridad de decisión y urgencia, y propone el siguiente paso (reunión o seguimiento).',
          },
          warm_recontact: {
            name: 'Agente de re-contacto',
            description:
              'Re-engage de leads fríos. Retoma una conversación previa y propone agendar reunión o un follow-up cercano.',
          },
          meeting_confirmation: {
            name: 'Agente de confirmación de reunión',
            description:
              'Confirma una reunión ya agendada. Si el lead no puede, reagenda en el mismo flujo.',
          },
          cart_recovery: {
            name: 'Agente de recuperación de carrito',
            description:
              'E-commerce. Llama a leads que abandonaron una cotización, indaga el bloqueo y ofrece resolverlo o transferir a sales.',
          },
          nps_survey: {
            name: 'Agente de encuesta NPS',
            description:
              'Pregunta NPS post-compra/post-servicio. Captura score 0-10 y comentario libre. Llamada corta.',
          },
        },
        useCaseStep: {
          eyebrow: 'Plano del agente',
          intro:
            'Elige un caso de uso para precargar el asistente con un guion seleccionado, horarios de llamadas y un conjunto de herramientas. Puedes editar todo después.',
          choicesA11y: 'Plantillas de casos de uso del agente',
          loadingTemplates: 'Cargando plantillas',
          loadError: 'No se pudieron cargar las plantillas. Aún puedes empezar en blanco abajo.',
          useTemplatePrefix: 'Usar plantilla',
          useTemplateSuffix: '',
          startBlankA11y: 'Empezar en blanco (Personalizado)',
          startBlankTitle: 'Empezar en blanco',
          startBlankDescription:
            'Comienza desde una configuración completamente vacía y escribe todo tú mismo.',
        },
        identity: {
          nameLabel: 'Nombre del agente *',
          namePlaceholder: 'ej. Bot de calificación Colombia',
          nameA11y: 'Nombre del agente',
          descriptionLabel: 'Descripción',
          descriptionPlaceholder: '¿Qué hace este agente?',
          descriptionA11y: 'Descripción del agente',
          useCaseLabel: 'Caso de uso *',
          useCaseA11y: 'Seleccionar caso de uso',
          languageLabel: 'Idioma *',
          languageA11y: 'Seleccionar idioma',
          autoDetectLanguageLabel: 'Detectar el idioma automáticamente',
          autoDetectLanguageA11y: 'Detectar el idioma automáticamente para cada lead',
          autoDetectLanguageHint:
            'El agente habla el idioma asignado al país del lead, o el idioma preferido del lead cuando está definido. Dejalo apagado para hablar siempre el idioma elegido arriba.',
          toneLabel: 'Tono',
          toneA11y: 'Seleccionar tono del agente',
        },
        voice: {
          voiceIdLabel: 'ID de voz de ElevenLabs *',
          voiceIdPlaceholder: 'Pega un ID de voz de ElevenLabs',
          voiceIdA11y: 'ID de voz',
          hint: 'Encuentra más voces en ElevenLabs → Biblioteca de voces.',
          selectLabel: 'Voz *',
          selectPlaceholder: 'Selecciona una voz',
          selectA11y: 'Selecciona una voz',
          catalogHint:
            'Toca una voz para seleccionarla y pulsa reproducir para escuchar una muestra.',
          selectVoiceA11yPrefix: 'Seleccionar voz',
          loading: 'Cargando voces…',
          loadError: 'No se pudieron cargar las voces. Inténtalo de nuevo.',
          empty: 'Aún no hay voces disponibles para este idioma.',
          customLabel: 'O ingresa un ID de voz personalizado',
          previewVoice: 'Previsualizar voz',
          previewPlay: 'Reproducir muestra de voz',
          previewPause: 'Pausar muestra de voz',
          previewPlaying: 'Reproduciendo…',
          previewUnavailable: 'Sin muestra disponible',
        },
        script: {
          openingLabel: 'Apertura *',
          openingPlaceholder: 'Cómo se presenta el agente',
          openingA11y: 'Guion de apertura',
          ctaLabel: 'Llamada a la acción *',
          ctaPlaceholder: 'ej. Agenda una demo',
          ctaA11y: 'Guion de llamada a la acción',
          closingLabel: 'Cierre *',
          closingPlaceholder: 'Palabras de cierre',
          closingA11y: 'Guion de cierre',
          builderTitle: 'Mesa de trabajo conversacional',
          builderHint:
            'Escribe con naturalidad y agrega datos confiables del CRM desde la biblioteca.',
          liveSafe: 'Seguro en vivo',
          variableLibrary: 'Biblioteca de variables',
          variableLibraryHint: 'Agrega el valor seleccionado donde está activo el cursor.',
          searchVariables: 'Buscar variables',
          searchVariablesA11y: 'Buscar variables disponibles para el guion',
          loadingVariables: 'Cargando variables seguras…',
          loadVariablesError: 'La biblioteca no está disponible. Puedes seguir editando el guion.',
          usedVariables: 'En uso',
          variableMap: 'Mapa de variables',
          autocompleteTitle: 'Insertar una variable',
          autocompleteKeyboardHint: '↑↓ Navegar · Enter seleccionar · Esc cerrar',
          autocompleteEmpty: 'Ninguna variable coincide con esta etiqueta.',
          noVariables: 'Aún sin variables',
          unknownVariable: '“{{variable}}” no está disponible e impedirá publicar.',
          previewTitle: 'Vista previa con ejemplos',
          previewEmpty: 'Empieza a escribir para escuchar cómo sonará esta sección.',
          openingShort: 'Apertura',
          ctaShort: 'Llamado a la acción',
          closingShort: 'Cierre',
          examplePrefix: 'Ejemplo: {{example}}',
          noVariableResults: 'Ninguna variable segura coincide con la búsqueda.',
        },
        knowledge: {
          introOnboarding:
            'Opcional — dale a tu agente un fragmento de contexto para que pueda responder preguntas sobre tu producto.',
          introFull:
            'Los elementos de la base de conocimiento se pueden agregar después de publicar. Usa la página de detalle del agente para subir documentos (PDF / texto / URL).',
          seedLabel: 'Contexto rápido (opcional)',
          seedPlaceholder:
            'Pega un párrafo sobre tu producto, preguntas frecuentes o propuesta de valor. Puedes agregar documentos completos más tarde.',
          seedA11y: 'Texto inicial opcional de la base de conocimiento',
          skipHint:
            'Pulsa Continuar para omitir — los administradores pueden subir PDF y URL desde la vista de detalle del agente una vez publicado.',
          seedTitle: 'Contexto rápido',
          addLabel: 'Agregar conocimiento',
          addText: 'Texto',
          addUrl: 'URL',
          addPdf: 'PDF',
          titlePlaceholder: 'Título',
          titleA11y: 'Título del elemento de conocimiento',
          textPlaceholder: 'Pega el texto que el agente puede consultar durante las llamadas.',
          textA11y: 'Contenido de texto del elemento de conocimiento',
          urlPlaceholder: 'https://ejemplo.com/pagina',
          urlA11y: 'URL del elemento de conocimiento',
          submitAdd: 'Agregar',
          submitTextA11y: 'Agregar elemento de conocimiento de texto',
          submitUrlA11y: 'Agregar elemento de conocimiento de URL',
          pickPdf: 'Elegir PDF',
          pickPdfA11y: 'Elegir un archivo PDF para subir',
          pdfHint: 'El PDF se sube y su texto se extrae y se adjunta al agente.',
          listLabel: 'Elementos agregados',
          listEmpty: 'Aún no se han agregado elementos de conocimiento.',
          deleteItemA11y: 'Eliminar elemento de conocimiento',
          completeEarlierSteps:
            'Completa los pasos anteriores para comenzar a agregar conocimiento.',
          sourceType: {
            text: 'Texto',
            url: 'URL',
            pdf: 'PDF',
            notion: 'Notion',
          },
          errors: {
            textRequired: 'Agrega un título y algo de texto antes de guardar.',
            urlRequired: 'Agrega un título y una URL antes de guardar.',
            uploadFailed: 'No se pudo agregar el elemento de conocimiento. Inténtalo de nuevo.',
            removeFailed: 'No se pudo eliminar el elemento de conocimiento. Inténtalo de nuevo.',
            pdfParseFailed: 'No se pudo leer este PDF. Prueba con otro archivo.',
          },
        },
        objections: {
          note: 'El manejo de objeciones se puede configurar después de guardar. El editor completo de objeciones llegará pronto.',
        },
        compliance: {
          heading: 'Política de divulgación',
          alwaysDiscloseLabel: 'Divulgar siempre la identidad de IA',
          alwaysDiscloseA11y: 'Divulgar siempre la identidad de IA',
          recordingLabel: 'Aviso de grabación',
          recordingA11y: 'Aviso de grabación',
          wordingLabel: 'Texto de divulgación (opcional)',
          wordingPlaceholder: 'Texto de divulgación personalizado (mín. 20 caracteres)',
          wordingA11y: 'Texto de divulgación',
        },
        tools: {
          heading: 'Herramientas habilitadas',
          hint: 'Elige qué acciones puede realizar este agente durante una llamada. Precargado desde la plantilla seleccionada — edítalo cuando quieras.',
          schedule_meeting: 'Agendar reunión',
          transfer_to_human: 'Transferir a humano',
          mark_do_not_call: 'Marcar no llamar',
          update_lead_qualification: 'Actualizar calificación del lead',
          increment_objection_counter: 'Incrementar contador de objeciones',
          finalize_call: 'Finalizar llamada',
          lookup_knowledge_base: 'Consultar base de conocimiento',
        },
        callingHours: {
          useLeadTimezoneLabel: 'Usar la zona horaria del lead',
          useLeadTimezoneA11y: 'Usar la zona horaria del lead',
          windowStartLabel: 'Inicio de la ventana (HH:mm)',
          windowStartA11y: 'Hora de inicio de la ventana de llamadas',
          windowEndLabel: 'Fin de la ventana (HH:mm)',
          windowEndA11y: 'Hora de fin de la ventana de llamadas',
          callingDaysLabel: 'Días de llamadas',
          dayMon: 'Lunes',
          dayTue: 'Martes',
          dayWed: 'Miércoles',
          dayThu: 'Jueves',
          dayFri: 'Viernes',
          daySat: 'Sábado',
          daySun: 'Domingo',
          dayToggleA11y: 'Habilitar {{day}}',
          dayStartA11y: 'Hora de inicio para {{day}}',
          dayEndA11y: 'Hora de fin para {{day}}',
          blackoutDatesLabel: 'Feriados / Fechas bloqueadas',
          blackoutDatePlaceholder: 'YYYY-MM-DD',
          blackoutDateAddLabel: 'Agregar',
          blackoutDateAddA11y: 'Agregar fecha bloqueada',
          blackoutDateRemoveA11y: 'Eliminar fecha bloqueada {{date}}',
          blackoutDateInvalid: 'Ingrese una fecha válida en formato YYYY-MM-DD.',
        },
        retry: {
          maxAttemptsLabel: 'Máximo de intentos (1–5)',
          maxAttemptsA11y: 'Máximo de intentos de reintento',
        },
        review: {
          name: 'Nombre',
          useCase: 'Caso de uso',
          language: 'Idioma',
          tone: 'Tono',
          voiceId: 'ID de voz',
          opening: 'Apertura',
          cta: 'CTA',
          closing: 'Cierre',
          disclosure: 'Divulgación',
          callingHours: 'Horario de llamadas',
          maxRetries: 'Máx. reintentos',
          enabled: 'Habilitado',
          disabled: 'Deshabilitado',
          draftSaved: 'Borrador guardado — ID:',
          edit: 'Editar',
          editPrefix: 'Editar',
        },
        footer: {
          back: 'Atrás',
          backA11y: 'Ir al paso anterior',
          cancel: 'Cancelar',
          cancelA11y: 'Cancelar asistente',
          publish: 'Publicar',
          publishA11y: 'Publicar agente de voz',
          continue: 'Continuar',
          continueToPrefix: 'Continuar a',
        },
        video: {
          forStepA11y: 'Video tutorial del paso',
          tutorialSuffix: 'tutorial',
          openForStepPrefix: 'Abrir el video tutorial del paso',
          openForStepSuffix: 'en tu navegador',
          tapToOpen: 'Pulsa para abrir el video tutorial.',
          placeholderA11y: 'Marcador de video tutorial del paso',
          comingSoon: 'Video tutorial próximamente. Sigue el formulario de abajo para continuar.',
        },
      },
      list: {
        title: 'Agentes de voz',
        countSingular: 'agente',
        countPlural: 'agentes',
        newAgent: 'Nuevo agente',
        newAgentA11y: 'Crear nuevo agente de voz',
        filterAll: 'Todos',
        filterDraft: 'Borrador',
        filterPublished: 'Publicado',
        filterArchived: 'Archivado',
        filterUseCaseLabel: 'Caso de uso',
        filterLanguageLabel: 'Idioma',
        filterAllUseCases: 'Todos los casos',
        filterAllLanguages: 'Todos los idiomas',
        emptyTitle: 'Aún no hay agentes de voz',
        emptyDescription:
          'Crea tu primer agente de voz con IA para empezar a calificar leads automáticamente.',
        emptyMatchTitle: 'Sin coincidencias',
        emptyMatchDescription: 'Ningún agente coincide con el filtro seleccionado.',
        startOnboarding: 'Comenzar configuración',
        listA11y: 'Lista de agentes de voz',
        errorTitle: 'No se pudieron cargar los agentes de voz',
        errorDescription: 'Ocurrió un problema al cargar tus agentes de voz.',
        retry: 'Reintentar',
      },
      detail: {
        loadingA11y: 'Cargando agente de voz',
        notFound: 'Agente no encontrado.',
        tabOverview: 'Resumen',
        tabConfig: 'Configuración',
        tabVersions: 'Versiones',
        tabKnowledge: 'Conocimiento',
        tabExperiments: 'Experimentos',
        tabAnalytics: 'Analíticas',
        tabCalls: 'Llamadas',
        configTitle: 'Configuración del agente',
        configDescription: 'Revisa el guion, las protecciones de cumplimiento y los reintentos.',
        editConfig: 'Editar configuración',
        descriptionTitle: 'Descripción',
        detailsTitle: 'Detalles',
        useCase: 'Caso de uso',
        language: 'Idioma',
        voiceId: 'ID de voz',
        model: 'Modelo',
        published: 'Publicado',
        analyticsSoonA11y: 'Métricas de analíticas próximamente',
        analyticsSoon: 'Las métricas de analíticas llegarán en una versión futura.',
        scriptTitle: 'Guion',
        opening: 'Apertura',
        cta: 'CTA',
        closing: 'Cierre',
        disclosureTitle: 'Divulgación',
        alwaysDisclose: 'Siempre divulgar',
        recording: 'Grabación',
        yes: 'Sí',
        no: 'No',
        retryPolicyTitle: 'Política de reintentos',
        maxAttempts: 'Intentos máximos',
        noVersions: 'Aún no hay versiones.',
        version: 'Versión',
        publishedLower: 'publicada',
        callsSoonA11y: 'Historial de llamadas próximamente',
        callsSoon: 'El historial de llamadas llegará en una versión futura.',
        statusLabel: 'Estado',
        preview: 'Vista previa',
        previewA11y: 'Previsualizar agente de voz en el navegador',
        edit: 'Editar',
        editA11y: 'Editar agente de voz',
        renameA11y: 'Renombrar agente de voz',
        renameInputA11y: 'Nombre del agente de voz',
        renameSaveA11y: 'Guardar nombre',
        renameCancelA11y: 'Cancelar renombrado',
        republish: 'Re-publicar',
        republishA11y: 'Re-publicar este agente como una nueva versión',
      },
      edit: {
        loadingA11y: 'Cargando agente de voz',
        screenA11y: 'Editar agente de voz',
      },
      previewScreen: {
        loadingA11y: 'Cargando agente…',
      },
      newScreen: {
        screenA11y: 'Asistente para crear nuevo agente de voz',
      },
      onboardingScreen: {
        screenA11y: 'Asistente de configuración de agente de voz',
      },
      refusal: {
        dailyLimit:
          'Este lead ya recibió {{used}} de {{limit}} llamadas permitidas por día. Podés volver a llamarlo a partir de {{freesAt}}.',
        dailyLimitNoHorizon:
          'Este lead ya recibió {{used}} de {{limit}} llamadas permitidas por día. El cupo se libera dentro de las próximas 24 horas.',
        doNotCall:
          'Este lead pidió no ser contactado de nuevo, así que las llamadas están bloqueadas.',
        consentRefused:
          'Este lead no autorizó el tratamiento de sus datos, así que las llamadas están bloqueadas.',
        generic: 'No se pudo hacer la llamada. Probá de nuevo en un momento.',
      },
      callsList: {
        deleteCall: 'Eliminar llamada',
        deleteTitle: 'Eliminar llamada',
        deleteBody: 'Esto elimina permanentemente el registro de la llamada de:',
        deleteConfirm: 'Eliminar',
        deleteError: 'No se pudo eliminar la llamada.',
        agentSelectLabel: 'Agente',
        leadSelectLabel: 'Lead',
        selectAgentPlaceholder: 'Selecciona un agente publicado',
        selectLeadPlaceholder: 'Selecciona un lead',
        noPublishedAgents: 'No hay agentes publicados. Publica un agente primero.',
        noLeads: 'No hay leads con número de teléfono.',
        title: 'Llamadas',
        results: 'llamadas',
        triggerCall: 'Disparar llamada',
        triggerNewCall: 'Disparar nueva llamada',
        filterByPrefix: 'Filtrar por',
        callToPrefix: 'Llamada a',
        statusPrefix: 'Estado:',
        unknown: 'desconocido',
        unknownLead: 'Lead desconocido',
        unknownAgent: 'Agente desconocido',
        statusFiltersA11y: 'Filtros de estado de llamada',
        listA11y: 'Lista de llamadas',
        loading: 'Cargando llamadas',
        loadingSkeleton: 'Cargando lista de llamadas',
        emptyTitle: 'Sin llamadas',
        emptyDescription: 'No se encontraron llamadas con los filtros seleccionados.',
        firstEmptyTitle: 'Aún no hay llamadas',
        firstEmptyDescription: 'Dispara tu primera llamada con IA y sigue su progreso aquí.',
        errorTitle: 'No se pudieron cargar las llamadas',
        errorDescription: 'Ocurrió un problema al cargar las llamadas. Intentá nuevamente.',
        retry: 'Reintentar',
        filterAll: 'Todas',
        filterScheduled: 'Programadas',
        filterInProgress: 'En curso',
        filterCompleted: 'Completadas',
        filterNoAnswer: 'Sin resp.',
        filterError: 'Error',
        fromPlaceholder: 'Desde (YYYY-MM-DD)',
        toPlaceholder: 'Hasta (YYYY-MM-DD)',
        countryPlaceholder: 'País (CO)',
        fromDateA11y: 'Fecha desde',
        toDateA11y: 'Fecha hasta',
        dateHint: 'Formato año-mes-día, por ejemplo 2026-05-05',
        countryA11y: 'Filtrar por país',
        countryHint: 'Código de país de dos letras, por ejemplo CO o MX',
        filtersTitle: 'Filtros',
        advancedFiltersA11y: 'Filtros avanzados de llamadas',
        showFilters: 'Más filtros',
        hideFilters: 'Ocultar filtros',
        clearFilters: 'Limpiar filtros',
        metricsTotal: 'Llamadas totales',
        metricsActive: 'En curso',
        metricsCompleted: 'Completadas',
        metricsNoAnswer: 'Sin respuesta',
        metricsAvgDuration: 'Duración prom.',
        fromLabel: 'Desde',
        toLabel: 'Hasta',
        countryLabel: 'País',
        agentIdLabel: 'ID del Agente',
        agentIdPlaceholder: 'UUID del agente',
        agentIdA11y: 'ID del agente de voz',
        leadIdLabel: 'ID del Lead',
        leadIdPlaceholder: 'UUID del lead',
        leadIdA11y: 'ID del lead',
        cancel: 'Cancelar',
        confirmTrigger: 'Confirmar disparo de llamada',
        trigger: 'Disparar',
        triggering: 'Disparando…',
        errorPrefix: 'Error:',
        unknownError: 'Error desconocido',
        consentRejectedError:
          'Este lead rechazó el consentimiento de tratamiento de datos, por lo que las llamadas quedan bloqueadas de forma permanente. Contáctelo por otro canal.',
        triggerConflictError:
          'No se puede realizar esta llamada en este momento. El lead puede estar en la lista de no llamar, haber rechazado el consentimiento, estar fuera del horario de llamadas o haber alcanzado el límite diario de llamadas.',
        outsideCallingHoursWarning:
          'Este agente solo puede realizar llamadas dentro de su horario configurado para la zona horaria del lead. Inténtalo de nuevo dentro del horario permitido.',
        status: {
          scheduled: 'Programada',
          dialing: 'Marcando',
          completed: 'Completada',
          noAnswer: 'Sin resp.',
          error: 'Error',
          cancelled: 'Cancelada',
        },
        filterSelectAll: 'Todos',
        filterAgentLabel: 'Agente',
        filterAgentPlaceholder: 'Todos los agentes',
        filterAgentA11y: 'Filtrar por agente',
        filterLeadLabel: 'Lead',
        filterLeadPlaceholder: 'Todos los leads',
        filterLeadA11y: 'Filtrar por lead',
        filterOutcomeLabel: 'Resultado',
        filterOutcomePlaceholder: 'Todos los resultados',
        filterOutcomeA11y: 'Filtrar por resultado',
        outcomeFilter: {
          qualified: 'Calificado',
          not_qualified: 'No calificado',
          callback_requested: 'Callback solicitado',
          meeting_booked: 'Reunión agendada',
          do_not_call: 'No llamar',
          no_answer: 'Sin respuesta',
          voicemail_left: 'Buzón de voz',
          technical_failure: 'Fallo técnico',
          incomplete: 'Incompleto',
        },
      },
      callDetail: {
        title: 'Llamada',
        screenA11y: 'Detalle de llamada',
        statusPrefix: 'Estado:',
        loadingDetail: 'Cargando detalle de llamada',
        notFound: 'Llamada no encontrada.',
        loadingAudio: 'Cargando audio',
        audioUnavailable: 'Audio no disponible.',
        audioUnavailableA11y: 'Audio no disponible',
        signedUrlExpires: 'URL firmada expira:',
        downloadAudio: 'Descargar audio',
        downloadAudioA11y: 'Descargar audio de la llamada (URL firmada con expiración 10 minutos)',
        audioSection: 'Audio',
        transcript: 'Transcripción',
        summary: 'Resumen',
        details: 'Detalles',
        from: 'Desde',
        to: 'Hacia',
        country: 'País',
        duration: 'Duración',
        startedAt: 'Inicio',
        endedAt: 'Fin',
        agent: 'Agente',
        agentVersion: 'Versión agente',
        latencyP50: 'Latencia P50 (ms)',
        latencyP95: 'Latencia P95 (ms)',
        telephonyCost: 'Costo telefonía',
        conversationCost: 'Costo conversación',
        viewReplay: 'Ver Replay',
        viewReplayA11y: 'Ver replay de la llamada',
        retry: 'Reintentar',
        retrying: 'Reintentando…',
        retryA11y: 'Reintentar llamada',
        status: {
          scheduled: 'Programada',
          contextLoaded: 'Contexto cargado',
          dialing: 'Marcando',
          detecting: 'Detectando',
          disclosure: 'Divulgación',
          discovery: 'Descubrimiento',
          handling: 'Manejo',
          cta: 'CTA',
          closing: 'Cierre',
          postProcessing: 'Post-proceso',
          completed: 'Completada',
          noAnswer: 'Sin respuesta',
          voicemail: 'Buzón de voz',
          hungUpByLead: 'Colgó el lead',
          technicalFailure: 'Fallo técnico',
          cancelled: 'Cancelada',
        },
      },
      replay: {
        title: 'Replay',
        screenA11y: 'Replay de llamada turno por turno',
        turns: 'turnos',
        tools: 'Herramientas',
        toolsPanelPrefix: 'Panel de herramientas,',
        callsLabel: 'llamadas',
        noToolCalls: 'Sin llamadas a herramientas en esta sesión.',
        turnLabel: 'Turno',
        toolPrefix: 'Herramienta',
        latencyLabel: 'latencia',
        statusLabel: 'estado',
        toolExecuted: 'Herramienta ejecutada',
        agent: 'Agente',
        lead: 'Lead',
        turnPrefix: 'Turno',
        atTime: 'a',
        tapToSeek: 'Toca para saltar al audio.',
        seekHint: 'Reproduce el audio desde este turno',
        restrictedTitle: 'Acceso restringido',
        restrictedBody: 'Solo los administradores y managers pueden ver el replay de llamadas.',
        loading: 'Cargando replay',
        unavailable: 'Replay no disponible.',
        audioUnavailable: 'Audio no disponible para esta llamada.',
        audioUnavailableA11y: 'Audio no disponible',
        noTurnsA11y: 'Sin turnos en el replay',
        noReplayData: 'Sin datos de replay disponibles.',
      },
      card: {
        delete: 'Eliminar',
        voiceAgent: 'agente de voz',
        statusPrefix: 'Estado',
        version: 'Versión',
        publishedOn: 'Publicado',
        duplicate: 'Duplicar',
        edit: 'Editar',
        archive: 'Archivar',
        tooltipEdit: 'Editar configuración del agente',
        tooltipDuplicate: 'Duplicar agente',
        tooltipArchive: 'Archivar agente',
        tooltipDelete: 'Eliminar agente',
        status: {
          draft: 'Borrador',
          published: 'Publicado',
          archived: 'Archivado',
        },
        useCase: {
          initial_qualification: 'Primer contacto y calificación',
          warm_recontact: 'Recontacto en caliente',
          meeting_confirmation: 'Confirmación de reunión',
          cart_recovery: 'Recuperación de carrito',
          nps_survey: 'Encuesta NPS',
          custom: 'Personalizado',
        },
        language: {
          es_LATAM: 'ES (LATAM)',
          es_CO: 'ES (Colombia)',
          es_MX: 'ES (México)',
          en_US: 'EN (US)',
          pt_BR: 'PT (Brasil)',
        },
      },
      analytics: {
        panelLabel: 'Panel de analíticas del agente',
        showWindowPrefix: 'Mostrar ventana',
        loading: 'Cargando analíticas',
        loadError: 'No se pudieron cargar las analíticas',
        unknownError: 'error desconocido',
        emptyPrefix: 'Aún no hay llamadas en los últimos',
        emptySuffix: 'Dispara una llamada para llenar este panel.',
        totalCalls: 'Llamadas totales',
        meetingBooked: 'Reunión agendada',
        ofCallsWithOutcome: 'de llamadas con resultado',
        avgDuration: 'Duración promedio',
        totalCost: 'Costo total',
        costPerMeeting: 'Costo por reunión',
        noMeetingsBooked: 'sin reuniones agendadas',
        turnTaking: 'Toma de turnos',
        outcomesTitle: 'Resultados',
        noOutcomes: 'Aún no se registran resultados.',
        of: 'de',
        period: {
          '7d': '7 días',
          '30d': '30 días',
          '90d': '90 días',
        },
        outcome: {
          qualified: 'Calificado',
          not_qualified: 'No calificado',
          callback_requested: 'Callback solicitado',
          meeting_booked: 'Reunión agendada',
          do_not_call: 'No llamar',
          no_answer: 'Sin respuesta',
          voicemail_left: 'Buzón de voz',
          technical_failure: 'Fallo técnico',
          incomplete: 'Incompleto',
        },
      },
      outcome: {
        none: 'Sin resultado',
        resultPrefix: 'Resultado',
        qualified: 'Calificado',
        meeting_booked: 'Reunión agendada',
        not_qualified: 'No calificado',
        do_not_call: 'No llamar',
        callback_requested: 'Callback solicitado',
        voicemail_left: 'Buzón dejado',
        no_answer: 'Sin respuesta',
        technical_failure: 'Fallo técnico',
        incomplete: 'Incompleto',
      },
      previewCall: {
        voiceAgentFallback: 'agente de voz',
        thisAgentFallback: 'este agente',
        emailSubjectPrefix: 'Vista previa de',
        emailBody: 'Abre este enlace en tu navegador (Chrome o Safari) para probar el agente:',
        browserOnlyTitle: 'La vista previa solo está disponible en navegador',
        browserOnlyBodyPrefix: 'La vista previa de',
        browserOnlyBodySuffix:
          'requiere un navegador web. Te enviamos el enlace por email para que lo abras en tu computadora con Chrome o Safari.',
        sendEmail: 'Enviar enlace por email',
        sendEmailA11y: 'Enviar enlace por email para abrir en navegador',
        resumeFoundTitle: 'Sesión previa encontrada',
        resumeFoundBody: 'Tienes una sesión de vista previa activa. ¿Deseas retomarla?',
        newSession: 'Nueva sesión',
        newSessionA11y: 'Iniciar nueva sesión de vista previa',
        resumeSession: 'Retomar sesión',
        resumeSessionA11y: 'Retomar sesión de vista previa anterior',
        resumeFailed: 'Esta sesión de vista previa ya no está disponible. Inicia una nueva.',
        previewTitle: 'Vista previa',
        startDescription:
          'Inicia una sesión de vista previa para conversar con este agente en modo de prueba. La transcripción se guarda para que puedas retomar la conversación más tarde.',
        startPreview: 'Iniciar vista previa',
        startPreviewA11y: 'Iniciar vista previa del agente',
        statusPrefix: 'Estado',
        elapsedPrefix: 'Tiempo transcurrido',
        agentSpeaking: 'El agente está hablando…',
        listening: 'Escuchando…',
        micPaused: 'Micrófono en pausa',
        mute: 'Silenciar',
        unmute: 'Activar mic',
        muteMicA11y: 'Silenciar micrófono',
        unmuteMicA11y: 'Activar micrófono',
        endPreview: 'Terminar vista previa',
        endPreviewA11y: 'Terminar vista previa',
        liveTranscript: 'Transcripción en vivo',
        toolCalls: 'Tool calls (sin efecto)',
        toolCallPrefix: 'Tool call',
        toolCallSimulated: 'Sin efecto',
        previousSession: 'Sesión anterior',
        sessionHistory: 'Historial de sesiones',
        previousSessionA11y: 'Transcripción de la sesión anterior',
        connectionError:
          'No se pudo conectar la vista previa. Verifica que el agente esté publicado e intenta de nuevo.',
        errorPayment: {
          title: 'Servicio de voz no disponible',
          body: 'El proveedor de voz rechazó esta sesión por un problema de facturación en la cuenta. Contacta a tu administrador para revisar el plan contratado.',
        },
        errorGeneric: {
          title: 'No se pudo conectar la vista previa',
          body: 'La vista previa no pudo conectarse. Intenta de nuevo y, si el problema persiste, contacta a tu administrador.',
        },
        startFailed:
          'No se pudo iniciar la vista previa. Asegúrate de que el agente esté publicado en ElevenLabs e intenta de nuevo.',
        submitError:
          'No se pudo guardar la sesión de vista previa. Es posible que los datos de la conversación no se hayan persistido.',
        status: {
          idle: 'Inactivo',
          connecting: 'Conectando…',
          connected: 'Conectado',
          disconnected: 'Desconectado',
          error: 'Error',
        },
      },
      tour: {
        a11yLabel: 'Tour de bienvenida del agente de voz',
        tip: 'Consejo',
        of: 'de',
        dismissA11y: 'Cerrar tour de bienvenida',
        skip: 'Omitir',
        skipA11y: 'Omitir tour de bienvenida',
        next: 'Siguiente',
        nextA11y: 'Siguiente consejo de bienvenida',
        gotIt: 'Entendido',
        finishA11y: 'Finalizar tour de bienvenida',
        steps: {
          welcome: {
            title: 'Bienvenido a tu nuevo agente',
            body: 'Este es el panel del agente. Todo lo que configuraste durante el onboarding vive aquí, organizado por pestaña.',
          },
          preview: {
            title: 'Prueba antes de lanzar',
            body: 'Usa el botón de Vista previa en la parte superior para hablar con el agente desde el navegador antes de ponerlo en una llamada real.',
          },
          analytics: {
            title: 'Observa las analíticas',
            body: 'La pestaña de Analíticas registra el volumen de llamadas, la tasa de calificación y la duración para que detectes qué ajustar.',
          },
        },
      },
      audio: {
        playerLabel: 'Reproductor de audio',
        playing: 'reproduciendo',
        paused: 'pausado',
        position: 'posición',
        of: 'de',
        forward: 'Adelantar',
        rewind: 'Retroceder',
        seconds: 'segundos',
        pause: 'Pausar',
        play: 'Reproducir',
        pauseAudio: 'Pausar audio',
        playAudio: 'Reproducir audio',
      },
      summary: {
        callSummary: 'Resumen de la llamada',
        structuredSummary: 'Resumen estructurado de la llamada',
        keyPoints: 'Puntos clave',
        objections: 'Objeciones',
        nextStep: 'Siguiente paso',
      },
      experiments: {
        loading: 'Cargando experimentos',
        emptyLabel: 'Aún no hay experimentos',
        emptyTitle: 'Aún no hay experimentos A/B para este agente.',
        emptyHint: 'Crea uno para comparar dos versiones de tu guion o voz.',
        listLabel: 'Lista de experimentos',
        experimentPrefix: 'Experimento',
        statusPrefix: 'estado',
        split: 'División',
        target: 'Objetivo',
        pause: 'Pausar',
        pauseA11y: 'Pausar experimento',
        start: 'Iniciar',
        resume: 'Reanudar',
        startA11y: 'Iniciar experimento',
        lift: 'Mejora',
        pValue: 'valor p',
        significant: '✓ 95% sig.',
        notSignificant: 'no sig.',
        significantA11y: 'Estadísticamente significativo al 95%',
        notSignificantA11y: 'Aún no es significativo',
        insufficientSample:
          'Muestra demasiado pequeña: se recomiendan al menos 30 llamadas por variante.',
        variant: 'Variante',
        calls: 'llamadas',
        conv: 'conv.',
        status: {
          draft: 'borrador',
          running: 'en curso',
          paused: 'pausado',
          concluded: 'concluido',
        },
        newExperiment: '+ Nuevo experimento',
        newExperimentA11y: 'Crear un nuevo experimento',
        createTitle: 'Nuevo experimento A/B',
        nameLabel: 'Nombre',
        namePlaceholder: 'ej. Apertura más cercana',
        descriptionLabel: 'Descripción (opcional)',
        descriptionPlaceholder: '¿Qué estás probando?',
        variantALabel: 'Versión de la variante A',
        variantBLabel: 'Versión de la variante B',
        splitLabel: 'Porcentaje de división (A/B)',
        outcomeTargetLabel: 'Objetivo de resultado',
        cancel: 'Cancelar',
        create: 'Crear experimento',
        createA11y: 'Crear experimento',
        creating: 'Creando…',
        errorPrefix: 'No se pudo crear el experimento',
        outcomeOptions: {
          meeting_booked: 'Reunión agendada',
          callback_requested: 'Callback solicitado',
          qualified: 'Calificado',
          disqualified: 'Descalificado',
          voicemail: 'Buzón de voz',
          no_answer: 'Sin respuesta',
          do_not_call: 'No llamar',
          other: 'Otro',
        },
      },
      transcript: {
        agent: 'Agente',
        lead: 'Lead',
        turn: 'Turno',
        at: 'a',
        seekHint: 'Presiona para ir al timestamp en el audio',
        emptyLabel: 'No hay entradas de transcripción disponibles',
        empty: 'Sin transcripción disponible.',
        callTranscript: 'Transcripción de la llamada',
      },
      batchCall: {
        title_one: 'Llamar a {{count}} lead',
        title_other: 'Llamar a {{count}} leads',
        confirm: 'Llamar leads',
        progressLabel: '{{completed}} de {{total}} leads procesados',
        creditsSkippedLabel: '{{count}} sin llamar por falta de créditos',
      },
      regulatoryDocs: {
        uploadSuccess: 'Documento subido correctamente.',
        uploadErrorGeneric: 'No se pudo subir el documento. Por favor, intentá de nuevo.',
        fileTooLarge: 'El archivo es demasiado grande. El tamaño máximo es 10 MB.',
        unsupportedFileType: 'Tipo de archivo no admitido. Permitidos: PDF, PNG, JPEG.',
        countryInvalid: 'El país debe ser un código ISO de 2 letras (ej. MX).',
        pickFilePrompt: 'Elegí un archivo primero (PDF, PNG o JPEG).',
      },
    },
    leads: {
      moveBlocked:
        'Este lead aun no puede moverse porque su cargo no coincide con tu perfil objetivo. Forza la calificacion para poder moverlo.',
      moveBlockedNonPrivileged:
        'Este lead aun no puede moverse: su cargo no coincide con tu perfil objetivo. Pedi a un manager o admin que fuerce la calificacion.',
      qualificationBlocked: {
        notQualified:
          'El cargo de este lead no coincide con tu perfil de cliente objetivo. Un manager o admin puede forzar la calificacion para poder moverlo.',
        pending:
          'El cargo de este lead aun se esta clasificando. Un manager o admin puede forzar la calificacion para poder moverlo.',
      },
      stages: {
        prospect: 'Prospecto',
        contacted: 'Contactado',
        qualified: 'Calificado',
        proposal: 'Propuesta',
        won: 'Ganado',
        lost: 'Perdido',
        negotiation: 'Negociación',
        customer: 'Cliente',
      },
      pipeline: {
        title: 'Pipeline de ventas',
        loading: 'Cargando pipeline...',
        failed: 'Error al cargar el pipeline',
        noStages: 'No hay etapas de embudo configuradas',
        noStagesHint: 'Crea una plantilla de embudo para comenzar',
        stagesLabel: 'Etapas del pipeline',
        dragHint: 'Arrastra tarjetas para mover entre etapas',
        noPipelineFound: 'Pipeline no encontrado',
        noPipelineHint: 'Crea una plantilla de embudo para comenzar',
        noActivePipeline: 'Sin pipeline activo',
        noActiveHint: 'Activa una plantilla de embudo para ver tu pipeline de ventas',
        goToSettings: 'Ir a Configuración',
      },
      kanban: {
        dropHere: 'Soltar aquí',
        scrollPrevious: 'Ver etapas anteriores',
        scrollNext: 'Ver etapas siguientes',
        noLeads: 'Sin leads en esta etapa',
      },
      list: {
        title: 'Leads',
        searchPlaceholder: 'Buscar por nombre, email o empresa...',
        allStages: 'Todas las etapas',
        noMatchFilters: 'Ningún lead coincide con tus filtros',
        noLeadsYet: 'Aún no hay leads',
        noMatchDescription: 'Intenta ajustar tu búsqueda o filtros',
        noLeadsDescription: 'Agrega tu primer lead para comenzar',
        selected: 'seleccionados',
        totalLeads: 'leads en total',
        totalLead: 'lead en total',
        sortBy: 'Ordenar por',
        sortNewest: 'Más recientes',
        sortOldest: 'Más antiguos',
        sortName: 'Nombre',
        sortCompany: 'Empresa',
        sortUpdated: 'Actualizado recientemente',
        dateRange: 'Rango de fechas',
        startDate: 'Desde',
        endDate: 'Hasta',
        clearFilters: 'Limpiar filtros',
        jobTitle: 'Cargo',
        industry: 'Industria',
        economicCapacity: 'Capacidad Económica',
        allCapacities: 'Todas las Capacidades',
        capacityHigh: 'Alta',
        capacityMedium: 'Media',
        capacityLow: 'Baja',
        location: 'Ubicación',
        tags: 'Etiquetas',
        tagsMore: '+{{count}} más',
        tagLabel: 'Etiqueta {{tag}}',
        autoTagLabel: 'Etiqueta automática {{tag}}',
        tagsListLabel: 'Etiquetas del lead',
      },
      detail: {
        notFound: 'Lead no encontrado',
        deleteConfirm: '¿Estás seguro de que deseas eliminar este lead?',
        deleteTitle: 'Eliminar Lead',
        callAction: {
          label: 'Llamar al lead',
          tooltip: 'Llamar',
          started: 'Llamada en curso',
          noTemplates: 'Sin plantillas de voz. Configura una en Ajustes primero.',
          goToVoiceSettings: 'Ir a configuración de voz',
          chooseTitle: '¿Cómo quieres llamar?',
          chooseContact: '¿A qué contacto quieres llamar?',
          humanOption: 'Llamar tú mismo',
          humanOptionHint: 'Marca al lead desde tu propio teléfono',
          agentOption: 'Agente de voz IA',
          agentOptionHint: 'Deja que un agente de voz publicado haga la llamada',
          unnamedContact: 'Contacto',
          dialFailed: 'No se pudo iniciar la llamada',
        },
        forceQualify: {
          button: 'Forzar calificacion',
          title: 'Forzar calificacion del lead',
          description:
            'Anular la calificacion automatica y marcar este lead como calificado. Por favor proporciona una razon.',
          reasonPlaceholder: 'Razon para forzar la calificacion de este lead...',
          confirm: 'Calificar',
          error: 'No se pudo forzar la calificacion del lead. Intenta de nuevo.',
        },
        qualification: {
          qualified: 'Cargo Verificado',
          notQualified: 'No Verificado - Cargo',
          pending: 'Verificacion de Cargo Pendiente',
        },
        contactInfo: 'Información de contacto',
        fullName: 'Nombre del Lead',
        contactName: 'Nombre de Contacto',
        email: 'Email',
        phone: 'Teléfono',
        company: 'Empresa',
        jobTitle: 'Cargo',
        sourceUrl: 'URL de origen',
        companyWebsiteUrl: 'Sitio web de la empresa',
        otherProfileUrl: 'Perfil de {{platform}}',
        otherNetworkEnrichment: 'Datos del perfil de {{platform}}',
        companyLinkedinUrl: 'LinkedIn de la empresa',
        source: 'Fuente',
        assignedTo: 'Asignado a',
        productInterest: 'Producto/Servicio de interés',
        productInterestEditor: {
          placeholder: '¿Qué se le está vendiendo a este lead?',
          inputLabel: 'Producto o servicio de interés',
          editLabel: 'Editar producto o servicio de interés',
          saveLabel: 'Guardar producto o servicio de interés',
          cancelLabel: 'Cancelar edición',
          suggestionHint: 'Sugerido por la IA',
          closestSuggestionHint: 'Producto más cercano · confianza baja',
          confirmSuggestion: 'Confirmar',
          confirmSuggestionLabel: 'Confirmar el producto o servicio de interés sugerido',
          unavailable: 'Producto ya no disponible',
        },
        review: {
          title: 'Requiere revisión manual',
          reasonConsentRefused:
            'El lead rechazó el consentimiento de tratamiento de datos durante una llamada del agente. Las llamadas quedaron bloqueadas — decida si contactarlo por otro canal, descartarlo o corregir sus datos.',
          flaggedOn: 'Marcado el {{date}}',
        },
        consent: {
          title: 'Consentimiento para llamadas (voz)',
          granted: 'Consentimiento otorgado',
          notGranted: 'Consentimiento revocado',
          unknown: 'Sin consentimiento registrado',
          grant: 'Otorgar',
          revoke: 'Revocar',
          grantTitle: 'Otorgar consentimiento para llamadas',
          grantBody: 'Registre la prueba de que este lead aceptó ser contactado por voz.',
          revokeTitle: 'Revocar consentimiento para llamadas',
          revokeBody:
            'Esto registra que el consentimiento ya no es válido. No se podrá llamar al lead hasta que se otorgue de nuevo.',
          sourceLabel: '¿Cómo se obtuvo el consentimiento?',
          sourceForm: 'Formulario',
          sourceWhatsapp: 'WhatsApp',
          sourceWeb: 'Sitio web',
          sourceManual: 'Manual',
          sourceApi: 'API',
          sourceVoiceCall: 'Llamada del agente de voz',
          sourceUnknown: 'Otro canal',
          evidenceUrlLabel: 'URL de evidencia (opcional)',
          evidenceUrlPlaceholder: 'https://...',
          dateObtainedLabel: 'Fecha de obtención',
          dateObtainedPlaceholder: 'AAAA-MM-DD',
          dateInvalid: 'Ingrese una fecha válida en formato AAAA-MM-DD.',
          grantedOn: 'Otorgado el {{date}} vía {{source}}',
          refusedOn: 'Rechazado el {{date}} vía {{source}}',
          submit: 'Guardar consentimiento',
          saving: 'Guardando…',
          successGranted: 'Consentimiento registrado',
          successRevoked: 'Consentimiento revocado',
          forbidden: 'Solo los propietarios y administradores pueden gestionar el consentimiento.',
          error: 'No se pudo guardar el consentimiento. Intente de nuevo.',
        },
        marketingConsent: {
          title: 'Consentimiento de marketing por WhatsApp',
          granted: 'Aceptó recibir mensajes',
          notGranted: 'No acepta mensajes',
          unknown: 'Sin definir',
          grant: 'Otorgar',
          revoke: 'Revocar',
          grantTitle: 'Otorgar consentimiento de marketing por WhatsApp',
          grantBody: 'Registre que este lead aceptó recibir mensajes de marketing por WhatsApp.',
          revokeTitle: 'Revocar consentimiento de marketing por WhatsApp',
          revokeBody:
            'Esto registra que el lead ya no acepta mensajes de marketing. Se bloquearán los mensajes de WhatsApp hacia él hasta que se otorgue de nuevo.',
          sourceLabel: '¿Cómo se obtuvo el consentimiento?',
          submit: 'Guardar consentimiento',
          saving: 'Guardando…',
          successGranted: 'Consentimiento de WhatsApp registrado',
          successRevoked: 'Consentimiento de WhatsApp revocado',
          forbidden: 'No tiene permiso para gestionar el consentimiento de este lead.',
          error: 'No se pudo guardar el consentimiento. Intente de nuevo.',
        },
        brand: {
          badge: 'Empresa',
        },
        tags: 'Etiquetas',
        tagsEditor: {
          placeholder: 'Agregar etiqueta',
          inputLabel: 'Nueva etiqueta',
          addLabel: 'Agregar etiqueta',
          removeLabel: 'Quitar etiqueta {{tag}}',
          autoTagLabel: 'Etiqueta automática {{tag}}, no editable',
          suggestionLabel: 'Agregar etiqueta sugerida {{tag}}',
        },
        lastContacted: 'Último contacto',
        customFields: 'Campos personalizados',
        notes: 'Notas',
        activity: 'Actividad',
        linkedinPosts: {
          title: 'Actividad en LinkedIn',
          instagramTitle: 'Actividad en Instagram',
          comments: 'Comentarios',
          reactions: 'Reacciones',
          showMore: 'Ver más',
          showLess: 'Ver menos',
          likes: 'me gusta',
          shares: 'compartidos',
          noComments: 'Sin comentarios',
          noReactions: 'Sin reacciones',
          postedBy: 'Publicado por',
          refreshButton: 'Actualizar publicación',
          refreshing: 'Actualizando...',
          noNewPosts: 'No hay publicaciones nuevas',
          refreshSuccess: 'Publicaciones actualizadas correctamente',
          refreshError: 'No se pudieron actualizar las publicaciones',
          lastRefreshed: 'Ultima actualizacion',
          enrichCommenter: 'Enriquecer',
          enriching: 'Enriqueciendo...',
          alreadyEnriched: 'Ya enriquecido',
          noProfileUrl: 'URL de perfil no disponible',
          more: 'mas',
        },
        enrichmentProgress: {
          checking: 'Verificando leads existentes...',
          title: 'Enriqueciendo Lead',
          completed: 'Enriquecimiento Completo',
          failed: 'Enriquecimiento Fallido',
          viewLead: 'Ver Lead',
          retry: 'Reintentar',
          missingProfileId: 'No se puede enriquecer: falta identificador del perfil',
        },
        enrichmentProcessing: 'Generando analisis de IA...',
        enrichProfile: 'Enriquecer Perfil',
        enrichFromUrl: 'Enriquecer desde URL',
        linkedinUrlPlaceholder: 'https://linkedin.com/in/...',
        instagramUrlPlaceholder: 'https://instagram.com/...',
        enrichLinkedin: 'Enriquecer desde LinkedIn',
        enrichInstagram: 'Enriquecer desde Instagram',
      },
      new: {
        title: 'Agregar prospecto',
        subtitle: 'Nuevo lead',
        noFunnel: 'No se encontró plantilla de embudo activa. Crea una plantilla primero.',
        contactInfo: 'Información de contacto',
        fullName: 'Nombre completo',
        fullNamePlaceholder: 'Ingresa nombre completo',
        contactName: 'Nombre de Contacto',
        contactNamePlaceholder: 'Ingresa nombre de contacto',
        companyOrContactRequired: 'Debe ingresar Empresa o Nombre de Contacto para crear el lead',
        emailOrPhoneRequired: 'Debe ingresar Email o Teléfono para crear el lead',
        phoneInvalid:
          'Ingresa un número de teléfono válido con indicativo de país (al menos 10 dígitos)',
        jobTitleRequired: 'El cargo es obligatorio',
        emailAddress: 'Dirección de email',
        emailPlaceholder: 'Ingresa dirección de email',
        phoneNumber: 'Número de teléfono',
        phonePlaceholder: 'Ingresa número de teléfono',
        phoneCountryTitle: 'Seleccionar país',
        phoneCountrySearch: 'Buscar país o código',
        company: 'Empresa',
        companyPlaceholder: 'Ingresa nombre de empresa',
        companyWebsiteUrl: 'URL del sitio web de la empresa',
        companyWebsiteUrlPlaceholder: 'https://empresa.com',
        companyLinkedinUrl: 'URL de LinkedIn de la empresa',
        companyLinkedinUrlPlaceholder: 'https://www.linkedin.com/company/...',
        jobTitle: 'Cargo',
        jobTitlePlaceholder: 'Ingresa el cargo',
        roleLabel: 'Rol',
        rolePlaceholder: 'Seleccionar rol',
        source: 'Fuente',
        sourceUrl: 'URL de Perfil',
        sourceUrlPlaceholder: 'https://linkedin.com/in/... o https://instagram.com/...',
        tags: 'Etiquetas (separadas por coma)',
        tagsPlaceholder: 'Ingresa etiquetas',
        createError: 'Error al crear lead. Verifica tu información e intenta de nuevo.',
        duplicateError: 'Ya existe un lead con este email en tu organización.',
        createLead: 'Crear lead',
        successToast: 'Lead creado',
      },
      edit: {
        title: 'Editar prospecto',
        subtitle: 'Editar lead',
        updateError: 'Error al actualizar lead. Verifica tu información e intenta de nuevo.',
        successToast: 'Lead actualizado',
      },
      contacts: {
        title: 'Contactos',
        singular: 'Contacto',
        primary: 'Principal',
        add: 'Agregar contacto',
        addNew: 'Nuevo contacto',
        remove: 'Eliminar',
        removeTitle: 'Eliminar contacto',
        removeMessage: '¿Estás seguro de que deseas eliminar a',
        removeWarning:
          'Toda la información asociada a este contacto se perderá de forma permanente y esta acción no se puede deshacer.',
        setPrimary: 'Establecer como principal',
        nameLabel: 'Nombre',
        emailLabel: 'Email',
        phoneLabel: 'Teléfono',
        jobTitleLabel: 'Cargo',
        profileLabel: 'Perfil',
        namePlaceholder: 'Nombre del contacto',
        emailPlaceholder: 'Correo electrónico',
        phonePlaceholder: 'Número de teléfono',
        jobTitlePlaceholder: 'Cargo',
        roleLabel: 'Rol',
        rolePlaceholder: 'Seleccionar rol',
        roles: {
          decision_maker: 'Tomador de decisiones',
          influencer: 'Influenciador',
          technical: 'Técnico',
          financial: 'Financiero',
          end_user: 'Usuario final',
          gatekeeper: 'Portero',
          champion: 'Campeón',
        },
      },
      assigneePicker: {
        unassigned: 'Sin asignar',
        placeholder: 'Seleccionar vendedor',
        overCapacityTitle: 'Excede capacidad',
        overCapacityWarning:
          '{{name}} ya tiene {{current}}/{{max}} leads asignados. ¿Deseas asignar de todos modos?',
        assignAnyway: 'Asignar de todos modos',
      },
      activityTimeline: {
        created: 'Creado',
        updated: 'Actualizado',
        stageChanged: 'Etapa cambiada',
        noteAdded: 'Nota agregada',
        assigned: 'Vendedor asignado',
        unassigned: 'Sin asignar',
        emailSent: 'Email enviado',
        titleException: 'Cargo verificado manualmente',
        activity: 'Actividad',
        noActivities: 'Sin actividad aún',
        by: 'por',
        aiAgent: 'Agente IA',
        system: 'Sistema',
        agentExecutionStarted: 'Agente iniciado',
        agentExecutionCompleted: 'Agente completado',
        agentExecutionFailed: 'Agente falló',
        chatToolCompleted: 'Acción de chat IA completada',
        voiceCallStarted: 'Llamada iniciada',
        voiceCallCompleted: 'Llamada completada',
        voiceCallSkippedNoCredits: 'Llamada no realizada: sin créditos',
        voiceCallStatusChanged: 'Estado de llamada actualizado',
        voiceCallAnalyzed: 'Llamada analizada',
        voiceMessageSent: 'Mensaje de voz enviado',
        autoCallCompleted: 'Llamada automática completada',
        leadEnriched: 'Lead enriquecido',
        automationSkipped: 'Paso omitido',
        enrichmentCompleted: 'Enriquecimiento completado',
        showAutomation: 'Ver automatización',
        loadMore: 'Cargar más',
        viaChat: 'vía chat',
      },
      analysis: {
        hot: 'Caliente',
        warm: 'Tibio',
        cold: 'Frío',
        lead: 'Lead',
        aiAnalysis: 'Análisis IA',
        sentiment: 'Sentimiento',
        intent: 'Intención',
        industry: 'Industria',
        size: 'Tamaño',
        keyInsights: 'Información clave',
      },
      instagramContent: {
        title: 'Contenido de Instagram',
        posts: 'Publicaciones',
        reels: 'Reels',
        analysis: 'Análisis',
        postBadge: 'Publicación',
        reelBadge: 'Reel',
        transcript: 'Transcripción',
        comments: 'comentarios',
        noCaption: 'Esta publicación no tiene descripción',
        open: 'Abrir',
        previous: 'Anterior',
        next: 'Siguiente',
        refresh: 'Actualizar',
        refreshing: 'Actualizando…',
        refreshUpdated: 'Contenido actualizado',
        refreshNoChanges: 'Sin contenido nuevo',
        refreshError: 'No se pudo actualizar',
        clientProfile: 'Sus clientes',
        evidence: 'Evidencia',
        priceSignals: 'Senales de precio',
        confidence: {
          high: 'Bien respaldado',
          medium: 'Parcialmente respaldado',
          low: 'Evidencia debil',
        },
        contentIdeas: 'Ideas de contenido',
        proposals: 'Qué le podemos ofrecer',
      },
      enrichment: {
        title: 'Enriquecimiento del Lead',
        brandAnalysisTitle: 'Análisis de Marca',
        interestsAnalysisTitle: 'Análisis de Intereses',
        interestsSummary: 'Intereses',
        interestTopics: 'Temas recurrentes',
        engagementLevel: 'Nivel de Engagement',
        interestScoreClear: 'Intereses claros',
        interestScorePartial: 'Intereses parcialmente claros',
        interestScoreUnclear: 'Intereses poco claros',
        contentAffinityScore: 'Afinidad de Contenido',
        postingActivityScore: 'Actividad de Publicación',
        audienceReachScore: 'Alcance de Audiencia',
        audienceInteractionScore: 'Interacción de Audiencia',
        signalGap: {
          title: 'Sin puntaje',
          private_profile:
            'Esta cuenta de Instagram es privada, así que no hay contenido público para analizar. Asignarle un puntaje sería inventarlo.',
          no_public_content:
            'Esta cuenta de Instagram no tiene biografía pública ni publicaciones legibles, así que no hay nada sobre lo que basar un análisis.',
        },
        brandBio: 'Biografía',
        brandNiche: 'Nicho',
        brandInsight: 'Análisis de Marca',
        contentType: 'Tipo de Contenido',
        brandCapacity: 'Capacidad de la Marca',
        brandFocusScore: 'Foco de Marca',
        contentQualityScore: 'Calidad del Contenido',
        digitalPresenceScore: 'Presencia Digital',
        marketPositionScore: 'Posición de Mercado',
        brandFitHigh: 'Marca con Alto Encaje',
        brandFitMedium: 'Marca con Encaje Medio',
        brandFitLow: 'Marca con Bajo Encaje',
        highPotential: 'Alto Potencial',
        mediumPotential: 'Potencial Medio',
        lowPotential: 'Bajo Potencial',
        company: 'Empresa',
        location: 'Ubicación',
        followers: 'Seguidores',
        industry: 'Industria',
        economicCapacity: 'Capacidad Económica',
        scoreBreakdown: 'Desglose de Puntuación',
        painPointsScore: 'Puntos de Dolor',
        economicScore: 'Capacidad Económica',
        investmentScore: 'Mentalidad de Inversión',
        urgencyScore: 'Urgencia',
        aiInsight: 'Insight IA',
        painPoints: 'Puntos de Dolor',
        enrichedLabel: 'Enriquecido',
        capacityHigh: 'Alta',
        capacityMedium: 'Media',
        capacityLow: 'Baja',
        sentimentVeryPositive: 'Muy Positivo',
        sentimentPositive: 'Positivo',
        sentimentNegative: 'Negativo',
        sentimentVeryNegative: 'Muy Negativo',
        sentimentNeutral: 'Neutral',
        qualificationHot: 'Caliente',
        qualificationWarm: 'Tibio',
        qualificationCold: 'Frío',
        qualificationUnqualified: 'No calificado',
        qualificationUnknown: 'Desconocido',
        potentialLabel: 'Potencial',
        method: {
          direct_apify: 'Perfil social',
          web_search: 'Búsqueda web',
          other: 'Automático',
        },
        translating: 'Traduciendo...',
      },
      companyProfile: {
        title: 'Perfil de la Empresa',
        industry: 'Industria',
        employees: 'Empleados',
        followers: 'Seguidores',
        website: 'Sitio Web',
        headquarters: 'Sede',
        founded: 'Fundada',
        specialities: 'Especialidades',
        description: 'Descripción',
        scrapedLabel: 'Extraído',
        highPotential: 'Alto Potencial',
        mediumPotential: 'Potencial Medio',
        lowPotential: 'Bajo Potencial',
        capacity: 'Capacidad de la Empresa',
        capacityHigh: 'Alta',
        capacityMedium: 'Media',
        capacityLow: 'Baja',
        scoreBreakdown: 'Desglose de Puntuación de la Empresa',
        industryFit: 'Afinidad de Industria',
        companySizeGrowth: 'Tamaño y Crecimiento',
        digitalPresence: 'Presencia Digital',
        marketPosition: 'Posición de Mercado',
        aiAnalysis: 'Análisis IA de la Empresa',
      },
      notes: {
        placeholder: 'Agregar una nota...',
        addNote: 'Agregar nota',
        noNotes: 'Sin notas aún',
        aiAgent: 'Agente IA',
      },
      pipelineSummary: {
        title: 'Resumen del pipeline',
        loading: 'Cargando datos del pipeline...',
        noLeads: 'Sin leads en el pipeline aún',
        total: 'total',
        bySource: 'Por fuente',
      },
      stageMove: {
        title: 'Mover a etapa',
      },
      viewMode: {
        myBoard: 'Mi Tablero',
        teamBoard: 'Tablero del Equipo',
        readOnly: 'Solo lectura',
      },
      webSearch: {
        searchContact: 'Buscar Contacto (Web)',
        searching: 'Buscando en la web...',
        scraping: 'Visitando paginas...',
        extracting: 'Extrayendo contactos...',
        viewResults: 'Ver Resultados',
        title: 'Busqueda Web',
        resultsTitle: 'Resultados de Busqueda',
        foundSummary: '{{count}} contacto(s) encontrado(s)',
        reviewingLinks: 'Revisando enlaces',
        currentlyVisiting: 'Visitando',
        confirmSave: 'Confirmar y Guardar',
        discard: 'Descartar',
        emailsFound: 'Emails encontrados',
        phonesFound: 'Teléfonos encontrados',
        noResults: 'No se encontraron contactos',
        noResultsDescription:
          'Buscamos en multiples paginas pero no pudimos encontrar informacion de contacto para este lead.',
        urlsVisited: 'URLs visitadas',
        cancelled: 'Busqueda cancelada',
        cancelledDescription: 'La busqueda web fue cancelada.',
        tryAgain: 'Intentar de Nuevo',
        cancel: 'Cancelar',
        requiresCompany: 'El lead debe tener un nombre de empresa para buscar.',
        sourceTypes: {
          official_website: 'Sitio Oficial',
          contact_page: 'Pagina de Contacto',
          about_page: 'Pagina Acerca de',
          social_network: 'Red Social',
          third_party_directory: 'Directorio',
          other: 'Otro',
        },
        confidence: {
          high: 'Alta',
          medium: 'Media',
          low: 'Baja',
        },
        contactType: {
          email: 'Correo',
          phone: 'Telefono',
        },
        confirmModal: {
          title: 'Guardar Contactos',
          message: 'Los contactos seleccionados se guardaran en este lead.',
        },
        successToast: 'Contactos guardados exitosamente',
        errorToast: 'Busqueda web fallida',
        sourceUrl: 'Fuente',
        selectedCount: '{{count}} seleccionado(s)',
        existingWarning:
          'Algunos tipos seleccionados ya existen en este lead y no ser\u00e1n sobrescritos.',
        overwriteTitle: '\u00bfSobrescribir datos existentes?',
        overwriteDescription:
          'El {{fields}} existente en este lead ser\u00e1 reemplazado con los nuevos valores.',
        overwriteConfirm: 'S\u00ed, sobrescribir',
        emailLabel: 'email',
        phoneLabel: 'tel\u00e9fono',
      },
      callResults: {
        sectionTitle: 'Resultados de llamadas',
        loading: 'Cargando llamadas\u2026',
        noCalls: 'Sin llamadas a\u00fan',
        followUpScheduled: 'Llamada de seguimiento programada',
        call: 'Llamada',
        viewCallDetail: 'Ver detalle de llamada',
        structuredSummary: 'Resumen estructurado de llamada',
        interestLevel: 'Inter\u00e9s',
        interestLevel_hot: 'Caliente',
        interestLevel_warm: 'Tibio',
        interestLevel_cold: 'Fr\u00edo',
        closingProbability: 'Probabilidad de cierre',
        fields: {
          resumen: 'Resumen',
          dolor_principal: 'Dolor principal',
          necesidad: 'Necesidad',
          presupuesto: 'Presupuesto',
          timeline_decision: 'L\u00ednea de tiempo de decisi\u00f3n',
          nivel_interes: 'Nivel de inter\u00e9s',
          probabilidad_cierre: 'Probabilidad de cierre',
          propuesta_sugerida: 'Propuesta sugerida',
          proximos_pasos: 'Pr\u00f3ximos pasos',
        },
        edit: {
          action: 'Editar',
          title: 'Editar resumen de llamada',
          save: 'Guardar',
          saving: 'Guardando\u2026',
          cancel: 'Cancelar',
          interestLabel: 'Nivel de inter\u00e9s',
          probabilityLabel: 'Probabilidad de cierre (0-100)',
          probabilityInvalid: 'Ingresa un n\u00famero entre 0 y 100',
          interestInvalid: 'Selecciona un nivel de inter\u00e9s v\u00e1lido',
          success: 'Resumen de llamada actualizado',
          forbidden: 'No tienes permiso para editar esta llamada',
          error: 'No se pudo actualizar el resumen de llamada',
        },
        reprocess: {
          action: 'Reprocesar an\u00e1lisis',
          pending: 'Reanalizando\u2026',
          success: 'Reprocesamiento del an\u00e1lisis iniciado',
          forbidden: 'No tienes permiso para reprocesar esta llamada',
          error: 'No se pudo reprocesar el an\u00e1lisis',
        },
      },
    },
    agents: {
      generalContext: {
        title: 'Contexto general',
        helper:
          'Estos datos compartidos de la empresa guían a los agentes que no tienen su propia personalización.',
        empty: 'Aún no hay contexto general.',
        industry: 'Industria',
        product: 'Producto',
        salesMotion: 'Modelo de ventas',
        targetAudience: 'Público objetivo',
        tone: 'Tono',
        website: 'Sitio web',
        languages: 'Idiomas',
      },
      insights: {
        title: 'Insights de Agentes IA',
        runAll: 'Ejecutar todo',
        cancel: 'Cancelar',
        cancelPipeline: 'Cancelar pipeline',
        generating: 'Generando análisis de IA...',
        qualification: 'Calificación',
        enrichment: 'Enriquecimiento',
        sentiment: 'Sentimiento',
        followUp: 'Seguimiento',
        statusNotRun: 'Sin ejecutar',
        statusDone: 'Completado',
        statusRuleBased: 'Basado en reglas',
        ruleBasedHint: 'La IA no estuvo disponible, así que este resultado se calculó con reglas.',
        statusRunning: 'En ejecución',
        statusQueued: 'En cola',
        statusFailed: 'Fallido',
        statusCancelled: 'Cancelado',
        noResults: 'Aún no hay resultados. Ejecuta este agente para analizar el lead.',
        confidence: 'de confianza',
        rerun: 'Volver a ejecutar {{agent}}',
        enrichmentFieldsFound: '{{count}} datos encontrados',
        enrichmentNothingFound: 'No se encontraron datos nuevos para este lead.',
        enrichmentField: {
          industry: 'Industria',
          jobTitle: 'Cargo',
          companySize: 'Tamaño de la empresa',
          location: 'Ubicación',
          website: 'Sitio web',
          revenue: 'Ingresos',
          linkedinUrl: 'LinkedIn',
          timezone: 'Zona horaria',
          technologies: 'Tecnologías',
        },
        sentimentNoSignals:
          'Aún no hay señales para analizar: el lead no tiene mensajes ni interacciones, por eso la confianza es baja.',
        risks: 'Riesgos',
        opportunities: 'Oportunidades',
        followUpNoSteps: 'No se planificaron pasos de seguimiento para este lead.',
        qualificationScoreLabel: 'Calificación IA',
        qualificationScoreA11y: 'Calificación IA: {{score}} de 100',
        channel: {
          email: 'Correo',
          call: 'Llamada',
          whatsapp: 'WhatsApp',
          meeting: 'Reunión',
        },
        urgency: {
          immediate: 'Inmediata',
          this_week: 'Esta semana',
          next_week: 'Próxima semana',
          nurture: 'Nutrir',
        },
      },
      status: {
        paused: 'Pausado',
        processing: 'Procesando',
        active: 'Activo',
        queued: '{{count}} en cola',
        executionsToday: 'Ejecuciones hoy',
        successful: 'Exitosas',
        successRate: 'Tasa de éxito',
      },
      activityFeed: {
        title: 'Actividad reciente',
        emptyTitle: 'Sin actividad de agentes aún',
        emptyDescription: 'Las ejecuciones de agentes aparecerán aquí una vez activadas',
        showToolCalls: 'Mostrar llamadas a herramientas',
        hideToolCalls: 'Ocultar llamadas a herramientas',
        viaChat: 'vía chat',
      },
      usage: {
        title: 'Uso del plan',
        percentUsed: '{{percent}}% de tu plan usado este mes',
        warningBanner:
          'Has usado el {{percent}}% de tu plan. Mejora tu plan antes de quedarte sin cupo.',
        criticalBanner:
          'Has usado el {{percent}}% de tu plan. Tus agentes se pausarán al llegar al 100%.',
        upgradeCta: 'Ver planes',
        unavailable: 'El uso del plan no está disponible por ahora.',
        runs: 'ejecuciones',
      },
      config: {
        title: 'Configuración de agentes',
        tiers: {
          fast: 'Rápido',
          balanced: 'Equilibrado',
          premium: 'Premium',
        },
      },
      customInstructions: {
        title: 'Instrucciones personalizadas',
        hint: 'Agrega tus propias instrucciones para extender el comportamiento base del agente...',
        placeholder: 'ej., Siempre priorizar leads de la industria de salud...',
        hidePreview: 'Ocultar vista previa',
        showPreview: 'Vista previa del prompt',
        saving: 'Guardando...',
        save: 'Guardar',
        previewTitle: 'Vista previa del prompt compuesto',
        loadingPreview: 'Cargando vista previa...',
        noPreview: 'Sin vista previa disponible',
        aiGenerate: 'Generar con IA',
        aiEnrich: 'Mejorar con IA',
        aiGenerating: 'Generando...',
        aiError: 'La sugerencia de IA falló. Intenta de nuevo.',
        aiUndo: 'Deshacer sugerencia de IA',
      },
      customization: {
        configure: 'Configurar {{name}}',
        skillsTab: 'Habilidades',
        instructionsTab: 'Instrucciones',
        skillsDescription:
          'Activa habilidades predefinidas o crea personalizadas para adaptar el agente.',
        createCustomSkill: '+ Crear habilidad personalizada',
        yourCustomSkills: 'TUS HABILIDADES PERSONALIZADAS',
        deleteSkillTitle: 'Eliminar habilidad',
        deleteSkillMessage: '¿Estás seguro de que deseas eliminar esta habilidad personalizada?',
        noSkills: 'No hay habilidades disponibles para este tipo de agente.',
      },
      execution: {
        noSelection: 'Sin ejecución seleccionada',
        lead: 'Lead',
        timing: 'Tiempo',
        model: 'Modelo',
        error: 'Error',
        reasoning: 'Razonamiento',
        name: 'Nombre',
        email: 'Email',
        started: 'Iniciado',
        completed: 'Completado',
        duration: 'Duración',
        tokens: 'Tokens',
        cost: 'Costo',
        cancelExecution: 'Cancelar ejecución',
        toolActions: 'Acciones de herramientas',
      },
      skills: {
        custom: 'Personalizado',
        autoExec: 'Ejecución automática',
        approval: 'Requiere aprobación',
      },
      toolActions: {
        noActions: 'Aún no hay acciones de herramientas.',
        approve: 'Aprobar',
        reject: 'Rechazar',
      },
      createSkill: {
        createTitle: 'Crear habilidad personalizada',
        editTitle: 'Editar habilidad',
        nameLabel: 'Nombre',
        namePlaceholder: 'ej., Cumplimiento de salud',
        categoryLabel: 'Categoría',
        categoryPlaceholder: 'ej., industria, comportamiento, cumplimiento',
        descriptionLabel: 'Descripción',
        descriptionPlaceholder: 'Descripción corta mostrada en la lista de habilidades...',
        promptLabel: 'Fragmento de prompt',
        promptHint:
          'La instrucción real inyectada en el prompt de IA cuando esta habilidad está activa.',
        promptPlaceholder:
          'ej., Al analizar leads, verificar indicadores de cumplimiento de la industria de salud...',
        executionModeLabel: 'Modo de ejecución',
        toolsLabel: 'Herramientas',
        toolsHint: 'Herramientas que esta habilidad puede usar mientras se ejecuta.',
        maxToolCallsLabel: 'Máximo de llamadas a herramientas',
        saving: 'Guardando...',
        createSkill: 'Crear habilidad',
        saveChanges: 'Guardar cambios',
      },
    },
    settings: {
      title: 'Configuración',
      subtitle: 'Administra tu cuenta, organización y preferencias',
      sections: {
        profile: 'Perfil',
        organization: 'Organización',
        team: 'Gestión de equipo',
        notifications: 'Notificaciones',
        appearance: 'Apariencia',
      },
      nav: {
        profile: 'Perfil',
        organization: 'Organización',
        businessContext: 'Contexto del negocio',
        team: 'Equipo',
        sellerProfiles: 'Perfiles de Vendedor',
        leadAssignment: 'Asignacion de Leads',
        jobTitles: 'Cargos',
        notifications: 'Notificaciones',
        appearance: 'Apariencia',
        analytics: 'Analítica',
        boardVisibility: 'Visibilidad del Tablero',
        businessHours: 'Horario laboral',
        voiceCalling: 'Llamadas de Voz',
        regulatoryDocs: 'Documentos Regulatorios',
        billing: 'Facturación',
        account: 'Cuenta',
      },
      billing: {
        title: 'Facturación y Plan',
        currentPlan: 'Plan actual',
        noPlan: 'Sin plan activo',
        noStatus: 'Desconocido',
        finalizing: 'Finalizando tu mejora…',
        paymentIncomplete:
          'El pago no se completó. Puedes reintentarlo ahora; si se procesa más tarde, se actualizará automáticamente.',
        pendingSelected: 'Seleccionaste',
        completePayment: 'Completar pago',
        monthly: 'Mensual',
        yearly: 'Anual',
        selectPlan: 'Seleccionar',
        currentPlanCta: 'Plan actual',
        contactSales: 'Contactar ventas',
        currencyNote: 'Moneda del pago:',
        checkoutUnavailable:
          'El pago no está disponible para esta organización o plan en este momento.',
        checkoutUnavailableCta: 'No disponible',
      },
      sellerProfiles: {
        title: 'Perfiles de Vendedor',
        loading: 'Cargando perfiles...',
        noMembers: 'No se encontraron miembros del equipo.',
        noProfile: 'Sin perfil',
        createProfile: 'Crear Perfil',
        editProfile: 'Editar Perfil',
        deleteProfile: 'Eliminar Perfil',
        deleteConfirmTitle: 'Eliminar Perfil de Vendedor',
        deleteConfirmMessage:
          '¿Estás seguro de que quieres eliminar el perfil de {{name}}? Esta acción no se puede deshacer.',
        sellerType: 'Tipo de Vendedor',
        sellerTypePlaceholder: 'Seleccionar tipo',
        status: 'Estado',
        statusPlaceholder: 'Seleccionar estado',
        maxLeads: 'Máx. Leads',
        maxLeadsPlaceholder: 'ej., 50',
        bio: 'Descripción',
        bioPlaceholder: 'Breve descripción...',
        phone: 'Teléfono',
        phonePlaceholder: 'ej., +54 11 1234-5678',
        industries: 'Industrias',
        noIndustries: 'No hay industrias disponibles',
        saveProfile: 'Guardar Perfil',
        saveError: 'Error al guardar perfil. Intenta de nuevo.',
        deleteError: 'Error al eliminar perfil. Intenta de nuevo.',
        industriesTitle: 'Industrias',
        addIndustry: 'Agregar Industria',
        industryPlaceholder: 'ej., SaaS, FinTech',
        noIndustriesList: 'No hay industrias configuradas.',
        deleteIndustryTitle: 'Eliminar Industria',
        deleteIndustryMessage: '¿Estás seguro de que quieres eliminar esta industria?',
        deleteIndustryError: 'No se puede eliminar una industria predeterminada.',
        addIndustryError: 'Error al agregar industria. Intenta de nuevo.',
      },
      profile: {
        title: 'Información del perfil',
        fullName: 'Nombre completo',
        fullNamePlaceholder: 'Ingresa tu nombre completo',
        email: 'Email',
        successMessage: 'Perfil actualizado exitosamente.',
        errorMessage: 'Error al actualizar perfil. Intenta de nuevo.',
      },
      organization: {
        title: 'Configuración de organización',
        nameLabel: 'Nombre de la organización',
        namePlaceholder: 'Nombre de la organización',
        yourRole: 'Tu rol',
        created: 'Creado',
        ownerOnly: 'Solo el propietario de la organización puede editar esta configuración.',
        successMessage: 'Organización actualizada exitosamente.',
        errorMessage: 'Error al actualizar. Intenta de nuevo.',
        timezoneLabel: 'Zona horaria de la organización',
        timezoneHint:
          'Se usa para agendar en el calendario y mostrar horas de eventos cuando un lead no tiene zona horaria propia.',
        useDeviceTimezone: 'Usar la zona horaria de mi dispositivo',
        timezoneNotSet: 'Sin definir (las llamadas usan America/Bogota)',
        timezoneUnsetHint:
          'Aún no se eligió una zona horaria. Las llamadas y reservas usan America/Bogota hasta que elijas una.',
      },
      businessContext: {
        title: 'Contexto del negocio',
        subtitle:
          'El contexto compartido que tus agentes de IA usan para calificar, enriquecer y hablar con tus leads. Se recopila durante el onboarding — actualízalo aquí cuando tu negocio cambie.',
        websiteLabel: 'Sitio web (opcional)',
        websitePlaceholder: 'https://tuempresa.com',
        languagesLabel: 'Idiomas (opcional)',
        languagesPlaceholder: 'Español, Inglés',
        languagesHelper: 'Separa varios idiomas con comas.',
        savedSuccess: 'Contexto del negocio actualizado exitosamente.',
        saveError: 'Error al actualizar. Intenta de nuevo.',
        readOnlyNotice:
          'Solo los propietarios y administradores de la organización pueden editar el contexto del negocio.',
        regenerateOfferTitle: 'Contexto guardado',
        regenerateOfferBody:
          'Tu configuración de IA se generó con el contexto anterior. Regenera el plan para aplicar el contexto actualizado a tus agentes.',
        regenerateCta: 'Regenerar plan de IA',
        regenerateLater: 'Ahora no',
        appliedSuccess: 'Plan de IA aplicado con tu contexto actualizado.',
        products: {
          title: 'Catálogo de productos',
          subtitle:
            'Los productos y servicios que tus agentes pueden mencionar en una conversación. Es independiente del resumen anterior.',
          empty: 'Aún no hay productos. Agrega el primero para que tus agentes sepan qué vendes.',
          add: 'Agregar producto',
          edit: 'Editar producto',
          remove: 'Eliminar producto',
          save: 'Guardar producto',
          cancel: 'Cancelar edición',
          saveError: 'No se pudo guardar el cambio en el catálogo. Intenta de nuevo.',
        },
      },
      team: {
        title: 'Miembros del equipo',
        loading: 'Cargando equipo...',
        noMembers: 'No se encontraron miembros.',
        pendingInvites: 'Invitaciones pendientes',
        noPending: 'Sin invitaciones pendientes.',
        inviteSent: 'Invitación enviada',
        inviteMember: 'Invitar miembro',
        emailPlaceholder: 'colega@empresa.com',
        role: 'Rol',
        invited: 'Invitado',
        seatUsage: 'Asientos usados: {{current}}/{{max}}',
        seatLimitTitle: 'Límite de asientos alcanzado',
        seatLimitMessage:
          'Alcanzaste el límite de asientos de tu plan actual. Mejorá tu plan para invitar más miembros.',
        removeMember: {
          title: 'Eliminar miembro',
          message: '¿Eliminar a {{name}} del equipo? Esta acción no se puede deshacer.',
        },
        revokeInvite: {
          title: 'Revocar invitación',
          message: '¿Revocar la invitación para {{email}}?',
        },
      },
      appearance: {
        title: 'Apariencia',
        language: 'Idioma',
        theme: 'Tema',
        dark: 'Oscuro',
        lightComingSoon: 'Claro (Próximamente)',
        languages: {
          english: 'English',
          spanish: 'Español',
          portuguese: 'Português',
        },
      },
      analytics: {
        title: 'Analítica',
        toggleLabel: 'Analítica de producto',
        toggleHint:
          'Permite el envío de analíticas de uso anónimas para ayudarnos a mejorar el producto. Si está desactivado, no se envían eventos de analítica.',
      },
      businessHours: {
        title: 'Horario laboral',
        description:
          'Los días y horas en que tu equipo puede reunirse. Al activarlo, los agentes solo ofrecen y agendan reuniones dentro de estas franjas.',
        enabledLabel: 'Aplicar horario laboral',
        enabledHint:
          'Los agentes no ofrecerán ni agendarán reuniones fuera de las franjas de abajo.',
        disabledHint:
          'Desactivado: los agentes pueden proponer cualquier día y hora con el calendario libre.',
        daysLabel: 'Días hábiles',
        startLabel: 'Abre a las',
        endLabel: 'Cierra a las',
        timezoneHint: 'Las horas se leen en la zona horaria de la organización:',
        timezoneNotSet: 'sin definir (America/Bogota)',
        perDayNotice:
          'Estas horas están configuradas por día o con varias franjas por día. Cambiar aquí los días u horas sobrescribiría esa configuración, por lo que se conserva tal cual; aún puedes activar o desactivar el horario laboral y editar las fechas cerradas.',
        blackoutLabel: 'Fechas cerradas',
        blackoutHint: 'Festivos o cierres. No se agenda nada en estas fechas.',
        saveSuccess: 'Horario laboral actualizado',
        saveError: 'No se pudo actualizar el horario laboral',
        days: {
          mon: 'Lun',
          tue: 'Mar',
          wed: 'Mié',
          thu: 'Jue',
          fri: 'Vie',
          sat: 'Sáb',
          sun: 'Dom',
        },
        errors: {
          noDays: 'Selecciona al menos un día hábil o desactiva el horario laboral.',
          endBeforeStart: 'La hora de cierre debe ser posterior a la de apertura.',
          perDayHours:
            'Los días u horas no se pueden cambiar aquí porque el horario está configurado por día. Restaura los valores originales para guardar.',
        },
      },
      boardVisibility: {
        title: 'Visibilidad del Tablero',
        description: 'Elige cómo se muestran los leads en el tablero del embudo',
        individual: 'Individual',
        individualDesc: 'Cada vendedor solo ve sus propios leads asignados',
        shared: 'Compartido',
        sharedDesc: 'Todos los miembros del equipo pueden ver todos los leads del tablero',
        hybrid: 'Híbrido',
        hybridDesc:
          'Cada vendedor tiene su tablero propio y acceso a una vista compartida del equipo',
        saveSuccess: 'Visibilidad del tablero actualizada',
        saveError: 'Error al actualizar la visibilidad del tablero',
        loading: 'Cargando...',
      },
      voiceCalling: {
        title: 'Llamadas de Voz',
        description:
          'Configura una plantilla de voz y la regla de llamado automático para tu equipo.',
        restricted: 'Solo los administradores y managers pueden configurar las llamadas de voz.',
        newTemplate: 'Nueva Plantilla',
      },
      account: {
        title: 'Cuenta',
        preamble: 'Zona de Peligro',
        description:
          'Cerrá sesión de tu cuenta en este dispositivo. Vas a tener que iniciar sesión de nuevo para acceder a la app.',
        signOut: 'Cerrar Sesión',
        signOutLoading: 'Cerrando sesión...',
      },
      leadAssignment: {
        title: 'Asignacion de Leads',
        description:
          'Configura como se distribuyen automaticamente los leads entrantes entre el equipo de ventas.',
        loading: 'Cargando configuracion de asignacion...',
        enableAutoAssign: 'Habilitar asignacion automatica',
        enableAutoAssignDesc:
          'Asigna automaticamente los nuevos leads a los vendedores segun la estrategia seleccionada.',
        strategyLabel: 'Estrategia de asignacion',
        strategy: {
          round_robin: 'Rotacion equitativa',
          round_robin_desc: 'Los leads se distribuyen en rotacion entre los vendedores activos.',
          load_based: 'Por carga de trabajo',
          load_based_desc: 'Los leads van al vendedor con menos leads activos.',
          manual_only: 'Solo manual',
          manual_only_desc: 'Sin asignacion automatica. Los leads deben asignarse manualmente.',
        },
        maxLeadsPerUser: 'Maximo de leads por vendedor',
        maxLeadsPerUserPlaceholder: 'Sin limite',
        assigneePool: 'Grupo de asignacion',
        assigneePoolDesc:
          'Selecciona que miembros del equipo participan en la asignacion automatica.',
        noMembers: 'No se encontraron miembros del equipo.',
        selected: 'En grupo',
        saveError: 'No se pudo guardar la configuracion. Intenta de nuevo.',
      },
      jobTitles: {
        title: 'Filtros de cargos',
        description:
          'Cuando esta habilitado, los leads se califican automaticamente segun su cargo. Los leads no calificados no pueden avanzar de etapa.',
        enableFilter: 'Habilitar filtro por cargo',
        enableFilterDescription:
          'Cuando esta deshabilitado, todos los leads se tratan como calificados sin importar el cargo.',
        addTitle: 'Agregar cargo',
        removeTitle: 'Eliminar cargo',
        titlePlaceholder: 'ej., CEO, CTO, VP Ventas',
        countryPlaceholder: 'Pais (opcional)',
        industryPlaceholder: 'Industria (opcional)',
        noTitles: 'No hay filtros de cargos configurados aun.',
        synonyms: 'Sinonimos',
        addSynonym: 'Agregar sinonimo',
        removeSynonym: 'Eliminar sinonimo',
        removeConfirm:
          'Esta seguro de que desea eliminar este elemento? Esta accion no se puede deshacer.',
        addError: 'No se pudo guardar. Intenta de nuevo.',
        synonymPlaceholder: 'ej., Director Ejecutivo',
        canonicalPlaceholder: 'Mapea a (ej., CEO)',
        noSynonyms: 'No hay sinonimos configurados aun.',
      },
      notifications: {
        title: 'Preferencias de notificaciones',
        email: {
          label: 'Notificaciones por email',
          description:
            'Recibe actualizaciones sobre leads, asignaciones y actividad del equipo por email',
        },
        push: {
          label: 'Notificaciones push',
          description: 'Recibe alertas en tiempo real en tu dispositivo para eventos importantes',
        },
        inApp: {
          label: 'Notificaciones en la app',
          description: 'Muestra insignias y banners de notificaciones dentro de la app',
        },
        digest: {
          label: 'Resumen semanal',
          description: 'Recibe un resumen semanal de la actividad de tu pipeline',
        },
        info: 'Las preferencias de notificaciones se guardan automáticamente.',
        updateError: 'No pudimos guardar tus preferencias de notificaciones. Inténtalo de nuevo.',
      },
    },
    notifications: {
      title: 'Notificaciones',
      markAllRead: 'Marcar todo como leído',
      empty: 'No hay notificaciones aún',
      sectionToday: 'Hoy',
      sectionYesterday: 'Ayer',
      sectionEarlier: 'Anteriores',
      emailDigest: 'Frecuencia de email',
      categories: {
        lead_activity: 'Actividad de leads',
        agent_automation: 'Agentes y automatización',
        team_organization: 'Equipo y organización',
        billing_system: 'Facturación y sistema',
      },
      channels: {
        push: 'Notificaciones push',
        pushDesc: 'Alertas en tiempo real en tu dispositivo',
        email: 'Notificaciones por email',
        emailDesc: 'Recibe actualizaciones por email',
        inApp: 'Notificaciones en la app',
        inAppDesc: 'Muestra notificaciones dentro de la app',
      },
      digest: {
        immediate: 'Inmediato',
        daily: 'Diario',
        weekly: 'Semanal',
      },
    },
    missionControl: {
      title: 'Centro de Misión',
      loading: 'Cargando Centro de Misión...',
      hud: {
        badge: 'Centro de Misión',
        activeCount: '{{count}} activos',
        roomLabel: 'Sala',
        boardLabel: 'Tablero',
        showRoom: 'Mostrar vista de sala',
        showBoard: 'Mostrar vista de tablero',
        standingBy: 'En espera...',
        processingTask: 'Procesando tarea',
        analyzing: 'Analizando...',
        errorOccurred: 'Ocurrió un error',
      },
      chat: {
        tabs: {
          chat: 'Chat',
          activity: 'Actividad',
        },
        agent: 'Agente',
        orchestratorError:
          'No pudimos procesar tu mensaje en este momento. Si el problema persiste, contacta a tu administrador.',
        cancelledWithActions:
          'Detenido. Estas acciones ya se habían ejecutado antes de cancelar: {{actions}}.',
        cancelledNoActions: 'Detenido. No se realizó ningún cambio antes de cancelar.',
        editLast: 'Editar último mensaje',
        emptyChat: 'Envía un mensaje para comenzar a chatear con este agente.',
        placeholder: 'Escribe un mensaje...',
        send: 'Enviar',
        noActivity: 'Sin actividad aún para este agente.',
      },
      taskBoard: {
        title: 'Tablero de tareas',
        columns: {
          queued: 'En cola',
          running: 'En ejecución',
          completed: 'Completado',
          failed: 'Fallido',
        },
        runningCount: '{{count}} en ejecución',
        newTask: 'Nueva tarea',
        noTasks: 'Sin tareas',
        noLead: 'Sin lead',
      },
      newTask: {
        title: 'Nueva tarea',
        subtitle: 'Selecciona un agente para ejecutar una tarea',
        creating: 'Creando...',
        createTask: 'Crear tarea',
      },
    },
    onboarding: {
      title: 'Primeros pasos',
      complete: '{{completed}}/{{total}} completados',
      dismiss: 'Cerrar lista de inicio',
      milestones: {
        createLead: 'Crea tu primer lead',
        moveLead: 'Mueve un lead entre etapas',
        inviteMember: 'Invita a un miembro del equipo',
      },
    },
    onboardingWizard: {
      navigation: {
        back: 'Atrás',
        stepCounter: 'Paso',
        stepCounterOf: 'de',
        regenerate: 'Regenerar plan',
      },
      profile: {
        title: 'Cuéntanos sobre tu negocio',
        subtitle:
          'Esto ayuda a nuestra IA a recomendar los agentes, habilidades y configuración de voz adecuados para ti.',
        organizationNameLabel: 'Nombre de la organización',
        organizationNamePlaceholder: 'Acme Consulting',
        targetMarketLocationLabel: 'Mercado o ubicación objetivo',
        targetMarketLocationPlaceholder: 'Ciudad de México, Colombia, LATAM, Sur de Florida',
        industryLabel: 'Industria',
        industryPlaceholder: 'ej. SaaS, Bienes raíces, Seguros',
        productSummaryLabel: '¿Qué vendes?',
        productSummaryPlaceholder: 'Describe brevemente tu producto o servicio',
        targetAudienceLabel: '¿Cuál es tu público objetivo?',
        targetAudiencePlaceholder: 'ej. Dueños de pequeñas empresas, gerentes de TI',
        salesMotionLabel: 'Estrategia de ventas (opcional)',
        salesMotionPlaceholder: 'ej. Inbound, outbound, autoservicio',
        toneLabel: 'Tono de marca (opcional)',
        tonePlaceholder: 'ej. Cercano y consultivo',
        next: 'Continuar',
      },
      products: {
        title: '¿Qué vendes?',
        subtitle:
          'Agrega tus productos o servicios a mano o sube una hoja de cálculo. Tus agentes de IA los mencionarán por nombre al hablar con los prospectos.',
        nameLabel: 'Nombre del producto o servicio',
        namePlaceholder:
          '¿Cómo le dicen tus clientes? Ej.: Pan y queso, Plan Starter, Asesoría contable',
        descriptionLabel: 'Descripción breve (opcional)',
        descriptionPlaceholder: 'Qué incluye y para quién es',
        priceRangeLabel: 'Rango de precio (opcional)',
        priceRangePlaceholder: 'ej. USD 200-400 por mes',
        add: 'Agregar al catálogo',
        remove: 'Quitar producto',
        edit: 'Editar producto',
        save: 'Guardar cambios',
        cancel: 'Cancelar edición',
        kind: {
          label: 'Tipo',
          product: 'Producto',
          service: 'Servicio',
        },
        aliases: {
          label: 'Otros nombres que usan tus clientes',
          placeholder: 'Ej.: pan con queso, combo desayuno',
          helper:
            'Separa los nombres con comas o pulsa Agregar. Tus agentes de IA también reconocerán estos nombres.',
          add: 'Agregar',
          removeLabel: 'Quitar el nombre {{alias}}',
          limit: 'Puedes agregar hasta {{max}} nombres más.',
          rejected: {
            tooLong:
              'Cada nombre puede tener hasta {{max}} caracteres. Acórtalo y pulsa Agregar de nuevo.',
            capped:
              'Puedes tener hasta {{max}} otros nombres. Los nombres que quedan en el campo no se agregaron.',
            duplicate:
              'Estos nombres ya están en la lista o coinciden con el nombre del producto. Edítalos o bórralos.',
          },
          alsoKnownAs: 'también:',
        },
        naming: {
          genericWarning:
            'Este nombre parece genérico. Tus agentes de IA y vendedores verán exactamente este nombre, así que usa el que dicen tus clientes.',
          suggestion: '¿Tu producto se llama «{{suggestion}}»?',
          useAsName: 'Usar como nombre',
          useAsNameLabel: 'Usar «{{suggestion}}» como nombre del producto',
          previewTitle: 'Así lo verán los agentes de IA',
          savedGenericNotice:
            'Este producto tiene un nombre genérico. Tus agentes de IA y vendedores lo verán tal cual.',
        },
        empty: 'Aún no agregaste productos. Puedes agregarlos más tarde desde configuración.',
        errors: {
          product_name_taken: 'Ya tienes un producto con ese nombre. Usa un nombre distinto.',
          product_name_required: 'Escribe el nombre del producto.',
          product_name_invalid: 'El nombre del producto debe ser texto.',
          product_name_too_long: 'El nombre del producto puede tener hasta {{max}} caracteres.',
          product_alias_invalid: 'Cada otro nombre debe ser texto.',
          product_alias_too_long: 'Cada otro nombre puede tener hasta {{max}} caracteres.',
          product_aliases_too_many: 'Puedes agregar hasta {{max}} otros nombres.',
          product_description_too_long: 'La descripción puede tener hasta {{max}} caracteres.',
          product_price_range_too_long: 'El rango de precios puede tener hasta {{max}} caracteres.',
        },
        deleteDialog: {
          title: '¿Eliminar «{{name}}»?',
          linkedLeadsOne:
            '{{count}} lead tiene este producto como interés; conservará el texto y verá «Producto ya no disponible».',
          linkedLeadsOther:
            '{{count}} leads tienen este producto como interés; conservarán el texto y verán «Producto ya no disponible».',
          cancel: 'Cancelar',
          confirm: 'Eliminar producto',
          linkedLeadsError:
            'No pudimos comprobar si hay leads que usan este producto. Aun así puedes eliminarlo.',
        },
        import: {
          divider: 'o sube un archivo',
          hint: 'Sube un archivo CSV, XLS o XLSX con las columnas Nombre (obligatoria), Descripción, Rango de precio, Alias (separados por comas) y Tipo (producto o servicio). Las filas sin nombre se omiten.',
          downloadTemplate: 'Descargar plantilla de ejemplo',
          summary: '{{added}} productos agregados, {{skipped}} filas omitidas',
          rowLabel: 'Fila {{row}}:',
          warningsTitle: 'Importados, pero conviene revisarlos:',
          warnings: {
            generic_name: '«{{name}}» parece un nombre genérico.',
            aliases_truncated:
              '«{{name}}»: algunos alias no se guardaron (máximo {{maxCount}}, de hasta {{maxLength}} caracteres).',
            unknown_kind: '«{{name}}»: no reconocimos el tipo; se guardó como producto.',
            unknown: 'Revisa el nombre de esta fila.',
            generic_name_with_suggestion:
              '«{{name}}» parece un nombre genérico. ¿Se llama «{{suggestion}}»?',
          },
          error: 'No pudimos leer ese archivo. Usa la plantilla .xlsx e intenta de nuevo.',
          reasons: {
            missing_name: 'falta el nombre',
            name_too_long: 'el nombre supera los {{max}} caracteres',
            description_too_long: 'la descripción supera los {{max}} caracteres',
            price_range_too_long: 'el rango de precio supera los {{max}} caracteres',
            duplicate_in_file: 'repetido en el archivo',
            already_exists: 'ya está en tu catálogo',
          },
        },
        next: 'Continuar',
      },
      generating: {
        title: 'Preparando tu configuración',
        subtitle:
          'Nuestra IA está analizando tu negocio para recomendar agentes, habilidades y un asistente de voz.',
        errorTitle: 'No pudimos generar tu configuración',
        retry: 'Intentar de nuevo',
      },
      review: {
        title: 'Revisa tu configuración recomendada',
        fallbackBanner:
          'Usamos nuestra configuración recomendada por defecto porque no pudimos personalizarla con confianza para tu negocio.',
        agentsTitle: 'Agentes de IA',
        skillsTitle: 'Habilidades',
        toolsTitle: 'Herramientas incluidas',
        voiceTitle: 'Agente de voz',
        voiceUseCase: 'Caso de uso',
        voiceSuccessCriteria: 'Criterios de éxito',
        apply: 'Aplicar configuración',
        applying: 'Aplicando...',
      },
      success: {
        title: '¡Todo listo!',
        subtitle: 'Tus agentes de IA y configuración se aplicaron a tu organización.',
        cta: 'Ir a Agente de IA',
      },
      modality: {
        guidedLabel: 'Formulario guiado',
        voiceLabel: 'Voz',
        voiceScaffoldTitle: 'La incorporación por voz llegará pronto',
        voiceScaffoldSubtitle:
          'Estamos construyendo una entrevista de voz para configurar tu organización. Por ahora, continúa con el formulario guiado.',
        continueGuided: 'Continuar con el formulario guiado',
      },
    },
    components: {
      errorBoundary: {
        title: '¡Ups! Algo salió mal',
        description:
          'Lamentamos la molestia. El error ha sido reportado y lo solucionaremos pronto.',
        devError: 'Detalles del error (Solo dev):',
      },
      confirmModal: {
        processing: 'Procesando...',
        warningIconLabel: 'Advertencia',
      },
      primitives: {
        searchPlaceholder: 'Buscar...',
        selectPlaceholder: 'Seleccionar...',
        closeSheet: 'Cerrar panel',
        close: 'Cerrar',
        removeBadge: 'Quitar',
        avatar: 'Avatar',
        avatarFor: 'Avatar de {{name}}',
      },
      landing: {
        aiLeadCapture: 'Captura de leads con IA',
        smartQualification: 'Calificación inteligente',
        realTimeAnalytics: 'Analítica en tiempo real',
        aiAutomation: 'Automatización con IA',
        emailConfirmed: 'Email confirmado',
        earlyAccessSecured: 'Acceso anticipado asegurado',
        humanTouch: 'Toque humano',
        automated: 'Automatizado',
      },
    },
    pages: {
      login: {
        heroTitle: 'Gestiona tu pipeline de ventas',
        heroSubtitle:
          'Rastrea leads, automatiza flujos de trabajo y cierra acuerdos más rápido con insights de IA.',
        statRevenue: 'Ingresos',
        statRevenueValue: '$48.2K',
        statLeads: 'Leads activos',
        statLeadsValue: '1,284',
        statConversion: 'Conversión',
        statConversionValue: '24.8%',
        loginFailed: 'Error al iniciar sesión. Intenta de nuevo.',
        googleFailed: 'Error con Google. Intenta de nuevo.',
        linkedinFailed: 'Error con LinkedIn. Intenta de nuevo.',
        welcomeBack: 'Bienvenido de nuevo',
        loginSubtitle: 'Inicia sesión en tu cuenta',
        continueAuth0: 'Iniciar Sesión',
        noAccount: '¿No tienes cuenta?',
        signUp: 'Regístrate',
      },
      signup: {
        heroTitle: 'Empieza a crecer tus ventas',
        heroSubtitle: 'Únete a miles de equipos que usan IA para cerrar negocios más rápido.',
        getStarted: 'COMIENZA',
        createAccount: 'Crea tu cuenta',
        continueAuth0: 'Regístrate',
        alreadyHaveAccount: '¿Ya tienes una cuenta?',
        logIn: 'Inicia sesión',
        signupFailed: 'Error al registrarse. Intenta de nuevo.',
        googleFailed: 'Error con Google. Intenta de nuevo.',
        linkedinFailed: 'Error con LinkedIn. Intenta de nuevo.',
        joiningOrg: 'Uniéndote a',
        inviteInvalid: 'Este enlace de invitación ya no es válido.',
        inviteExpired: 'Esta invitación ha expirado.',
      },
      waitlist: {
        backToHome: 'Volver al inicio',
        earlyAccess: 'ACCESO ANTICIPADO',
        joinThe: 'Únete al ',
        future: 'Futuro',
        ofSales: ' de las ventas',
        subtitle: 'Sé el primero en experimentar la automatización de ventas con IA...',
        teamsWaiting: 'equipos de ventas ya esperando',
        reserveSpot: 'Reserva tu lugar',
        reserveSubtitle: 'Únete a la lista de espera y te notificaremos cuando lancemos',
        whatYouGet: 'Lo que obtendrás',
        benefits: {
          earlyAccess: {
            title: 'Acceso anticipado',
            description: 'Sé el primero en probar nuevas funciones',
          },
          launchPricing: {
            title: 'Precio de lanzamiento',
            description: 'Asegura el precio especial de lanzamiento para siempre',
          },
          productUpdates: {
            title: 'Actualizaciones del producto',
            description: 'Recibe actualizaciones exclusivas detrás de escena',
          },
          shapeProduct: {
            title: 'Moldea el producto',
            description: 'Tu feedback influye directamente en nuestra hoja de ruta',
          },
        },
        trustBadges: {
          noCreditCard: 'Sin tarjeta de crédito',
          cancelAnytime: 'Cancela cuando quieras',
          prioritySupport: 'Soporte prioritario',
        },
      },
      funnel: {
        title: 'Pipeline de ventas',
        refreshTooltip: 'Actualizar',
      },
    },
    campaigns: {
      title: 'Campañas',
      createNew: 'Nueva Campaña',
      empty: {
        title: 'Sin campañas aún',
        description: 'Crea tu primera campaña multicanal para llegar a tus leads',
      },
      form: {
        name: 'Nombre de la Campaña',
        namePlaceholder: 'Ej: Campaña Email Q1',
        description: 'Descripción',
        descriptionPlaceholder: 'Describe el propósito de esta campaña',
        channels: 'Canales',
        channelsHint: 'Selecciona al menos un canal',
        audienceType: 'Audiencia',
        manual: 'Selección Manual',
        segment: 'Segmento por Filtros',
        schedule: 'Programar',
        scheduleLater: 'Programar para después',
        create: 'Crear Campaña',
      },
      channels: {
        email: 'Email',
        sms: 'SMS',
        whatsapp: 'WhatsApp',
        voice: 'Voz',
      },
      status: {
        draft: 'Borrador',
        active: 'Activa',
        completed: 'Completada',
      },
      actions: {
        activate: 'Activar',
        complete: 'Completar',
        edit: 'Editar',
        delete: 'Eliminar',
      },
      detail: {
        leads: 'Leads',
        sent: 'Enviados',
        delivered: 'Entregados',
        failed: 'Fallidos',
        createdBy: 'Creada por',
        activatedOn: 'Activada el',
        completedOn: 'Completada el',
      },
      segment: {
        stageFilter: 'Etapa del Embudo',
        tagsFilter: 'Etiquetas',
        sourceFilter: 'Fuente del Lead',
        dateRangeFilter: 'Rango de Fecha de Creación',
        previewCount: 'leads coinciden con este filtro',
      },
      confirmActivate: '¿Estás seguro de que quieres activar esta campaña?',
      confirmDelete: '¿Estás seguro de que quieres eliminar esta campaña?',
    },
    whatsapp: {
      title: 'WhatsApp',
      subtitle: 'Conversaciones con tus leads',
      searchPlaceholder: 'Buscar conversaciones...',
      empty: {
        title: 'Aún no hay conversaciones',
        description: 'Las conversaciones de WhatsApp aparecerán aquí cuando un lead te escriba.',
      },
      window: {
        open: 'Ventana de 24 h abierta',
        closed: 'Ventana cerrada',
        useTemplate: 'Enviar mensaje de plantilla',
        hint: 'La ventana de 24 h se cerró. Usá una plantilla para retomar la conversación.',
        remaining: 'restantes',
      },
      input: {
        placeholder: 'Escribí un mensaje...',
      },
      templates: {
        title: 'Plantillas de mensaje',
        sync: 'Sincronizar desde Meta',
        syncing: 'Sincronizando...',
        empty: 'No se encontraron plantillas aprobadas. Sincronizá desde Meta para cargarlas.',
      },
      status: {
        sent: 'Enviado',
        delivered: 'Entregado',
        read: 'Leído',
        failed: 'Falló',
      },
    },
    linkedinSearch: {
      title: 'Busqueda LinkedIn',
      description: 'Crea segmentos de busqueda para encontrar y enriquecer perfiles de LinkedIn',
      newSegment: 'Nuevo Segmento',
      backToSegments: 'Volver a segmentos',
      emptyTitle: 'Sin segmentos de busqueda',
      emptyDescription: 'Crea un segmento para empezar a buscar y enriquecer perfiles de LinkedIn',
      statuses: {
        draft: 'Borrador',
        running: 'Ejecutando',
        completed: 'Completado',
        failed: 'Fallido',
      },
      segments: {
        found: 'Encontrados',
        enriched: 'Enriquecidos',
        skipped: 'Omitidos',
        failed: 'Fallidos',
        running: 'Ejecutando...',
        execute: 'Ejecutar',
        viewResults: 'Ver Resultados',
        deleteSegment: 'Eliminar segmento',
        failedHint:
          'La búsqueda falló. Intentá de nuevo — si sigue fallando, contactá a soporte para revisar el acceso a LinkedIn.',
      },
      results: {
        title: 'Resultados',
        score: 'Puntuación',
        leads: 'leads',
      },
      form: {
        title: 'Nuevo Segmento de Busqueda',
        segmentName: 'Nombre del Segmento',
        segmentNamePlaceholder: 'ej. CEOs de Marketing en LATAM',
        keywords: 'Palabras Clave',
        keywordsPlaceholder: 'ej. CEO, Director de Marketing, Fundador',
        roleFilter: 'Filtro de Rol / Titulo',
        roleFilterPlaceholder: 'ej. C-Level, VP, Director',
        location: 'Ubicación',
        locationPlaceholder: 'ej. Ciudad de Mexico, Estados Unidos',
        industry: 'Industria',
        industryPlaceholder: 'ej. Marketing y Publicidad',
        companySize: 'Tamaño de Empresa',
        maxResults: 'Resultados Maximos (1-200)',
        createSegment: 'Crear Segmento',
      },
    },
    leadImport: {
      title: 'Importar Leads',
      steps: {
        upload: 'Importar Leads',
        mapping: 'Mapear Columnas',
        preview: 'Vista Previa',
        importing: 'Importando...',
        complete: 'Importacion Completa',
      },
      mappingHint: 'La IA mapeo tus columnas. Revisa y ajusta si es necesario.',
      maxRows: 'Maximo 5,000 filas',
      uploadAndAnalyze: 'Subir y Analizar',
      preview: 'Vista Previa',
      importNLeads: 'Importar {{n}} Leads',
      noActiveTemplate:
        'No se encontró una plantilla de embudo activa. Activa una plantilla antes de importar.',
      done: 'Listo',
      totalRowsPreview:
        '{{total}} filas encontradas. Vista previa de las primeras {{preview}} filas:',
      fileUpload: {
        dragAndDrop: 'Haz clic para buscar o arrastra y suelta',
        fileTypes: 'CSV, XLS o XLSX (max 10MB)',
        chooseFile: 'Elegir Archivo',
        unsupportedType: 'Ese tipo de archivo no es compatible. Usa un archivo CSV, XLS o XLSX.',
      },
      columnMapping: {
        fileColumn: 'Columna del Archivo',
        crmField: 'Campo CRM',
      },
      progress: {
        title: 'Importando Leads',
        processing: 'Procesando {{n}} registros...',
        doNotClose: 'Por favor no cierres esta ventana',
      },
      summary: {
        title: 'Importacion Completa',
        imported: 'Importados',
        duplicates: 'Duplicados',
        errors: 'Errores',
        errorDetails: 'Detalles de Errores',
        duplicateDetails: 'Emails Duplicados',
        andMore: '...y {{count}} mas',
        rowError: 'Fila {{row}}: {{message}}',
      },
      confidence: {
        high: 'Alto',
        medium: 'Medio',
        low: 'Bajo',
      },
      downloadTemplate: 'Descargar Plantilla',
      crmFields: {
        firstName: 'Nombre',
        lastName: 'Apellido',
        email: 'Correo Electronico',
        phone: 'Telefono',
        company: 'Empresa',
        jobTitle: 'Cargo',
        companyUrl: 'URL de Empresa',
        linkedinUrl: 'URL de LinkedIn',
        tags: 'Etiquetas',
        skip: 'Omitir',
      },
    },
    leadSources: {
      page: {
        title: 'Fuentes de Leads',
        subtitle:
          'Conecta formularios y herramientas externas para enviar leads directamente a tu embudo.',
      },
      sections: {
        connected: 'Conectadas',
        available: 'Disponibles',
      },
      empty: {
        connectedTitle: 'Aún no hay conexiones',
        connectedDescription:
          'Conecta un formulario o herramienta a continuación para empezar a recibir leads automáticamente.',
      },
      status: {
        active: 'Activa',
        paused: 'Pausada',
        archived: 'Eliminada',
      },
      actions: {
        create: 'Nueva conexión',
        rotate: 'Rotar credencial',
        revoke: 'Eliminar conexión',
        delete: 'Eliminar permanentemente',
        viewDetails: 'Ver detalles',
        copy: 'Copiar',
        done: 'Listo',
        cancel: 'Cancelar',
        retry: 'Reintentar',
      },
      readOnlyNotice:
        'Pide a un propietario o administrador que gestione las conexiones. Puedes seguir viendo el estado y el mapeo.',
      confirmRevoke: {
        title: '¿Eliminar esta conexión?',
        message:
          'La recepción se detiene de inmediato. Los leads ya recibidos conservan su origen.',
      },
      confirmDelete: {
        title: '¿Eliminar esta conexión de forma permanente?',
        message: 'Los leads ya recibidos se conservan, pero pierden su referencia de conexión.',
      },
      errors: {
        loadFailed:
          'No se pudieron cargar las fuentes de leads. Desliza para actualizar o inténtalo más tarde.',
        catalogLoadFailedTitle: 'No se pudieron cargar los proveedores',
        catalogLoadFailed:
          'Algo salió mal al cargar los proveedores disponibles. Por favor, intenta de nuevo.',
      },
      secretModal: {
        title: 'Guarda tu credencial ahora',
        warning:
          'Esta es la única vez que mostraremos este valor. Solo guardamos un hash — si la pierdes, rota para obtener una nueva.',
        urlLabel: 'URL de recepción',
        keyLabel: 'Clave de API',
        copied: 'Copiado al portapapeles',
        acknowledge: 'He guardado esta credencial',
        doneDisabledHint: 'Confirma que guardaste la credencial para continuar',
      },
      detail: {
        title: 'Detalles de la conexión',
        mappingTitle: 'Mapeo de campos',
        credentialsTitle: 'Credenciales',
        errorsTitle: 'Errores recientes',
        noErrors: 'No se reportaron errores',
        lastErrorAt: 'Último error',
        lastErrorCode: 'Código de error',
        lastSubmissionAt: 'Último envío',
        rateLimitLabel: 'Límite de tasa',
      },
    },
    legal: {
      terms: {
        heading: 'Terminos del servicio',
        placeholder: 'TODO: contenido pendiente — completar con texto legal antes de publicar.',
      },
      cookiePolicy: {
        heading: 'Politica de cookies',
        placeholder: 'TODO: contenido pendiente — completar con texto legal antes de publicar.',
      },
      gdpr: {
        heading: 'GDPR y privacidad de datos',
        placeholder: 'TODO: contenido pendiente — completar con texto legal antes de publicar.',
      },
    },
    marketing: {
      about: {
        heading: 'Sobre nosotros',
        placeholder:
          'TODO: contenido pendiente — completar con historia de la empresa antes de publicar.',
      },
      press: {
        heading: 'Kit de prensa',
        placeholder:
          'TODO: contenido pendiente — completar con recursos de prensa antes de publicar.',
      },
    },
  },
  pt: {
    common: {
      appName: 'FlowSoft Painel de Funis',
      creditsExhausted: 'Créditos de IA esgotados — renovam em {{date}}',
      creditsExhaustedNoDate: 'Créditos de IA esgotados — renovam no próximo período de cobrança',
      selectSearchPlaceholder: 'Buscar…',
      selectNoMatches: 'Sem resultados',
      tagline: 'Rastreie, analise e otimize seu funil de vendas',
      startFreeTrial: 'Comece o teste gratis',
      watchDemo: 'Ver demo →',
      languageLabel: 'Idioma',
      english: 'Inglês',
      spanish: 'Espanhol',
      portuguese: 'Português',
      englishShort: 'EN',
      spanishShort: 'ES',
      portugueseShort: 'PT',
      statuses: {
        active: 'Ativo',
        paused: 'Pausado',
        pending: 'Pendente',
        processing: 'Processando',
        running: 'Em execução',
        completed: 'Concluído',
        failed: 'Falhou',
        queued: 'Na fila',
        cancelled: 'Cancelado',
        success: 'Sucesso',
        partialSuccess: 'Sucesso parcial',
        error: 'Erro',
        live: 'Ao vivo',
        offline: 'Offline',
      },
      errors: {
        generic: 'Algo deu errado. Tente novamente.',
      },
      loading: 'Carregando...',
      actions: {
        on: 'Ativado',
        off: 'Desativado',
        cancel: 'Cancelar',
        delete: 'Excluir',
        confirm: 'Confirmar',
        save: 'Salvar',
        saveChanges: 'Salvar alterações',
        edit: 'Editar',
        create: 'Criar',
        remove: 'Remover',
        add: 'Adicionar',
        clear: 'Limpar',
        setPrimary: 'Definir como principal',
        close: 'Fechar',
        goBack: 'Voltar',
        tryAgain: 'Tentar novamente',
        send: 'Enviar',
        go: 'Ir',
        done: 'Pronto',
        move: 'Mover',
        export: 'Exportar',
        import: 'Importar',
        refresh: 'Atualizar',
        logout: 'Sair',
        addLead: 'Adicionar lead',
        revoke: 'Revogar',
        sendInvite: 'Enviar convite',
        call: 'Ligar',
      },
      unsavedChanges: {
        title: 'Alterações não salvas',
        message: 'Você tem alterações não salvas. Descarta-las?',
        discard: 'Descartar',
        keepEditing: 'Continuar editando',
      },
      offline: 'Você esta offline. Alguns recursos podem não estar disponíveis.',
      and: 'e',
      states: {
        loading: 'Carregando...',
        saving: 'Salvando...',
        creating: 'Criando...',
        processing: 'Processando...',
        on: 'Sim',
        off: 'Não',
      },
      sources: {
        email: 'Email',
        linkedin: 'LinkedIn',
        instagram: 'Instagram',
        facebook: 'Facebook',
        twitter: 'Twitter',
        form: 'Formulário',
        scraping: 'Scraping',
        manual: 'Manual',
        website: 'Site',
        referral: 'Indicação',
        other: 'Outro',
        allSources: 'Todas as fontes',
        webForm: 'Formulário web',
        linkedinEnrichment: 'Enriquecimento do LinkedIn',
        linkedinSegmentSearch: 'Busca por segmento do LinkedIn',
        instagramEnrichment: 'Enriquecimento do Instagram',
        instagramSegmentSearch: 'Busca por segmento do Instagram',
        import: 'Importação',
        event: 'Evento',
      },
      deviceTypes: {
        mobile: 'Móvel',
        desktop: 'Desktop',
        tablet: 'Tablet',
        unknown: 'Desconhecido',
      },
      agentTypes: {
        leadQualification: 'Qualificação de leads',
        leadEnrichment: 'Enriquecimento de leads',
        followUpScheduler: 'Agendador de follow-up',
        sentimentAnalysis: 'Análise de sentimento',
        promptEnricher: 'Enriquecedor de Prompts',
        linkedinCompanyResearch: 'Pesquisa LinkedIn',
        browserCompanyResearch: 'Pesquisa Web',
        flowsellaiCompanyResearch: 'Dados FlowSellAI',
        instagramCompanyResearch: 'Pesquisa Instagram',
        chatOrchestrator: 'Orquestrador de Chat',
        stageProspectIntake: 'Entrada de prospects',
        stageFirstContact: 'Primeiro contato',
        stageQualificationDeep: 'Qualificação profunda',
        stageProposalPrep: 'Preparação de proposta',
        stageWonOnboarding: 'Onboarding de ganhos',
        stageLostAnalysis: 'Análise de perdidos',
        linkedinEnrichment: 'Enriquecimento LinkedIn',
        instagramEnrichment: 'Enriquecimento Instagram',
        instagramCompanyEnrichment: 'Enriquecimento de empresa Instagram',
        companyEnrichment: 'Enriquecimento de empresa',
        voiceCall: 'Resumo da chamada',
        generic: 'Agente de IA',
      },
      agentLabels: {
        qualifier: 'Qualificador',
        enricher: 'Enriquecedor',
        scheduler: 'Agendador',
        analyst: 'Analista',
      },
      timeRanges: {
        sevenDays: '7D',
        thirtyDays: '30D',
        ninetyDays: '90D',
      },
      fallbacks: {
        unknownLead: 'Lead desconhecido',
        unknown: 'Desconhecido',
        user: 'Usuário',
        member: 'Membro',
      },
      detail: 'Detalhe',
      roles: {
        owner: 'Proprietário',
        admin: 'Administrador',
        member: 'Membro',
        viewer: 'Visualizador',
      },
    },
    layout: {
      title: 'FlowSellAI',
      login: 'Entrar',
      dashboard: 'Painel',
      analytics: 'Analíticos',
      workflows: 'Fluxos de trabalho',
      workflowExecutions: 'Execuções de Fluxos',
      sidebar: {
        brandName: 'FlowsellAI',
        brandSubtitle: 'Centro de Comando',
        settings: 'Configurações',
        logout: 'Sair',
        expandSidebar: 'Expandir barra lateral',
        collapseSidebar: 'Recolher barra lateral',
      },
      mobileTabBar: {
        dashboard: 'Painel',
        pipeline: 'Pipeline',
        leads: 'Leads',
        chat: 'Chat',
        calls: 'Chamadas',
        analytics: 'Analíticos',
        settings: 'Configurações',
      },
      nav: {
        dashboard: 'Painel',
        pipeline: 'Pipeline',
        leads: 'Leads',
        calendar: 'Agenda',
        chat: 'Chat',
        analytics: 'Analíticos',
        aiAgents: 'Agentes IA',
        missionControl: 'Centro de Missão',
        workflows: 'Fluxos de trabalho',
        voiceAgent: 'Agente de Voz',
        voiceAgents: 'Agentes de Voz',
        campaigns: 'Campanhas',
        whatsapp: 'WhatsApp',
        leadSources: 'Fontes de Leads',
        settings: 'Configurações',
      },
      navSections: {
        main: 'PRINCIPAL',
        insights: 'INSIGHTS',
        automation: 'AUTOMAÇÃO',
      },
    },
    auth: {
      title: 'FlowSellAI',
      subtitle: 'Rastreie, analise e otimize seu funil de vendas',
      panelHeadline: 'Feche mais negócios, no piloto automático.',
      panelSubhead:
        'O FlowSellAI captura, qualifica e nutre cada lead—para sua equipe só entrar na conversa que importa.',
      panelSocialProof: 'Fundadores estão entrando na lista de espera agora.',
      getStarted: 'Começar',
      magicLinkHint: 'Enviaremos um código de 6 dígitos para você entrar.',
      checkSpam: 'Não chegou? Verifique sua pasta de spam.',
      features: {
        analytics: {
          title: 'Analíticos em tempo real',
          description: 'Monitore o desempenho do seu funil instantaneamente',
        },
        conversion: {
          title: 'Rastreamento de conversões',
          description: 'Acompanhe cada etapa da jornada do seu cliente',
        },
        leads: {
          title: 'Gestão de leads',
          description: 'Organize e nutra seus leads de forma eficaz',
        },
        campaigns: {
          title: 'Otimização de campanhas',
          description: 'Melhore o ROI com insights baseados em dados',
        },
      },
      cta: 'Continuar com Auth0',
      secureNote: 'Autenticação segura com Auth0',
      footer: '(c) 2026 FlowSoftDev. Todos os direitos reservados.',
      sendMagicLink: 'Enviar link mágico',
      checkInbox: 'Verifique sua caixa de entrada',
      enterCode: 'Digite o código de 6 dígitos que enviamos',
      verifyCode: 'Verificar código',
      codeInvalid: 'Esse código e inválido ou expirou — tente novamente.',
      linkInvalid: 'Link mágico não e mais valido — solicite um novo.',
      resendIn: 'Reenviar em {{seconds}} segundos',
      codeResent: 'Código reenviado para {{email}}',
      accountDisabled:
        'Esta conta foi desativada. Por favor entre em contato com o administrador do seu espaço de trabalho.',
      connectionUnavailable:
        'Não foi possível enviar o link agora. Por favor tente novamente mais tarde.',
      differentEmail: 'Endereço errado? Use um email diferente.',
      inviteMismatch: 'Este convite foi enviado para um endereço de email diferente.',
      emailTaken: 'Este email já esta vinculado a outra conta.',
      confirmJoinOrg: 'Entrar em {{organizationName}} como {{role}}?',
      invitedBy: 'Convidado por {{inviter}}',
      confirmJoin: 'Entrar',
      declineJoin: 'Agora não',
      rateLimited: 'Muitas tentativas. Aguarde antes de tentar novamente.',
      unknownError: 'Algo deu errado. Por favor tente novamente.',
      freeCapacityExhaustedTitle: 'Sem vagas disponíveis no momento',
      freeCapacityExhausted:
        'O acesso gratuito está lotado no momento. Entre na lista de espera e avisaremos por e-mail assim que uma vaga abrir.',
      joinWaitlist: 'Entrar na lista de espera',
      orgDomainExistsTitle: 'Sua empresa já tem um espaço de trabalho',
      orgDomainExists:
        '{{organizationName}} já está cadastrada no FlowSoft. Peça a um administrador para convidar você e depois entre com o mesmo e-mail.',
      orgDomainExistsInline:
        'Esta empresa já tem um espaço de trabalho. Peça um convite a um administrador ou use outro e-mail.',
      orgDomainExistsOrgFallback: 'Sua empresa',
    },
    dashboard: {
      overviewTitle: 'Visão geral do painel',
      greeting: {
        morning: 'Bom dia',
        afternoon: 'Boa tarde',
        evening: 'Boa noite',
      },
      header: {
        badge: 'Visão geral',
        subtitle: 'Bem-vindo de volta! Aqui esta o resumo do seu desempenho de hoje.',
      },
      metrics: {
        revenue: {
          title: 'Receita total',
          subtitle: 'Últimos 30 dias',
        },
        visitors: {
          title: 'Total de visitantes',
          subtitle: 'Últimos 30 dias',
        },
        conversion: {
          title: 'Taxa de conversão',
          subtitle: 'Leads para compras',
        },
        campaigns: {
          title: 'Campanhas ativas',
          subtitle: 'Em execução este mês',
        },
        comingSoon: 'Em breve',
        vsLastPeriod: 'vs período anterior',
      },
      recentActivity: {
        title: 'Atividade Recente',
        viewAll: 'Ver Tudo',
        loading: 'Carregando atividade...',
        empty: {
          title: 'Sem atividade recente',
          description: 'A atividade dos seus leads aparecerá aqui',
        },
        activities: {
          leadCreated: {
            title: 'Novo lead adicionado',
            description: 'entrou via LinkedIn',
          },
          stageChanged: {
            title: 'Lead movido para Qualificado',
            description: 'progrediu no funil',
          },
          workflowTriggered: {
            title: 'Enriquecimento concluído',
            description: 'Dados do perfil atualizados para',
          },
          noteAdded: {
            title: 'Nota adicionada',
            description: 'Follow-up agendado com lead Empresarial',
          },
        },
        timeAgo: {
          minutes: 'minutos atrás',
          hour: 'hora atrás',
          hours: 'horas atrás',
        },
      },
      quickActions: {
        title: 'Ações Rápidas',
        newLead: {
          label: 'Novo Lead',
          description: 'Adicione um novo lead ao seu funil',
        },
        analytics: {
          label: 'Analíticos',
          description: 'Ver métricas de desempenho',
        },
        runWorkflow: {
          label: 'Executar Fluxo',
          description: 'Acionar uma automação',
        },
        voiceCampaign: {
          label: 'Campanha de Voz',
          description: 'Lançar contato por voz com IA',
        },
      },
      comingSoonTitle: 'Em Breve',
      comingSoonItems: [
        'Visualização do funil de vendas',
        'Gráfico de fontes de tráfego',
        'Mapa de distribuição geográfica',
        'Analíticos em tempo real',
      ],
      activityTypes: {
        leadCreated: 'Lead criado',
        stageChanged: 'Etapa alterada',
        noteAdded: 'Nota adicionada',
        leadAssigned: 'Lead atribuído',
        emailSent: 'Email enviado',
        activity: 'Atividade',
        unknownLead: 'Lead desconhecido',
      },
    },
    analytics: {
      comingSoon: {
        title: 'Em Breve',
        predictiveAnalytics: 'Analíticos preditivos avançados com modelos de ML',
        cohortAnalysis: 'Analise de coortes e segmentação de clientes',
        funnelBuilder: 'Construtor de funis personalizado com arrastar e soltar',
      },
      overview: {
        metrics: {
          leadVelocityRate: 'TAXA DE VELOCIDADE DE LEADS',
          leadVelocityRateSubtitle: 'Leads que chegam a qualificado em relação ao período anterior',
          pipelineScore: 'PONTUAÇÃO DO PIPELINE',
          pipelineScoreSubtitle: 'Contagem de leads ponderada por etapa',
          pipelineScoreCoverage_one:
            'Contagem ponderada por etapa de {{count}} lead em etapas abertas',
          pipelineScoreCoverage_other:
            'Contagem ponderada por etapa de {{count}} leads em etapas abertas',
          winRate: 'TAXA DE GANHO',
          winRateSubtitle: 'Ganhos sobre tudo o que foi fechado no período',
        },
        untouchedAlert_one: '{{count}} lead não teve nenhuma tentativa de contato',
        untouchedAlert_other: '{{count}} leads não tiveram nenhuma tentativa de contato',
        leadsOverTime: 'Leads ao longo do tempo',
        leadsOverTimeSubtitle_one: '{{count}} lead criado neste período',
        leadsOverTimeSubtitle_other: '{{count}} leads criados neste período',
        leadSources: 'Fontes de leads',
        leadSourcesSubtitle_one: '{{count}} lead por fonte',
        leadSourcesSubtitle_other: '{{count}} leads por fonte',
        conversionFunnel: 'Funil de conversão',
        activityByDay: 'Atividade por dia',
        activityByDaySubtitle: 'Contatos de IA e humanos, somados por dia da semana neste período',
        qualificationScore: 'Pontuação de qualificação por IA',
        qualificationCoverage: '{{scored}} de {{total}} leads com pontuação',
        noActivity: 'Não há atividade de leads nem pontuações de qualificação neste período',
        insights: 'Insights',
        topSource: 'PRINCIPAL FONTE',
        topSourceDescription_one: '{{count}} lead desta fonte',
        topSourceDescription_other: '{{count}} leads desta fonte',
        busiestStage: 'ETAPA COM MAIS CARGA',
        busiestStageDescription_one: '{{count}} lead aberto aguardando aqui',
        busiestStageDescription_other: '{{count}} leads abertos aguardando aqui',
        busiestStageEmpty: 'Nenhuma etapa aberta tem leads',
        newLeads: 'LEADS NOVOS',
        newLeadsDescription: 'Criados neste período',
        weekdays: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
      },
      funnel: {
        metrics: {
          avgCycleTime: 'TEMPO MÉDIO DE CICLO',
          median: 'Mediana: {{value}}',
          bottleneck: 'GARGALO',
          bottleneckSubtitle: 'A etapa mais lenta pela qual os leads realmente passaram',
        },
        conversionFunnelSubtitle:
          'Barras: leads que estão em cada etapa agora. %: porcentagem da etapa inicial que chegou até esta; nas etapas de fechamento, a que terminou ali.',
        timeInStage: {
          title: 'Tempo por etapa',
          subtitle:
            'Média de dias que levaram os leads que avançaram de cada etapa neste período, não os leads que estão ali agora.',
        },
        stageDropoff: {
          title: 'Análise de abandono por etapa',
          subtitle: 'Resultados de todos os leads que estiveram em cada etapa durante este período',
          population_one: '{{count}} lead',
          population_other: '{{count}} leads',
          categories: {
            advanced: 'Avançados',
            won: 'Ganhos',
            active: 'Ativos',
            stale: 'Parados',
            lost: 'Perdidos',
            regressed: 'Retrocedidos',
          },
        },
      },
      team: {
        metrics: {
          avgResponseTime: 'TEMPO MÉDIO DE RESPOSTA',
          median: 'Mediana: {{value}}',
          responseTimeCoverage: 'Mediana: {{value}} · {{responded}} de {{total}} leads contatados',
          responseTimeFallback: 'Tempo até o primeiro contato',
          untouchedLeads: 'LEADS SEM CONTATO',
          untouchedSubtitle: '{{percentage}} dos leads com mais de {{days}} dias',
          untouchedFallback: 'Leads sem nenhuma tentativa de contato',
        },
        untouchedAlert_one:
          '{{count}} lead não teve nenhuma tentativa de contato em mais de {{days}} dias',
        untouchedAlert_other:
          '{{count}} leads não tiveram nenhuma tentativa de contato em mais de {{days}} dias',
        leaderboard: {
          title: 'Ranking de agentes',
          subtitle: 'Desempenho ordenado por taxa de conversão',
          subtitleSingle: 'O único agente com leads atribuídos',
          empty: 'Não há dados de desempenho da equipe',
          coverageNote_one:
            'Cobre {{assigned}} de {{total}} leads. {{count}} lead sem atribuição não é exibido aqui.',
          coverageNote_other:
            'Cobre {{assigned}} de {{total}} leads. {{count}} leads sem atribuição não são exibidos aqui.',
          columns: {
            rank: '#',
            agent: 'AGENTE',
            assigned: 'ATRIBUÍDOS',
            won: 'GANHOS',
            conversion: 'CONV %',
            cycle: 'CICLO',
          },
        },
      },
      campaigns: {
        sourcePerformance: {
          title: 'Desempenho por fonte',
          subtitle: 'Leads e taxa de conversão por fonte',
        },
        deviceBreakdown: {
          title: 'Distribuição por dispositivo',
          subtitle: 'Leads por tipo de dispositivo',
          centerLabel: 'Leads',
        },
        geo: {
          title: 'Distribuição geográfica',
          subtitle: 'Leads por país',
          leads_one: '{{count}} lead',
          leads_other: '{{count}} leads',
        },
        empty: 'Não há dados de campanhas para este período',
      },
      sourcePerformance: {
        columns: {
          source: 'FONTE',
          leadCount: 'LEADS',
          wonCount: 'GANHOS',
          conversionRate: 'CONV %',
        },
        empty: 'Não há dados de fontes disponíveis',
      },
      timeInStage: {
        empty: 'Não há dados de etapas disponíveis',
        terminalMarker: 'fechado',
        legend: {
          fast: '< 3 dias',
          medium: '3-7 dias',
          slow: '> 7 dias',
          terminal: 'resultado fechado',
          notMeasured: '{{dash}} não medido',
        },
      },
      screen: {
        title: 'Análises',
        subtitle: 'Acompanhe o desempenho do seu funil',
      },
      tabs: {
        overview: 'Visão geral',
        funnel: 'Funil',
        campaigns: 'Campanhas',
        team: 'Equipe',
      },
      states: {
        errorTitle: 'Algo deu errado',
        errorMessage: 'Não foi possível carregar os dados de análise. Tente novamente.',
        retry: 'Tentar novamente',
        retryAccessibilityLabel: 'Tentar carregar os dados novamente',
        emptyTitle: 'Ainda não há nada para mostrar',
        emptyMessage: 'Não há dados para este período',
      },
      alert: {
        dismiss: 'Descartar alerta',
      },
      units: {
        days: '{{value}} dias',
      },
    },
    topbar: {
      dateRange: 'Últimos 30 dias',
      searchPlaceholder: 'Pesquisar...',
      workspace: 'Espaço de trabalho',
    },
    landing: {
      hero: {
        badge: 'AUTOMAÇÃO DE VENDAS COM IA',
        social: 'Fundadores estão entrando na lista agora',
        trust: {
          founding: 'Preço de membro fundador',
          noCard: 'Sem cartão',
          earlyAccess: 'Acesso antecipado',
        },
        ctaNote: 'Sem cartão de crédito',
        preTitle: 'Sua IA trabalha o funil.',
        mainTitle: 'Seus vendedores fecham.',
        titleLead: 'Transforme seu',
        titleHighlight: 'Funil de Vendas',
        titleTail: 'com IA inteligente',
        subtitle:
          'O FlowSellAI captura, qualifica e nutre cada lead automaticamente—sua equipe só entra para a conversa que precisa de um humano.',
        primaryCta: 'Entrar',
        secondaryCta: 'Ver demo →',
        stats: {
          fasterResponse: 'Resposta mais rápida',
          automationRate: 'Taxa de automação',
          conversionGrowth: 'Crescimento de conversão',
        },
      },
      features: {
        badge: 'RECURSOS PODEROSOS',
        title: 'Tudo que você precisa',
        accent: 'para escalar',
        subtitle:
          'Automação com IA que transforma como sua equipe de vendas trabalha. Feche mais negócios com menos esforço.',
        learnMore: 'Saiba mais →',
        sourceFlow: {
          linkedin: 'LinkedIn',
          email: 'E-mail',
          webForm: 'Formulário web',
          enrichedLead: 'Lead enriquecido',
        },
        items: {
          aiLeadCapture: {
            title: 'Captura de leads com IA',
            description:
              'Capture e enriqueca leads automaticamente de email, LinkedIn e formulários web com categorização inteligente.',
          },
          smartQualification: {
            title: 'Qualificação inteligente',
            description:
              'A IA analisa engajamento, comportamento e adequação para pontuar leads e priorizar as melhores oportunidades.',
          },
          automatedNurturing: {
            title: 'Nutrição automatizada',
            description:
              'Follow-ups personalizados em escala com timing e mensagens otimizados por IA para cada prospecto.',
          },
          realTimeAnalytics: {
            title: 'Analíticos em tempo real',
            description:
              'Acompanhe o desempenho do funil, taxas de conversão e eficacia da IA com insights acionáveis.',
          },
        },
      },
      product: {
        badge: 'O PRODUTO',
        title: 'Seu pipeline,',
        accent: 'totalmente automatizado',
        subtitle:
          'Veja cada lead avançar pelo seu funil—capturado, qualificado e nutrido por IA em tempo real. Sua equipe só entra para fechar.',
        board: {
          live: 'AO VIVO',
          stages: {
            newLead: 'Novo Lead',
            discovery: 'Descoberta',
            proposal: 'Proposta',
            closedWon: 'Ganho',
          },
        },
        items: {
          pipeline: {
            title: 'Pipeline em tempo real',
            description: 'Atualizações ao vivo de cada negócio',
          },
          aiActivity: {
            title: 'Atividade de agentes IA',
            description: 'Monitore o desempenho da automação',
          },
          analytics: {
            title: 'Analíticos avançados',
            description: 'Insights baseados em dados',
          },
        },
      },
      foundingMembers: {
        badge: 'MEMBROS FUNDADORES',
        title: 'Entre cedo.',
        accent: 'Ajude a moldar o que construimos',
        subtitle:
          'O FlowSellAI esta em pré-lançamento. Membros fundadores ganham mais que um desconto—ganham um lugar a mesa.',
        benefits: {
          pricing: {
            title: 'Preço fundador, travado para sempre',
            description:
              'Pague a tarifa de membro fundador para sempre—mesmo após o lançamento público.',
          },
          access: {
            title: 'Acesso antecipado',
            description:
              'Entre primeiro conforme abrimos em ondas. Sem espera depois que você entra.',
          },
          voice: {
            title: 'Linha direta com o time',
            description:
              'Seu feedback molda o roadmap. Construimos com os membros fundadores, não apenas para eles.',
          },
          onboarding: {
            title: 'Onboarding prioritário',
            description: 'Configuração guiada para você capturar leads desde o primeiro dia.',
          },
        },
        vision: {
          quote:
            'Estamos construindo a ferramenta de vendas que sempre quisemos—um único sistema que captura, qualifica e nutre cada lead, para que os vendedores facam o que só um humano pode: fechar. Entre cedo e ajude a moldar.',
          attribution: 'O time da FlowSellAI',
        },
      },
      pricing: {
        badge: 'PREÇOS DE LANÇAMENTO',
        title: 'Planos que crescem com você',
        subtitle:
          'Garanta o preço de membro fundador antes do lançamento. Sem compromisso, sem cartão—apenas seu lugar na fila.',
        subtitleComingSoon:
          'Estamos ajustando os preços. Enquanto isso, veja o que cada plano inclui.',
        monthly: 'Mensal',
        yearly: 'Anual',
        yearlyDiscount: 'Economize 20%',
        mostPopular: 'Mais Popular',
        included: 'Incluído',
        notIncluded: 'Não incluído',
        perMonth: '/mês',
        billedYearly: 'cobrado anualmente',
        from: 'A partir de',
        priceLoading: 'Carregando preço',
        priceUnavailable: 'Preço indisponível',
        comingSoon: 'Em breve',
        currencyNote: 'Preços exibidos na sua moeda local:',
        countrySelector: {
          label: 'Região de faturamento:',
          colombia: 'Colômbia (COP)',
          international: 'Internacional (USD)',
        },
        checkoutUnavailable:
          'O checkout não está disponível para esta organização ou plano no momento.',
        checkoutUnavailableCta: 'Checkout indisponível',
        creditsDisclaimer:
          'A voz com IA é cobrada via créditos. As tarifas se ajustam automaticamente conforme o país de destino.',
        usageBased: 'Por consumo',
        plans: {
          starter: {
            name: 'Starter',
            description: 'Perfeito para fundadores solo validando outbound somente por email.',
            cta: 'Iniciar pagamento',
          },
          growth: {
            name: 'Growth',
            description:
              'Para equipes em crescimento que ampliam o alcance com e-mail e voz com IA.',
            cta: 'Iniciar pagamento',
          },
          professional: {
            name: 'Professional',
            description:
              'Para equipes em expansão que precisam de maior volume e IA avançada em cada canal.',
            cta: 'Iniciar pagamento',
          },
          business: {
            name: 'Business',
            description: 'Para times de vendas multinacionais de alto volume com suporte dedicado.',
            cta: 'Iniciar pagamento',
          },
          enterprise: {
            name: 'Enterprise',
            description:
              'Soluções personalizadas para grandes organizações com necessidades avançadas e SLA.',
            cta: 'Falar com Vendas',
          },
        },
        features: {
          credits: 'Créditos / mês',
          aiAgents: 'Agentes IA',
          aiLeadGeneration: 'Geração de leads com IA',
          emailOutreach: 'Email Outreach',
          whatsapp: 'WhatsApp',
          sms: 'SMS',
          aiVoiceAgent: 'Agente de Voz IA',
          aiLeadScoring: 'Lead Scoring IA',
          analytics: 'Analíticos',
          workspaces: 'Espaços de trabalho',
          teamMembers: 'Membros da equipe',
          support: 'Suporte',
          onboarding: 'Onboarding',
        },
        // TODO(pricing): confirm Professional/Business feature limits — placeholder values
        values: {
          starter: {
            credits: '25K',
            aiAgents: 'Ilimitados (por créditos)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: false,
            sms: false,
            aiVoiceAgent: false,
            aiLeadScoring: 'Básico',
            analytics: 'Básico',
            workspaces: '1',
            teamMembers: '1',
            support: 'Email',
            onboarding: 'Autoatendimento',
          },
          growth: {
            credits: '150K',
            aiAgents: 'Ilimitados (por créditos)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: 'Por consumo',
            sms: 'Por consumo',
            aiVoiceAgent: 'Por consumo',
            aiLeadScoring: 'Avançado',
            analytics: 'Avançado + Previsões',
            workspaces: '5',
            teamMembers: '2',
            support: 'Prioritário + Chat',
            onboarding: 'Configuração guiada',
          },
          // TODO(pricing): confirm Professional feature limits — placeholder values
          professional: {
            credits: '350K',
            aiAgents: 'Ilimitados (por créditos)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: 'Por consumo',
            sms: 'Por consumo',
            aiVoiceAgent: 'Por consumo',
            aiLeadScoring: 'Avançado + Personalizado',
            analytics: 'Avançado + Previsões',
            workspaces: '12',
            teamMembers: '5',
            support: 'Prioritário + Chat',
            onboarding: 'Configuração guiada',
          },
          // TODO(pricing): confirm Business feature limits — placeholder values
          business: {
            credits: '750K',
            aiAgents: 'Ilimitados (por créditos)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: 'Por consumo',
            sms: 'Por consumo',
            aiVoiceAgent: 'Por consumo',
            aiLeadScoring: 'Avançado + Personalizado',
            analytics: 'Completo + Relatórios',
            workspaces: '25',
            teamMembers: '5',
            support: 'Gerente dedicado',
            onboarding: 'Implementação 1-a-1',
          },
          enterprise: {
            credits: 'Personalizado',
            aiAgents: 'Ilimitados (por créditos)',
            aiLeadGeneration: true,
            emailOutreach: true,
            whatsapp: true,
            sms: true,
            aiVoiceAgent: 'Personalizado',
            aiLeadScoring: 'Modelos Personalizados',
            analytics: 'Completo + Exportação BI',
            workspaces: 'Personalizado',
            teamMembers: '5',
            support: 'Dedicado + SLA',
            onboarding: 'Onboarding completo',
          },
        },
      },
      problemSolution: {
        badge: 'O PROBLEMA',
        title: 'Seu stack de vendas esta disperso.',
        accent: 'Nos unimos tudo',
        subtitle:
          'Um único sistema que captura, qualifica e nutre cada lead—em vez de cinco ferramentas que nunca se falam.',
        todayLabel: 'Como as equipes de vendas trabalham hoje',
        ourLabel: 'Como o FlowSellAI funciona',
        problems: {
          spreadsheets: 'Leads enterrados em planilhas',
          followups: 'Follow-ups que se perdem',
          qualify: 'Sem forma de qualificar em escala',
          channels: 'Canais que não se falam',
          guessing: 'Vendedores adivinhando quem chamar',
        },
        solutions: {
          capture: {
            title: 'Cada lead capturado automaticamente',
            description: 'Email, LinkedIn e formulários web—enriquecidos e deduplicados na hora.',
          },
          qualify: {
            title: 'Qualificado por comportamento real',
            description:
              'A IA pontua fit e intenção, então os vendedores atacam primeiro o mais quente.',
          },
          nurture: {
            title: 'Nutrido no piloto automático',
            description: 'Follow-ups personalizados no momento certo, em cada canal.',
          },
          unified: {
            title: 'Um pipeline unificado',
            description: 'Cada canal e sinal em um único funil que toda a sua equipe pode ver.',
          },
        },
      },
      automation: {
        badge: 'FLUXO DE AUTOMAÇÃO IA',
        title: 'A IA cuida de tudo',
        accent: 'exceto o fechamento',
        subtitle:
          'Nossos agentes de IA automatizam cada etapa do seu funil, do primeiro contato até a reunião qualificada, para que sua equipe foque em fechar.',
        steps: {
          capture: {
            stage: 'Capture e qualifique',
            automation: '100%',
            description: 'A IA captura leads de cada canal e os pontua por fit e intenção.',
          },
          nurture: {
            stage: 'Nutra e agende',
            automation: '100%',
            description:
              'Follow-ups personalizados e agendamento inteligente até ficarem prontos para vender.',
          },
          close: {
            stage: 'Fechamento',
            automation: 'Humano',
            description: 'Sua equipe entra para a única conversa que precisa de um humano.',
            isHuman: true,
          },
        },
        ctaTitle: 'Coloque seu funil no piloto automático',
        ctaText:
          'Seja um dos primeiros a automatizar captura, qualificação e nutrição—membros fundadores ganham acesso antecipado e preço travado para sempre.',
      },
      socialProof: {
        badge: '(star) CONFIADO MUNDIALMENTE',
        title: 'Junte-se a equipes de vendas de alto desempenho',
        stats: {
          salesTeams: 'Equipes de vendas',
          leadsProcessed: 'Leads processados',
          automationRate: 'Taxa de automação',
          averageRoi: 'ROI médio',
        },
        testimonialsTitle: 'O que dizem os líderes de vendas',
        testimonials: [
          {
            quote:
              'A IA do Funnel Panel transformou completamente nosso processo comercial. Estamos fechando 3x mais negócios com o mesmo time. A automação e incrível.',
            author: 'Sarah Chen',
            role: 'VP de Vendas',
            company: 'TechCorp',
          },
          {
            quote:
              'Nossa equipe gasta 80% menos tempo em tarefas administrativas e 80% mais falando com leads qualificados. Esta plataforma mudou o jogo para vendas B2B.',
            author: 'Michael Rodriguez',
            role: 'Diretor de Vendas',
            company: 'Growth Labs',
          },
          {
            quote:
              'De horas de qualificação manual para pontuação instantânea com IA. O ROI foi imediato e continua crescendo a cada trimestre.',
            author: 'Emily Watson',
            role: 'CEO',
            company: 'SaaS Ventures',
          },
        ],
        companiesLabel: 'CONFIADO POR EMPRESAS LÍDERES',
        companies: ['Stripe', 'Shopify', 'Atlassian', 'Notion', 'Linear', 'Figma'],
      },
      faq: {
        badge: 'FAQ',
        title: 'Perguntas?',
        accent: 'Temos respostas',
        subtitle: 'Tudo o que você precisa saber antes de entrar na lista de espera.',
        items: {
          live: {
            q: 'O FlowSellAI já esta disponível?',
            a: 'Estamos em pré-lançamento. Entre na lista de espera e os membros fundadores ganham acesso antecipado assim que abrirmos—com preço travado para sempre.',
          },
          cost: {
            q: 'Quanto vai custar?',
            a: 'O preço de membro fundador começa bem abaixo das tarifas públicas e fica travado. Você verá os planos completos antes de pagar—sem cartão para entrar na lista.',
          },
          channels: {
            q: 'Quais canais suporta?',
            a: 'E-mail, LinkedIn, formulários web e voz com IA. Cada canal alimenta um único pipeline.',
          },
          qualify: {
            q: 'Como a IA qualifica os leads?',
            a: 'Ela pontua cada lead por fit e intenção usando comportamento, engajamento e dados de enriquecimento—então seus vendedores atacam primeiro o mais quente.',
          },
          security: {
            q: 'Meus dados estão seguros?',
            a: 'Seus dados são criptografados em transito e em repouso, isolados por organização e nunca vendidos. Você mantem o controle.',
          },
          access: {
            q: 'Quando os membros fundadores tem acesso?',
            a: 'Incorporamos membros fundadores em ondas conforme abrimos. Entre na lista para garantir seu lugar—os primeiros entram antes.',
          },
        },
      },
      cta: {
        title: 'Seja o primeiro a transformar seu processo de vendas',
        subtitle:
          'Entre na lista de espera e seja um dos primeiros a automatizar seu funil com IA. Membros fundadores ganham acesso antecipado e preço bloqueado.',
        primaryCta: 'Entrar',
        perks: {
          freeTrial: 'Acesso antecipado no lançamento',
          noCard: 'Preço de membro fundador',
          setup: 'Onboarding prioritário',
        },
      },
      footer: {
        brand: 'FlowSellAI',
        tagline: 'Automação de funis de vendas com IA para equipes modernas',
        columns: {
          product: 'Produto',
          company: 'Empresa',
          resources: 'Recursos',
          legal: 'Legal',
        },
        links: {
          features: 'Funcionalidades',
          pricing: 'Preços',
          integrations: 'Integrações',
          changelog: 'Histórico de alterações',
          about: 'Sobre nos',
          blog: 'Blog',
          careers: 'Carreiras',
          press: 'Kit de imprensa',
          documentation: 'Documentação',
          helpCenter: 'Central de ajuda',
          api: 'Referência da API',
          status: 'Status',
          privacy: 'Política de Privacidade',
          terms: 'Termos de Serviço',
          cookie: 'Política de Cookies',
          gdpr: 'LGPD',
        },
        copyright: 'Todos os direitos reservados.',
        social: {
          twitter: 'Twitter',
          linkedin: 'LinkedIn',
          github: 'GitHub',
        },
      },
    },
    changelog: {
      title: 'Histórico de alterações',
      subtitle: 'Novidades, melhorias e correções.',
      backToChangelog: 'Voltar ao histórico',
      backToHome: 'Voltar ao início',
      loading: 'Carregando...',
      empty: 'Nenhuma entrada ainda. Volte em breve.',
      error: 'Não foi possível carregar o histórico. Tente novamente.',
      version: 'v',
      publishedOn: 'Publicado em',
      noEntries: 'Sem atualizações ainda',
    },
    blog: {
      title: 'Blog',
      subtitle: 'Ideias, guias e histórias de produto.',
      backToBlog: 'Voltar ao blog',
      backToHome: 'Voltar ao início',
      loading: 'Carregando...',
      empty: 'Nenhuma publicação ainda. Volte em breve.',
      error: 'Não foi possível carregar o blog. Tente novamente.',
      publishedOn: 'Publicado em',
      readMore: 'Ler mais',
    },
    waitlist: {
      emailLabel: 'Endereço de Email',
      emailPlaceholder: 'seu@email.com',
      emailRequired: 'O email e obrigatório',
      emailInvalid: 'Por favor, insira um email valido',
      nameLabel: 'Nome Completo (Opcional)',
      namePlaceholder: 'João Silva',
      nameInvalid: 'O nome deve ter entre 2 e 100 caracteres',
      companyLabel: 'Empresa (Opcional)',
      companyPlaceholder: 'Minha Empresa Ltda.',
      companyInvalid: 'O nome da empresa deve ter entre 2 e 100 caracteres',
      phoneLabel: 'Telefone (Opcional)',
      phonePlaceholder: '+55 (11) 99999-9999',
      phoneInvalid: 'Por favor, insira um número de telefone valido (pelo menos 10 dígitos)',
      submitting: 'Entrando na lista de espera...',
      alreadyRegistered: 'Este email já esta na lista de espera',
      errorGeneric: 'Algo deu errado. Por favor, tente novamente.',
      successTitle: 'Você esta na lista!',
      successMessage: 'Obrigado por entrar na nossa lista de espera. Avisaremos quando lançarmos.',
    },
    workflow: {
      title: 'Acionar Enriquecimento de Perfil',
      description:
        'Preencha os detalhes abaixo para acionar o fluxo de trabalho de enriquecimento de perfil.',
      closeButton: 'Fechar',
      nameLabel: 'Nome',
      namePlaceholder: 'Insira o nome',
      nameRequired: 'O nome e obrigatório',
      mode: {
        profile: 'Enriquecer um perfil',
        discover: 'Descobrir contas',
      },
      discovery: {
        title: 'Descobrir contas do Instagram',
        intro:
          'Encontre contas do Instagram que você ainda não conhece. Buscar não cria leads: depois você escolhe quais enriquecer.',
        hashtagsLabel: 'Hashtags',
        hashtagsPlaceholder: '#cafeespecial, #barista',
        keywordLabel: 'Palavra-chave',
        keywordPlaceholder: 'barista, torrefação de café\u2026',
        placeLabel: 'Local',
        placeHelper:
          'Com uma palavra-chave ou hashtag, filtra esses resultados para contas locais. Sozinho, encontra contas que publicam aqui.',
        placePlaceholder: 'Bogota',
        search: 'Buscar no Instagram',
        searching: 'Buscando\u2026',
        maxResultsLabel: 'Resultados máximos',
        limitedByPlan:
          'Seu plano permite {{max}} resultados a mais neste mês, então a busca foi ajustada a esse número.',
        found: '{{count}} contas encontradas \u2014 {{selected}} selecionadas',
        alreadyKnown: 'Outras {{count}} já estavam no seu pipeline, portanto foram omitidas.',
        selectAll: 'Selecionar todas',
        clearAll: 'Limpar todas',
        business: 'Negócio',
        matches: '{{count}} publicações correspondentes',
        localityBadge: 'Local',
        localityReasonPostedAtPlace: 'Publicou aqui',
        localityReasonPostTaggedAtPlace: 'Marcou uma publicação aqui',
        localityReasonProfileMentionsPlace: 'Menciona este local no perfil',
        droppedForLocalityNote:
          '{{count}} contas corresponderam à sua palavra-chave ou hashtag, mas não mostraram sinais de estar em {{place}}.',
        hiddenOneTimePostersNote: '{{count}} contas com apenas uma publicação lá foram ocultadas.',
        placeSearchFailedWarning:
          'Não conseguimos buscar esse local, então os resultados foram filtrados pelos demais sinais: publicações marcadas lá e o que os perfis mencionam.',
        searchPartiallyFailedWarning:
          'Parte da busca não respondeu; alguns resultados podem estar faltando.',
        segmentNameLabel: 'Nome do segmento',
        segmentNamePlaceholder: 'Pessoas do café em Bogota',
        import: 'Enriquecer {{count}} contas',
        importing: 'Enfileirando\u2026',
        queued: '{{count}} contas enfileiradas para enriquecimento',
        queuedHint:
          'Cada uma passa pela mesma analise do Instagram que um perfil colado manualmente. Elas vão aparecer no seu pipeline conforme terminarem.',
        done: 'Pronto',
        emptyNoResults:
          'Nenhuma conta correspondeu a esses critérios. Tente uma hashtag, palavra-chave ou local mais amplo.',
        emptyPlaceNotFound:
          'Não encontramos esse local no Instagram. Verifique o nome ou tente novamente.',
        emptyPlaceSearchFailed:
          'Não conseguimos buscar esse local, então não encontramos contas locais. Tente novamente ou busque sem local.',
        emptyNoLocalMatch:
          '{{count}} contas corresponderam à sua palavra-chave ou hashtag, mas nenhuma mostrou sinais de estar em {{place}}.',
        emptyAllKnown:
          'Todas as contas encontradas ({{count}}) já estavam no seu pipeline, portanto nada novo foi adicionado.',
        emptySearchFailed: 'A busca não pôde ser concluída. Tente novamente em alguns minutos.',
        tryAgain: 'Tentar novamente',
      },
      socialMediaLabel: 'Plataforma de Rede Social',
      socialMediaRequired: 'A plataforma de rede social e obrigatória',
      profileUrlLabel: 'URL do Perfil',
      profileUrlPlaceholder: 'https://linkedin.com/in/usuário',
      profileUrlPlaceholderInstagram: 'https://instagram.com/usuário',
      profileUrlRequired: 'A URL do perfil e obrigatória',
      profileUrlInvalid: 'Por favor, insira uma URL valida',
      profileUrlInvalidLinkedIn:
        'Insira uma URL de perfil do LinkedIn como linkedin.com/in/usuário — links de publicações, empresas ou do feed não podem ser enriquecidos',
      profileUrlInvalidInstagram:
        'Insira uma URL de perfil do Instagram como instagram.com/usuário — links de publicações, reels ou stories não podem ser enriquecidos',
      submitButton: 'Acionar Fluxo de Trabalho',
      submittingButton: 'Acionando...',
      errorGeneric: 'Falha ao acionar o fluxo de trabalho. Por favor, tente novamente.',
      errors: {
        authRequired:
          'A autenticação do LinkedIn expirou. Por favor, reconecte seu acesso ao LinkedIn nas configurações.',
        enrichmentUnavailable:
          'Não foi possível enriquecer este perfil agora. O provedor de dados esta temporariamente indisponível — por favor, tente novamente mais tarde.',
        profileNotFound:
          'Não encontramos este perfil. Verifique o usuário ou a URL e tente novamente.',
      },
      success: {
        title: 'Fluxo de Trabalho Acionado com Sucesso!',
        platform: 'Plataforma',
        webhookId: 'ID do Webhook',
        enrichedDataTitle: 'Dados Enriquecidos',
        dataItems: {
          name: 'Nome',
          username: 'Usuário',
          profileUrl: 'Perfil',
          score: 'Pontuação',
          economicCapacity: 'Capacidade Econômica',
          status: 'Status',
          industry: 'Indústria',
          painPoints: 'Pontos de Dor',
          capturedContent: 'Conteúdo Capturado',
        },
      },
      platforms: {
        linkedin: 'LinkedIn',
        facebook: 'Facebook',
        twitter: 'Twitter',
        instagram: 'Instagram',
      },
      progress: {
        title: 'Enriquecimento em Progresso',
        scraping: 'Obtendo perfil...',
        scraping_comments: 'Obtendo comentários...',
        analyzing: 'Analisando com IA...',
        persisting: 'Salvando lead enriquecido...',
        completed: 'Enriquecimento Concluído!',
        partialSuccess: 'Enriquecimento concluído com problemas',
        insufficientData: 'O perfil não pôde ser analisado',
        failed: 'Enriquecimento Falhou',
        viewLead: 'Ver Lead',
        retry: 'Tentar Novamente',
        score: 'Pontuação',
        newRun: 'Novo Enriquecimento',
      },
      stages: {
        title: 'Resultado passo a passo',
        profile: 'Perfil do contato',
        posts: 'Publicações do contato',
        reels: 'Reels',
        comments: 'Comentários das publicações',
        analysis: 'Analise de IA',
        company: 'Perfil da empresa',
        company_posts: 'Publicações da empresa',
      },
      stageStatus: {
        success: 'Concluído',
        failed: 'Falhou',
        skipped: 'Ignorado',
        not_reached: 'Não iniciado',
      },
      stageReasons: {
        provider_unavailable:
          'O provedor de dados não concluiu esta etapa. Tente novamente mais tarde.',
        auth_required: 'A autenticação expirou. Reconecte seu acesso nas configurações.',
        profile_not_found:
          'Este perfil não foi encontrado. Verifique o nome de usuário e tente novamente.',
        unknown: 'Algo inesperado aconteceu nesta etapa. Tente novamente.',
      },
      attachLead: {
        title: 'E o mesmo lead?',
        description:
          'Estes leads parecem relacionados. Anexe este perfil a um deles ou crie um lead separado.',
        attachTo: 'Anexar a',
        alreadyHasProfile: 'já tem este perfil',
        createNew: 'Criar um lead novo',
      },
      leadExists: {
        title: 'Lead Já Existe',
        description:
          'Já existe um lead com esta URL de perfil na sua organização. Você pode visualizar o lead existente.',
        viewLead: 'Ver Lead Existente',
        newSearch: 'Tentar Outra URL',
      },
      selectPlatform: 'Selecionar plataforma',
      platformLockedHint: 'A plataforma está fixada no LinkedIn para este fluxo.',
      cancel: 'Cancelar',
      page: {
        badge: 'Automação',
        title: 'Central de Fluxos',
        subtitle:
          'Automatize o enriquecimento de leads e analise de perfis com fluxos de trabalho poderosos.',
        newWorkflow: 'Novo Fluxo',
        emptyTitle: 'Sem fluxos de trabalho',
        emptyDescription:
          'Crie seu primeiro fluxo de trabalho para automatizar o enriquecimento de leads.',
        emptyAction: 'Criar Fluxo',
      },
      card: {
        status: {
          active: 'Ativo',
          paused: 'Pausado',
          error: 'Erro',
        },
        neverRun: 'Nunca executado',
        executions: 'execuções',
        pauseButton: 'Pausar',
        resumeButton: 'Retomar',
        runButton: 'Executar',
        comingSoon: 'Em breve',
        workflowPaused: 'Este workflow esta pausado. Retome-o antes de executar.',
      },
      types: {
        linkedinEnrichment: 'Enriquecimento LinkedIn',
        instagramEnrichment: 'Enriquecimento Instagram',
        webSearchContact: 'Busca Web',
      },
      executions: {
        title: 'Execuções de Workflows',
        subtitle: 'Histórico completo de todas as execuções de workflows da sua organização.',
        backToFlowCenter: 'Voltar ao Centro de Fluxos',
        totalRuns: 'Total',
        successful: 'Bem-sucedidas',
        failed: 'Falhas',
        emptyTitle: 'Nenhuma execução ainda',
        emptyDescription: 'Execute um workflow para ver o histórico aqui.',
      },
      visualizer: {
        nodes: {
          manualTrigger: 'Gatilho Manual',
          fetchProfile: 'Buscar Perfil',
          enrichData: 'Enriquecer Dados',
          updateLead: 'Atualizar Lead',
        },
      },
      history: {
        title: 'Execuções Recentes',
        previous: 'Anterior',
        next: 'Próxima',
        page: 'Página',
        of: 'de',
        loading: 'Carregando execuções...',
        emptyTitle: 'Sem execuções ainda',
        emptyDescription: 'Execute um fluxo de trabalho para ver o histórico',
        status: {
          success: 'Sucesso',
          failed: 'Falhou',
          running: 'Executando',
        },
      },
      detail: {
        title: 'Detalhes da execução',
        outcome: 'Resultado da execução',
        results: 'Impacto comercial',
        leadsCreated: 'Leads criados',
        leadsUpdated: 'Leads atualizados',
        errors: 'Erros',
        timing: 'Cronologia',
        started: 'Início',
        completed: 'Conclusão',
        duration: 'Duração',
        stageProgress: 'Detalhamento por etapa',
        triggerContext: 'Contexto de ativação',
        platform: 'Plataforma',
        profileUrl: 'URL do perfil',
        company: 'Empresa',
        leadId: 'ID do lead',
        executionId: 'ID da execução',
        openHint: 'Abrir detalhes da execução',
      },
      mock: {
        linkedinEnrichment: {
          name: 'Enriquecimento LinkedIn',
          description:
            'Enriqueca perfis de leads com dados do LinkedIn. Obtem cargo, empresa e conexões.',
        },
        facebookScraper: {
          name: 'Scraper do Facebook',
          description:
            'Extraia dados públicos de perfis do Facebook para enriquecimento de leads e insights sociais.',
        },
        leadScoring: {
          name: 'Pontuação de Leads',
          description:
            'Calcule automaticamente pontuações de leads com base no engajamento e completude do perfil.',
        },
        webScraper: {
          name: 'Scraper Web',
          description: 'Extraia informações de empresas de sites para enriquecimento de leads B2B.',
        },
        instagramEnrichment: {
          name: 'Enriquecimento Instagram',
          description:
            'Extraia perfis, publicações e comentários do Instagram para enriquecimento de leads e insights sociais.',
        },
      },
      time: {
        hoursAgo: 'há {{count}} horas',
        dayAgo: 'há 1 dia',
        minsAgo: 'há {{count}} mins',
        twoHoursAgo: 'há 2 horas',
        threeHoursAgo: 'há 3 horas',
        fiveHoursAgo: 'há 5 horas',
        thirtyMinsAgo: 'há 30 mins',
        twoMinsAgo: 'há 2 mins',
      },
    },
    calendar: {
      eventSource: {
        manual: {
          label: 'Manual',
        },
        voiceAgent: {
          label: 'Agente de voz',
          accessibilityLabel: 'Agendado por um agente de voz',
        },
        platformAgent: {
          label: 'Agente da plataforma',
          accessibilityLabel: 'Agendado por um agente da plataforma',
        },
        providerSync: {
          label: 'Sincronizado',
          accessibilityLabel: 'Sincronizado de um calendário externo',
        },
      },
      eventStatus: {
        confirmed: 'Confirmada',
        cancelled: 'Cancelada',
        completed: 'Concluída',
        noShow: 'Não compareceu',
      },
      agenda: {
        today: 'Hoje',
        allDay: 'Dia inteiro',
        meetingCount: '{{count}} reuniões',
        cancelledCount: '{{count}} canceladas',
        emptyTitle: 'Nada por vir',
        emptyDescription: 'As reuniões agendadas para os próximos 30 dias aparecerão aqui.',
      },
      filter: {
        hideCancelled: 'Ocultar canceladas',
      },
      joinCall: {
        label: 'Entrar',
        accessibilityLabel: 'Entrar na videochamada desta reunião',
      },
    },
    voice: {
      page: {
        badge: 'Agente de Voz',
        title: 'Assistente de Voz IA',
        subtitle: 'Crie mensagens de voz personalizadas para seus leads usando Eleven Labs IA',
        newTemplate: 'Novo Template',
      },
      tabs: {
        templates: 'Templates',
        recentJobs: 'Trabalhos Recentes',
      },
      stats: {
        title: 'Estatísticas de Voz',
        totalJobs: 'Total de Trabalhos',
        completed: 'Concluídos',
        pending: 'Pendentes',
        failed: 'Falhados',
        charactersUsed: 'Caracteres Usados',
        creditsUsed: 'Créditos Usados',
      },
      template: {
        status: {
          active: 'Ativo',
          inactive: 'Inativo',
        },
        characters: 'caracteres',
        voice: 'Voz',
        noPreview: 'Sem preview salvo',
        preview: 'Preview',
        sendToLead: 'Enviar para Lead',
        actions: {
          edit: 'Editar',
          duplicate: 'Duplicar',
          delete: 'Excluir',
        },
        empty: {
          title: 'Sem templates de voz ainda',
          cta: 'Crie Seu Primeiro Template',
        },
        deleteModal: {
          title: 'Excluir Template',
          message: 'Tem certeza que deseja excluir "{{name}}"? Esta ação não pode ser desfeita.',
          confirm: 'Excluir',
          cancel: 'Cancelar',
        },
      },
      job: {
        status: {
          pending: 'Pendente',
          processing: 'Processando',
          completed: 'Concluído',
          failed: 'Falhou',
          cancelled: 'Cancelado',
        },
        type: {
          single_message: 'Mensagem Individual',
          bulk_campaign: 'Campanha em Massa',
          voice_note: 'Nota de Voz',
          lead_qualification: 'Qualificação de Lead',
          scheduled_call: 'Chamada Agendada',
        },
        delivery: {
          in_app: 'No App',
          phone_call: 'Chamada Telefônica',
          voicemail: 'Correio de Voz',
          download: 'Download',
        },
        customScript: 'Script Personalizado',
        created: 'Criado',
        processed: 'Processado',
        duration: 'Duração',
        characters: 'Caracteres',
        credits: 'Créditos',
        retry: 'Tentar novamente',
        cancel: 'Cancelar',
        hideAudio: 'Ocultar Áudio',
        showAudio: 'Mostrar Áudio',
        empty: {
          title: 'Sem trabalhos de voz ainda',
          description: 'Crie um template e envie uma mensagem de voz para um lead.',
        },
      },
      form: {
        createTitle: 'Novo Template de Voz',
        editTitle: 'Editar Template',
        name: {
          label: 'Nome do Template',
          placeholder: 'ex., Mensagem de Boas-vindas',
          required: 'O nome do template e obrigatório',
        },
        description: {
          label: 'Descrição',
          placeholder: 'Breve descrição de quando usar este template',
        },
        voice: {
          label: 'Voz',
          placeholder: 'Selecione uma voz',
          required: 'A voz e obrigatória',
        },
        script: {
          label: 'Script do Template',
          placeholder: 'Ola {{lead.fullName}}, sou {{user.fullName}} da {{organization.name}}...',
          required: 'O script e obrigatório',
          characters: 'caracteres',
          insertVariables: 'Inserir variáveis:',
        },
        variables: {
          leadName: 'Nome do Lead',
          leadEmail: 'Email do Lead',
          company: 'Empresa',
          userName: 'Nome do Usuário',
          organization: 'Organização',
        },
        settings: {
          title: 'Configurações de Voz',
          stability: 'Estabilidade',
          similarityBoost: 'Impulso de Similaridade',
          speakerBoost: 'Impulso do Falante',
          templateActive: 'Template Ativo',
        },
        preview: {
          title: 'Preview',
          generate: 'Gerar Preview',
          generateAndSave: 'Gerar e Salvar Preview',
          generating: 'Gerando...',
        },
        submit: {
          create: 'Criar Template',
          update: 'Atualizar Template',
          saving: 'Salvando...',
          cancel: 'Cancelar',
        },
      },
      player: {
        webOnly: 'Reprodução de áudio disponível apenas na web',
        error: 'Falha ao carregar áudio',
        failedToPlay: 'Falha ao reproduzir áudio',
        generating: 'Gerando áudio...',
      },
    },
    voiceAgents: {
      tabs: {
        agents: 'Agentes',
        calls: 'Chamadas',
      },
      deleteDialog: {
        title: 'Excluir agente',
        step1Body: 'Isto remove permanentemente o agente e sua configuração:',
        step2Body: 'Esta ação não pode ser desfeita. Confirmar exclusão?',
        cancel: 'Cancelar',
        continue: 'Continuar',
        back: 'Voltar',
        delete: 'Excluir',
        errorPrefix: 'Não foi possível excluir o agente.',
      },
      wizard: {
        rootA11y: 'Formulário de configuração do agente de voz',
        stepsA11yLabel: 'Etapas do assistente',
        publishedOnlyNameEditable:
          'Este agente est\u00e1 publicado: s\u00f3 o nome pode ser alterado. Publique novamente para mudar o resto.',
        stepCounter: 'Etapa',
        stepCounterOf: 'de',
        steps: {
          useCase: 'Caso de uso',
          identity: 'Identidade',
          voice: 'Voz',
          script: 'Roteiro',
          knowledge: 'Conhecimento',
          objections: 'Objeções',
          toolsCompliance: 'Ferramentas e conformidade',
          callingHours: 'Horário de chamadas',
          retryPolicy: 'Política de novas tentativas',
          reviewPublish: 'Revisar e publicar',
          completedHint: 'Etapa concluída. Ative para revisar.',
          availableHint: 'Etapa disponível. Ative para navegar.',
          lockedHint: 'Conclua as etapas anteriores para desbloquear.',
        },
        useCase: {
          initial_qualification: 'Primeiro contato e qualificação',
          warm_recontact: 'Recontato morno',
          meeting_confirmation: 'Confirmação de reunião',
          cart_recovery: 'Recuperação de carrinho',
          nps_survey: 'Pesquisa NPS',
          custom: 'Personalizado',
        },
        language: {
          es_CO: 'ES (Colômbia)',
          es_LATAM: 'ES (LATAM)',
          es_MX: 'ES (México)',
          en_US: 'EN (EUA)',
          pt_BR: 'PT (Brasil)',
        },
        tone: {
          friendly_formal: 'Amigável e formal',
          professional: 'Profissional',
          empathetic: 'Empático',
          energetic: 'Enérgico',
          neutral: 'Neutro',
        },
        errors: {
          useCaseRequired: 'Escolha um modelo de caso de uso ou comece em branco para continuar.',
          nameRequired: 'O nome do agente é obrigatório.',
          voiceIdRequired: 'O ID de voz é obrigatório.',
          openingRequired: 'O roteiro de abertura é obrigatório (mín. 10 caracteres).',
          callingHoursRangeInvalid: 'Cada dia habilitado deve começar antes do horário de término.',
          draftSaveFailed: 'Falha ao salvar o rascunho do agente. Tente novamente.',
          saveBeforePublish: 'Salve o agente primeiro concluindo todas as etapas.',
          discloseRequired:
            'A divulgação de identidade de IA é obrigatória para publicar. Ative "Sempre divulgar identidade de IA" em Ferramentas e Conformidade.',
          recordingAnnouncementRequired:
            'O aviso de gravação é obrigatório para publicar. Ative "Anúncio de gravação" em Ferramentas e Conformidade.',
          publishFailed: 'Falha ao publicar',
        },
        templates: {
          initial_qualification: {
            name: 'Agente de primeiro contato e qualificação',
            description:
              'Primeiro contato com um lead recém-criado. Se apresenta, investiga interesse, orçamento, necessidade, autoridade de decisão e urgência, e propõe o próximo passo (reunião ou follow-up).',
          },
          warm_recontact: {
            name: 'Agente de recontato',
            description:
              'Reengaja leads frios. Retoma uma conversa anterior e propõe agendar reunião ou um follow-up próximo.',
          },
          meeting_confirmation: {
            name: 'Agente de confirmação de reunião',
            description:
              'Confirma uma reunião já agendada. Se o lead não puder, reagenda no mesmo fluxo.',
          },
          cart_recovery: {
            name: 'Agente de recuperação de carrinho',
            description:
              'E-commerce. Liga para leads que abandonaram um orçamento, investiga o bloqueio e oferece resolvê-lo ou transferir para vendas.',
          },
          nps_survey: {
            name: 'Agente de pesquisa NPS',
            description:
              'Pergunta NPS pós-compra/pós-serviço. Captura nota de 0 a 10 e comentário livre. Chamada curta.',
          },
        },
        useCaseStep: {
          eyebrow: 'Plano do agente',
          intro:
            'Escolha um caso de uso para preencher o assistente com um roteiro selecionado, horário de chamadas e conjunto de ferramentas. Você pode editar tudo depois.',
          choicesA11y: 'Modelos de casos de uso do agente',
          loadingTemplates: 'Carregando modelos',
          loadError:
            'Não foi possível carregar os modelos. Você ainda pode começar em branco abaixo.',
          useTemplatePrefix: 'Usar modelo',
          useTemplateSuffix: '',
          startBlankA11y: 'Começar em branco (Personalizado)',
          startBlankTitle: 'Começar em branco',
          startBlankDescription:
            'Comece a partir de uma configuração completamente vazia e escreva tudo você mesmo.',
        },
        identity: {
          nameLabel: 'Nome do agente *',
          namePlaceholder: 'ex. Bot de qualificação Colômbia',
          nameA11y: 'Nome do agente',
          descriptionLabel: 'Descrição',
          descriptionPlaceholder: 'O que este agente faz?',
          descriptionA11y: 'Descrição do agente',
          useCaseLabel: 'Caso de uso *',
          useCaseA11y: 'Selecionar caso de uso',
          languageLabel: 'Idioma *',
          languageA11y: 'Selecionar idioma',
          autoDetectLanguageLabel: 'Detectar o idioma automaticamente',
          autoDetectLanguageA11y: 'Detectar o idioma automaticamente para cada lead',
          autoDetectLanguageHint:
            'O agente fala o idioma atribuído ao país do lead, ou o idioma preferido do lead quando definido. Deixe desligado para falar sempre o idioma escolhido acima.',
          toneLabel: 'Tom',
          toneA11y: 'Selecionar tom do agente',
        },
        voice: {
          voiceIdLabel: 'ID de voz do ElevenLabs *',
          voiceIdPlaceholder: 'Cole um ID de voz do ElevenLabs',
          voiceIdA11y: 'ID de voz',
          hint: 'Encontre mais vozes no ElevenLabs → Biblioteca de vozes.',
          selectLabel: 'Voz *',
          selectPlaceholder: 'Selecione uma voz',
          selectA11y: 'Selecione uma voz',
          catalogHint:
            'Toque em uma voz para selecioná-la e pressione reproduzir para ouvir uma amostra.',
          selectVoiceA11yPrefix: 'Selecionar voz',
          loading: 'Carregando vozes…',
          loadError: 'Não foi possível carregar as vozes. Tente novamente.',
          empty: 'Ainda não há vozes disponíveis para este idioma.',
          customLabel: 'Ou insira um ID de voz personalizado',
          previewVoice: 'Pré-visualizar voz',
          previewPlay: 'Reproduzir amostra de voz',
          previewPause: 'Pausar amostra de voz',
          previewPlaying: 'Reproduzindo…',
          previewUnavailable: 'Nenhuma amostra disponível',
        },
        script: {
          openingLabel: 'Abertura *',
          openingPlaceholder: 'Como o agente se apresenta',
          openingA11y: 'Roteiro de abertura',
          ctaLabel: 'Chamada para ação *',
          ctaPlaceholder: 'ex. Agende uma demonstração',
          ctaA11y: 'Roteiro de chamada para ação',
          closingLabel: 'Encerramento *',
          closingPlaceholder: 'Palavras de encerramento',
          closingA11y: 'Roteiro de encerramento',
          builderTitle: 'Área de trabalho da conversa',
          builderHint: 'Escreva naturalmente e adicione dados confiáveis do CRM pela biblioteca.',
          liveSafe: 'Seguro ao vivo',
          variableLibrary: 'Biblioteca de variáveis',
          variableLibraryHint: 'Adiciona o valor selecionado onde o cursor está ativo.',
          searchVariables: 'Pesquisar variáveis',
          searchVariablesA11y: 'Pesquisar variáveis disponíveis para o roteiro',
          loadingVariables: 'Carregando variáveis seguras…',
          loadVariablesError: 'A biblioteca está indisponível. O roteiro ainda pode ser editado.',
          usedVariables: 'Em uso',
          variableMap: 'Mapa de variáveis',
          autocompleteTitle: 'Inserir uma variável',
          autocompleteKeyboardHint: '↑↓ Navegar · Enter selecionar · Esc fechar',
          autocompleteEmpty: 'Nenhuma variável corresponde a esta tag.',
          noVariables: 'Nenhuma variável ainda',
          unknownVariable: '“{{variable}}” não é suportada e impedirá a publicação.',
          previewTitle: 'Prévia resolvida com exemplos',
          previewEmpty: 'Comece a escrever para ver como esta seção vai soar.',
          openingShort: 'Abertura',
          ctaShort: 'Chamada para ação',
          closingShort: 'Encerramento',
          examplePrefix: 'Exemplo: {{example}}',
          noVariableResults: 'Nenhuma variável segura corresponde à pesquisa.',
        },
        knowledge: {
          introOnboarding:
            'Opcional — dê ao seu agente um trecho de contexto para que ele possa responder perguntas sobre o seu produto.',
          introFull:
            'Os itens da base de conhecimento podem ser adicionados após a publicação. Use a página de detalhes do agente para enviar documentos (PDF / texto / URL).',
          seedLabel: 'Contexto rápido (opcional)',
          seedPlaceholder:
            'Cole um parágrafo sobre o seu produto, FAQ ou proposta de valor. Você pode adicionar documentos completos depois.',
          seedA11y: 'Texto inicial opcional da base de conhecimento',
          skipHint:
            'Toque em Continuar para pular — os administradores podem enviar PDFs e URLs pela visualização de detalhes do agente após a publicação.',
          seedTitle: 'Contexto rápido',
          addLabel: 'Adicionar conhecimento',
          addText: 'Texto',
          addUrl: 'URL',
          addPdf: 'PDF',
          titlePlaceholder: 'Título',
          titleA11y: 'Título do item de conhecimento',
          textPlaceholder: 'Cole o texto que o agente pode consultar durante as chamadas.',
          textA11y: 'Conteúdo de texto do item de conhecimento',
          urlPlaceholder: 'https://exemplo.com/página',
          urlA11y: 'URL do item de conhecimento',
          submitAdd: 'Adicionar',
          submitTextA11y: 'Adicionar item de conhecimento de texto',
          submitUrlA11y: 'Adicionar item de conhecimento de URL',
          pickPdf: 'Escolher PDF',
          pickPdfA11y: 'Escolher um arquivo PDF para enviar',
          pdfHint: 'O PDF é enviado e seu texto é extraído e anexado ao agente.',
          listLabel: 'Itens adicionados',
          listEmpty: 'Nenhum item de conhecimento adicionado ainda.',
          deleteItemA11y: 'Excluir item de conhecimento',
          completeEarlierSteps:
            'Conclua as etapas anteriores para começar a adicionar conhecimento.',
          sourceType: {
            text: 'Texto',
            url: 'URL',
            pdf: 'PDF',
            notion: 'Notion',
          },
          errors: {
            textRequired: 'Adicione um título e algum texto antes de salvar.',
            urlRequired: 'Adicione um título e uma URL antes de salvar.',
            uploadFailed: 'Não foi possível adicionar o item de conhecimento. Tente novamente.',
            removeFailed: 'Não foi possível remover o item de conhecimento. Tente novamente.',
            pdfParseFailed: 'Não foi possível ler este PDF. Tente outro arquivo.',
          },
        },
        objections: {
          note: 'O tratamento de objeções pode ser configurado após salvar. O editor completo de objeções chegará em breve.',
        },
        compliance: {
          heading: 'Política de divulgação',
          alwaysDiscloseLabel: 'Sempre divulgar a identidade de IA',
          alwaysDiscloseA11y: 'Sempre divulgar a identidade de IA',
          recordingLabel: 'Aviso de gravação',
          recordingA11y: 'Aviso de gravação',
          wordingLabel: 'Texto de divulgação (opcional)',
          wordingPlaceholder: 'Texto de divulgação personalizado (mín. 20 caracteres)',
          wordingA11y: 'Texto de divulgação',
        },
        tools: {
          heading: 'Ferramentas habilitadas',
          hint: 'Escolha quais ações este agente pode realizar durante uma chamada. Pré-preenchido a partir do modelo selecionado — edite quando quiser.',
          schedule_meeting: 'Agendar reunião',
          transfer_to_human: 'Transferir para humano',
          mark_do_not_call: 'Marcar não ligar',
          update_lead_qualification: 'Atualizar qualificação do lead',
          increment_objection_counter: 'Incrementar contador de objeções',
          finalize_call: 'Finalizar chamada',
          lookup_knowledge_base: 'Consultar base de conhecimento',
        },
        callingHours: {
          useLeadTimezoneLabel: 'Usar o fuso horário do lead',
          useLeadTimezoneA11y: 'Usar o fuso horário do lead',
          windowStartLabel: 'Início da janela (HH:mm)',
          windowStartA11y: 'Hora de início da janela de chamadas',
          windowEndLabel: 'Fim da janela (HH:mm)',
          windowEndA11y: 'Hora de fim da janela de chamadas',
          callingDaysLabel: 'Dias de chamadas',
          dayMon: 'Segunda',
          dayTue: 'Terça',
          dayWed: 'Quarta',
          dayThu: 'Quinta',
          dayFri: 'Sexta',
          daySat: 'Sábado',
          daySun: 'Domingo',
          dayToggleA11y: 'Habilitar {{day}}',
          dayStartA11y: 'Hora de início para {{day}}',
          dayEndA11y: 'Hora de fim para {{day}}',
          blackoutDatesLabel: 'Feriados / Datas bloqueadas',
          blackoutDatePlaceholder: 'YYYY-MM-DD',
          blackoutDateAddLabel: 'Adicionar',
          blackoutDateAddA11y: 'Adicionar data bloqueada',
          blackoutDateRemoveA11y: 'Remover data bloqueada {{date}}',
          blackoutDateInvalid: 'Insira uma data válida no formato YYYY-MM-DD.',
        },
        retry: {
          maxAttemptsLabel: 'Máximo de tentativas (1–5)',
          maxAttemptsA11y: 'Número máximo de novas tentativas',
        },
        review: {
          name: 'Nome',
          useCase: 'Caso de uso',
          language: 'Idioma',
          tone: 'Tom',
          voiceId: 'ID de voz',
          opening: 'Abertura',
          cta: 'CTA',
          closing: 'Encerramento',
          disclosure: 'Divulgação',
          callingHours: 'Horário de chamadas',
          maxRetries: 'Máx. de tentativas',
          enabled: 'Habilitado',
          disabled: 'Desabilitado',
          draftSaved: 'Rascunho salvo — ID:',
          edit: 'Editar',
          editPrefix: 'Editar',
        },
        footer: {
          back: 'Voltar',
          backA11y: 'Ir para a etapa anterior',
          cancel: 'Cancelar',
          cancelA11y: 'Cancelar assistente',
          publish: 'Publicar',
          publishA11y: 'Publicar agente de voz',
          continue: 'Continuar',
          continueToPrefix: 'Continuar para',
        },
        video: {
          forStepA11y: 'Vídeo tutorial da etapa',
          tutorialSuffix: 'tutorial',
          openForStepPrefix: 'Abrir o vídeo tutorial da etapa',
          openForStepSuffix: 'no seu navegador',
          tapToOpen: 'Toque para abrir o vídeo tutorial.',
          placeholderA11y: 'Espaço reservado para vídeo tutorial da etapa',
          comingSoon: 'Vídeo tutorial em breve. Siga o formulário abaixo para continuar.',
        },
      },
      list: {
        title: 'Agentes de voz',
        countSingular: 'agente',
        countPlural: 'agentes',
        newAgent: 'Novo agente',
        newAgentA11y: 'Criar novo agente de voz',
        filterAll: 'Todos',
        filterDraft: 'Rascunho',
        filterPublished: 'Publicado',
        filterArchived: 'Arquivado',
        filterUseCaseLabel: 'Caso de uso',
        filterLanguageLabel: 'Idioma',
        filterAllUseCases: 'Todos os casos',
        filterAllLanguages: 'Todos os idiomas',
        emptyTitle: 'Ainda não há agentes de voz',
        emptyDescription:
          'Crie seu primeiro agente de voz com IA para começar a qualificar leads automaticamente.',
        emptyMatchTitle: 'Sem correspondências',
        emptyMatchDescription: 'Nenhum agente corresponde ao filtro selecionado.',
        startOnboarding: 'Iniciar configuração',
        listA11y: 'Lista de agentes de voz',
        errorTitle: 'Não foi possível carregar os agentes de voz',
        errorDescription: 'Ocorreu um problema ao carregar seus agentes de voz.',
        retry: 'Tentar novamente',
      },
      detail: {
        loadingA11y: 'Carregando agente de voz',
        notFound: 'Agente não encontrado.',
        tabOverview: 'Visão geral',
        tabConfig: 'Configuração',
        tabVersions: 'Versões',
        tabKnowledge: 'Conhecimento',
        tabExperiments: 'Experimentos',
        tabAnalytics: 'Análises',
        tabCalls: 'Chamadas',
        configTitle: 'Configuração do agente',
        configDescription: 'Revise o roteiro, as proteções de conformidade e as novas tentativas.',
        editConfig: 'Editar configuração',
        descriptionTitle: 'Descrição',
        detailsTitle: 'Detalhes',
        useCase: 'Caso de uso',
        language: 'Idioma',
        voiceId: 'ID da voz',
        model: 'Modelo',
        published: 'Publicado',
        analyticsSoonA11y: 'Métricas de análise em breve',
        analyticsSoon: 'As métricas de análise chegarão em uma versão futura.',
        scriptTitle: 'Roteiro',
        opening: 'Abertura',
        cta: 'CTA',
        closing: 'Encerramento',
        disclosureTitle: 'Divulgação',
        alwaysDisclose: 'Sempre divulgar',
        recording: 'Gravação',
        yes: 'Sim',
        no: 'Não',
        retryPolicyTitle: 'Política de novas tentativas',
        maxAttempts: 'Tentativas máximas',
        noVersions: 'Ainda não há versões.',
        version: 'Versão',
        publishedLower: 'publicada',
        callsSoonA11y: 'Histórico de chamadas em breve',
        callsSoon: 'O histórico de chamadas chegará em uma versão futura.',
        statusLabel: 'Status',
        preview: 'Pré-visualização',
        previewA11y: 'Pré-visualizar agente de voz no navegador',
        edit: 'Editar',
        editA11y: 'Editar agente de voz',
        renameA11y: 'Renomear agente de voz',
        renameInputA11y: 'Nome do agente de voz',
        renameSaveA11y: 'Salvar nome',
        renameCancelA11y: 'Cancelar renomeação',
        republish: 'Republicar',
        republishA11y: 'Republicar este agente como uma nova versão',
      },
      edit: {
        loadingA11y: 'Carregando agente de voz',
        screenA11y: 'Editar agente de voz',
      },
      previewScreen: {
        loadingA11y: 'Carregando agente…',
      },
      newScreen: {
        screenA11y: 'Assistente para criar novo agente de voz',
      },
      onboardingScreen: {
        screenA11y: 'Assistente de configuração de agente de voz',
      },
      refusal: {
        dailyLimit:
          'Este lead já recebeu {{used}} de {{limit}} chamadas permitidas por dia. Você pode ligar novamente a partir de {{freesAt}}.',
        dailyLimitNoHorizon:
          'Este lead já recebeu {{used}} de {{limit}} chamadas permitidas por dia. A cota é liberada nas próximas 24 horas.',
        doNotCall:
          'Este lead pediu para não ser contatado novamente, então as chamadas estão bloqueadas.',
        consentRefused:
          'Este lead não autorizou o tratamento dos seus dados, então as chamadas estão bloqueadas.',
        generic: 'Não foi possível fazer a chamada. Tente novamente em instantes.',
      },
      callsList: {
        deleteCall: 'Excluir chamada',
        deleteTitle: 'Excluir chamada',
        deleteBody: 'Isto remove permanentemente o registro da chamada de:',
        deleteConfirm: 'Excluir',
        deleteError: 'Não foi possível excluir a chamada.',
        agentSelectLabel: 'Agente',
        leadSelectLabel: 'Lead',
        selectAgentPlaceholder: 'Selecione um agente publicado',
        selectLeadPlaceholder: 'Selecione um lead',
        noPublishedAgents: 'Nenhum agente publicado. Publique um agente primeiro.',
        noLeads: 'Nenhum lead com número de telefone.',
        title: 'Chamadas',
        results: 'chamadas',
        triggerCall: 'Disparar chamada',
        triggerNewCall: 'Disparar nova chamada',
        filterByPrefix: 'Filtrar por',
        callToPrefix: 'Chamada para',
        statusPrefix: 'Status:',
        unknown: 'desconhecido',
        unknownLead: 'Lead desconhecido',
        unknownAgent: 'Agente desconhecido',
        statusFiltersA11y: 'Filtros de status de chamada',
        listA11y: 'Lista de chamadas',
        loading: 'Carregando chamadas',
        loadingSkeleton: 'Carregando lista de chamadas',
        emptyTitle: 'Sem chamadas',
        emptyDescription: 'Nenhuma chamada encontrada com os filtros selecionados.',
        firstEmptyTitle: 'Ainda não há chamadas',
        firstEmptyDescription: 'Dispare sua primeira chamada com IA e acompanhe o progresso aqui.',
        errorTitle: 'Não foi possível carregar as chamadas',
        errorDescription: 'Ocorreu um problema ao carregar as chamadas. Tente novamente.',
        retry: 'Tentar novamente',
        filterAll: 'Todas',
        filterScheduled: 'Agendadas',
        filterInProgress: 'Em andamento',
        filterCompleted: 'Concluídas',
        filterNoAnswer: 'Sem resp.',
        filterError: 'Erro',
        fromPlaceholder: 'De (YYYY-MM-DD)',
        toPlaceholder: 'Até (YYYY-MM-DD)',
        countryPlaceholder: 'País (CO)',
        fromDateA11y: 'Data inicial',
        toDateA11y: 'Data final',
        dateHint: 'Formato ano-mês-dia, por exemplo 2026-05-05',
        countryA11y: 'Filtrar por país',
        countryHint: 'Código de país de duas letras, por exemplo CO ou MX',
        filtersTitle: 'Filtros',
        advancedFiltersA11y: 'Filtros avançados de chamadas',
        showFilters: 'Mais filtros',
        hideFilters: 'Ocultar filtros',
        clearFilters: 'Limpar filtros',
        metricsTotal: 'Chamadas totais',
        metricsActive: 'Em andamento',
        metricsCompleted: 'Concluídas',
        metricsNoAnswer: 'Sem resposta',
        metricsAvgDuration: 'Duração média',
        fromLabel: 'De',
        toLabel: 'Até',
        countryLabel: 'País',
        agentIdLabel: 'ID do Agente',
        agentIdPlaceholder: 'UUID do agente',
        agentIdA11y: 'ID do agente de voz',
        leadIdLabel: 'ID do Lead',
        leadIdPlaceholder: 'UUID do lead',
        leadIdA11y: 'ID do lead',
        cancel: 'Cancelar',
        confirmTrigger: 'Confirmar disparo de chamada',
        trigger: 'Disparar',
        triggering: 'Disparando…',
        errorPrefix: 'Erro:',
        unknownError: 'Erro desconhecido',
        consentRejectedError:
          'Este lead recusou o consentimento de tratamento de dados, portanto as chamadas ficam permanentemente bloqueadas. Entre em contato por outro canal.',
        triggerConflictError:
          'Não é possível fazer esta chamada agora. O lead pode estar na lista de não ligar, ter recusado o consentimento, estar fora do horário de chamadas ou ter atingido o limite diário de chamadas.',
        outsideCallingHoursWarning:
          'Este agente só pode fazer chamadas dentro do horário configurado para o fuso horário do lead. Tente novamente dentro da janela permitida.',
        status: {
          scheduled: 'Agendada',
          dialing: 'Discando',
          completed: 'Concluída',
          noAnswer: 'Sem resp.',
          error: 'Erro',
          cancelled: 'Cancelada',
        },
        filterSelectAll: 'Todos',
        filterAgentLabel: 'Agente',
        filterAgentPlaceholder: 'Todos os agentes',
        filterAgentA11y: 'Filtrar por agente',
        filterLeadLabel: 'Lead',
        filterLeadPlaceholder: 'Todos os leads',
        filterLeadA11y: 'Filtrar por lead',
        filterOutcomeLabel: 'Resultado',
        filterOutcomePlaceholder: 'Todos os resultados',
        filterOutcomeA11y: 'Filtrar por resultado',
        outcomeFilter: {
          qualified: 'Qualificado',
          not_qualified: 'Não qualificado',
          callback_requested: 'Retorno solicitado',
          meeting_booked: 'Reunião agendada',
          do_not_call: 'Não ligar',
          no_answer: 'Sem resposta',
          voicemail_left: 'Caixa postal',
          technical_failure: 'Falha técnica',
          incomplete: 'Incompleto',
        },
      },
      callDetail: {
        title: 'Chamada',
        screenA11y: 'Detalhe da chamada',
        statusPrefix: 'Status:',
        loadingDetail: 'Carregando detalhe da chamada',
        notFound: 'Chamada não encontrada.',
        loadingAudio: 'Carregando áudio',
        audioUnavailable: 'Áudio indisponível.',
        audioUnavailableA11y: 'Áudio indisponível',
        signedUrlExpires: 'URL assinada expira:',
        downloadAudio: 'Baixar áudio',
        downloadAudioA11y: 'Baixar áudio da chamada (URL assinada com expiração de 10 minutos)',
        audioSection: 'Áudio',
        transcript: 'Transcrição',
        summary: 'Resumo',
        details: 'Detalhes',
        from: 'De',
        to: 'Para',
        country: 'País',
        duration: 'Duração',
        startedAt: 'Início',
        endedAt: 'Fim',
        agent: 'Agente',
        agentVersion: 'Versão do agente',
        latencyP50: 'Latência P50 (ms)',
        latencyP95: 'Latência P95 (ms)',
        telephonyCost: 'Custo de telefonia',
        conversationCost: 'Custo de conversa',
        viewReplay: 'Ver Replay',
        viewReplayA11y: 'Ver replay da chamada',
        retry: 'Tentar novamente',
        retrying: 'Tentando novamente…',
        retryA11y: 'Tentar chamada novamente',
        status: {
          scheduled: 'Agendada',
          contextLoaded: 'Contexto carregado',
          dialing: 'Discando',
          detecting: 'Detectando',
          disclosure: 'Divulgação',
          discovery: 'Descoberta',
          handling: 'Tratamento',
          cta: 'CTA',
          closing: 'Encerramento',
          postProcessing: 'Pós-processamento',
          completed: 'Concluída',
          noAnswer: 'Sem resposta',
          voicemail: 'Caixa postal',
          hungUpByLead: 'Lead desligou',
          technicalFailure: 'Falha técnica',
          cancelled: 'Cancelada',
        },
      },
      replay: {
        title: 'Replay',
        screenA11y: 'Replay da chamada turno a turno',
        turns: 'turnos',
        tools: 'Ferramentas',
        toolsPanelPrefix: 'Painel de ferramentas,',
        callsLabel: 'chamadas',
        noToolCalls: 'Sem chamadas de ferramentas nesta sessão.',
        turnLabel: 'Turno',
        toolPrefix: 'Ferramenta',
        latencyLabel: 'latência',
        statusLabel: 'status',
        toolExecuted: 'Ferramenta executada',
        agent: 'Agente',
        lead: 'Lead',
        turnPrefix: 'Turno',
        atTime: 'em',
        tapToSeek: 'Toque para ir ao áudio.',
        seekHint: 'Reproduz o áudio a partir deste turno',
        restrictedTitle: 'Acesso restrito',
        restrictedBody: 'Apenas administradores e gerentes podem ver o replay das chamadas.',
        loading: 'Carregando replay',
        unavailable: 'Replay indisponível.',
        audioUnavailable: 'Áudio indisponível para esta chamada.',
        audioUnavailableA11y: 'Áudio indisponível',
        noTurnsA11y: 'Sem turnos no replay',
        noReplayData: 'Sem dados de replay disponíveis.',
      },
      card: {
        delete: 'Excluir',
        voiceAgent: 'agente de voz',
        statusPrefix: 'Status',
        version: 'Versão',
        publishedOn: 'Publicado',
        duplicate: 'Duplicar',
        edit: 'Editar',
        archive: 'Arquivar',
        tooltipEdit: 'Editar configuração do agente',
        tooltipDuplicate: 'Duplicar agente',
        tooltipArchive: 'Arquivar agente',
        tooltipDelete: 'Excluir agente',
        status: {
          draft: 'Rascunho',
          published: 'Publicado',
          archived: 'Arquivado',
        },
        useCase: {
          initial_qualification: 'Primeiro contato e qualificação',
          warm_recontact: 'Recontato quente',
          meeting_confirmation: 'Confirmação de reunião',
          cart_recovery: 'Recuperação de carrinho',
          nps_survey: 'Pesquisa NPS',
          custom: 'Personalizado',
        },
        language: {
          es_LATAM: 'ES (LATAM)',
          es_CO: 'ES (Colômbia)',
          es_MX: 'ES (México)',
          en_US: 'EN (US)',
          pt_BR: 'PT (Brasil)',
        },
      },
      analytics: {
        panelLabel: 'Painel de análises do agente',
        showWindowPrefix: 'Mostrar janela',
        loading: 'Carregando análises',
        loadError: 'Não foi possível carregar as análises',
        unknownError: 'erro desconhecido',
        emptyPrefix: 'Ainda não há chamadas nos últimos',
        emptySuffix: 'Dispare uma chamada para preencher este painel.',
        totalCalls: 'Total de chamadas',
        meetingBooked: 'Reunião agendada',
        ofCallsWithOutcome: 'das chamadas com resultado',
        avgDuration: 'Duração média',
        totalCost: 'Custo total',
        costPerMeeting: 'Custo por reunião',
        noMeetingsBooked: 'nenhuma reunião agendada',
        turnTaking: 'Troca de turnos',
        outcomesTitle: 'Resultados',
        noOutcomes: 'Nenhum resultado registrado ainda.',
        of: 'de',
        period: {
          '7d': '7 dias',
          '30d': '30 dias',
          '90d': '90 dias',
        },
        outcome: {
          qualified: 'Qualificado',
          not_qualified: 'Não qualificado',
          callback_requested: 'Retorno solicitado',
          meeting_booked: 'Reunião agendada',
          do_not_call: 'Não ligar',
          no_answer: 'Sem resposta',
          voicemail_left: 'Caixa postal',
          technical_failure: 'Falha técnica',
          incomplete: 'Incompleto',
        },
      },
      outcome: {
        none: 'Sem resultado',
        resultPrefix: 'Resultado',
        qualified: 'Qualificado',
        meeting_booked: 'Reunião agendada',
        not_qualified: 'Não qualificado',
        do_not_call: 'Não ligar',
        callback_requested: 'Retorno solicitado',
        voicemail_left: 'Caixa postal deixada',
        no_answer: 'Sem resposta',
        technical_failure: 'Falha técnica',
        incomplete: 'Incompleto',
      },
      previewCall: {
        voiceAgentFallback: 'agente de voz',
        thisAgentFallback: 'este agente',
        emailSubjectPrefix: 'Prévia de',
        emailBody: 'Abra este link no seu navegador (Chrome ou Safari) para testar o agente:',
        browserOnlyTitle: 'A prévia só está disponível no navegador',
        browserOnlyBodyPrefix: 'A prévia de',
        browserOnlyBodySuffix:
          'requer um navegador web. Enviamos o link por e-mail para que você o abra no seu computador com o Chrome ou Safari.',
        sendEmail: 'Enviar link por e-mail',
        sendEmailA11y: 'Enviar link por e-mail para abrir no navegador',
        resumeFoundTitle: 'Sessão anterior encontrada',
        resumeFoundBody: 'Você tem uma sessão de prévia ativa. Deseja retomá-la?',
        newSession: 'Nova sessão',
        newSessionA11y: 'Iniciar nova sessão de prévia',
        resumeSession: 'Retomar sessão',
        resumeSessionA11y: 'Retomar sessão de prévia anterior',
        resumeFailed: 'Esta sessão de prévia não está mais disponível. Inicie uma nova.',
        previewTitle: 'Prévia',
        startDescription:
          'Inicie uma sessão de prévia para conversar com este agente em modo de teste. A transcrição é salva para que você possa retomar a conversa mais tarde.',
        startPreview: 'Iniciar prévia',
        startPreviewA11y: 'Iniciar prévia do agente',
        statusPrefix: 'Status',
        elapsedPrefix: 'Tempo decorrido',
        agentSpeaking: 'O agente está falando…',
        listening: 'Ouvindo…',
        micPaused: 'Microfone pausado',
        mute: 'Silenciar',
        unmute: 'Ativar mic',
        muteMicA11y: 'Silenciar microfone',
        unmuteMicA11y: 'Ativar microfone',
        endPreview: 'Encerrar prévia',
        endPreviewA11y: 'Encerrar prévia',
        liveTranscript: 'Transcrição ao vivo',
        toolCalls: 'Tool calls (sem efeito)',
        toolCallPrefix: 'Tool call',
        toolCallSimulated: 'Sem efeito',
        previousSession: 'Sessão anterior',
        sessionHistory: 'Histórico de sessões',
        previousSessionA11y: 'Transcrição da sessão anterior',
        connectionError:
          'Não foi possível conectar a prévia. Verifique se o agente está publicado e tente novamente.',
        errorPayment: {
          title: 'Serviço de voz indisponível',
          body: 'O provedor de voz rejeitou esta sessão devido a um problema de faturamento na conta. Entre em contato com seu administrador para revisar o plano contratado.',
        },
        errorGeneric: {
          title: 'Não foi possível conectar a prévia',
          body: 'A prévia não pôde se conectar. Tente novamente e, se o problema persistir, entre em contato com seu administrador.',
        },
        startFailed:
          'Não foi possível iniciar a prévia. Verifique se o agente está publicado no ElevenLabs e tente novamente.',
        submitError:
          'Falha ao salvar a sessão de prévia. Os dados da conversa podem não ter sido persistidos.',
        status: {
          idle: 'Inativo',
          connecting: 'Conectando…',
          connected: 'Conectado',
          disconnected: 'Desconectado',
          error: 'Erro',
        },
      },
      tour: {
        a11yLabel: 'Tour de boas-vindas do agente de voz',
        tip: 'Dica',
        of: 'de',
        dismissA11y: 'Fechar tour de boas-vindas',
        skip: 'Pular',
        skipA11y: 'Pular tour de boas-vindas',
        next: 'Próximo',
        nextA11y: 'Próxima dica de boas-vindas',
        gotIt: 'Entendi',
        finishA11y: 'Concluir tour de boas-vindas',
        steps: {
          welcome: {
            title: 'Bem-vindo ao seu novo agente',
            body: 'Este é o painel do agente. Tudo o que você configurou no onboarding fica aqui, organizado por aba.',
          },
          preview: {
            title: 'Faça a prévia antes de lançar',
            body: 'Use o botão Prévia no topo para falar com o agente direto do navegador antes de colocá-lo em uma chamada real.',
          },
          analytics: {
            title: 'Acompanhe as análises',
            body: 'A aba Análises acompanha o volume de chamadas, a taxa de qualificação e a duração para você identificar o que ajustar.',
          },
        },
      },
      audio: {
        playerLabel: 'Reprodutor de áudio',
        playing: 'reproduzindo',
        paused: 'pausado',
        position: 'posição',
        of: 'de',
        forward: 'Avançar',
        rewind: 'Retroceder',
        seconds: 'segundos',
        pause: 'Pausar',
        play: 'Reproduzir',
        pauseAudio: 'Pausar áudio',
        playAudio: 'Reproduzir áudio',
      },
      summary: {
        callSummary: 'Resumo da chamada',
        structuredSummary: 'Resumo estruturado da chamada',
        keyPoints: 'Pontos-chave',
        objections: 'Objeções',
        nextStep: 'Próximo passo',
      },
      experiments: {
        loading: 'Carregando experimentos',
        emptyLabel: 'Ainda não há experimentos',
        emptyTitle: 'Ainda não há experimentos A/B para este agente.',
        emptyHint: 'Crie um para comparar duas versões do seu roteiro ou voz.',
        listLabel: 'Lista de experimentos',
        experimentPrefix: 'Experimento',
        statusPrefix: 'status',
        split: 'Divisão',
        target: 'Meta',
        pause: 'Pausar',
        pauseA11y: 'Pausar experimento',
        start: 'Iniciar',
        resume: 'Retomar',
        startA11y: 'Iniciar experimento',
        lift: 'Ganho',
        pValue: 'valor p',
        significant: '✓ 95% sig.',
        notSignificant: 'não sig.',
        significantA11y: 'Estatisticamente significativo a 95%',
        notSignificantA11y: 'Ainda não significativo',
        insufficientSample:
          'Amostra pequena demais — recomendam-se ao menos 30 chamadas por variante.',
        variant: 'Variante',
        calls: 'chamadas',
        conv: 'conv.',
        status: {
          draft: 'rascunho',
          running: 'em execução',
          paused: 'pausado',
          concluded: 'concluído',
        },
        newExperiment: '+ Novo experimento',
        newExperimentA11y: 'Criar um novo experimento',
        createTitle: 'Novo experimento A/B',
        nameLabel: 'Nome',
        namePlaceholder: 'ex. Abertura mais amigável',
        descriptionLabel: 'Descrição (opcional)',
        descriptionPlaceholder: 'O que você está testando?',
        variantALabel: 'Versão da variante A',
        variantBLabel: 'Versão da variante B',
        splitLabel: 'Percentual de divisão (A/B)',
        outcomeTargetLabel: 'Meta de resultado',
        cancel: 'Cancelar',
        create: 'Criar experimento',
        createA11y: 'Criar experimento',
        creating: 'Criando…',
        errorPrefix: 'Não foi possível criar o experimento',
        outcomeOptions: {
          meeting_booked: 'Reunião agendada',
          callback_requested: 'Retorno solicitado',
          qualified: 'Qualificado',
          disqualified: 'Desqualificado',
          voicemail: 'Correio de voz',
          no_answer: 'Sem resposta',
          do_not_call: 'Não ligar',
          other: 'Outro',
        },
      },
      transcript: {
        agent: 'Agente',
        lead: 'Lead',
        turn: 'Turno',
        at: 'em',
        seekHint: 'Toque para ir ao timestamp no áudio',
        emptyLabel: 'Nenhuma entrada de transcrição disponível',
        empty: 'Nenhuma transcrição disponível.',
        callTranscript: 'Transcrição da chamada',
      },
      batchCall: {
        title_one: 'Ligar para {{count}} lead',
        title_other: 'Ligar para {{count}} leads',
        confirm: 'Ligar para leads',
        progressLabel: '{{completed}} de {{total}} leads processados',
        creditsSkippedLabel: '{{count}} sem ligação por falta de créditos',
      },
      regulatoryDocs: {
        uploadSuccess: 'Documento enviado com sucesso.',
        uploadErrorGeneric: 'Não foi possível enviar o documento. Tente novamente.',
        fileTooLarge: 'O arquivo é muito grande. O tamanho máximo é 10 MB.',
        unsupportedFileType: 'Tipo de arquivo não suportado. Permitidos: PDF, PNG, JPEG.',
        countryInvalid: 'O país deve ser um código ISO de 2 letras (ex. MX).',
        pickFilePrompt: 'Escolha um arquivo primeiro (PDF, PNG ou JPEG).',
      },
    },
    leads: {
      moveBlocked:
        'Este lead ainda não pode ser movido porque o cargo não corresponde ao seu perfil-alvo. Force a qualificação para move-lo.',
      moveBlockedNonPrivileged:
        'Este lead ainda não pode ser movido: o cargo não corresponde ao seu perfil-alvo. Peça a um gerente ou admin para forçar a qualificação.',
      qualificationBlocked: {
        notQualified:
          'O cargo deste lead não corresponde ao seu perfil de cliente-alvo. Um gerente ou admin pode forçar a qualificação para move-lo.',
        pending:
          'O cargo deste lead ainda esta sendo classificado. Um gerente ou admin pode forçar a qualificação para move-lo.',
      },
      stages: {
        prospect: 'Prospecto',
        contacted: 'Contatado',
        qualified: 'Qualificado',
        proposal: 'Proposta',
        won: 'Ganho',
        lost: 'Perdido',
        negotiation: 'Negociação',
        customer: 'Cliente',
      },
      pipeline: {
        title: 'Pipeline de vendas',
        loading: 'Carregando pipeline...',
        failed: 'Falha ao carregar o pipeline',
        noStages: 'Nenhuma etapa de funil configurada',
        noStagesHint: 'Crie um template de funil para começar',
        stagesLabel: 'Etapas do pipeline',
        dragHint: 'Arraste cartões para mover entre etapas',
        noPipelineFound: 'Pipeline não encontrado',
        noPipelineHint: 'Crie um template de funil para começar',
        noActivePipeline: 'Sem pipeline ativo',
        noActiveHint: 'Ative um template de funil para ver seu pipeline de vendas',
        goToSettings: 'Ir para Configurações',
      },
      kanban: {
        dropHere: 'Soltar aqui',
        scrollPrevious: 'Ver etapas anteriores',
        scrollNext: 'Ver próximas etapas',
        noLeads: 'Sem leads nesta etapa',
      },
      list: {
        title: 'Leads',
        searchPlaceholder: 'Pesquisar por nome, email ou empresa...',
        allStages: 'Todas as etapas',
        noMatchFilters: 'Nenhum lead corresponde aos seus filtros',
        noLeadsYet: 'Ainda sem leads',
        noMatchDescription: 'Tente ajustar sua pesquisa ou filtros',
        noLeadsDescription: 'Adicione seu primeiro lead para começar',
        selected: 'selecionados',
        totalLeads: 'leads no total',
        totalLead: 'lead no total',
        sortBy: 'Ordenar por',
        sortNewest: 'Mais recentes',
        sortOldest: 'Mais antigos',
        sortName: 'Nome',
        sortCompany: 'Empresa',
        sortUpdated: 'Atualizado recentemente',
        dateRange: 'Período',
        startDate: 'De',
        endDate: 'Até',
        clearFilters: 'Limpar filtros',
        jobTitle: 'Cargo',
        industry: 'Indústria',
        economicCapacity: 'Capacidade Econômica',
        allCapacities: 'Todas as Capacidades',
        capacityHigh: 'Alta',
        capacityMedium: 'Média',
        capacityLow: 'Baixa',
        location: 'Localização',
        tags: 'Tags',
        tagsMore: '+{{count}} mais',
        tagLabel: 'Tag {{tag}}',
        autoTagLabel: 'Tag automática {{tag}}',
        tagsListLabel: 'Tags do lead',
      },
      detail: {
        notFound: 'Lead não encontrado',
        deleteConfirm: 'Tem certeza que deseja excluir este lead?',
        deleteTitle: 'Excluir Lead',
        callAction: {
          label: 'Ligar para o lead',
          tooltip: 'Ligar',
          started: 'Chamada em andamento',
          noTemplates: 'Sem templates de voz. Configure um em Configurações primeiro.',
          goToVoiceSettings: 'Ir para configurações de voz',
          chooseTitle: 'Como você quer ligar?',
          chooseContact: 'Para qual contato você quer ligar?',
          humanOption: 'Ligar você mesmo',
          humanOptionHint: 'Disque para o lead do seu próprio telefone',
          agentOption: 'Agente de voz IA',
          agentOptionHint: 'Deixe um agente de voz publicado fazer a ligação',
          unnamedContact: 'Contato',
          dialFailed: 'Não foi possível iniciar a chamada',
        },
        forceQualify: {
          button: 'Forçar qualificação',
          title: 'Forçar qualificação do lead',
          description:
            'Substituir a qualificação automática e marcar este lead como qualificado. Forneca um motivo.',
          reasonPlaceholder: 'Motivo para forçar a qualificação...',
          confirm: 'Qualificar',
          error: 'Não foi possível forçar a qualificação do lead. Tente novamente.',
        },
        qualification: {
          qualified: 'Cargo Verificado',
          notQualified: 'Não Verificado - Cargo',
          pending: 'Verificação de Cargo Pendente',
        },
        contactInfo: 'Informações de contato',
        fullName: 'Nome do Lead',
        contactName: 'Nome do Contato',
        email: 'Email',
        phone: 'Telefone',
        company: 'Empresa',
        jobTitle: 'Cargo',
        sourceUrl: 'URL de origem',
        companyWebsiteUrl: 'Site da empresa',
        otherProfileUrl: 'Perfil no {{platform}}',
        otherNetworkEnrichment: 'Dados do perfil no {{platform}}',
        companyLinkedinUrl: 'LinkedIn da empresa',
        source: 'Fonte',
        assignedTo: 'Atribuído a',
        productInterest: 'Produto/Serviço de interesse',
        productInterestEditor: {
          placeholder: 'O que esta sendo vendido a este lead?',
          inputLabel: 'Produto ou serviço de interesse',
          editLabel: 'Editar produto ou serviço de interesse',
          saveLabel: 'Salvar produto ou serviço de interesse',
          cancelLabel: 'Cancelar edição',
          suggestionHint: 'Sugerido pela IA',
          closestSuggestionHint: 'Produto mais próximo · confiança baixa',
          confirmSuggestion: 'Confirmar',
          confirmSuggestionLabel: 'Confirmar o produto ou serviço de interesse sugerido',
          unavailable: 'Produto não está mais disponível',
        },
        review: {
          title: 'Requer revisão manual',
          reasonConsentRefused:
            'O lead recusou o consentimento de tratamento de dados durante uma chamada do agente. As chamadas foram bloqueadas — decida se deve contatá-lo por outro canal, descartá-lo ou corrigir seus dados.',
          flaggedOn: 'Marcado em {{date}}',
        },
        consent: {
          title: 'Consentimento para chamadas (voz)',
          granted: 'Consentimento concedido',
          notGranted: 'Consentimento revogado',
          unknown: 'Sem consentimento registrado',
          grant: 'Conceder',
          revoke: 'Revogar',
          grantTitle: 'Conceder consentimento para chamadas',
          grantBody: 'Registre a prova de que este lead concordou em ser contatado por voz.',
          revokeTitle: 'Revogar consentimento para chamadas',
          revokeBody:
            'Isso registra que o consentimento não é mais válido. O lead não poderá ser chamado até que o consentimento seja concedido novamente.',
          sourceLabel: 'Como o consentimento foi obtido?',
          sourceForm: 'Formulário',
          sourceWhatsapp: 'WhatsApp',
          sourceWeb: 'Site',
          sourceManual: 'Manual',
          sourceApi: 'API',
          sourceVoiceCall: 'Chamada do agente de voz',
          sourceUnknown: 'Outro canal',
          evidenceUrlLabel: 'URL de evidência (opcional)',
          evidenceUrlPlaceholder: 'https://...',
          dateObtainedLabel: 'Data de obtenção',
          dateObtainedPlaceholder: 'AAAA-MM-DD',
          dateInvalid: 'Insira uma data válida no formato AAAA-MM-DD.',
          grantedOn: 'Concedido em {{date}} via {{source}}',
          refusedOn: 'Recusado em {{date}} via {{source}}',
          submit: 'Salvar consentimento',
          saving: 'Salvando…',
          successGranted: 'Consentimento registrado',
          successRevoked: 'Consentimento revogado',
          forbidden: 'Apenas proprietários e administradores podem gerenciar o consentimento.',
          error: 'Não foi possível salvar o consentimento. Tente novamente.',
        },
        marketingConsent: {
          title: 'Consentimento de marketing por WhatsApp',
          granted: 'Aceitou receber mensagens',
          notGranted: 'Não aceita mensagens',
          unknown: 'Não definido',
          grant: 'Conceder',
          revoke: 'Revogar',
          grantTitle: 'Conceder consentimento de marketing por WhatsApp',
          grantBody: 'Registre que este lead aceitou receber mensagens de marketing pelo WhatsApp.',
          revokeTitle: 'Revogar consentimento de marketing por WhatsApp',
          revokeBody:
            'Isso registra que o lead não aceita mais mensagens de marketing. As mensagens de WhatsApp para ele serão bloqueadas até que o consentimento seja concedido novamente.',
          sourceLabel: 'Como o consentimento foi obtido?',
          submit: 'Salvar consentimento',
          saving: 'Salvando…',
          successGranted: 'Consentimento de WhatsApp registrado',
          successRevoked: 'Consentimento de WhatsApp revogado',
          forbidden: 'Você não tem permissão para gerenciar o consentimento deste lead.',
          error: 'Não foi possível salvar o consentimento. Tente novamente.',
        },
        brand: {
          badge: 'Empresa',
        },
        tags: 'Tags',
        tagsEditor: {
          placeholder: 'Adicionar tag',
          inputLabel: 'Nova tag',
          addLabel: 'Adicionar tag',
          removeLabel: 'Remover tag {{tag}}',
          autoTagLabel: 'Tag automática {{tag}}, não editável',
          suggestionLabel: 'Adicionar tag sugerida {{tag}}',
        },
        lastContacted: 'Último contato',
        customFields: 'Campos personalizados',
        notes: 'Notas',
        activity: 'Atividade',
        linkedinPosts: {
          title: 'Atividade no LinkedIn',
          instagramTitle: 'Atividade no Instagram',
          comments: 'Comentários',
          reactions: 'Reações',
          showMore: 'Ver mais',
          showLess: 'Ver menos',
          likes: 'curtidas',
          shares: 'compartilhamentos',
          noComments: 'Sem comentários',
          noReactions: 'Sem reações',
          postedBy: 'Publicado por',
          refreshButton: 'Atualizar publicação',
          refreshing: 'Atualizando...',
          noNewPosts: 'Nenhuma nova publicação encontrada',
          refreshSuccess: 'Publicações atualizadas com sucesso',
          refreshError: 'Não foi possível atualizar as publicações',
          lastRefreshed: 'Última atualização',
          enrichCommenter: 'Enriquecer',
          enriching: 'Enriquecendo...',
          alreadyEnriched: 'Já enriquecido',
          noProfileUrl: 'URL do perfil não disponível',
          more: 'mais',
        },
        enrichmentProgress: {
          checking: 'Verificando leads existentes...',
          title: 'Enriquecendo Lead',
          completed: 'Enriquecimento Completo',
          failed: 'Enriquecimento Falhou',
          viewLead: 'Ver Lead',
          retry: 'Tentar novamente',
          missingProfileId: 'Não e possível enriquecer: identificador do perfil ausente',
        },
        enrichmentProcessing: 'Gerando analise de IA...',
        enrichProfile: 'Enriquecer Perfil',
        enrichFromUrl: 'Enriquecer a partir de URL',
        linkedinUrlPlaceholder: 'https://linkedin.com/in/...',
        instagramUrlPlaceholder: 'https://instagram.com/...',
        enrichLinkedin: 'Enriquecer a partir do LinkedIn',
        enrichInstagram: 'Enriquecer a partir do Instagram',
      },
      new: {
        title: 'Adicionar prospecto',
        subtitle: 'Novo lead',
        noFunnel: 'Nenhum template de funil ativo encontrado. Crie um template primeiro.',
        contactInfo: 'Informações de contato',
        fullName: 'Nome completo',
        fullNamePlaceholder: 'Insira o nome completo',
        contactName: 'Nome do Contato',
        contactNamePlaceholder: 'Insira o nome do contato',
        companyOrContactRequired: 'Empresa ou Nome do Contato e obrigatório para criar o lead',
        emailOrPhoneRequired: 'Email ou Telefone e obrigatório para criar o lead',
        phoneInvalid:
          'Insira um número de telefone valido com código do pais (pelo menos 10 dígitos)',
        jobTitleRequired: 'O cargo e obrigatório',
        emailAddress: 'Endereço de email',
        emailPlaceholder: 'Insira o endereço de email',
        phoneNumber: 'Número de telefone',
        phonePlaceholder: 'Insira o número de telefone',
        phoneCountryTitle: 'Selecionar pais',
        phoneCountrySearch: 'Buscar pais ou código',
        company: 'Empresa',
        companyPlaceholder: 'Insira o nome da empresa',
        companyWebsiteUrl: 'URL do site da empresa',
        companyWebsiteUrlPlaceholder: 'https://empresa.com',
        companyLinkedinUrl: 'URL do LinkedIn da empresa',
        companyLinkedinUrlPlaceholder: 'https://www.linkedin.com/company/...',
        jobTitle: 'Cargo',
        jobTitlePlaceholder: 'Insira o cargo',
        roleLabel: 'Função',
        rolePlaceholder: 'Selecionar função',
        source: 'Fonte',
        sourceUrl: 'URL do Perfil',
        sourceUrlPlaceholder: 'https://linkedin.com/in/... ou https://instagram.com/...',
        tags: 'Tags (separadas por vírgula)',
        tagsPlaceholder: 'Insira as tags',
        createError: 'Falha ao criar lead. Verifique suas informações e tente novamente.',
        duplicateError: 'Já existe um lead com este email na sua organização.',
        createLead: 'Criar lead',
        successToast: 'Lead criado',
      },
      edit: {
        title: 'Editar prospecto',
        subtitle: 'Editar lead',
        updateError: 'Falha ao atualizar lead. Verifique suas informações e tente novamente.',
        successToast: 'Lead atualizado',
      },
      contacts: {
        title: 'Contatos',
        singular: 'Contato',
        primary: 'Principal',
        add: 'Adicionar contato',
        addNew: 'Novo contato',
        remove: 'Remover',
        removeTitle: 'Remover contato',
        removeMessage: 'Tem certeza de que deseja remover',
        removeWarning:
          'Todas as informações associadas a este contato serão perdidas permanentemente e esta ação não pode ser desfeita.',
        setPrimary: 'Definir como principal',
        nameLabel: 'Nome',
        emailLabel: 'Email',
        phoneLabel: 'Telefone',
        jobTitleLabel: 'Cargo',
        profileLabel: 'Perfil',
        namePlaceholder: 'Nome do contato',
        emailPlaceholder: 'Endereço de email',
        phonePlaceholder: 'Número de telefone',
        jobTitlePlaceholder: 'Cargo',
        roleLabel: 'Função',
        rolePlaceholder: 'Selecionar função',
        roles: {
          decision_maker: 'Tomador de decisão',
          influencer: 'Influenciador',
          technical: 'Técnico',
          financial: 'Financeiro',
          end_user: 'Usuário final',
          gatekeeper: 'Porteiro',
          champion: 'Campeão',
        },
      },
      assigneePicker: {
        unassigned: 'Não atribuído',
        placeholder: 'Selecionar vendedor',
        overCapacityTitle: 'Excede capacidade',
        overCapacityWarning:
          '{{name}} já tem {{current}}/{{max}} leads atribuídos. Deseja atribuir mesmo assim?',
        assignAnyway: 'Atribuir mesmo assim',
      },
      activityTimeline: {
        created: 'Criado',
        updated: 'Atualizado',
        stageChanged: 'Etapa alterada',
        noteAdded: 'Nota adicionada',
        assigned: 'Vendedor atribuído',
        unassigned: 'Não atribuído',
        emailSent: 'Email enviado',
        titleException: 'Cargo verificado manualmente',
        activity: 'Atividade',
        noActivities: 'Sem atividades ainda',
        by: 'por',
        aiAgent: 'Agente IA',
        system: 'Sistema',
        agentExecutionStarted: 'Agente iniciado',
        agentExecutionCompleted: 'Agente concluído',
        agentExecutionFailed: 'Agente falhou',
        chatToolCompleted: 'Ação de chat IA concluída',
        voiceCallStarted: 'Ligação iniciada',
        voiceCallCompleted: 'Ligação concluída',
        voiceCallSkippedNoCredits: 'Ligação não realizada: sem créditos',
        voiceCallStatusChanged: 'Status da ligação atualizado',
        voiceCallAnalyzed: 'Ligação analisada',
        voiceMessageSent: 'Mensagem de voz enviada',
        autoCallCompleted: 'Ligação automática concluída',
        leadEnriched: 'Lead enriquecido',
        automationSkipped: 'Etapa ignorada',
        enrichmentCompleted: 'Enriquecimento concluído',
        showAutomation: 'Ver automação',
        loadMore: 'Carregar mais',
        viaChat: 'via chat',
      },
      analysis: {
        hot: 'Quente',
        warm: 'Morno',
        cold: 'Frio',
        lead: 'Lead',
        aiAnalysis: 'Analise IA',
        sentiment: 'Sentimento',
        intent: 'Intenção',
        industry: 'Indústria',
        size: 'Tamanho',
        keyInsights: 'Insights principais',
      },
      instagramContent: {
        title: 'Conteúdo do Instagram',
        posts: 'Publicações',
        reels: 'Reels',
        analysis: 'Analise',
        postBadge: 'Publicação',
        reelBadge: 'Reel',
        transcript: 'Transcrição',
        comments: 'comentários',
        noCaption: 'Esta publicação não tem legenda',
        open: 'Abrir',
        previous: 'Anterior',
        next: 'Próximo',
        refresh: 'Atualizar',
        refreshing: 'Atualizando…',
        refreshUpdated: 'Conteúdo atualizado',
        refreshNoChanges: 'Sem conteúdo novo',
        refreshError: 'Não foi possível atualizar',
        clientProfile: 'Os clientes dele',
        evidence: 'Evidência',
        priceSignals: 'Sinais de preço',
        confidence: {
          high: 'Bem fundamentado',
          medium: 'Parcialmente fundamentado',
          low: 'Evidência fraca',
        },
        contentIdeas: 'Ideias de conteúdo',
        proposals: 'O que podemos oferecer',
      },
      enrichment: {
        title: 'Enriquecimento do Lead',
        brandAnalysisTitle: 'Análise da Marca',
        interestsAnalysisTitle: 'Analise de Interesses',
        interestsSummary: 'Interesses',
        interestTopics: 'Temas recorrentes',
        engagementLevel: 'Nível de Engajamento',
        interestScoreClear: 'Interesses claros',
        interestScorePartial: 'Interesses parcialmente claros',
        interestScoreUnclear: 'Interesses pouco claros',
        contentAffinityScore: 'Afinidade de Conteúdo',
        postingActivityScore: 'Atividade de Publicação',
        audienceReachScore: 'Alcance de Audiência',
        audienceInteractionScore: 'Interação da Audiência',
        signalGap: {
          title: 'Sem pontuação',
          private_profile:
            'Esta conta do Instagram e privada, portanto não há conteúdo público para analisar. Atribuir uma pontuação seria inventa-la.',
          no_public_content:
            'Esta conta do Instagram não tem biografia pública nem publicações legíveis, portanto não há base para uma analise.',
        },
        brandBio: 'Biografia',
        brandNiche: 'Nicho',
        brandInsight: 'Análise da Marca',
        contentType: 'Tipo de Conteúdo',
        brandCapacity: 'Capacidade da Marca',
        brandFocusScore: 'Foco da Marca',
        contentQualityScore: 'Qualidade do Conteúdo',
        digitalPresenceScore: 'Presença Digital',
        marketPositionScore: 'Posição de Mercado',
        brandFitHigh: 'Marca com Alto Encaixe',
        brandFitMedium: 'Marca com Encaixe Médio',
        brandFitLow: 'Marca com Baixo Encaixe',
        highPotential: 'Alto Potencial',
        mediumPotential: 'Potencial Médio',
        lowPotential: 'Baixo Potencial',
        company: 'Empresa',
        location: 'Localização',
        followers: 'Seguidores',
        industry: 'Indústria',
        economicCapacity: 'Capacidade Econômica',
        scoreBreakdown: 'Detalhamento da Pontuação',
        painPointsScore: 'Pontos de Dor',
        economicScore: 'Capacidade Econômica',
        investmentScore: 'Mentalidade de Investimento',
        urgencyScore: 'Urgência',
        aiInsight: 'Insight IA',
        painPoints: 'Pontos de Dor',
        enrichedLabel: 'Enriquecido',
        capacityHigh: 'Alta',
        capacityMedium: 'Média',
        capacityLow: 'Baixa',
        sentimentVeryPositive: 'Muito Positivo',
        sentimentPositive: 'Positivo',
        sentimentNegative: 'Negativo',
        sentimentVeryNegative: 'Muito Negativo',
        sentimentNeutral: 'Neutro',
        qualificationHot: 'Quente',
        qualificationWarm: 'Morno',
        qualificationCold: 'Frio',
        qualificationUnqualified: 'Não qualificado',
        qualificationUnknown: 'Desconhecido',
        potentialLabel: 'Potencial',
        method: {
          direct_apify: 'Perfil social',
          web_search: 'Pesquisa na web',
          other: 'Automático',
        },
        translating: 'Traduzindo...',
      },
      companyProfile: {
        title: 'Perfil da Empresa',
        industry: 'Indústria',
        employees: 'Funcionários',
        followers: 'Seguidores',
        website: 'Site',
        headquarters: 'Sede',
        founded: 'Fundada',
        specialities: 'Especialidades',
        description: 'Descrição',
        scrapedLabel: 'Extraído',
        highPotential: 'Alto Potencial',
        mediumPotential: 'Potencial Médio',
        lowPotential: 'Baixo Potencial',
        capacity: 'Capacidade da Empresa',
        capacityHigh: 'Alta',
        capacityMedium: 'Média',
        capacityLow: 'Baixa',
        scoreBreakdown: 'Detalhamento da Pontuação da Empresa',
        industryFit: 'Afinidade da Indústria',
        companySizeGrowth: 'Tamanho e Crescimento',
        digitalPresence: 'Presença Digital',
        marketPosition: 'Posição de Mercado',
        aiAnalysis: 'Analise IA da Empresa',
      },
      notes: {
        placeholder: 'Adicionar uma nota...',
        addNote: 'Adicionar nota',
        noNotes: 'Sem notas ainda',
        aiAgent: 'Agente IA',
      },
      pipelineSummary: {
        title: 'Visão geral do pipeline',
        loading: 'Carregando dados do pipeline...',
        noLeads: 'Sem leads no pipeline ainda',
        total: 'total',
        bySource: 'Por fonte',
      },
      stageMove: {
        title: 'Mover para etapa',
      },
      viewMode: {
        myBoard: 'Meu Quadro',
        teamBoard: 'Quadro da Equipe',
        readOnly: 'Somente leitura',
      },
      webSearch: {
        searchContact: 'Buscar Contato (Web)',
        searching: 'Buscando na web...',
        scraping: 'Visitando páginas...',
        extracting: 'Extraindo contatos...',
        viewResults: 'Ver Resultados',
        title: 'Busca Web',
        resultsTitle: 'Resultados da Busca',
        foundSummary: '{{count}} contato(s) encontrado(s)',
        reviewingLinks: 'Revisando links',
        currentlyVisiting: 'Visitando',
        confirmSave: 'Confirmar e Salvar',
        discard: 'Descartar',
        emailsFound: 'Emails encontrados',
        phonesFound: 'Telefones encontrados',
        noResults: 'Nenhum contato encontrado',
        noResultsDescription:
          'Buscamos em múltiplas páginas mas não conseguimos encontrar informações de contato para este lead.',
        urlsVisited: 'URLs visitadas',
        cancelled: 'Busca cancelada',
        cancelledDescription: 'A busca web foi cancelada.',
        tryAgain: 'Tentar Novamente',
        cancel: 'Cancelar',
        requiresCompany: 'O lead deve ter um nome de empresa para buscar.',
        sourceTypes: {
          official_website: 'Site Oficial',
          contact_page: 'Página de Contato',
          about_page: 'Página Sobre',
          social_network: 'Rede Social',
          third_party_directory: 'Diretório',
          other: 'Outro',
        },
        confidence: {
          high: 'Alta',
          medium: 'Média',
          low: 'Baixa',
        },
        contactType: {
          email: 'Email',
          phone: 'Telefone',
        },
        confirmModal: {
          title: 'Salvar Contatos',
          message: 'Os contatos selecionados serão salvos neste lead.',
        },
        successToast: 'Contatos salvos com sucesso',
        errorToast: 'Busca web falhou',
        sourceUrl: 'Fonte',
        selectedCount: '{{count}} selecionado(s)',
        existingWarning:
          'Alguns tipos selecionados já existem neste lead e não serão sobrescritos.',
        overwriteTitle: 'Sobrescrever dados existentes?',
        overwriteDescription:
          'O {{fields}} existente neste lead será substituído pelos novos valores.',
        overwriteConfirm: 'Sim, sobrescrever',
        emailLabel: 'email',
        phoneLabel: 'telefone',
      },
      callResults: {
        sectionTitle: 'Resultados de chamadas',
        loading: 'Carregando chamadas…',
        noCalls: 'Sem chamadas ainda',
        followUpScheduled: 'Chamada de acompanhamento agendada',
        call: 'Chamada',
        viewCallDetail: 'Ver detalhe da chamada',
        structuredSummary: 'Resumo estruturado da chamada',
        interestLevel: 'Interesse',
        interestLevel_hot: 'Quente',
        interestLevel_warm: 'Morno',
        interestLevel_cold: 'Frio',
        closingProbability: 'Probabilidade de fechamento',
        fields: {
          resumen: 'Resumo',
          dolor_principal: 'Principal dor',
          necesidad: 'Necessidade',
          presupuesto: 'Orçamento',
          timeline_decision: 'Prazo de decisão',
          nivel_interes: 'Nível de interesse',
          probabilidad_cierre: 'Probabilidade de fechamento',
          propuesta_sugerida: 'Proposta sugerida',
          proximos_pasos: 'Próximos passos',
        },
        edit: {
          action: 'Editar',
          title: 'Editar resumo da chamada',
          save: 'Salvar',
          saving: 'Salvando…',
          cancel: 'Cancelar',
          interestLabel: 'Nível de interesse',
          probabilityLabel: 'Probabilidade de fechamento (0-100)',
          probabilityInvalid: 'Insira um número entre 0 e 100',
          interestInvalid: 'Selecione um nível de interesse válido',
          success: 'Resumo da chamada atualizado',
          forbidden: 'Você não tem permissão para editar esta chamada',
          error: 'Não foi possível atualizar o resumo da chamada',
        },
        reprocess: {
          action: 'Reprocessar análise',
          pending: 'Reanalisando…',
          success: 'Reprocessamento da análise iniciado',
          forbidden: 'Você não tem permissão para reprocessar esta chamada',
          error: 'Não foi possível reprocessar a análise',
        },
      },
    },
    agents: {
      generalContext: {
        title: 'Contexto geral',
        helper:
          'Estes dados compartilhados da empresa orientam os agentes que não tem sua própria personalização.',
        empty: 'Ainda não há contexto geral.',
        industry: 'Setor',
        product: 'Produto',
        salesMotion: 'Modelo de vendas',
        targetAudience: 'Público-alvo',
        tone: 'Tom',
        website: 'Site',
        languages: 'Idiomas',
      },
      insights: {
        title: 'Insights de Agentes IA',
        runAll: 'Executar tudo',
        cancel: 'Cancelar',
        cancelPipeline: 'Cancelar pipeline',
        generating: 'Gerando analise de IA...',
        qualification: 'Qualificação',
        enrichment: 'Enriquecimento',
        sentiment: 'Sentimento',
        followUp: 'Acompanhamento',
        statusNotRun: 'Não executado',
        statusDone: 'Concluído',
        statusRuleBased: 'Baseado em regras',
        ruleBasedHint: 'A IA não estava disponível, então este resultado foi calculado por regras.',
        statusRunning: 'Em execução',
        statusQueued: 'Na fila',
        statusFailed: 'Falhou',
        statusCancelled: 'Cancelado',
        noResults: 'Ainda sem resultados. Execute este agente para analisar o lead.',
        confidence: 'de confiança',
        rerun: 'Executar {{agent}} novamente',
        enrichmentFieldsFound: '{{count}} dados encontrados',
        enrichmentNothingFound: 'Nenhum dado novo foi encontrado para este lead.',
        enrichmentField: {
          industry: 'Setor',
          jobTitle: 'Cargo',
          companySize: 'Tamanho da empresa',
          location: 'Localização',
          website: 'Site',
          revenue: 'Receita',
          linkedinUrl: 'LinkedIn',
          timezone: 'Fuso horário',
          technologies: 'Tecnologias',
        },
        sentimentNoSignals:
          'Ainda não há sinais para analisar: o lead não tem mensagens nem interações, por isso a confiança é baixa.',
        risks: 'Riscos',
        opportunities: 'Oportunidades',
        followUpNoSteps: 'Nenhuma etapa de acompanhamento foi planejada para este lead.',
        qualificationScoreLabel: 'Qualificação IA',
        qualificationScoreA11y: 'Qualificação IA: {{score}} de 100',
        channel: {
          email: 'E-mail',
          call: 'Ligação',
          whatsapp: 'WhatsApp',
          meeting: 'Reunião',
        },
        urgency: {
          immediate: 'Imediata',
          this_week: 'Esta semana',
          next_week: 'Próxima semana',
          nurture: 'Nutrir',
        },
      },
      status: {
        paused: 'Pausado',
        processing: 'Processando',
        active: 'Ativo',
        queued: '{{count}} na fila',
        executionsToday: 'Execuções hoje',
        successful: 'Bem-sucedidas',
        successRate: 'Taxa de sucesso',
      },
      activityFeed: {
        title: 'Atividade recente',
        emptyTitle: 'Sem atividade de agentes ainda',
        emptyDescription: 'As execuções dos agentes aparecerão aqui quando acionadas',
        showToolCalls: 'Mostrar chamadas de ferramentas',
        hideToolCalls: 'Ocultar chamadas de ferramentas',
        viaChat: 'via chat',
      },
      usage: {
        title: 'Uso do plano',
        percentUsed: '{{percent}}% do seu plano usado este mês',
        warningBanner: 'Você usou {{percent}}% do seu plano. Faça upgrade antes que acabe.',
        criticalBanner:
          'Você usou {{percent}}% do seu plano. Seus agentes serão pausados ao chegar a 100%.',
        upgradeCta: 'Ver planos',
        unavailable: 'O uso do plano não está disponível no momento.',
        runs: 'execuções',
      },
      config: {
        title: 'Configuração de agentes',
        tiers: {
          fast: 'Rápido',
          balanced: 'Equilibrado',
          premium: 'Premium',
        },
      },
      customInstructions: {
        title: 'Instruções personalizadas',
        hint: 'Adicione suas próprias instruções para estender o comportamento base do agente...',
        placeholder: 'ex., Sempre priorizar leads da indústria de saúde...',
        hidePreview: 'Ocultar preview',
        showPreview: 'Preview do prompt',
        saving: 'Salvando...',
        save: 'Salvar',
        previewTitle: 'Preview do prompt composto',
        loadingPreview: 'Carregando preview...',
        noPreview: 'Sem preview disponível',
        aiGenerate: 'Gerar com IA',
        aiEnrich: 'Melhorar com IA',
        aiGenerating: 'Gerando...',
        aiError: 'A sugestão de IA falhou. Tente novamente.',
        aiUndo: 'Desfazer sugestão de IA',
      },
      customization: {
        configure: 'Configurar {{name}}',
        skillsTab: 'Habilidades',
        instructionsTab: 'Instruções',
        skillsDescription:
          'Ative habilidades predefinidas ou crie personalizadas para adaptar o agente.',
        createCustomSkill: '+ Criar habilidade personalizada',
        yourCustomSkills: 'SUAS HABILIDADES PERSONALIZADAS',
        deleteSkillTitle: 'Excluir habilidade',
        deleteSkillMessage: 'Tem certeza que deseja excluir esta habilidade personalizada?',
        noSkills: 'Nenhuma habilidade disponível para este tipo de agente.',
      },
      execution: {
        noSelection: 'Nenhuma execução selecionada',
        lead: 'Lead',
        timing: 'Tempo',
        model: 'Modelo',
        error: 'Erro',
        reasoning: 'Raciocínio',
        name: 'Nome',
        email: 'Email',
        started: 'Iniciado',
        completed: 'Concluído',
        duration: 'Duração',
        tokens: 'Tokens',
        cost: 'Custo',
        cancelExecution: 'Cancelar execução',
        toolActions: 'Ações de ferramentas',
      },
      skills: {
        custom: 'Personalizado',
        autoExec: 'Execução automática',
        approval: 'Requer aprovação',
      },
      toolActions: {
        noActions: 'Ainda não há ações de ferramentas.',
        approve: 'Aprovar',
        reject: 'Rejeitar',
      },
      createSkill: {
        createTitle: 'Criar habilidade personalizada',
        editTitle: 'Editar habilidade',
        nameLabel: 'Nome',
        namePlaceholder: 'ex., Conformidade de saúde',
        categoryLabel: 'Categoria',
        categoryPlaceholder: 'ex., indústria, comportamento, conformidade',
        descriptionLabel: 'Descrição',
        descriptionPlaceholder: 'Descrição curta exibida na lista de habilidades...',
        promptLabel: 'Trecho de prompt',
        promptHint: 'A instrução real injetada no prompt de IA quando esta habilidade esta ativa.',
        promptPlaceholder:
          'ex., Ao analisar leads, verificar indicadores de conformidade da indústria de saúde...',
        executionModeLabel: 'Modo de execução',
        toolsLabel: 'Ferramentas',
        toolsHint: 'Ferramentas que esta habilidade pode usar durante a execução.',
        maxToolCallsLabel: 'Máximo de chamadas de ferramentas',
        saving: 'Salvando...',
        createSkill: 'Criar habilidade',
        saveChanges: 'Salvar alterações',
      },
    },
    settings: {
      title: 'Configurações',
      subtitle: 'Gerencie sua conta, organização e preferências',
      sections: {
        profile: 'Perfil',
        organization: 'Organização',
        team: 'Gestão de equipe',
        notifications: 'Notificações',
        appearance: 'Aparência',
      },
      nav: {
        profile: 'Perfil',
        organization: 'Organização',
        businessContext: 'Contexto do negócio',
        team: 'Equipe',
        sellerProfiles: 'Perfis de Vendedor',
        leadAssignment: 'Atribuição de Leads',
        jobTitles: 'Cargos',
        notifications: 'Notificações',
        appearance: 'Aparência',
        analytics: 'Análise',
        boardVisibility: 'Visibilidade do Quadro',
        businessHours: 'Horário comercial',
        voiceCalling: 'Chamadas de Voz',
        regulatoryDocs: 'Documentos Regulatórios',
        billing: 'Faturamento',
        account: 'Conta',
      },
      billing: {
        title: 'Faturamento e Plano',
        currentPlan: 'Plano atual',
        noPlan: 'Sem plano ativo',
        noStatus: 'Desconhecido',
        finalizing: 'Finalizando sua atualização…',
        paymentIncomplete:
          'O pagamento não foi concluído. Você pode tentar novamente agora; se for processado depois, será atualizado automaticamente.',
        pendingSelected: 'Você selecionou',
        completePayment: 'Concluir pagamento',
        monthly: 'Mensal',
        yearly: 'Anual',
        selectPlan: 'Selecionar',
        currentPlanCta: 'Plano atual',
        contactSales: 'Falar com vendas',
        currencyNote: 'Moeda do pagamento:',
        checkoutUnavailable:
          'O checkout não está disponível para esta organização ou plano no momento.',
        checkoutUnavailableCta: 'Indisponível',
      },
      sellerProfiles: {
        title: 'Perfis de Vendedor',
        loading: 'Carregando perfis...',
        noMembers: 'Nenhum membro da equipe encontrado.',
        noProfile: 'Sem perfil',
        createProfile: 'Criar Perfil',
        editProfile: 'Editar Perfil',
        deleteProfile: 'Excluir Perfil',
        deleteConfirmTitle: 'Excluir Perfil de Vendedor',
        deleteConfirmMessage:
          'Tem certeza de que deseja excluir o perfil de {{name}}? Esta ação não pode ser desfeita.',
        sellerType: 'Tipo de Vendedor',
        sellerTypePlaceholder: 'Selecionar tipo',
        status: 'Status',
        statusPlaceholder: 'Selecionar status',
        maxLeads: 'Max. Leads',
        maxLeadsPlaceholder: 'ex., 50',
        bio: 'Descrição',
        bioPlaceholder: 'Breve descrição...',
        phone: 'Telefone',
        phonePlaceholder: 'ex., +55 11 9 1234-5678',
        industries: 'Indústrias',
        noIndustries: 'Nenhuma indústria disponível',
        saveProfile: 'Salvar Perfil',
        saveError: 'Falha ao salvar perfil. Tente novamente.',
        deleteError: 'Falha ao excluir perfil. Tente novamente.',
        industriesTitle: 'Indústrias',
        addIndustry: 'Adicionar Indústria',
        industryPlaceholder: 'ex., SaaS, FinTech',
        noIndustriesList: 'Nenhuma indústria configurada ainda.',
        deleteIndustryTitle: 'Excluir Indústria',
        deleteIndustryMessage: 'Tem certeza de que deseja excluir esta indústria?',
        deleteIndustryError: 'Não e possível excluir uma indústria padrão.',
        addIndustryError: 'Falha ao adicionar indústria. Tente novamente.',
      },
      profile: {
        title: 'Informações do perfil',
        fullName: 'Nome completo',
        fullNamePlaceholder: 'Insira seu nome completo',
        email: 'Email',
        successMessage: 'Perfil atualizado com sucesso.',
        errorMessage: 'Falha ao atualizar perfil. Tente novamente.',
      },
      organization: {
        title: 'Configurações da organização',
        nameLabel: 'Nome da organização',
        namePlaceholder: 'Nome da organização',
        yourRole: 'Seu cargo',
        created: 'Criado em',
        ownerOnly: 'Apenas o proprietário da organização pode editar estas configurações.',
        successMessage: 'Organização atualizada com sucesso.',
        errorMessage: 'Falha ao atualizar. Tente novamente.',
        timezoneLabel: 'Fuso horário da organização',
        timezoneHint:
          'Usado para agendamentos no calendário e horários de eventos quando um lead não tem fuso horário próprio.',
        useDeviceTimezone: 'Usar o fuso horário do meu dispositivo',
        timezoneNotSet: 'Não definido (as chamadas usam America/Bogota)',
        timezoneUnsetHint:
          'Nenhum fuso horário escolhido ainda. Chamadas e agendamentos usam America/Bogota até você escolher um.',
      },
      businessContext: {
        title: 'Contexto do negócio',
        subtitle:
          'O contexto compartilhado que seus agentes de IA usam para qualificar, enriquecer e conversar com seus leads. Coletado durante o onboarding — atualize aqui sempre que seu negócio mudar.',
        websiteLabel: 'Site (opcional)',
        websitePlaceholder: 'https://suaempresa.com',
        languagesLabel: 'Idiomas (opcional)',
        languagesPlaceholder: 'Português, Inglês',
        languagesHelper: 'Separe varios idiomas com vírgulas.',
        savedSuccess: 'Contexto do negócio atualizado com sucesso.',
        saveError: 'Falha ao atualizar. Tente novamente.',
        readOnlyNotice:
          'Apenas proprietários e administradores da organização podem editar o contexto do negócio.',
        regenerateOfferTitle: 'Contexto salvo',
        regenerateOfferBody:
          'Sua configuração de IA foi gerada com o contexto anterior. Regenere o plano para aplicar o contexto atualizado aos seus agentes.',
        regenerateCta: 'Regenerar plano de IA',
        regenerateLater: 'Agora não',
        appliedSuccess: 'Plano de IA aplicado com seu contexto atualizado.',
        products: {
          title: 'Catálogo de produtos',
          subtitle:
            'Os produtos e serviços que seus agentes podem mencionar em uma conversa. Independente do resumo acima.',
          empty:
            'Ainda não há produtos. Adicione o primeiro para que seus agentes saibam o que você vende.',
          add: 'Adicionar produto',
          edit: 'Editar produto',
          remove: 'Remover produto',
          save: 'Salvar produto',
          cancel: 'Cancelar edição',
          saveError: 'Não foi possível salvar a alteração no catálogo. Tente novamente.',
        },
      },
      team: {
        title: 'Membros da equipe',
        loading: 'Carregando equipe...',
        noMembers: 'Nenhum membro encontrado.',
        pendingInvites: 'Convites pendentes',
        noPending: 'Sem convites pendentes.',
        inviteSent: 'Convite enviado',
        inviteMember: 'Convidar membro',
        emailPlaceholder: 'colega@empresa.com',
        role: 'Cargo',
        invited: 'Convidado',
        seatUsage: 'Assentos usados: {{current}}/{{max}}',
        seatLimitTitle: 'Limite de assentos atingido',
        seatLimitMessage:
          'Você atingiu o limite de assentos do seu plano atual. Faça upgrade do plano para convidar mais membros.',
        removeMember: {
          title: 'Remover membro',
          message: 'Remover {{name}} da equipe? Esta ação não pode ser desfeita.',
        },
        revokeInvite: {
          title: 'Revogar convite',
          message: 'Revogar o convite para {{email}}?',
        },
      },
      appearance: {
        title: 'Aparência',
        language: 'Idioma',
        theme: 'Tema',
        dark: 'Escuro',
        lightComingSoon: 'Claro (Em breve)',
        languages: {
          english: 'English',
          spanish: 'Espanol',
          portuguese: 'Português',
        },
      },
      analytics: {
        title: 'Análise',
        toggleLabel: 'Análise de produto',
        toggleHint:
          'Permite o envio de análises de uso anônimas para nos ajudar a melhorar o produto. Quando desativado, nenhum evento de análise é enviado.',
      },
      businessHours: {
        title: 'Horário comercial',
        description:
          'Os dias e horários em que sua equipe pode se reunir. Quando ativado, os agentes só oferecem e agendam reuniões dentro dessas janelas.',
        enabledLabel: 'Aplicar horário comercial',
        enabledHint: 'Os agentes não oferecerão nem agendarão reuniões fora das janelas abaixo.',
        disabledHint:
          'Desativado: os agentes podem propor qualquer dia e horário com a agenda livre.',
        daysLabel: 'Dias úteis',
        startLabel: 'Abre às',
        endLabel: 'Fecha às',
        timezoneHint: 'Os horários são lidos no fuso horário da organização:',
        timezoneNotSet: 'não definido (America/Bogota)',
        perDayNotice:
          'Estes horários estão configurados por dia ou com várias janelas por dia. Alterar aqui os dias ou horários sobrescreveria essa configuração, por isso ela é mantida como está; você ainda pode ativar ou desativar o horário comercial e editar as datas fechadas.',
        blackoutLabel: 'Datas fechadas',
        blackoutHint: 'Feriados ou fechamentos. Nada é agendado nessas datas.',
        saveSuccess: 'Horário comercial atualizado',
        saveError: 'Não foi possível atualizar o horário comercial',
        days: {
          mon: 'Seg',
          tue: 'Ter',
          wed: 'Qua',
          thu: 'Qui',
          fri: 'Sex',
          sat: 'Sáb',
          sun: 'Dom',
        },
        errors: {
          noDays: 'Selecione pelo menos um dia útil ou desative o horário comercial.',
          endBeforeStart: 'O horário de fechamento deve ser posterior ao de abertura.',
          perDayHours:
            'Os dias ou horários não podem ser alterados aqui porque o horário está configurado por dia. Restaure os valores originais para salvar.',
        },
      },
      boardVisibility: {
        title: 'Visibilidade do Quadro',
        description: 'Escolha como os leads aparecem no quadro do funil',
        individual: 'Individual',
        individualDesc: 'Cada vendedor vê apenas seus próprios leads atribuídos',
        shared: 'Compartilhado',
        sharedDesc: 'Todos os membros da equipe podem ver todos os leads no quadro',
        hybrid: 'Híbrido',
        hybridDesc:
          'Vendedores têm seu próprio quadro e acesso a uma visão compartilhada da equipe',
        saveSuccess: 'Visibilidade do quadro atualizada',
        saveError: 'Falha ao atualizar a visibilidade do quadro',
        loading: 'Carregando...',
      },
      voiceCalling: {
        title: 'Chamadas de Voz',
        description: 'Configure um modelo de voz e a regra de chamada automática para sua equipe.',
        restricted: 'Apenas administradores e gerentes podem configurar chamadas de voz.',
        newTemplate: 'Novo Modelo',
      },
      account: {
        title: 'Conta',
        preamble: 'Zona de Perigo',
        description:
          'Saia da sua conta neste dispositivo. Você precisará fazer login novamente para acessar o aplicativo.',
        signOut: 'Sair',
        signOutLoading: 'Saindo...',
      },
      leadAssignment: {
        title: 'Atribuição de Leads',
        description:
          'Configure como os leads recebidos são distribuídos automaticamente entre a equipe de vendas.',
        loading: 'Carregando configurações de atribuição...',
        enableAutoAssign: 'Habilitar atribuição automática',
        enableAutoAssignDesc:
          'Atribui automaticamente novos leads aos vendedores com base na estratégia selecionada.',
        strategyLabel: 'Estratégia de atribuição',
        strategy: {
          round_robin: 'Rotação equitativa',
          round_robin_desc: 'Os leads são distribuídos em rotação entre os vendedores ativos.',
          load_based: 'Por carga de trabalho',
          load_based_desc: 'Os leads vão para o vendedor com menos leads ativos.',
          manual_only: 'Somente manual',
          manual_only_desc: 'Sem atribuição automática. Os leads devem ser atribuídos manualmente.',
        },
        maxLeadsPerUser: 'Máximo de leads por vendedor',
        maxLeadsPerUserPlaceholder: 'Sem limite',
        assigneePool: 'Grupo de atribuição',
        assigneePoolDesc: 'Selecione quais membros da equipe participam da atribuição automática.',
        noMembers: 'Nenhum membro da equipe encontrado.',
        selected: 'No grupo',
        saveError: 'Não foi possível salvar a configuração. Tente novamente.',
      },
      jobTitles: {
        title: 'Filtros de cargos',
        description:
          'Quando habilitado, os leads são qualificados automaticamente com base no cargo. Leads não qualificados não podem avançar de etapa.',
        enableFilter: 'Habilitar filtro por cargo',
        enableFilterDescription:
          'Quando desabilitado, todos os leads são tratados como qualificados independente do cargo.',
        addTitle: 'Adicionar cargo',
        removeTitle: 'Remover cargo',
        titlePlaceholder: 'ex., CEO, CTO, VP Vendas',
        countryPlaceholder: 'Pais (opcional)',
        industryPlaceholder: 'Indústria (opcional)',
        noTitles: 'Nenhum filtro de cargo configurado ainda.',
        synonyms: 'Sinônimos',
        addSynonym: 'Adicionar sinônimo',
        removeSynonym: 'Remover sinônimo',
        removeConfirm:
          'Tem certeza de que deseja remover este item? Esta ação não pode ser desfeita.',
        addError: 'Não foi possível salvar. Tente novamente.',
        synonymPlaceholder: 'ex., Diretor Executivo',
        canonicalPlaceholder: 'Mapeia para (ex., CEO)',
        noSynonyms: 'Nenhum sinônimo configurado ainda.',
      },
      notifications: {
        title: 'Preferências de notificações',
        email: {
          label: 'Notificações por email',
          description:
            'Receba atualizações sobre leads, atribuições e atividade da equipe por email',
        },
        push: {
          label: 'Notificações push',
          description: 'Receba alertas em tempo real no seu dispositivo para eventos importantes',
        },
        inApp: {
          label: 'Notificações no app',
          description: 'Exiba badges e banners de notificações dentro do app',
        },
        digest: {
          label: 'Resumo semanal',
          description: 'Receba um resumo semanal da atividade do seu pipeline',
        },
        info: 'As preferências de notificações são salvas automaticamente.',
        updateError: 'Não foi possível salvar suas preferências de notificações. Tente novamente.',
      },
    },
    notifications: {
      title: 'Notificações',
      markAllRead: 'Marcar tudo como lido',
      empty: 'Nenhuma notificação ainda',
      sectionToday: 'Hoje',
      sectionYesterday: 'Ontem',
      sectionEarlier: 'Anteriores',
      emailDigest: 'Frequência de email',
      categories: {
        lead_activity: 'Atividade de leads',
        agent_automation: 'Agentes e automação',
        team_organization: 'Equipe e organização',
        billing_system: 'Faturamento e sistema',
      },
      channels: {
        push: 'Notificações push',
        pushDesc: 'Alertas em tempo real no seu dispositivo',
        email: 'Notificações por email',
        emailDesc: 'Receba atualizações por email',
        inApp: 'Notificações no app',
        inAppDesc: 'Exiba notificações dentro do app',
      },
      digest: {
        immediate: 'Imediato',
        daily: 'Diário',
        weekly: 'Semanal',
      },
    },
    missionControl: {
      title: 'Centro de Missão',
      loading: 'Carregando Centro de Missão...',
      hud: {
        badge: 'Centro de Missão',
        activeCount: '{{count}} ativos',
        roomLabel: 'Sala',
        boardLabel: 'Quadro',
        showRoom: 'Mostrar vista da sala',
        showBoard: 'Mostrar vista do quadro',
        standingBy: 'Em espera...',
        processingTask: 'Processando tarefa',
        analyzing: 'Analisando...',
        errorOccurred: 'Ocorreu um erro',
      },
      chat: {
        tabs: {
          chat: 'Chat',
          activity: 'Atividade',
        },
        agent: 'Agente',
        orchestratorError:
          'Não conseguimos processar sua mensagem no momento. Se o problema persistir, entre em contato com seu administrador.',
        cancelledWithActions:
          'Interrompido. Estas ações já haviam sido executadas antes de cancelar: {{actions}}.',
        cancelledNoActions: 'Interrompido. Nenhuma alteração foi feita antes de cancelar.',
        editLast: 'Editar última mensagem',
        emptyChat: 'Envie uma mensagem para começar a conversar com este agente.',
        placeholder: 'Digite uma mensagem...',
        send: 'Enviar',
        noActivity: 'Sem atividade ainda para este agente.',
      },
      taskBoard: {
        title: 'Quadro de tarefas',
        columns: {
          queued: 'Na fila',
          running: 'Em execução',
          completed: 'Concluído',
          failed: 'Falhou',
        },
        runningCount: '{{count}} em execução',
        newTask: 'Nova tarefa',
        noTasks: 'Sem tarefas',
        noLead: 'Sem lead',
      },
      newTask: {
        title: 'Nova tarefa',
        subtitle: 'Selecione um agente para executar uma tarefa',
        creating: 'Criando...',
        createTask: 'Criar tarefa',
      },
    },
    onboarding: {
      title: 'Primeiros passos',
      complete: '{{completed}}/{{total}} concluídos',
      dismiss: 'Fechar lista de início',
      milestones: {
        createLead: 'Crie seu primeiro lead',
        moveLead: 'Mova um lead entre etapas',
        inviteMember: 'Convide um membro da equipe',
      },
    },
    onboardingWizard: {
      navigation: {
        back: 'Voltar',
        stepCounter: 'Passo',
        stepCounterOf: 'de',
        regenerate: 'Gerar plano novamente',
      },
      profile: {
        title: 'Conte-nos sobre seu negócio',
        subtitle:
          'Isso ajuda nossa IA a recomendar os agentes, habilidades e configuração de voz certos para você.',
        organizationNameLabel: 'Nome da organização',
        organizationNamePlaceholder: 'Acme Consulting',
        targetMarketLocationLabel: 'Mercado ou localização-alvo',
        targetMarketLocationPlaceholder: 'Cidade do México, Colômbia, LATAM, Sul da Flórida',
        industryLabel: 'Setor',
        industryPlaceholder: 'ex. SaaS, Imobiliário, Seguros',
        productSummaryLabel: 'O que você vende?',
        productSummaryPlaceholder: 'Descreva brevemente seu produto ou serviço',
        targetAudienceLabel: 'Qual e o seu público-alvo?',
        targetAudiencePlaceholder: 'ex. Donos de pequenas empresas, gerentes de TI',
        salesMotionLabel: 'Estratégia de vendas (opcional)',
        salesMotionPlaceholder: 'ex. Inbound, outbound, autoatendimento',
        toneLabel: 'Tom da marca (opcional)',
        tonePlaceholder: 'ex. Próximo e consultivo',
        next: 'Continuar',
      },
      products: {
        title: 'O que você vende?',
        subtitle:
          'Adicione seus produtos ou serviços manualmente ou envie uma planilha. Seus agentes de IA vão mencionar cada um pelo nome ao falar com os leads.',
        nameLabel: 'Nome do produto ou serviço',
        namePlaceholder:
          'Como seus clientes chamam? Ex.: Pão com queijo, Plano Starter, Consultoria contábil',
        descriptionLabel: 'Descrição breve (opcional)',
        descriptionPlaceholder: 'O que inclui e para quem e',
        priceRangeLabel: 'Faixa de preço (opcional)',
        priceRangePlaceholder: 'ex. USD 200-400 por mês',
        add: 'Adicionar ao catálogo',
        remove: 'Remover produto',
        edit: 'Editar produto',
        save: 'Salvar alterações',
        cancel: 'Cancelar edição',
        kind: {
          label: 'Tipo',
          product: 'Produto',
          service: 'Serviço',
        },
        aliases: {
          label: 'Outros nomes que seus clientes usam',
          placeholder: 'Ex.: pão com queijo, combo café da manhã',
          helper:
            'Separe os nomes com vírgulas ou toque em Adicionar. Seus agentes de IA também vão reconhecer esses nomes.',
          add: 'Adicionar',
          removeLabel: 'Remover o nome {{alias}}',
          limit: 'Você pode adicionar até {{max}} outros nomes.',
          rejected: {
            tooLong:
              'Cada nome pode ter até {{max}} caracteres. Encurte-o e toque em Adicionar novamente.',
            capped:
              'Você pode ter até {{max}} outros nomes. Os nomes que ficaram no campo não foram adicionados.',
            duplicate:
              'Estes nomes já estão na lista ou coincidem com o nome do produto. Edite-os ou apague-os.',
          },
          alsoKnownAs: 'também:',
        },
        naming: {
          genericWarning:
            'Este nome parece genérico. Seus agentes de IA e vendedores verão exatamente este nome, então use o que seus clientes dizem.',
          suggestion: 'Seu produto se chama «{{suggestion}}»?',
          useAsName: 'Usar como nome',
          useAsNameLabel: 'Usar «{{suggestion}}» como nome do produto',
          previewTitle: 'Como os agentes de IA vão ver',
          savedGenericNotice:
            'Este produto tem um nome genérico. Seus agentes de IA e vendedores vão vê-lo como está.',
        },
        empty: 'Nenhum produto adicionado ainda. Você pode adicionar depois nas configurações.',
        errors: {
          product_name_taken: 'Você já tem um produto com esse nome. Use um nome diferente.',
          product_name_required: 'Digite o nome do produto.',
          product_name_invalid: 'O nome do produto deve ser texto.',
          product_name_too_long: 'O nome do produto pode ter até {{max}} caracteres.',
          product_alias_invalid: 'Cada outro nome deve ser texto.',
          product_alias_too_long: 'Cada outro nome pode ter até {{max}} caracteres.',
          product_aliases_too_many: 'Você pode adicionar até {{max}} outros nomes.',
          product_description_too_long: 'A descrição pode ter até {{max}} caracteres.',
          product_price_range_too_long: 'A faixa de preço pode ter até {{max}} caracteres.',
        },
        deleteDialog: {
          title: 'Excluir «{{name}}»?',
          linkedLeadsOne:
            '{{count}} lead tem este produto como interesse; manterá o texto e verá «Produto não está mais disponível».',
          linkedLeadsOther:
            '{{count}} leads têm este produto como interesse; manterão o texto e verão «Produto não está mais disponível».',
          cancel: 'Cancelar',
          confirm: 'Excluir produto',
          linkedLeadsError:
            'Não foi possível verificar se há leads usando este produto. Você ainda pode excluí-lo.',
        },
        import: {
          divider: 'ou envie um arquivo',
          hint: 'Envie um arquivo CSV, XLS ou XLSX com as colunas Nome (obrigatória), Descrição, Faixa de preço, Apelidos (separados por vírgulas) e Tipo (produto ou serviço). Linhas sem nome são ignoradas.',
          downloadTemplate: 'Baixar modelo de exemplo',
          summary: '{{added}} produtos adicionados, {{skipped}} linhas ignoradas',
          rowLabel: 'Linha {{row}}:',
          warningsTitle: 'Importados, mas vale a pena revisar:',
          warnings: {
            generic_name: '«{{name}}» parece um nome genérico.',
            aliases_truncated:
              '«{{name}}»: alguns apelidos não foram salvos (máximo {{maxCount}}, de até {{maxLength}} caracteres).',
            unknown_kind: '«{{name}}»: não reconhecemos o tipo; foi salvo como produto.',
            unknown: 'Revise o nome desta linha.',
            generic_name_with_suggestion:
              '«{{name}}» parece um nome genérico. Chama-se «{{suggestion}}»?',
          },
          error: 'Não conseguimos ler esse arquivo. Use o modelo .xlsx e tente de novo.',
          reasons: {
            missing_name: 'nome ausente',
            name_too_long: 'o nome passa de {{max}} caracteres',
            description_too_long: 'a descrição passa de {{max}} caracteres',
            price_range_too_long: 'a faixa de preço passa de {{max}} caracteres',
            duplicate_in_file: 'repetido no arquivo',
            already_exists: 'já está no seu catálogo',
          },
        },
        next: 'Continuar',
      },
      generating: {
        title: 'Preparando sua configuração',
        subtitle:
          'Nossa IA esta analisando seu negócio para recomendar agentes, habilidades e um assistente de voz.',
        errorTitle: 'Não conseguimos gerar sua configuração',
        retry: 'Tentar novamente',
      },
      review: {
        title: 'Revise sua configuração recomendada',
        fallbackBanner:
          'Usamos nossa configuração recomendada padrão porque não conseguimos personaliza-la com confiança para o seu negócio.',
        agentsTitle: 'Agentes de IA',
        skillsTitle: 'Habilidades',
        toolsTitle: 'Ferramentas incluídas',
        voiceTitle: 'Agente de voz',
        voiceUseCase: 'Caso de uso',
        voiceSuccessCriteria: 'Critérios de sucesso',
        apply: 'Aplicar configuração',
        applying: 'Aplicando...',
      },
      success: {
        title: 'Tudo pronto!',
        subtitle: 'Seus agentes de IA e configuração foram aplicados a sua organização.',
        cta: 'Ir para Agente de IA',
      },
      modality: {
        guidedLabel: 'Formulário guiado',
        voiceLabel: 'Voz',
        voiceScaffoldTitle: 'A integração por voz esta chegando em breve',
        voiceScaffoldSubtitle:
          'Estamos construindo uma entrevista por voz para configurar sua organização. Por enquanto, continue com o formulário guiado.',
        continueGuided: 'Continuar com o formulário guiado',
      },
    },
    components: {
      errorBoundary: {
        title: 'Ops! Algo deu errado',
        description:
          'Pedimos desculpas pelo inconveniente. O erro foi reportado e será corrigido em breve.',
        devError: 'Detalhes do erro (Somente dev):',
      },
      confirmModal: {
        processing: 'Processando...',
        warningIconLabel: 'Aviso',
      },
      primitives: {
        searchPlaceholder: 'Pesquisar...',
        selectPlaceholder: 'Selecionar...',
        closeSheet: 'Fechar painel',
        close: 'Fechar',
        removeBadge: 'Remover',
        avatar: 'Avatar',
        avatarFor: 'Avatar de {{name}}',
      },
      landing: {
        aiLeadCapture: 'Captura de leads com IA',
        smartQualification: 'Qualificação inteligente',
        realTimeAnalytics: 'Analíticos em tempo real',
        aiAutomation: 'Automação com IA',
        emailConfirmed: 'Email confirmado',
        earlyAccessSecured: 'Acesso antecipado garantido',
        humanTouch: 'Toque humano',
        automated: 'Automatizado',
      },
    },
    pages: {
      login: {
        heroTitle: 'Gerencie seu pipeline de vendas',
        heroSubtitle:
          'Rastreie leads, automatize fluxos de trabalho e feche negócios mais rápido com insights de IA.',
        statRevenue: 'Receita',
        statRevenueValue: '$48.2K',
        statLeads: 'Leads ativos',
        statLeadsValue: '1.284',
        statConversion: 'Conversão',
        statConversionValue: '24,8%',
        loginFailed: 'Falha ao entrar. Tente novamente.',
        googleFailed: 'Falha com Google. Tente novamente.',
        linkedinFailed: 'Falha com LinkedIn. Tente novamente.',
        welcomeBack: 'Bem-vindo de volta',
        loginSubtitle: 'Entre na sua conta',
        continueAuth0: 'Entrar',
        noAccount: 'Não tem conta?',
        signUp: 'Cadastre-se',
      },
      signup: {
        heroTitle: 'Comece a crescer suas vendas',
        heroSubtitle: 'Junte-se a milhares de equipes usando IA para fechar negócios mais rápido.',
        getStarted: 'COMECE AGORA',
        createAccount: 'Crie sua conta',
        continueAuth0: 'Cadastre-se',
        alreadyHaveAccount: 'Já tem uma conta?',
        logIn: 'Entrar',
        signupFailed: 'Erro ao cadastrar. Tente novamente.',
        googleFailed: 'Erro com Google. Tente novamente.',
        linkedinFailed: 'Erro com LinkedIn. Tente novamente.',
        joiningOrg: 'Entrando em',
        inviteInvalid: 'Este link de convite não e mais valido.',
        inviteExpired: 'Este convite expirou.',
      },
      waitlist: {
        backToHome: 'Voltar ao início',
        earlyAccess: 'ACESSO ANTECIPADO',
        joinThe: 'Junte-se ao ',
        future: 'Futuro',
        ofSales: ' das vendas',
        subtitle: 'Seja o primeiro a experimentar a automação de vendas com IA...',
        teamsWaiting: 'equipes de vendas já esperando',
        reserveSpot: 'Reserve seu lugar',
        reserveSubtitle: 'Entre na lista de espera e avisaremos quando lançarmos',
        whatYouGet: 'O que você receberá',
        benefits: {
          earlyAccess: {
            title: 'Acesso antecipado',
            description: 'Seja o primeiro a testar novos recursos',
          },
          launchPricing: {
            title: 'Preço de lançamento',
            description: 'Garanta o preço especial de lançamento para sempre',
          },
          productUpdates: {
            title: 'Atualizações do produto',
            description: 'Receba atualizações exclusivas dos bastidores',
          },
          shapeProduct: {
            title: 'Molde o produto',
            description: 'Seu feedback influencia diretamente nosso roadmap',
          },
        },
        trustBadges: {
          noCreditCard: 'Sem cartão de crédito',
          cancelAnytime: 'Cancele quando quiser',
          prioritySupport: 'Suporte prioritário',
        },
      },
      funnel: {
        title: 'Pipeline de vendas',
        refreshTooltip: 'Atualizar',
      },
    },
    campaigns: {
      title: 'Campanhas',
      createNew: 'Nova Campanha',
      empty: {
        title: 'Sem campanhas ainda',
        description: 'Crie sua primeira campanha multicanal para alcançar seus leads',
      },
      form: {
        name: 'Nome da Campanha',
        namePlaceholder: 'Ex: Campanha Email Q1',
        description: 'Descrição',
        descriptionPlaceholder: 'Descreva o propósito desta campanha',
        channels: 'Canais',
        channelsHint: 'Selecione pelo menos um canal',
        audienceType: 'Audiência',
        manual: 'Seleção Manual',
        segment: 'Segmento por Filtros',
        schedule: 'Agendar',
        scheduleLater: 'Agendar para depois',
        create: 'Criar Campanha',
      },
      channels: {
        email: 'Email',
        sms: 'SMS',
        whatsapp: 'WhatsApp',
        voice: 'Voz',
      },
      status: {
        draft: 'Rascunho',
        active: 'Ativa',
        completed: 'Concluída',
      },
      actions: {
        activate: 'Ativar',
        complete: 'Concluir',
        edit: 'Editar',
        delete: 'Excluir',
      },
      detail: {
        leads: 'Leads',
        sent: 'Enviados',
        delivered: 'Entregues',
        failed: 'Falhos',
        createdBy: 'Criada por',
        activatedOn: 'Ativada em',
        completedOn: 'Concluída em',
      },
      segment: {
        stageFilter: 'Etapa do Funil',
        tagsFilter: 'Tags',
        sourceFilter: 'Fonte do Lead',
        dateRangeFilter: 'Faixa de Data de Criação',
        previewCount: 'leads correspondem a este filtro',
      },
      confirmActivate: 'Tem certeza de que deseja ativar esta campanha?',
      confirmDelete: 'Tem certeza de que deseja excluir esta campanha?',
    },
    whatsapp: {
      title: 'WhatsApp',
      subtitle: 'Conversas com seus leads',
      searchPlaceholder: 'Buscar conversas...',
      empty: {
        title: 'Ainda não há conversas',
        description: 'As conversas do WhatsApp aparecerão aqui quando um lead enviar mensagem.',
      },
      window: {
        open: 'Janela de 24 h aberta',
        closed: 'Janela fechada',
        useTemplate: 'Enviar mensagem de modelo',
        hint: 'A janela de 24 h fechou. Use um modelo para retomar a conversa.',
        remaining: 'restantes',
      },
      input: {
        placeholder: 'Escreva uma mensagem...',
      },
      templates: {
        title: 'Modelos de mensagem',
        sync: 'Sincronizar do Meta',
        syncing: 'Sincronizando...',
        empty: 'Nenhum modelo aprovado encontrado. Sincronize do Meta para carrega-los.',
      },
      status: {
        sent: 'Enviado',
        delivered: 'Entregue',
        read: 'Lido',
        failed: 'Falhou',
      },
    },
    linkedinSearch: {
      title: 'Busca LinkedIn',
      description: 'Crie segmentos de busca para encontrar e enriquecer perfis do LinkedIn',
      newSegment: 'Novo Segmento',
      backToSegments: 'Voltar aos segmentos',
      emptyTitle: 'Nenhum segmento de busca',
      emptyDescription: 'Crie um segmento para começar a buscar e enriquecer perfis do LinkedIn',
      statuses: {
        draft: 'Rascunho',
        running: 'Executando',
        completed: 'Concluído',
        failed: 'Falhou',
      },
      segments: {
        found: 'Encontrados',
        enriched: 'Enriquecidos',
        skipped: 'Ignorados',
        failed: 'Falhados',
        running: 'Executando...',
        execute: 'Executar',
        viewResults: 'Ver Resultados',
        deleteSegment: 'Excluir segmento',
        failedHint:
          'A busca falhou. Tente novamente — se continuar falhando, contate o suporte para verificar o acesso ao LinkedIn.',
      },
      results: {
        title: 'Resultados',
        score: 'Pontuação',
        leads: 'leads',
      },
      form: {
        title: 'Novo Segmento de Busca',
        segmentName: 'Nome do Segmento',
        segmentNamePlaceholder: 'ex. CEOs de Marketing na LATAM',
        keywords: 'Palavras-chave',
        keywordsPlaceholder: 'ex. CEO, Diretor de Marketing, Fundador',
        roleFilter: 'Filtro de Cargo / Título',
        roleFilterPlaceholder: 'ex. C-Level, VP, Diretor',
        location: 'Localização',
        locationPlaceholder: 'ex. Sao Paulo, Brasil',
        industry: 'Indústria',
        industryPlaceholder: 'ex. Marketing e Publicidade',
        companySize: 'Tamanho da Empresa',
        maxResults: 'Resultados Máximos (1-200)',
        createSegment: 'Criar Segmento',
      },
    },
    leadImport: {
      title: 'Importar Leads',
      steps: {
        upload: 'Importar Leads',
        mapping: 'Mapear Colunas',
        preview: 'Pré-visualizar Importação',
        importing: 'Importando...',
        complete: 'Importação Completa',
      },
      mappingHint: 'A IA mapeou suas colunas. Revise e ajuste se necessário.',
      maxRows: 'Máximo 5.000 linhas',
      uploadAndAnalyze: 'Enviar e Analisar',
      preview: 'Pré-visualizar',
      importNLeads: 'Importar {{n}} Leads',
      noActiveTemplate:
        'Nenhum modelo de funil ativo encontrado. Ative um modelo antes de importar.',
      done: 'Concluído',
      totalRowsPreview:
        '{{total}} linhas encontradas. Pré-visualização das primeiras {{preview}} linhas:',
      fileUpload: {
        dragAndDrop: 'Clique para buscar ou arraste e solte',
        fileTypes: 'CSV, XLS ou XLSX (max 10MB)',
        chooseFile: 'Escolher Arquivo',
        unsupportedType: 'Esse tipo de arquivo não é compatível. Use um arquivo CSV, XLS ou XLSX.',
      },
      columnMapping: {
        fileColumn: 'Coluna do Arquivo',
        crmField: 'Campo CRM',
      },
      progress: {
        title: 'Importando Leads',
        processing: 'Processando {{n}} registros...',
        doNotClose: 'Por favor não feche esta janela',
      },
      summary: {
        title: 'Importação Completa',
        imported: 'Importados',
        duplicates: 'Duplicados',
        errors: 'Erros',
        errorDetails: 'Detalhes dos Erros',
        duplicateDetails: 'Emails Duplicados',
        andMore: '...e mais {{count}}',
        rowError: 'Linha {{row}}: {{message}}',
      },
      confidence: {
        high: 'Alto',
        medium: 'Médio',
        low: 'Baixo',
      },
      downloadTemplate: 'Baixar Modelo',
      crmFields: {
        firstName: 'Nome',
        lastName: 'Sobrenome',
        email: 'E-mail',
        phone: 'Telefone',
        company: 'Empresa',
        jobTitle: 'Cargo',
        companyUrl: 'URL da Empresa',
        linkedinUrl: 'URL do LinkedIn',
        tags: 'Tags',
        skip: 'Pular',
      },
    },
    leadSources: {
      page: {
        title: 'Fontes de Leads',
        subtitle:
          'Conecte formulários e ferramentas externas para enviar leads direto ao seu funil.',
      },
      sections: {
        connected: 'Conectadas',
        available: 'Disponíveis',
      },
      empty: {
        connectedTitle: 'Ainda sem conexões',
        connectedDescription:
          'Conecte um formulário ou ferramenta abaixo para começar a receber leads automaticamente.',
      },
      status: {
        active: 'Ativa',
        paused: 'Pausada',
        archived: 'Removida',
      },
      actions: {
        create: 'Nova conexão',
        rotate: 'Rotacionar credencial',
        revoke: 'Remover conexão',
        delete: 'Excluir permanentemente',
        viewDetails: 'Ver detalhes',
        copy: 'Copiar',
        done: 'Concluído',
        cancel: 'Cancelar',
        retry: 'Tentar novamente',
      },
      readOnlyNotice:
        'Peça a um proprietário ou administrador para gerenciar as conexões. Você ainda pode ver o status e o mapeamento.',
      confirmRevoke: {
        title: 'Remover esta conexão?',
        message: 'A recepção para imediatamente. Os leads já recebidos mantem sua origem.',
      },
      confirmDelete: {
        title: 'Excluir esta conexão permanentemente?',
        message: 'Os leads já recebidos são mantidos, mas perdem a referência da conexão.',
      },
      errors: {
        loadFailed:
          'Não foi possível carregar as fontes de leads. Puxe para atualizar ou tente novamente mais tarde.',
        catalogLoadFailedTitle: 'Não foi possível carregar os provedores',
        catalogLoadFailed:
          'Algo deu errado ao carregar os provedores disponíveis. Por favor, tente novamente.',
      },
      secretModal: {
        title: 'Salve sua credencial agora',
        warning:
          'Esta e a única vez que mostraremos esse valor. Guardamos apenas um hash — se você perde-lo, rotacione para obter um novo.',
        urlLabel: 'URL de recepção',
        keyLabel: 'Chave de API',
        copied: 'Copiado para a área de transferência',
        acknowledge: 'Salvei esta credencial',
        doneDisabledHint: 'Confirme que salvou a credencial para continuar',
      },
      detail: {
        title: 'Detalhes da conexão',
        mappingTitle: 'Mapeamento de campos',
        credentialsTitle: 'Credenciais',
        errorsTitle: 'Erros recentes',
        noErrors: 'Nenhum erro reportado',
        lastErrorAt: 'Último erro',
        lastErrorCode: 'Código do erro',
        lastSubmissionAt: 'Último envio',
        rateLimitLabel: 'Limite de taxa',
      },
    },
    legal: {
      terms: {
        heading: 'Termos de serviço',
        placeholder: 'TODO: conteúdo pendente — preencher com texto jurídico antes de publicar.',
      },
      cookiePolicy: {
        heading: 'Política de cookies',
        placeholder: 'TODO: conteúdo pendente — preencher com texto jurídico antes de publicar.',
      },
      gdpr: {
        heading: 'LGPD e privacidade de dados',
        placeholder: 'TODO: conteúdo pendente — preencher com texto jurídico antes de publicar.',
      },
    },
    marketing: {
      about: {
        heading: 'Sobre nos',
        placeholder:
          'TODO: conteúdo pendente — preencher com história da empresa antes de publicar.',
      },
      press: {
        heading: 'Kit de imprensa',
        placeholder:
          'TODO: conteúdo pendente — preencher com materiais de imprensa antes de publicar.',
      },
    },
  },
};
